<template>
    <n-layout class="w-100vw h-100vh overflow-hidden" content-class="flex flex-col">
      <n-layout-header class="flex-shrink-0">
        <editor-toolbar
            :project-id="projectId"
            :initial-snap-settings="initialPreferences?.snapSettings ?? null"
            :project-name="effectiveProjectTitle"
            :current-drawing-tool="currentDrawingTool"
            :is-sketch-plane-selected="isSketchPlaneSelected"
            :shaded-sketch-contours-enabled="shadedSketchContoursEnabled"
            :operation-override="toolbarOperationOverride"
            :active-tab-type="activeEditorTabType"
            :supports-feature-commands="activeDocumentCapabilities.canExecuteFeatureCommands"
            :assembly-edit-component-disabled="assemblyEditComponentDisabled"
            :readonly-preview="versionWorkspaceStore.isReadonlyPreview"
            :onboarding-disabled="!isOnBoardingReady || isModelLoading"
            @start-onboarding="onBoarding.start()"
            @command="handleEditorCommand"
            @submit="handleOpenSubmitWorkspaceDialog"
            @export-step="handleExportStep"
            @export-offline-package="handleExportOfflinePackage"
            @export-glb="handleExportGlb"
            @import-offline-package="handleImportOfflinePackage"
            @cancel-drawing="handleDrawingCancel"
            @finish-drawing="handleDrawingFinish"
            @undo-point="handleDrawingUndoPoint"
        />
        <input
            ref="offlinePackageInputRef"
            class="hidden"
            type="file"
            accept=".puppy"
            @change="handleOfflinePackageFileChange"
        />
      </n-layout-header>

      <n-layout-content class="flex-1" content-class="flex">
        <EditorSideRail
            class="flex h-full"
            :active-key="activeSidePanel ?? ''"
            :disabled-keys="disabledSideRailKeys"
            @select="handleSideRailSelect"
        />

        <EditorSidePanelDrawer
            ref="editorSidePanelDrawerRef"
            :show="Boolean(activeSidePanel)"
            :width="editorSidePanelWidth"
            :resizing="isEditorSidePanelResizing"
            resize-aria-label="调整左侧面板宽度"
            @resize-start="handleEditorSidePanelResizePointerDown"
        >
            <VersionHistoryPanel
                v-if="activeSidePanel === 'history'"
                :records="versionWorkspaceStore.commitRecords"
                :selected-commit-id="versionWorkspaceStore.selectedCommitId"
                :loading="isVersionHistoryPanelLoading"
                :switching="versionWorkspaceStore.isVersionSwitching"
                :switching-commit-id="versionWorkspaceStore.switchingCommitId"
                :readonly-preview="versionWorkspaceStore.isReadonlyPreview"
                @select="versionWorkspaceController.selectCommit"
                @continue-edit="handleContinueEditingFromCommit"
                @refresh="handleRefreshVersionHistoryPanel"
            />
            <AssemblyBranchNodeListPanel
                v-else-if="activeSidePanel === 'branch'"
                :assembly-node-name="assemblyBranchNodeListStore.currentAssemblyNodeName"
                :nodes="assemblyBranchNodeListStore.nodes"
                :loading="isAssemblyBranchNodeListPanelLoading"
                :switching-version="assemblyBranchNodeListStore.isSwitchingVersion"
                :readonly-preview="versionWorkspaceStore.isReadonlyPreview"
                @switch-version="handleOpenSwitchNodeVersionDialog"
                @edit-responsible-branch="handleOpenResponsibleBranchWorkspace"
                @hover-node="handleAssemblyBranchNodeHover"
                @refresh="handleRefreshAssemblyBranchNodeListPanel"
            />

        </EditorSidePanelDrawer>

        <n-split
            direction="horizontal"
            :default-size="0.8"
            :min="0.3"
            :max="0.85"
            class="h-full w-full flex-1 min-w-0"
        >
          <!-- 中央视口 -->
          <template #1>
            <div class="w-full h-full relative" data-onboarding="viewport">
              <editor-viewport-canvas
                  :on-container-ready="handleViewportContainerReady"
                  :is-viewport-renderer-ready="isViewportRendererReady"
                  :drawing-hint="drawingHint"
              >
                <!-- ------ 剪裁实体覆盖层 开始------ -->
                <SketchTrimPowerOverlay
                    :viewport-container="viewportContainerRef"
                    :sketch-trim-mode="sketchTrimMode"
                    :is-workflow-active="Boolean(sketchTrimMode)"
                />
                <!-- ------ 剪裁实体覆盖层 结束------ -->
              </editor-viewport-canvas>

              <div v-if="isModelLoading" class="model-loading-overlay">
                <n-spin size="large" />
                <span class="model-loading-text">{{ modelLoadingText }}</span>
              </div>

              <ViewportToolbar />
            </div>
          </template>

          <!-- 右侧管理面板 -->
          <template #2>
            <n-tabs v-model:value="rightPanelTab" type="segment" size="small" class="h-full flex flex-col min-w-200px" data-onboarding="manager-panel">
              <n-tab-pane name="feature" tab="设计树" display-directive="show" class="h-full w-full flex flex-col overflow-auto">
                <feature-tree />
              </n-tab-pane>
              <n-tab-pane name="property" tab="属性" class="h-full w-full flex flex-col overflow-auto">
                <property-manager
                    :context="buildPropertyManagerContext()"
                    :force-confirm="propertyManagerForceConfirm"
                />
              </n-tab-pane>
              <n-tab-pane name="config" tab="配置" class="h-full w-full flex flex-col overflow-auto">
                <div></div>
              </n-tab-pane>
              <n-tab-pane name="display" tab="显示" class="h-full w-full flex flex-col overflow-auto">
                <div></div>
              </n-tab-pane>
            </n-tabs>
          </template>
        </n-split>
      </n-layout-content>

      <n-layout-footer class="flex-shrink-0" data-onboarding="status-bar">
        <!-- 零件标签栏暂时隐藏，后续可能恢复；使用 v-if 避免隐藏期间挂载组件及创建监听。 -->
        <PartTabBar
            v-if="false"
            :tabs="glbLoader.partTabsStore.tabs"
            :active-tab-id="glbLoader.displayActivePartTabId.value"
            @select="glbLoader.handlePartTabSelect"
        />
        <EditorFooter
            :selected-object-name="selectedObjectName"
            :engine-ready="isViewportRendererReady"
        />
      </n-layout-footer>
    </n-layout>

    <OnBoardingOverlay :controller="onBoarding" />

    <MeasureDialog
      v-model:show="measureDialogVisible"
      v-model:active-tool="activeMeasureTool"
      v-model:collapsed="measureDialogCollapsed"
      :project-name="projectName"
      @tool-click="handleMeasureToolClick"
      @panel-interact="resumeMeasurePick"
      @close="handleMeasureDialogClose"
      @help="handleMeasureDialogHelp"
    />

    <EditorViewportContextMenu
      :readonly-preview="versionWorkspaceStore.isReadonlyPreview"
      @deleted="handleViewportContextMenuDeleted"
      @insert-sketch="handleViewportContextMenuInsertSketch"
    />

    <SubmitWorkspaceDialog
      :show="isSubmitWorkspaceDialogVisible"
      :branch-name="currentWorkspaceBranchName"
      :submitting="isSubmittingWorkspace"
      @confirm="handleConfirmSubmitWorkspace"
      @cancel="handleCloseSubmitWorkspaceDialog"
    />

    <SwitchAssemblyChildNodeVersionDialog
      :show="isSwitchNodeVersionDialogVisible"
      :node="switchNodeVersionTargetNode"
      :commits="switchNodeVersionCommits"
      :adopted-commit-id="switchNodeVersionTargetNode?.adoptedCommitId ?? null"
      :loading="isSwitchNodeVersionCommitsLoading || assemblyBranchNodeListStore.isSwitchingVersion"
      @select-commit="handleSelectSwitchNodeVersionCommit"
      @cancel="handleCloseSwitchNodeVersionDialog"
    />

    <SmartDimensionModifyPopover
      v-if="editorSmartDimensionPopover"
      :visible="true"
      :x="editorSmartDimensionPopover.x"
      :y="editorSmartDimensionPopover.y"
      :mode="editorSmartDimensionPopover.mode"
      :initial-value="editorSmartDimensionPopover.initialValue"
      :is-angle="editorSmartDimensionPopover.isAngle"
      :decision-message="editorSmartDimensionPopover.decisionMessage"
      @confirm="handleEditorSmartDimensionConfirm"
      @cancel="handleEditorSmartDimensionCancel"
      @convert-driven="handleEditorSmartDimensionConvertDriven"
    />

    <SmartDimensionModifyPopover
      v-if="editorDimensionEditPopover"
      :visible="true"
      :x="editorDimensionEditPopover.x"
      :y="editorDimensionEditPopover.y"
      mode="input"
      :initial-value="editorDimensionEditPopover.initialValue"
      :is-angle="editorDimensionEditPopover.isAngle"
      :role-toggle-label="editorDimensionEditPopover.roleToggleLabel"
      :arc-condition-groups="editorDimensionEditPopover.arcConditionGroups"
      @confirm="handleEditorDimensionEditConfirm"
      @cancel="handleEditorDimensionEditCancel"
      @toggle-role="handleEditorDimensionToggleRole"
      @change-arc-condition="handleEditorDimensionArcConditionChange"
    />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { OnBoarding, createEditorOnBoardingConfig } from '@/onboarding';
import OnBoardingOverlay from '@/components/onboarding/OnBoardingOverlay.vue';
import { Code, ConnectError } from '@connectrpc/connect';
import * as THREE from 'three';
import type { Object3D as WebGpuObject3D } from 'three/webgpu';
import type { Subscription } from 'rxjs';
import { useRoute, useRouter } from 'vue-router';
import {NSplit, useDialog, useMessage } from 'naive-ui';
import {
  EditorPanelEventName,
  EditorPanelShowDesignTreeReason,
  EditorPanelShowPropertyReason,
  RawPointerEventType,
  createAssemblyComponentHistoryPatch,
  createAssemblyHistoryAdapter,
  createAssemblyMateDocumentHistoryEntityId,
  createAssemblyMateHistoryAdapter,
  createAssemblyMateHistoryPatch,
  createAssemblySceneHistoryStore,
  createAssemblyStructureHistoryAdapter,
  createAssemblyStructureHistoryPatch,
  createAssemblyStructureHistorySnapshot,
  createAssemblyTransformHistoryPatch,
  createChangeSet,
  createEmptyDirtyScope,
  createEmptySnapshotScope,
  createExistingHistorySnapshot,
  createMissingHistorySnapshot,
  createStepExportRequest,
  createFeatureHistoryAdapter,
  createFeatureDefinitionHistoryPatch,
  createFeatureRollbackHistoryAdapter,
  createFeatureRollbackHistoryPatch,
  createDatumAxisHistoryAdapter,
  createDatumAxisHistoryPatch,
  createDatumPlaneHistoryAdapter,
  createDatumPlaneHistoryPatch,
  createDatumPointHistoryAdapter,
  createDatumPointHistoryPatch,
  createSketchEntityReferenceHistoryAdapter,
  createSketchEntityReferenceHistoryPatch,
  createSketchEntityReferenceHistorySnapshot,
  createLinearComponentPatternHistoryAdapter,
  createLinearComponentPatternHistoryPatch,
  createLinearComponentPatternSubtreeRemovalImpactPlan,
  captureFeatureHistorySnapshotFromSummary,
  applyFeatureSnapshotToCommandHistory,
  createHistoryDomainAdapterRegistry,
  createQueuedSessionModelHistoryRuntime,
  createIndexedDBHistoryPersistence,
  createOPFSHistoryBlobWriter,
  clearIndexedDBHistoryPersistence,
  clearOPFSHistoryStorage,
  DATUM_AXIS_HISTORY_ENTITY_ID,
  DATUM_PLANE_HISTORY_ENTITY_ID,
  DATUM_POINT_HISTORY_ENTITY_ID,
  ASSEMBLY_STRUCTURE_HISTORY_ENTITY_ID,
  LINEAR_COMPONENT_PATTERN_HISTORY_ENTITY_ID,
  SKETCH_GROUP_RUNTIME_HISTORY_ADAPTER_ID,
  createSketchGroupRuntimeHistoryAdapter,
  createSketchGroupRuntimeHistoryPatch,
  createSketchHistoryAdapter,
  createSketchSolverIslandHistoryPatch,
  createSketchSolverHistoryAdapter,
  createSketchDimensionHistoryAdapter,
  createSketchDimensionSolverSupplementProvider,
  convertMToMm,
  parseSketchDimensionValueInput,
  configureSketchSolverHistoryRuntime,
  createAssemblyMateDocument,
  assertImportedDocumentCanPersist,
  isImportedDocumentFeatureKind,
  restoreSketchSolverRuntimeSnapshot,
  resolveEntityPatchOperation,
  SketchDisplayState,
  Viewer,
  ViewerMode,
  exportGlb,
  exportPuppyPackage,
  exportStep,
  deleteAssemblyChildIfExists,
  deleteProjectModelLoadResultNode,
  getAssemblyDocumentNode,
  importPuppyPackage,
  listAssemblyDocumentDirectChildNodes,
  replaceAssemblyChildVersion,
  resolvePuppyAssemblyPackageRootFile,
  resolveImportedDocumentCapabilities,
  useAddEvent,
  DEFAULT_CHAMFER_DISTANCE_M,
  DEFAULT_CHAMFER_VERTEX_DISTANCES_M,
} from '@puppycad/cad-core';
import type {
  AssemblyDocument,
  AssemblyDocumentNode,
  AssemblyStructureHistorySnapshot,
  AssemblyTransformHistoryRecord,
  AssemblyMateDocumentChangeContext,
  AssemblyMateDocumentModel,
  AssemblyComponentHistorySnapshot,
  AssemblyTransform,
  CadCommand,
  CadTransaction,
  EditorDisplayConfig,
  EditorPreferences,
  FeatureDefinitionHistorySnapshot,
  FeatureRollbackHistorySnapshot,
  EditorFeatureHistoryRecord,
  EntityPatch,
  HistoryApplyDirection,
  HistoryEntitySnapshot,
  DatumAxisHistorySnapshot,
  DatumPlaneHistorySnapshot,
  DatumPointHistorySnapshot,
  LinearComponentPatternHistorySnapshot,
  SketchEntityReferenceRecord,
  SketchDimensionSnapshot,
  SketchDimensionTransactionInput,
  SmartDimensionCommandSnapshot,
  SketchEntityHistorySnapshot,
  SketchGroupRuntimeHistoryRecord,
  SketchGroupRuntimeHistorySnapshot,
  SketchSolverIslandSnapshot,
  SketchSolverRecord,
  PuppyPackageDocumentType,
  ProjectModelLoadResult,
  ModelAssetNode,
  ResolvedPlacementTransform,
  SelectionInteractionMeta,
  WorkerGeometryStateSummary,
} from '@puppycad/cad-core';
import { storeToRefs } from 'pinia';
import { CadFileKind } from '@puppycad/proto';
import FeatureTree from '@/components/editor/FeatureTree.vue';
import PropertyManager from '@/components/editor/PropertyManager.vue';
import EditorToolbar from '@/components/editor/EditorToolbar.vue';
import PartTabBar from '@/components/editor/PartTabBar.vue';
import EditorSideRail from '@/components/editor/EditorSideRail.vue';
import EditorSidePanelDrawer from '@/components/editor/EditorSidePanelDrawer.vue';
import EditorFooter from '@/components/editor/EditorFooter.vue';
import VersionHistoryPanel from '@/components/editor/VersionHistoryPanel.vue';
import SmartDimensionModifyPopover from '@/components/editor/smart-dimension/SmartDimensionModifyPopover.vue';
import AssemblyBranchNodeListPanel from '@/components/editor/AssemblyBranchNodeListPanel.vue';
import SubmitWorkspaceDialog from '@/components/editor/SubmitWorkspaceDialog.vue';
import SwitchAssemblyChildNodeVersionDialog from '@/components/editor/SwitchAssemblyChildNodeVersionDialog.vue';
import EditorViewportCanvas from '@/views/editor/EditorViewportCanvas.vue';
import EditorViewportContextMenu from '@/views/editor/EditorViewportContextMenu.vue';
import MeasureDialog from '@/components/editor/evalution/measure/MeasureDialog.vue';
import ViewportToolbar from '@/views/editor/ViewportToolbar.vue';
import SketchTrimPowerOverlay from '@/views/editor/SketchTrimPowerOverlay.vue';
import { useEditorAssemblyWorkflow } from '@/views/editor/useEditorAssemblyWorkflow';
import {
  type AssemblyReferenceCopyPreparationHandle,
  type InsertPreparationHandle,
  type ProjectModelLoadFailureInput,
  LinearComponentPatternFeatureTreeSyncReason,
  useEditorGlbLoader,
} from '@/views/editor/useEditorGlbLoader';
import type { InsertCadSourceDescriptor } from '@/services/loaders/insert-cad-loader';
import { isImportedModelLoadAbortedError } from '@/services/loaders/imported-model-load-guard';
import {
  resolveAssemblyHistorySceneObject as resolveProjectedAssemblyHistorySceneObject,
} from '@/views/editor/assemblyHistoryProjection';
import { cancelAssemblyInsertionWorkflow } from '@/views/editor/cancel-assembly-insertion-workflow';
import { useEditorPropertyManagerContext } from '@/views/editor/useEditorPropertyManagerContext';
import { useEditorViewportInteractionSync } from '@/views/editor/useEditorViewportInteractionSync';
import { useEditorMeasureTreeSelectionSync } from '@/views/editor/useEditorMeasureTreeSelectionSync';
import {
  createGeometryReferenceWorkflowDependencies,
} from '@/views/editor/createGeometryReferenceWorkflowDependencies';
import { useEditorGeometryReferenceWorkflow } from '@/views/editor/useEditorGeometryReferenceWorkflow';
import { useEditorSketchWorkflow } from '@/views/editor/useEditorSketchWorkflow';
import { useEditorEvaluationWorkflow } from '@/views/editor/useEditorEvaluationWorkflow';
import { useEditorViewportAdapter } from '@/views/editor/useEditorViewportAdapter';
import {
  useVersionWorkspaceController,
  type VersionWorkspaceReloadContext,
} from '@/views/editor/useVersionWorkspaceController';
import { EditorInitialOpenProjectLoadCoordinator } from '@/views/editor/EditorInitialOpenProjectLoadCoordinator';
import { FeatureTreeHistoryRefreshCoordinator } from '@/views/editor/FeatureTreeHistoryRefreshCoordinator';
import { createAssemblyBranchNodeListController } from '@/views/editor/useAssemblyBranchNodeListController';
import {
  VERSION_HISTORY_PANEL_DEFAULT_WIDTH,
  resolveVersionHistoryPanelWidth,
} from '@/views/editor/resolve-version-history-panel-width';
import {
  resolveAssemblyBranchNodeHoverTarget,
  type AssemblyBranchNodeHoverTarget,
} from '@/views/editor/resolveAssemblyBranchNodeHoverTarget';
import { assertAssemblyBranchNodeReferenceTargetStableForAppend } from '@/views/editor/resolveAssemblyBranchNodeReferenceTarget';
import { resolveProjectModelDirectChildForWorkingNode } from '@/views/editor/resolveProjectModelDirectChildForWorkingNode';
import { resolveProjectModelNodeBySceneObjectIds } from '@/views/editor/resolveProjectModelNodeBySceneObjectIds';
import {reportEditorAsyncFailure as reportEditorAsyncFailureToUser} from '@/views/editor/EditorAsyncFailureReporter';
import {
  createPendingInsertionHandleRef,
  setPendingInsertionHandle,
} from '@/views/editor/insertion-handle-state';
import {
  useAssemblyBranchNodeListStore,
  useCadSessionStore,
  useProjectStore,
  useToolbarCommandStore,
  useVersionWorkspaceStore,
} from '@/stores';
import { editorPreferencesRepo } from '@/services/db';
import {
  createVersionWorkspaceService,
  type ProjectCommitSnapshot,
  type VersionWorkspaceCreationResult,
  type VersionWorkspaceRepositoryContext,
} from '@/services/version-workspace';
import {
  createAssemblyBranchNodeListService,
  decodeAssemblyChildNodeReference,
  encodeAssemblyChildNodeReference,
  type AssemblyBranchNodeListProjectContext,
  type AssemblyChildNodeBranchSummary,
  type AssemblyChildNodeCommit,
  type AssemblyChildNodeSummary,
} from '@/services/assembly-branch-node-list';
import {
  clearAssemblyDeletedSubtreePayloadStorage,
  createCadNodeTreeReloadAdapter,
  mergePreparedAppendMissingFirstLevelNodeVersionResult,
  mergePreparedSwitchFirstLevelNodeVersionResult,
  type ProjectNodeFileReadResult,
} from '@/services/project-model-loading';
import {
  createInsertComponentService,
  type InsertComponentProjectContext,
  type InsertSubAssemblyResult,
} from '@/services/insert-component';
import { createPartPackageCommitService } from '@/services/part-package-commit';
import { createAssemblyWorkspaceCommitService } from '@/services/assembly-workspace-commit';
import type { EditorSceneDebugInspectorService } from '@/services/debug/EditorSceneDebugInspectorService';
import { downloadBinaryExportResult as downloadExportResult } from '@/utils/downloadBinaryExportResult';
import {
  getCadNodeTreeSession,
} from '@/services/api/cad-session';
import {
  normalizeEditorDisplayConfig,
  createDefaultEditorDisplayConfig,
} from '@/config/editor-settings/EditorSettings';
import { useViewer } from '@/hooks/useViewer';
import { isDefaultDatumPlaneSummary, usePlaneStore } from '@/hooks/usePlane';
import {
  isIdleViewerMode as isViewerIdleMode,
  useSketchStateStore,
} from '@/hooks/useSketchState';
import { useDrawingStore } from '@/hooks/useDrawing';
import { useExtrudeFeature } from '@/hooks/useExtrudeFeature';
import { useRevolveFeature } from '@/composables/revolve/useRevolveFeature';
import { useRevolveCutFeature } from '@/composables/revolve/useRevolveCutFeature';
import { useBlendFeature } from '@/composables/blend/useBlendFeature';
import { useInsertComponent } from '@/composables/useInsertComponent';
import {
  ComponentPlacementAnchorMode,
  useComponentPlacement,
} from '@/composables/useComponentPlacement';
import { registerBlendPreviewGuideOverlay } from '@/core/features/blend/preview/registerBlendPreviewGuideOverlay';
import { EditorModelLoadingCoordinator } from '@/core/editor-loading/EditorModelLoadingCoordinator';
import { useFeatureTreeStore } from '@/hooks/useFeatureTree';
import {
  FeatureTreeDatumReferenceKind,
  type FeatureTreeAssemblyComponentDeleteActionInput,
  type FeatureTreeDatumReferenceDeleteActionInput,
  type FeatureTreeFeatureVisibilityActionInput,
  type FeatureTreeRollbackActionInput,
  type FeatureTreeSketchLifecycleActionInput,
} from '@/hooks/feature-tree/feature-tree-types';
import { createFeatureTreeFeatureDeleteActionHandler } from '@/views/editor/createFeatureTreeFeatureDeleteActionHandler';
import {
  requestFeatureTreeDeleteConfirmation,
} from '@/views/editor/requestFeatureTreeDeleteConfirmation';
import { requestAssemblyChildNodeVersionSwitchConfirmation } from '@/views/editor/requestAssemblyChildNodeVersionSwitchConfirmation';
import {
  resolveAssemblyChildVersionApplyScope,
  type AssemblyChildVersionApplyScope,
} from '@/views/editor/resolveAssemblyChildVersionApplyScope';
import {
  collectAssemblySubtreeMateRemovalAssemblyNodeIds,
  collectAssemblySubtreeRemovalRuntimeRootObjects,
  createAssemblySubtreeMateRemovalImpactPlan as createAssemblySubtreeMateRemovalImpactPlanFromDocument,
  type AssemblySubtreeMateRemovalImpactPlan,
  type AssemblySubtreeMateRemovalTarget,
} from '@/views/editor/assemblySubtreeMateRemovalImpactPlan';
import {
  requestAssemblyComponentDeleteScopeSelection,
  type AssemblyComponentDeleteScope,
} from '@/views/editor/requestAssemblyComponentDeleteScopeSelection';
import {
  useGetMultiSelect,
  useGetSelectArc,
  useGetSelectCircle,
  useGetSelectEllipse,
  useGetSelectLine,
  useGetSelectPoint,
  useGetSelectPolygon,
  useGetSelectRectangle,
  useGetSelectSlot,
  useGetSelectSpline,
  useGetSelectStyleSpline
} from '@/hooks/selection';
import {
  EditorRouteDocumentType,
  resolveEditorActiveTabType,
  resolveEditorRouteDocumentType,
} from '@/router/editor-entry-source';
import {
  buildOpenProjectRouteQuery,
  resolveOpenProjectRouteContext,
} from '@/router/open-project-route-context';
import { extractFileName } from '@/utils/file-name';
import { logger } from '@/utils/logger';
import { DebugTool } from '@/tool';
import { requestEditorDesignTreeTab } from '@/views/editor/requestEditorDesignTreeTab';
import { requestEditorPropertyTab } from '@/views/editor/requestEditorPropertyTab';

import '@/registries/property-manager/rectangle/register-rectangle';
import '@/registries/property-manager/line/register-line';
import '@/registries/property-manager/insert-line/register-insert-line';
import '@/registries/property-manager/circle/register-circle';
import '@/registries/property-manager/arc/register-arc';
import '@/registries/property-manager/ellipse/register-ellipse';
import '@/registries/property-manager/polygon/register-polygon';
import '@/registries/property-manager/slot/register-slot';
import '@/registries/property-manager/point/register-point';
import '@/registries/property-manager/sketch-corner/register-sketch-corner';
import '@/registries/property-manager/convert-entities/register-convert-entities';
import '@/registries/property-manager/sketch-trim/register-sketch-trim';
import '@/registries/property-manager/sketch-offset/register-sketch-offset';
import '@/registries/property-manager/entities-transform/register-sketch-move-entities';
import '@/registries/property-manager/entities-transform/register-sketch-copy-entities';
import '@/registries/property-manager/entities-transform/register-sketch-rotate-entities';
import '@/registries/property-manager/sketch-circular-pattern/register-sketch-circular-pattern';
import '@/registries/property-manager/entities-transform/register-sketch-mirror-entities';
import '@/registries/property-manager/style-spline/register-style-spline';
import '@/registries/property-manager/spline/register-spline';
import '@/registries/property-manager/body/register-body';
import '@/registries/property-manager/blend/register-chamfer';
import '@/registries/property-manager/blend/register-fillet';
import '@/registries/property-manager/shell/register-shell';
import '@/registries/property-manager/rib/register-rib';
import '@/registries/property-manager/extrude/register-extrude';
import '@/registries/property-manager/revolve/register-revolve';
import '@/registries/property-manager/revolve-cut/register-revolve-cut';
import '@/registries/property-manager/reference/register-reference-axis';
import '@/registries/property-manager/reference/register-reference-plane';
import '@/registries/property-manager/reference/register-reference-point';
import '@/registries/property-manager/section-view/register-section-view';
import '@/registries/property-manager/assemble-reference/register-assemble-reference-axis';
import '@/registries/property-manager/assemble-reference/register-assemble-reference-plane';
import '@/registries/property-manager/assemble-reference/register-assemble-reference-point';
import '@/registries/property-manager/geometry-relations/register-geometry-relations';
import '@/registries/property-manager/smart-dimension/register-smart-dimension';
import '@/registries/property-manager/smart-dimension/register-path-length-dimension';
import '@/registries/property-manager/smart-dimension/register-ordinate-dimension';
import '@/registries/property-manager/insert-component/register-insert-component';
import '@/registries/property-manager/move-component/register-move-component';
import '@/registries/property-manager/linear-component-pattern/register-linear-component-pattern';
import '@/registries/property-manager/assembly-mate/register-assembly-mate';

// 引导实例先于 Viewer 和业务组合函数建立，捕获快捷键时才能优先隔离 CAD 操作。
const onBoarding = new OnBoarding(createEditorOnBoardingConfig({
  isReadonly: () => versionWorkspaceStore.isReadonlyPreview,
  canOpenNodeBranches: () => canOpenAssemblyBranchNodeListPanel.value,
  enterSidePanel: enterOnBoardingSidePanel,
}));
const isOnBoardingReady = ref(false);
const route = useRoute();
const router = useRouter();
const message = useMessage();
const dialog = useDialog();
const propertyManagerDialog = {
  /**
   * PropertyManager 只需要确认弹窗创建能力。这里用适配器隔离 Naive UI 具体类型，
   * 避免下游属性栏组件直接依赖 UI 库的完整 Dialog API。
   */
  create: (options: Record<string, unknown>) => dialog.create(options as Parameters<typeof dialog.create>[0]),
};
const projectId = computed(() => route.params.projectId as string);

/**
 * 统一处理编辑器异步链路中的失败。
 *
 * 设计说明：
 * 1. 宿主 UI 不再直接散落 `console.log`；
 * 2. 用户可见错误统一走消息提示；
 * 3. 仍通过 `reportError` 把异常抛给宿主环境，避免静默吞掉上下文。
 */
const reportEditorAsyncFailure = (error: unknown, fallbackMessage: string): void => {
  reportEditorAsyncFailureToUser({
    error,
    fallbackMessage,
    message,
    reportError: typeof window !== 'undefined' && typeof window.reportError === 'function'
      ? window.reportError.bind(window)
      : undefined,
  });
};

const projectStore = useProjectStore();
const cadSessionStore = useCadSessionStore();
const sketchStateStore = useSketchStateStore();
const drawingStore = useDrawingStore();
const featureTreeStore = useFeatureTreeStore();
const editorHistoryFeatureTreeRefreshCoordinator = new FeatureTreeHistoryRefreshCoordinator(
  () => featureTreeStore.refreshTree().then(() => undefined),
  (error) => reportEditorAsyncFailure(error, '撤销重做后刷新设计树失败'),
);
const planeStore = usePlaneStore();
const toolbarCommandStore = useToolbarCommandStore();
const versionWorkspaceStore = useVersionWorkspaceStore();
const assemblyBranchNodeListStore = useAssemblyBranchNodeListStore();
const assemblyBranchNodeListService = createAssemblyBranchNodeListService({
  getProjectContext: resolveAssemblyBranchNodeListProjectContext,
});
const cadNodeTreeReloadAdapter = createCadNodeTreeReloadAdapter({
  getCadNodeTree: getCadNodeTreeSession,
});
const insertComponentService = createInsertComponentService({
  getProjectContext: resolveInsertComponentProjectContext,
});
const partPackageCommitService = createPartPackageCommitService();
const assemblyWorkspaceCommitService = createAssemblyWorkspaceCommitService();
const activeSidePanel = ref<EditorOnBoardingSidePanel>(null);
const editorSidePanelDrawerRef = ref<EditorSidePanelDrawerHandle | null>(null);
const offlinePackageRuntimeDocumentType = ref<PuppyPackageDocumentType | null>(null);
const editorSidePanelWidth = ref(VERSION_HISTORY_PANEL_DEFAULT_WIDTH);
const isEditorSidePanelResizing = ref(false);
let editorSidePanelResizeStartX = 0;
let editorSidePanelResizeStartWidth = VERSION_HISTORY_PANEL_DEFAULT_WIDTH;

/**
 * 解析提交记录联调所需的仓库上下文。
 *
 * 设计说明：提交记录真实接口依赖 OpenProject 后建立的 repositoryId/branchId，
 * 页面只提供上下文读取函数，具体 ListCommits 请求仍由 service 边界统一适配。
 *
 * @returns 当前可用的版本仓库上下文；仓库尚未建立时返回 null。
 */
function resolveVersionWorkspaceRepositoryContext(): VersionWorkspaceRepositoryContext | null {
  const repositoryId = cadSessionStore.currentRepositoryId;
  if (repositoryId === null) {
    return null;
  }

  return {
    repositoryId,
    branchId: cadSessionStore.currentBranchId ?? undefined,
    currentCommitId: cadSessionStore.currentCommitId || undefined,
  };
}

/**
 * 解析分支与节点真实接口所需的项目上下文。
 *
 * 设计说明：页面只负责从路由和 CAD 会话 Store 读取当前根装配体上下文，
 * proto 请求字段仍收口在 `createAssemblyBranchNodeListService`，避免页面直接理解接口细节。
 *
 * @returns 当前可用于分支与节点接口的项目上下文；模型尚未完成 OpenProject 时返回 null。
 */
function resolveAssemblyBranchNodeListProjectContext(): AssemblyBranchNodeListProjectContext | null {
  const repositoryId = cadSessionStore.currentRepositoryId;
  if (repositoryId === null) {
    return null;
  }

  const projectIdText = projectId.value.trim();
  if (!projectIdText) {
    return null;
  }

  try {
    return {
      projectId: BigInt(projectIdText),
      repositoryId,
      branchId: cadSessionStore.currentBranchId ?? undefined,
      currentCommitId: resolveCurrentAssemblyWriteBaseCommitId() || undefined,
    };
  } catch {
    return null;
  }
}

/**
 * 解析当前装配继续写入时应使用的 base commit。
 *
 * 设计说明：OpenProject 后 `currentCommitId` 与 `writeBaseCommitId` 通常一致；插入零部件等
 * 后端装配结构写入后，后端已经产生新的父装配提交，此时后续写操作必须沿用 `writeBaseCommitId`，
 * 不能退回 OpenProject 初始快照。
 *
 * @returns 当前工作期写入基线；缺失时返回空字符串。
 */
function resolveCurrentAssemblyWriteBaseCommitId(): string {
  return cadSessionStore.writeBaseCommitId.trim() || cadSessionStore.currentCommitId.trim();
}

/**
 * 解析插入零部件真实接口所需的父装配上下文。
 *
 * 设计说明：插入零部件和分支节点列表都依赖当前 OpenProject 建立的根装配仓库上下文。
 * 这里复用已校验过的分支节点上下文，再把 `repositoryId` 映射成
 * `CreateSubAssemblyRequest.parentRepositoryId`，避免页面散落 proto 字段转换。
 *
 * @returns 当前可用于插入零部件的项目上下文；模型尚未完成 OpenProject 时返回 null。
 */
function resolveInsertComponentProjectContext(): InsertComponentProjectContext | null {
  const context = resolveAssemblyBranchNodeListProjectContext();
  if (!context) {
    return null;
  }

  return {
    projectId: context.projectId,
    parentRepositoryId: context.repositoryId,
    ...(context.branchId !== undefined ? { branchId: context.branchId } : {}),
    ...(context.currentCommitId !== undefined ? { currentCommitId: context.currentCommitId } : {}),
  };
}

const versionWorkspaceService = createVersionWorkspaceService({
  getRepositoryContext: resolveVersionWorkspaceRepositoryContext,
});

const READONLY_PREVIEW_EDITING_MESSAGE = '历史版本仅支持查看。如需编辑，请基于此版本继续编辑。';

const currentWorkspaceBranchName = computed(() => {
  const currentWorkspaceCommit = versionWorkspaceStore.commitRecords.find((record) => record.isCurrentWorkspaceCommit);
  const openProjectBranchName = cadSessionStore.openProjectRootDisplayContext?.branchName.trim() ?? '';
  if (openProjectBranchName) {
    return openProjectBranchName;
  }

  return currentWorkspaceCommit?.branchName ?? assemblyBranchNodeListStore.currentBranchId ?? 'main';
});

/**
 * 历史版本预览态的编辑入口守卫。
 *
 * 设计说明：只读保护必须同时在 UI 层隐藏入口、在宿主动作层硬拦截。
 * 这样即使旧按钮事件、右键菜单 select 或外部脚本绕过 UI，也不会写入 CAD 主状态。
 *
 * @returns 若当前编辑动作已被只读预览态拦截，则返回 true。
 */
function guardReadonlyPreviewEditing(): boolean {
  if (!versionWorkspaceStore.isReadonlyPreview) {
    return false;
  }
  message.warning(READONLY_PREVIEW_EDITING_MESSAGE);
  return true;
}

/**
 * 拦截当前模型已要求重新加载时的结构写操作。
 *
 * 设计说明：结构写操作会同时影响 working、root、设计树和后端写入基线。若当前会话已经要求
 * reload，继续发起后端副作用会扩大本地与远端快照的分裂，因此必须在调用接口前失败。
 *
 * @param actionLabel 当前动作的用户可读名称。
 * @returns 已拦截时返回 true。
 */
function guardProjectModelStructureWrite(actionLabel: string): boolean {
  if (!projectModelRequiresReload.value && !cadSessionStore.requiresReload) {
    return false;
  }

  message.error(`当前项目模型需要重新加载后才能${actionLabel}`);
  return true;
}

const { openProjectRootDisplayContext } = storeToRefs(cadSessionStore);
const { viewerMode, isSketchActive, currentSketchGroupId, pendingSketchToolCommand } = storeToRefs(sketchStateStore);
const { isDrawingMode, currentDrawingTool, lineDrawingPointCount, drawingPointCount, drawingHint } = storeToRefs(drawingStore);
const { selectedObject, selectedKeys } = storeToRefs(featureTreeStore);
const { isSketchPlaneSelected, currentDrawingDatumPlaneId, selectedDatumPlaneId } = storeToRefs(planeStore);

const { getViewer: getSharedViewer, setViewer, clearViewer } = useViewer();
let viewer: Viewer | null = null;
/** 当前 Editor Viewer 对应的 Three.js 场景调试面板服务。 */
let editorSceneDebugInspectorService: EditorSceneDebugInspectorService | null = null;

/**
 * 读取编辑器当前 Viewer 运行时句柄。
 *
 * 说明：Viewer 是 three.js / cad-core 的非响应式运行时对象，不能放进 computed/ref 链路。
 * Editor 本地句柄是当前页面最直接的生命周期归属，共享句柄只作为跨组件兜底读取入口。
 *
 * @returns 当前 Viewer；尚未初始化时返回 null。
 */
function getViewer(): Viewer | null {
  return viewer ?? getSharedViewer();
}

/**
 * 几何引用工作流依赖装配入口。
 *
 * 说明：
 * 1. `Editor.vue` 只负责把 Viewer / 草图上下文 / 消息能力装配给 workflow；
 * 2. Convert Entities 的候选解析、preview 与 create 提交都继续收口在 workflow + cad-core；
 * 3. 宿主组件不直接拼来源 descriptor，也不直接操作 preview / create service。
 */
const geometryReferenceWorkflowDependencies = createGeometryReferenceWorkflowDependencies({
  getViewer,
  currentSketchGroupId,
});

/**
 * 几何引用工作流宿主入口。
 */
const {
  convertEntitiesState,
  enterConvertEntities,
  cancelConvertEntities,
  confirmConvertEntities,
  removeConvertEntitiesSelection,
  setConvertEntitiesAsConstruction,
  setConvertEntitiesSelectChain,
  setConvertEntitiesInnerLoopsOneByOne,
  selectAllConvertEntitiesInnerLoops,
  removeSketchRelation,
  ensureConvertEntitiesReferenceRuntime,
} = useEditorGeometryReferenceWorkflow({
  dependencies: geometryReferenceWorkflowDependencies,
  notifyInfo: (content) => {
    message.info(content);
  },
  notifyWarning: (content) => {
    message.warning(content);
  },
  notifyError: (content) => {
    message.error(content);
  },
  notifySuccess: (content) => {
    message.success(content);
  },
});

const openProjectRouteContext = computed(() => resolveOpenProjectRouteContext(route));
const editorHistoryAdapterRegistry = createHistoryDomainAdapterRegistry([
  createAssemblyHistoryAdapter(),
  createAssemblyMateHistoryAdapter(),
  createAssemblyStructureHistoryAdapter(),
  createSketchHistoryAdapter(),
  createFeatureHistoryAdapter(),
  createFeatureRollbackHistoryAdapter(),
  createSketchSolverHistoryAdapter(),
  createSketchDimensionHistoryAdapter(),
  createSketchGroupRuntimeHistoryAdapter(),
  createSketchEntityReferenceHistoryAdapter(),
  createLinearComponentPatternHistoryAdapter(),
  createDatumAxisHistoryAdapter(),
  createDatumPlaneHistoryAdapter(),
  createDatumPointHistoryAdapter(),
]);
const editorHistoryPersistence = createIndexedDBHistoryPersistence({
  writeBlob: createOPFSHistoryBlobWriter(),
});
const editorModelHistoryRuntime = createQueuedSessionModelHistoryRuntime({
  persistence: editorHistoryPersistence,
  hotTransactionLimit: 10,
  adapterRegistry: editorHistoryAdapterRegistry,
  createDomainContext: createEditorHistoryDomainContext,
  beforeApplyTransaction: () => {
    advanceEditorHistoryWorkerEpoch();
  },
  afterCommitTransaction: () => {
    advanceEditorHistoryWorkerEpoch();
  },
  onDiscardTransactions: (transactions) => {
    releaseEditorSketchRuntimeHistoryTransactionSnapshots(transactions);
  },
});
const editorSketchEntityHistorySnapshots = new Map<string, SketchEntityHistorySnapshot>();
const editorFeatureDefinitionHistorySnapshots = new Map<string, FeatureDefinitionHistorySnapshot>();
const editorPendingFeatureHistoryRestores = new Map<string, HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>>();
let editorPendingFeatureRollbackHistoryRestore: FeatureRollbackHistorySnapshot | null = null;
let editorPendingAssemblyStructureHistoryRestore: AssemblyStructureHistorySnapshot | null = null;
const editorSketchSolverIslandHistorySnapshots = new Map<string, SketchSolverIslandSnapshot>();
let disposeEditorSketchSolverHistoryRuntime: (() => void) | null = null;
let editorSketchSolverRecordSeq = 0;
let editorSketchSolverHistoryCommitPending = false;
let editorAssemblyMateDocumentHistorySnapshot: AssemblyMateDocumentModel | null = null;
let isRestoringAssemblyMateHistory = false;
const SKETCH_SOLVER_RUNTIME_HISTORY_COMMAND_PREFIX = 'sketch.runtime.solver.';

/**
 * 推进编辑器历史相关 Worker epoch。
 */
function advanceEditorHistoryWorkerEpoch(): void {
  getViewer()?.advanceHistoryWorkerEpoch();
}

/**
 * 编辑器历史领域上下文。
 *
 * 设计说明：
 * 装配体已有场景写入器；草图、Feature、Solver 在完整 CadDocument 落地前先通过轻量主状态镜像接住
 * PatchApplier 写入，避免 adapter 注册后仍因 unsupported 写入器中断 undo / redo。
 */
type EditorHistoryDomainContext = ReturnType<typeof createAssemblySceneHistoryStore> & {
  readonly stageAssemblyStructureHistoryRestore: (snapshot: AssemblyStructureHistorySnapshot) => void;
  readonly upsertSketchEntity: (snapshot: SketchEntityHistorySnapshot) => void;
  readonly deleteSketchEntity: (entityId: string) => void;
  readonly upsertFeatureDefinition: (snapshot: FeatureDefinitionHistorySnapshot) => void;
  readonly deleteFeatureDefinition: (featureId: string) => void;
  readonly stageFeatureRollbackHistoryRestore: (snapshot: FeatureRollbackHistorySnapshot) => void;
  readonly upsertSketchSolverIsland: (snapshot: SketchSolverIslandSnapshot) => void;
  readonly deleteSketchSolverIsland: (islandId: string) => void;
  readonly upsertSketchDimension: (snapshot: SketchDimensionSnapshot) => void;
  readonly deleteSketchDimension: (dimensionId: string) => void;
  readonly restoreSketchGroupRuntimeSnapshot: (snapshot: SketchGroupRuntimeHistorySnapshot) => void;
  readonly deleteSketchGroupRuntimeSnapshot: (sketchId: string) => void;
  readonly restoreReference: (input: {
    readonly reference: SketchEntityReferenceRecord;
    readonly previousReference: SketchEntityReferenceRecord | null;
  }) => void;
  readonly deleteReference: (input: {
    readonly referenceId: string;
    readonly previousReference: SketchEntityReferenceRecord | null;
  }) => void;
  readonly restoreAssemblyMateDocumentSnapshot: (snapshot: AssemblyMateDocumentModel) => void;
  readonly restoreLinearComponentPatternSnapshot: (snapshot: LinearComponentPatternHistorySnapshot) => void;
  readonly restoreDatumAxisHistorySnapshot: (snapshot: DatumAxisHistorySnapshot) => void;
  readonly restoreDatumPlaneHistorySnapshot: (snapshot: DatumPlaneHistorySnapshot) => void;
  readonly restoreDatumPointHistorySnapshot: (snapshot: DatumPointHistorySnapshot) => void;
};

/**
 * 解析 solver island 快照对应的当前运行时种子对象。
 *
 * @param snapshot solver island 快照。
 * @returns 可用于恢复运行时快照的种子对象。
 */
function resolveSketchSolverHistorySeedObjects(snapshot: SketchSolverIslandSnapshot): readonly unknown[] {
  const targetIds = new Set([
    ...snapshot.entityIds,
    ...snapshot.pointIds,
  ]);
  const seedObjects: THREE.Object3D[] = [];

  getViewer()?.getScene()?.traverse((object) => {
    if (targetIds.has(object.uuid) || targetIds.has(object.name)) {
      seedObjects.push(object);
    }
  });

  return seedObjects;
}

/**
 * 创建编辑器历史领域上下文。
 *
 * @returns 历史领域上下文。
 */
/**
 * 读取历史恢复后应该回落的空闲 Viewer 模式。
 *
 * 设计说明：
 * 草图 Undo/Redo 可能从编辑态恢复到非编辑态。此时宿主层需要把 Pinia 与 Viewer 同步回文档空闲态；
 * 装配体文档继续回到 Assembly，普通零件文档回到 Default，避免草图结束后残留 Sketch 交互模式。
 *
 * @returns 空闲 Viewer 模式。
 */
function resolveEditorIdleViewerModeAfterHistoryRestore(): ViewerMode {
  if (offlinePackageRuntimeDocumentType.value === 'assembly') {
    return ViewerMode.Assembly;
  }

  if (offlinePackageRuntimeDocumentType.value === 'part') {
    return ViewerMode.Default;
  }

  //------ 编辑器路由文档类型识别 开始------
  const routeDocumentType = resolveEditorRouteDocumentType(projectId.value);

  if (routeDocumentType === EditorRouteDocumentType.Assembly) {
    return ViewerMode.Assembly;
  }

  if (routeDocumentType === EditorRouteDocumentType.Part) {
    return ViewerMode.Default;
  }

  if (glbLoader.partTabsStore.activeTab?.type === 'assembly') {
    return ViewerMode.Assembly;
  }
  //------ 编辑器路由文档类型识别 结束------

  return ViewerMode.Default;
}

//------ 编辑器路由文档类型识别 开始------
/**
 * 将当前路由对应的文档空闲模式同步到 Store 与 Viewer。
 *
 * 设计说明：
 * 1. 默认标签异步加载完成后，标签类型才可能从导入结果回填；
 * 2. 这里只在当前仍处于空闲模式时对齐，避免覆盖草图、移动零部件或其它命令态；
 * 3. 路由类型优先级高于标签类型，保证 `.part/` 与 `.assembly.cad/` 的直接访问行为稳定。
 */
function syncEditorIdleViewerModeFromRoute(): void {
  if (!isViewerIdleMode(viewerMode.value)) {
    return;
  }

  const idleMode = resolveEditorIdleViewerModeAfterHistoryRestore();
  sketchStateStore.setViewerMode(idleMode);
  getViewer()?.setMode(idleMode);
}
//------ 编辑器路由文档类型识别 结束------

/**
 * 刷新历史恢复后的设计树。
 */
function refreshFeatureTreeAfterHistoryRestore(): Promise<void> {
  return editorHistoryFeatureTreeRefreshCoordinator.request();
}

/**
 * 暂存 feature 历史恢复目标。
 *
 * 设计说明：
 * PatchApplier 是同步内核，feature replay 必须异步进入 Worker；因此 adapter 只记录 plain snapshot，
 * 真正的 Worker 回放由 `applyQueuedEditorModelUndoRedo` 在同步栈切换完成后统一 flush。
 *
 * @param featureId Feature ID。
 * @param snapshot 目标快照。
 */
function stageEditorFeatureHistoryRestore(
  featureId: string,
  snapshot: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>,
): void {
  editorPendingFeatureHistoryRestores.set(featureId, structuredClone(snapshot));
}

/**
 * 清空 feature 历史恢复暂存区。
 */
function clearEditorPendingFeatureHistoryRestores(): void {
  editorPendingFeatureHistoryRestores.clear();
}

/**
 * 暂存退回控制棒历史恢复目标。
 *
 * @param snapshot 退回控制棒历史快照。
 */
function stageEditorFeatureRollbackHistoryRestore(snapshot: FeatureRollbackHistorySnapshot): void {
  editorPendingFeatureRollbackHistoryRestore = structuredClone(snapshot);
}

/**
 * 清空退回控制棒历史恢复暂存区。
 */
function clearEditorPendingFeatureRollbackHistoryRestore(): void {
  editorPendingFeatureRollbackHistoryRestore = null;
}

/**
 * 暂存装配结构历史恢复目标。
 *
 * @param snapshot 装配结构历史快照。
 */
function stageEditorAssemblyStructureHistoryRestore(snapshot: AssemblyStructureHistorySnapshot): void {
  editorPendingAssemblyStructureHistoryRestore = structuredClone(snapshot);
}

/**
 * 清空装配结构历史恢复暂存区。
 */
function clearEditorPendingAssemblyStructureHistoryRestore(): void {
  editorPendingAssemblyStructureHistoryRestore = null;
}

/**
 * 恢复编辑器当前 Mate 文档快照。
 *
 * 设计说明：
 * 装配结构 Undo/Redo 可能同时改变模型树和 Mate 文档。这里复用 Mate controller 的正式加载入口，
 * 让引用恢复、组件 transform 索引失效和本地历史基线更新保持一致。模型结构已经由 Loader
 * 完成局部投影，Mate 只更新配合文件夹，不能再次刷新整棵模型树。
 *
 * @param snapshot Mate 文档快照。
 */
function restoreEditorAssemblyMateDocumentSnapshot(snapshot: AssemblyMateDocumentModel): void {
  const controller = getViewer()?.assemblyMateController;
  if (!controller) {
    throw new Error('装配体配合控制器未初始化，无法恢复配合历史快照。');
  }

  const sourceTabId = glbLoader.partTabsStore.activeTabId;
  let restoredDocument: AssemblyMateDocumentModel;
  isRestoringAssemblyMateHistory = true;
  try {
    controller.loadDocument(snapshot, { restoreReferences: true });
    restoredDocument = controller.getDocument();
    editorAssemblyMateDocumentHistorySnapshot = restoredDocument;
  } finally {
    isRestoringAssemblyMateHistory = false;
  }
  featureTreeStore.applyAssemblyMateDocument(sourceTabId, restoredDocument);
}

/**
 * 将暂存的装配结构快照恢复到项目模型、场景和 working 文档。
 *
 * @param direction 历史应用方向。
 */
async function flushEditorAssemblyStructureHistoryRestore(direction: HistoryApplyDirection): Promise<void> {
  void direction;
  const snapshot = editorPendingAssemblyStructureHistoryRestore;
  if (!snapshot) {
    return;
  }

  const draftRecord = glbLoader.assemblyDraftDocumentStore.get(snapshot.rootNodeKey);
  const previousWorkingDocument = draftRecord?.workingAssemblyDocument ?? null;

  await glbLoader.applyAssemblyStructureHistorySnapshotLocally(snapshot, {
    beforeCommit: () => {
      glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
        snapshot.rootNodeKey,
        snapshot.workingAssemblyDocument,
      );
    },
    rollbackBeforeCommit: () => {
      if (previousWorkingDocument) {
        glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
          snapshot.rootNodeKey,
          previousWorkingDocument,
        );
      }
    },
  });
  if (snapshot.assemblyMateDocument) {
    restoreEditorAssemblyMateDocumentSnapshot(snapshot.assemblyMateDocument);
  }

  selectedKeys.value = [];
  selectedObject.value = null;
  void refreshAssemblyBranchNodeListAfterStructureChanged().catch((error) => {
    reportEditorAsyncFailure(error, '撤销重做后刷新装配体子节点列表失败');
  });
}

/**
 * 将暂存的退回控制棒快照恢复到 Viewer。
 */
async function flushEditorFeatureRollbackHistoryRestore(): Promise<void> {
  const snapshot = editorPendingFeatureRollbackHistoryRestore;
  if (!snapshot) {
    return;
  }

  const currentViewer = getViewer();
  if (!currentViewer?.restoreFeatureRollbackHistorySnapshot) {
    throw new Error('Viewer 未初始化，无法恢复退回控制棒历史。');
  }

  await currentViewer.restoreFeatureRollbackHistorySnapshot(snapshot);
  await refreshFeatureTreeAfterHistoryRestore();
}

/**
 * 判断历史元数据里的显示态是否为草图显示态枚举值。
 *
 * @param value 待判断值。
 * @returns 是否为有效草图显示态。
 */
function isSketchDisplayStateValue(value: unknown): value is SketchDisplayState {
  return value === SketchDisplayState.Edit
    || value === SketchDisplayState.Shown
    || value === SketchDisplayState.Hidden;
}

/**
 * 在 feature undo/redo 后恢复被消费源草图的显示态。
 *
 * 设计说明：
 * 1. 拉伸/旋转等特征提交会在 cad-core 内自动隐藏源草图，这是模型操作的派生显示语义；
 * 2. feature replay 只恢复 Worker 命令和结果网格，不能自动知道 undo/redo 前后的草图显示态；
 * 3. 这里只从 transaction metadata 读取 plain data，再通过 Viewer 门面恢复显示态，避免把运行时对象放进 Vue 状态。
 *
 * @param transaction 当前历史事务。
 * @param direction 当前应用方向。
 */
function restoreFeatureSourceSketchDisplayStatesAfterHistoryRestore(
  transaction: CadTransaction,
  direction: HistoryApplyDirection,
): void {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  transaction.changeSet.patches.forEach((patch) => {
    if (patch.entityKind !== 'feature') {
      return;
    }

    const sourceSketchId = patch.metadata?.sourceSketchId;
    if (typeof sourceSketchId !== 'string' || sourceSketchId.trim().length === 0) {
      return;
    }

    const targetDisplayState = direction === 'undo'
      ? patch.metadata?.sourceSketchDisplayStateBefore
      : patch.metadata?.sourceSketchDisplayStateAfter;
    if (!isSketchDisplayStateValue(targetDisplayState)) {
      return;
    }

    currentViewer.restoreSketchGroupDisplayStateFromHistory(sourceSketchId, targetDisplayState);
  });
}

/**
 * 将暂存的 feature 快照回放到 Worker 与 Viewer mesh。
 *
 * @param transaction 当前 undo / redo 事务。
 */
/**
 * 在 feature undo/redo 后恢复特征结果体隐藏状态。
 *
 * 设计说明：
 * 1. 特征隐藏是模型显示状态，不写入 Worker 命令参数；
 * 2. Worker replay 会重建结果体，因此必须在 replay 后根据历史快照重新应用显隐；
 * 3. 只消费快照中的 plain `hidden` 字段，避免把 Object3D.visible 泄漏进历史模型。
 *
 * @param transaction 当前历史事务。
 * @param direction 当前应用方向。
 */
function restoreFeatureVisibilityStatesAfterHistoryRestore(
  transaction: CadTransaction,
  direction: HistoryApplyDirection,
): void {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  transaction.changeSet.patches.forEach((patch) => {
    if (patch.entityKind !== 'feature') {
      return;
    }

    const targetSnapshot = direction === 'undo' ? patch.before : patch.after;
    const snapshotData = isRecord(targetSnapshot.data) ? targetSnapshot.data : null;
    const hidden = snapshotData?.hidden;
    if (typeof hidden !== 'boolean') {
      return;
    }

    currentViewer.setFeatureResultVisibility(patch.entityId, !hidden);
  });
}

async function flushEditorFeatureHistoryRestores(
  transaction: CadTransaction,
  direction: HistoryApplyDirection,
): Promise<void> {
  if (editorPendingFeatureHistoryRestores.size === 0) {
    return;
  }

  const currentViewer = getViewer();
  if (!currentViewer) {
    throw new Error('Viewer 未初始化，无法恢复 feature 历史。');
  }

  const activeSummary = await currentViewer.getGeometryStateSummary({syncHistoryProjection: false});
  const summary = await currentViewer.getFeatureTreeStateSummary();
  const fullCommands = summary.history.map((command) => structuredClone(command));
  const activeRollbackCommands = activeSummary.history.map((command) => structuredClone(command));
  const nextCommands = Array.from(editorPendingFeatureHistoryRestores.entries())
    .reduce<CadCommand[]>((commands, [featureId, snapshot]) => (
      applyFeatureSnapshotToCommandHistory({
        commands,
        featureId,
        snapshot,
      })
    ), fullCommands);
  const firstRestoredSnapshot = Array.from(editorPendingFeatureHistoryRestores.values())
    .find((snapshot) => snapshot.exists && snapshot.data);

  await currentViewer.replayFeatureHistoryCommands(nextCommands, {
    targetFeatureId: firstRestoredSnapshot?.data?.featureId,
    expectedResultHash: firstRestoredSnapshot?.data?.resultHash,
    expectedCommandSeq: firstRestoredSnapshot?.data?.commandSeq,
    expectedFeatureRevision: firstRestoredSnapshot?.data?.featureRevision,
    rollbackCommands: activeRollbackCommands,
    beforeSummary: activeSummary,
    restoreFullFeatureHistory: true,
  });
  restoreFeatureVisibilityStatesAfterHistoryRestore(transaction, direction);
  restoreFeatureSourceSketchDisplayStatesAfterHistoryRestore(transaction, direction);
  await refreshFeatureTreeAfterHistoryRestore();
  transaction.changeSet.patches.forEach((patch) => {
    if (patch.entityKind !== 'feature') {
      return;
    }
    const targetSnapshot = direction === 'undo' ? patch.before : patch.after;
    if (targetSnapshot.exists && targetSnapshot.data) {
      editorFeatureDefinitionHistorySnapshots.set(patch.entityId, targetSnapshot.data as FeatureDefinitionHistorySnapshot);
    } else {
      editorFeatureDefinitionHistorySnapshots.delete(patch.entityId);
    }
  });
}

/**
 * 清理宿主层草图命令态。
 *
 * 设计说明：
 * 模型历史恢复已经由 cad-core 写入真实草图运行时，web-client 这里只清理 UI 命令态；
 * 不持有、不包裹任何 Three.js 对象，避免把运行时对象误放入 Vue 响应式链路。
 */
function clearEditorSketchCommandStateAfterHistoryRestore(): void {
  currentDrawingTool.value = null;
  currentDrawingCommand.value = null;
  drawingStore.stopDrawingHintTimer();
  sketchStateStore.clearPendingSketchToolCommand();
  sketchCornerEnable.value = false;
  clearSketchTrimWorkflow();
  getViewer()?.clearSketchCornerMode?.(false);
}

/**
 * 按草图运行时快照同步宿主层草图会话状态。
 *
 * @param snapshot 草图组运行时历史快照。
 */
function syncEditorSketchSessionStateAfterHistoryRestore(snapshot: SketchGroupRuntimeHistorySnapshot): void {
  const currentViewer = getViewer();

  clearEditorSketchCommandStateAfterHistoryRestore();
  if (snapshot.displayState === SketchDisplayState.Edit) {
    const planeId = currentViewer?.getSketchGroupReferencePlaneId?.(snapshot.sketchId) ?? -1;
    sketchStateStore.setViewerMode(ViewerMode.Sketch);
    currentViewer?.setMode(ViewerMode.Sketch);
    currentSketchGroupId.value = snapshot.sketchId;
    currentDrawingDatumPlaneId.value = planeId;
    selectedDatumPlaneId.value = planeId;
    if (planeId !== -1) {
      const plane = currentViewer?.getDatumPlaneSummary?.(planeId) ?? null;
      planeStore.captureDefaultPlaneSnapshot(currentViewer);
      if (plane && isDefaultDatumPlaneSummary(plane)) {
        planeStore.rememberSketchPlaneVisibility(planeId, currentViewer);
      }
      // 模型 Undo/Redo 只恢复 CAD 状态，不驱动视图历史；相机进入基准面视图应由显式草图命令负责。
      planeStore.hideDefaultDatumPlanes(currentViewer);
    }
    currentViewer?.syncSketchEditingVisualLayering?.();
    refreshFeatureTreeAfterHistoryRestore();
    return;
  }

  if (currentSketchGroupId.value === snapshot.sketchId || currentViewer?.getActiveSketchGroupId?.() === snapshot.sketchId) {
    const idleMode = resolveEditorIdleViewerModeAfterHistoryRestore();
    sketchStateStore.setViewerMode(idleMode);
    currentViewer?.setMode(idleMode);
    // 模型 Undo/Redo 不能退出当前相机视图，否则会和独立的 View Undo/Redo 栈混淆。
    currentViewer?.clearSketchEditingVisualLayering?.();
    currentSketchGroupId.value = null;
    currentDrawingDatumPlaneId.value = -1;
    selectedDatumPlaneId.value = -1;
    planeStore.restoreSketchPlaneVisibility(currentViewer);
    planeStore.restoreDefaultPlaneSnapshot(undefined, currentViewer);
    planeStore.clearDefaultPlaneSnapshot();
    planeStore.clearSelectedDatumPlanePreview(currentViewer);
  }
  refreshFeatureTreeAfterHistoryRestore();
}

/**
 * 删除草图快照后同步宿主层状态。
 *
 * @param sketchId 被删除的草图组 ID。
 */
function syncEditorSketchSessionStateAfterHistoryDelete(sketchId: string): void {
  const currentViewer = getViewer();
  if (currentSketchGroupId.value !== sketchId) {
    refreshFeatureTreeAfterHistoryRestore();
    return;
  }

  const idleMode = resolveEditorIdleViewerModeAfterHistoryRestore();
  clearEditorSketchCommandStateAfterHistoryRestore();
  sketchStateStore.setViewerMode(idleMode);
  currentViewer?.setMode(idleMode);
  // 删除草图属于模型历史恢复，不应重置或移动相机。
  currentViewer?.clearSketchEditingVisualLayering?.();
  currentSketchGroupId.value = null;
  currentDrawingDatumPlaneId.value = -1;
  selectedDatumPlaneId.value = -1;
  planeStore.restoreSketchPlaneVisibility(currentViewer);
  planeStore.restoreDefaultPlaneSnapshot(undefined, currentViewer);
  planeStore.clearDefaultPlaneSnapshot();
  planeStore.clearSelectedDatumPlanePreview(currentViewer);
  refreshFeatureTreeAfterHistoryRestore();
}

/**
 * 恢复转换实体引用历史快照。
 *
 * @param input 恢复输入。
 */
function restoreEditorSketchEntityReference(input: {
  readonly reference: SketchEntityReferenceRecord;
  readonly previousReference: SketchEntityReferenceRecord | null;
}): void {
  const currentViewer = getViewer();
  if (!currentViewer) {
    throw new Error('当前 Viewer 尚未创建，无法恢复转换实体引用历史补丁。');
  }
  const restored = currentViewer.restoreSketchEntityReferenceFromHistorySync(input);
  if (!restored) {
    throw new Error(`转换实体引用 Controller 尚未就绪，无法恢复历史补丁：${input.reference.referenceId}`);
  }
  ensureConvertEntitiesReferenceRuntime();
  // 引用恢复已经同步完成，画面与同一历史事务内的其它变化一起提交。
  currentViewer.requestRender();
}

/**
 * 删除转换实体引用历史快照。
 *
 * @param input 删除输入。
 */
function deleteEditorSketchEntityReference(input: {
  readonly referenceId: string;
  readonly previousReference: SketchEntityReferenceRecord | null;
}): void {
  const currentViewer = getViewer();
  if (!currentViewer) {
    throw new Error('当前 Viewer 尚未创建，无法删除转换实体引用历史补丁。');
  }
  const deleted = currentViewer.deleteSketchEntityReferenceFromHistorySync(input);
  if (!deleted) {
    throw new Error(`转换实体引用 Controller 尚未就绪，无法删除历史补丁：${input.referenceId}`);
  }
  currentViewer.requestRender();
}

function createEditorHistoryDomainContext(): EditorHistoryDomainContext {
  const assemblyHistoryStore = createAssemblySceneHistoryStore({
    resolveObject: resolveAssemblyHistorySceneObject,
    createObject: (snapshot) => (
      glbLoader.restoreAssemblyReferenceCopyFromHistorySnapshot(snapshot) as unknown as WebGpuObject3D | null
    ),
    removeObject: (componentInstanceId) => {
      glbLoader.removeAssemblyReferenceCopy(componentInstanceId);
    },
    // 一个事务可能恢复多个联动组件；矩阵和主状态同步写回，普通重绘由 Viewer 合帧。
    requestRender: () => getViewer()?.requestRender(),
    recomputeAssemblyReferences: () => getViewer()?.recomputeAssemblyDatumReferences?.(),
    syncComponentTransform: (object) => {
      getViewer()?.updateImportedModelInstanceMatrices?.(object);
    },
    syncComponentState: (snapshot) => {
      glbLoader.applyAssemblyComponentHistorySnapshot(snapshot);
    },
  });

  return {
    ...assemblyHistoryStore,
    stageAssemblyStructureHistoryRestore(snapshot) {
      stageEditorAssemblyStructureHistoryRestore(snapshot);
    },
    upsertSketchEntity(snapshot) {
      editorSketchEntityHistorySnapshots.set(snapshot.entityId, snapshot);
    },
    deleteSketchEntity(entityId) {
      editorSketchEntityHistorySnapshots.delete(entityId);
    },
    upsertFeatureDefinition(snapshot) {
      editorFeatureDefinitionHistorySnapshots.set(snapshot.featureId, snapshot);
      stageEditorFeatureHistoryRestore(snapshot.featureId, createExistingHistorySnapshot(snapshot));
    },
    deleteFeatureDefinition(featureId) {
      editorFeatureDefinitionHistorySnapshots.delete(featureId);
      stageEditorFeatureHistoryRestore(featureId, createMissingHistorySnapshot());
    },
    stageFeatureRollbackHistoryRestore(snapshot) {
      stageEditorFeatureRollbackHistoryRestore(snapshot);
    },
    upsertSketchSolverIsland(snapshot) {
      const seedObjects = resolveSketchSolverHistorySeedObjects(snapshot);

      if (seedObjects.length === 0) {
        throw new Error(`无法恢复 solver island ${snapshot.islandId}：当前场景缺少对应草图运行时对象。`);
      }

      restoreSketchSolverRuntimeSnapshot({
        seedObjects,
        snapshot,
      });
      editorSketchSolverIslandHistorySnapshots.set(snapshot.islandId, snapshot);
      getViewer()?.requestRender();
    },
    deleteSketchSolverIsland(islandId) {
      editorSketchSolverIslandHistorySnapshots.delete(islandId);
      getViewer()?.requestRender();
    },
    upsertSketchDimension(snapshot) {
      const repository = getViewer()?.getSketchDimensionRepository();

      if (!repository) {
        throw new Error(`无法恢复尺寸 ${snapshot.dimensionId}：草图尺寸仓库未初始化。`);
      }
      repository.restoreDimension(snapshot);
    },
    deleteSketchDimension(dimensionId) {
      getViewer()?.getSketchDimensionRepository()?.removeDimensionForRestore(dimensionId);
    },
    restoreSketchGroupRuntimeSnapshot(snapshot) {
      getViewer()?.restoreSketchGroupRuntimeHistorySnapshot(snapshot);
      syncEditorSketchSessionStateAfterHistoryRestore(snapshot);
    },
    deleteSketchGroupRuntimeSnapshot(sketchId) {
      getViewer()?.removeSketchGroup?.(sketchId);
      syncEditorSketchSessionStateAfterHistoryDelete(sketchId);
      getViewer()?.requestRender();
    },
    restoreReference(input) {
      restoreEditorSketchEntityReference(input);
    },
    deleteReference(input) {
      deleteEditorSketchEntityReference(input);
    },
    restoreAssemblyMateDocumentSnapshot(snapshot) {
      restoreEditorAssemblyMateDocumentSnapshot(snapshot);
    },
    restoreLinearComponentPatternSnapshot(snapshot) {
      const manager = getViewer()?.linearComponentPatternManager;
      if (!manager) {
        throw new Error('线性零部件阵列管理器未初始化，无法恢复历史快照。');
      }

      manager.restoreHistorySnapshot(snapshot);
      //------ 线性零部件阵列撤销重做设计树同步 开始------
      /**
       * Undo/Redo 恢复发生在历史同步栈内，此时设计树 active owner 可能仍是上一轮刷新值。
       * 因此优先使用当前实际挂载的 GLB tab 来重建运行时阵列树，确保 redo 后阵列分组与零件节点
       * 写回同一棵装配体权威 hierarchy。
       */
      const linearPatternHistoryRestoreTabId = glbLoader.mountedGlbTabId.value
        ?? featureTreeStore.activeTreeTabId
        ?? glbLoader.partTabsStore.activeTabId
        ?? null;
      const syncedLinearPatternTree = glbLoader.syncLinearComponentPatternFeatureTree(
        manager.getCommittedRecords(),
        {
          tabId: linearPatternHistoryRestoreTabId,
          reason: LinearComponentPatternFeatureTreeSyncReason.HistoryRestore,
        },
      );
      if (!syncedLinearPatternTree) {
        refreshFeatureTreeAfterHistoryRestore();
      }
      //------ 线性零部件阵列撤销重做设计树同步 结束------
    },
    restoreDatumAxisHistorySnapshot(snapshot) {
      const viewer = getViewer();
      const controller = viewer?.referenceAxisController;
      const manager = viewer?.datumAxisManager;
      if (controller) {
        controller.restoreHistorySnapshot(snapshot);
        refreshFeatureTreeAfterHistoryRestore();
        return;
      }
      if (!manager) {
        throw new Error('基准轴管理器未初始化，无法恢复历史快照。');
      }

      manager.restoreHistorySnapshot(snapshot);
      refreshFeatureTreeAfterHistoryRestore();
    },
    restoreDatumPlaneHistorySnapshot(snapshot) {
      const manager = getViewer()?.datumPlaneManager;
      if (!manager) {
        throw new Error('基准面管理器未初始化，无法恢复历史快照。');
      }

      manager.restoreHistorySnapshot(snapshot);
      refreshFeatureTreeAfterHistoryRestore();
    },
    restoreDatumPointHistorySnapshot(snapshot) {
      const viewer = getViewer();
      const controller = viewer?.referencePointController;
      const manager = viewer?.datumPointManager;
      if (controller) {
        controller.restoreHistorySnapshot(snapshot);
        refreshFeatureTreeAfterHistoryRestore();
        return;
      }
      if (!manager) {
        throw new Error('基准点管理器未初始化，无法恢复历史快照。');
      }

      manager.restoreHistorySnapshot(snapshot);
      refreshFeatureTreeAfterHistoryRestore();
    },
  };
}

/**
 * 判断记录是否为普通对象。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

/**
 * 根据组件实例 ID 解析场景对象。
 *
 * @param componentInstanceId 组件实例 ID。
 * @param snapshot 装配组件快照。
 * @returns 场景对象。
 */
function resolveAssemblyHistorySceneObject(
  componentInstanceId: string,
  snapshot: AssemblyComponentHistorySnapshot,
): WebGpuObject3D | null {
  return resolveProjectedAssemblyHistorySceneObject(getViewer(), {
    ...snapshot,
    componentInstanceId,
  }) as unknown as WebGpuObject3D | null;
}

/**
 * 在历史队列中提交 CAD 模型事务。
 *
 * 设计说明：
 * 撤销重做历史只允许存在于当前页面会话内，但会在会话内使用 cad-core 的
 * IndexedDB/OPFS adapter 作为冷存储。刷新或关闭页面后，下一次打开必须从空历史栈重新开始。
 *
 * @param transaction 待提交事务。
 */
async function commitEditorCadTransactionInQueue(transaction: CadTransaction): Promise<CadTransaction | null> {
  return editorModelHistoryRuntime.commitTransaction(transaction);
}

const handleFeatureTreeFeatureDeleteAction = createFeatureTreeFeatureDeleteActionHandler({
  getViewer,
  dialog,
  message,
  runExclusive: (work) => editorModelHistoryRuntime.runExclusive(work),
  allocateHistorySequence: () => editorModelHistoryRuntime.allocateSequence(),
  commitTransaction: commitEditorCadTransactionInQueue,
  getDocumentId: () => String(projectId.value),
  reportFailure: reportEditorAsyncFailure,
  markFeatureDefinitionsDeleted: (featureIds) => {
    featureIds.forEach((featureId) => {
      editorFeatureDefinitionHistorySnapshots.delete(featureId);
    });
  },
});

function flattenAssemblyTransformHistoryRecords(
  record: AssemblyTransformHistoryRecord,
): AssemblyTransformHistoryRecord[] {
  return [record, ...(record.relatedRecords ?? [])];
}

/**
 * 提交 feature 主状态历史记录。
 *
 * @param record Feature 提交记录。
 */
async function commitEditorFeatureHistoryRecord(record: EditorFeatureHistoryRecord): Promise<void> {
  const operation = resolveEntityPatchOperation(record.before, record.after);
  const affectedSketchIds = record.sourceSketchId ? [record.sourceSketchId] : [];

  if (operation === 'noop') {
    return;
  }

  const patch = createFeatureDefinitionHistoryPatch({
    entityId: record.featureId,
    operation,
    before: record.before,
    after: record.after,
    metadata: {
      featureId: record.featureId,
      sourceSketchId: record.sourceSketchId ?? undefined,
      sourceSketchDisplayStateBefore: record.sourceSketchDisplayStateBefore ?? null,
      sourceSketchDisplayStateAfter: record.sourceSketchDisplayStateAfter ?? null,
      renderObjectIds: record.renderObjectIds,
    },
  });
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const transaction: CadTransaction = {
    id: `txn:feature:${record.featureId}:${Date.now()}`,
    documentId: String(projectId.value),
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    clientId: 'web-client',
    snapshotScope: {
      ...createEmptySnapshotScope(),
      entityIds: [record.featureId, ...affectedSketchIds],
      includeWorkerCommandHistory: true,
    },
    dirtyScope: {
      ...createEmptyDirtyScope(),
      featureIds: [record.featureId],
      sketchIds: affectedSketchIds,
      renderObjectIds: [...record.renderObjectIds],
      fullRebuild: true,
    },
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  const committedTransaction = await commitEditorCadTransactionInQueue(transaction);
  if (!committedTransaction) {
    return;
  }
  if (record.after.exists && record.after.data) {
    editorFeatureDefinitionHistorySnapshots.set(record.featureId, record.after.data);
  } else {
    editorFeatureDefinitionHistorySnapshots.delete(record.featureId);
  }
}

/**
 * 读取设计树特征显隐动作的历史标签。
 *
 * @param action 显隐动作。
 * @returns 用户可读历史标签。
 */
function getFeatureTreeFeatureVisibilityHistoryLabel(
  action: FeatureTreeFeatureVisibilityActionInput['action'],
): string {
  return action === 'hide' ? '隐藏特征' : '显示特征';
}

/**
 * 解析特征显隐影响到的渲染对象。
 *
 * @param summary Worker 几何状态摘要。
 * @param featureId 特征 ID。
 * @returns 渲染对象 ID 列表。
 */
function resolveFeatureVisibilityRenderObjectIds(
  summary: WorkerGeometryStateSummary,
  featureId: string,
): string[] {
  const objectIds = new Set<string>();

  summary.objects.forEach((object) => {
    const metadata = isRecord(object.metadata) ? object.metadata : {};
    if (object.id === featureId || object.commandId === featureId || metadata.featureId === featureId) {
      objectIds.add(object.id);
    }
  });

  return Array.from(objectIds);
}

/**
 * 执行设计树特征显隐动作并提交模型历史。
 *
 * @param input 特征显隐动作。
 * @returns 动作是否成功。
 */
async function applyFeatureTreeFeatureVisibilityActionInQueue(
  input: FeatureTreeFeatureVisibilityActionInput,
): Promise<boolean> {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return false;
  }

  const beforeSummary = await currentViewer.getGeometryStateSummary({syncHistoryProjection: false});
  const beforeHidden = currentViewer.isFeatureResultHidden(input.featureId);
  const afterHidden = input.action === 'hide';
  const before = captureFeatureHistorySnapshotFromSummary(beforeSummary, input.featureId, {
    hidden: beforeHidden,
  });

  if (!before.exists) {
    return false;
  }

  if (beforeHidden === afterHidden) {
    return true;
  }

  currentViewer.setFeatureResultVisibility(input.featureId, !afterHidden);

  const afterSummary = await currentViewer.getGeometryStateSummary({syncHistoryProjection: false});
  const after = captureFeatureHistorySnapshotFromSummary(afterSummary, input.featureId, {
    hidden: afterHidden,
  });
  if (!after.exists) {
    currentViewer.setFeatureResultVisibility(input.featureId, !beforeHidden);
    return false;
  }

  const operation = resolveEntityPatchOperation(before, after);
  if (operation === 'noop') {
    return true;
  }

  const renderObjectIds = resolveFeatureVisibilityRenderObjectIds(afterSummary, input.featureId);
  const patch = createFeatureDefinitionHistoryPatch({
    entityId: input.featureId,
    operation,
    before,
    after,
    metadata: {
      featureId: input.featureId,
      renderObjectIds,
    },
  });
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const transaction: CadTransaction = {
    id: `txn:feature-visibility:${input.featureId}:${historySeq}`,
    documentId: String(projectId.value),
    seq: historySeq,
    historySeq,
    label: getFeatureTreeFeatureVisibilityHistoryLabel(input.action),
    commandType: `feature-tree.feature.${input.action}`,
    createdAt: Date.now(),
    clientId: 'web-client',
    snapshotScope: {
      ...createEmptySnapshotScope(),
      entityIds: [input.featureId],
      includeWorkerCommandHistory: true,
    },
    dirtyScope: {
      ...createEmptyDirtyScope(),
      featureIds: [input.featureId],
      renderObjectIds,
    },
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };
  const committedTransaction = await commitEditorCadTransactionInQueue(transaction);

  if (!committedTransaction) {
    currentViewer.setFeatureResultVisibility(input.featureId, !beforeHidden);
    return false;
  }

  if (after.data) {
    editorFeatureDefinitionHistorySnapshots.set(input.featureId, after.data);
  }
  return true;
}

/**
 * 串行执行设计树特征显隐动作。
 *
 * @param input 特征显隐动作。
 * @returns 动作是否成功。
 */
async function handleFeatureTreeFeatureVisibilityAction(
  input: FeatureTreeFeatureVisibilityActionInput,
): Promise<boolean> {
  let succeeded = false;

  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      succeeded = await applyFeatureTreeFeatureVisibilityActionInQueue(input);
    });
  } catch (error) {
    reportEditorAsyncFailure(error, `${getFeatureTreeFeatureVisibilityHistoryLabel(input.action)}失败`);
    return false;
  }

  return succeeded;
}

/**
 * 创建退回控制棒历史实体 ID。
 *
 * @returns 文档级退回控制棒历史实体 ID。
 */
function createFeatureRollbackHistoryEntityId(): string {
  return `feature-rollback:${String(projectId.value)}`;
}

/**
 * 比较退回控制棒历史快照。
 *
 * @param before 变更前快照。
 * @param after 变更后快照。
 * @returns 是否语义一致。
 */
function areFeatureRollbackHistorySnapshotsEqual(
  before: FeatureRollbackHistorySnapshot,
  after: FeatureRollbackHistorySnapshot,
): boolean {
  return JSON.stringify(before) === JSON.stringify(after);
}

/**
 * 执行退回控制棒 Viewer 动作。
 *
 * @param input 退回控制棒动作输入。
 * @returns 是否成功执行。
 */
async function executeFeatureRollbackViewerAction(input: FeatureTreeRollbackActionInput): Promise<boolean> {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return false;
  }

  switch (input.action) {
    case 'move-to-index':
      if (!currentViewer.moveFeatureRollbackBar) {
        return false;
      }
      await currentViewer.moveFeatureRollbackBar({cursorIndex: input.cursorIndex});
      return true;
    case 'rollback-to-feature':
      return currentViewer.rollbackToFeature?.(input.featureId) ?? false;
    case 'roll-forward':
      return currentViewer.rollFeatureRollbackBarForward?.(input.featureId) ?? false;
    case 'roll-to-previous':
      return currentViewer.rollFeatureRollbackBarToPrevious?.() ?? false;
    case 'roll-to-end':
      return currentViewer.rollFeatureRollbackBarToEnd?.() ?? false;
    default:
      return false;
  }
}

/**
 * 执行退回控制棒动作并提交模型历史事务。
 *
 * 设计说明：
 * 1. pointermove 只更新 UI 插入线，不进入历史；
 * 2. mouseup / 菜单 / 键盘提交时捕获 before/after，形成一笔文档级模型事务；
 * 3. 若历史提交失败，必须恢复 before，避免出现“模型已变但无法撤销”的状态。
 *
 * @param input 退回控制棒动作输入。
 * @returns 是否成功。
 */
async function applyFeatureTreeRollbackActionInQueue(input: FeatureTreeRollbackActionInput): Promise<boolean> {
  const currentViewer = getViewer();
  if (!currentViewer?.captureFeatureRollbackHistorySnapshot || !currentViewer.restoreFeatureRollbackHistorySnapshot) {
    return false;
  }

  const documentId = String(projectId.value);
  const before = currentViewer.captureFeatureRollbackHistorySnapshot(documentId);
  const actionSucceeded = await executeFeatureRollbackViewerAction(input);
  if (!actionSucceeded) {
    return false;
  }

  const after = currentViewer.captureFeatureRollbackHistorySnapshot(documentId);
  if (areFeatureRollbackHistorySnapshotsEqual(before, after)) {
    return true;
  }

  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const entityId = createFeatureRollbackHistoryEntityId();
  const patch = createFeatureRollbackHistoryPatch({
    entityId,
    operation: 'update',
    before: createExistingHistorySnapshot(before),
    after: createExistingHistorySnapshot(after),
    metadata: {
      renderObjectIds: [],
    },
  });
  const transaction: CadTransaction = {
    id: `txn:feature-rollback:${historySeq}`,
    documentId,
    seq: historySeq,
    historySeq,
    label: '移动退回控制棒',
    commandType: 'feature-tree.rollback-bar.move',
    createdAt: Date.now(),
    clientId: 'web-client',
    snapshotScope: {
      ...createEmptySnapshotScope(),
      entityIds: [entityId],
      includeWorkerCommandHistory: true,
    },
    dirtyScope: {
      ...createEmptyDirtyScope(),
      entityIds: [entityId],
      fullRebuild: true,
    },
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };
  const committedTransaction = await commitEditorCadTransactionInQueue(transaction);

  if (!committedTransaction) {
    await currentViewer.restoreFeatureRollbackHistorySnapshot(before);
    return false;
  }

  return true;
}

/**
 * 解析退回控制棒重生成 loading 文案。
 *
 * 设计说明：
 * 1. pointermove 只更新插入位置提示，不触发模型重放，也不应显示全局 loading；
 * 2. mouseup、菜单或键盘提交后才进入 Worker replay，此时可能需要布尔合并/解合并重建；
 * 3. 文案只表达当前正在重生成回退位置，不暴露内部命令实现细节。
 *
 * @param input 退回控制棒动作输入。
 * @returns loading 文案。
 */
function resolveFeatureRollbackLoadingText(input: FeatureTreeRollbackActionInput): string {
  switch (input.action) {
    case 'roll-to-end':
      return '正在恢复完整特征历史...';
    case 'roll-forward':
      return '正在前滚回退控制棒...';
    case 'roll-to-previous':
      return '正在恢复上一次回退位置...';
    default:
      return '正在重生成回退位置...';
  }
}

/**
 * 串行执行退回控制棒动作。
 *
 * @param input 退回控制棒动作输入。
 * @returns 是否成功。
 */
async function handleFeatureTreeRollbackAction(input: FeatureTreeRollbackActionInput): Promise<boolean> {
  let succeeded = false;
  editorModelLoadingCoordinator.show(
    resolveFeatureRollbackLoadingText(input),
    FEATURE_ROLLBACK_LOADING_OWNER,
  );

  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      succeeded = await applyFeatureTreeRollbackActionInQueue(input);
    });
  } catch (error) {
    reportEditorAsyncFailure(error, '移动退回控制棒失败');
    return false;
  } finally {
    if (editorModelLoadingCoordinator.isOwnedBy(FEATURE_ROLLBACK_LOADING_OWNER)) {
      editorModelLoadingCoordinator.hide(FEATURE_ROLLBACK_LOADING_OWNER);
    }
  }

  return succeeded;
}

/**
 * 判断值是否为草图组运行时历史快照。
 *
 * @param value 待判断值。
 * @returns 是否为草图运行时快照。
 */
function isSketchGroupRuntimeHistorySnapshot(value: unknown): value is SketchGroupRuntimeHistorySnapshot {
  return Boolean(value)
    && typeof value === 'object'
    && typeof (value as SketchGroupRuntimeHistorySnapshot).runtimeSnapshotId === 'string'
    && (value as SketchGroupRuntimeHistorySnapshot).persistent === false;
}

/**
 * 释放草图运行时快照。
 *
 * @param snapshot 草图运行时快照。
 */
function releaseEditorSketchRuntimeHistorySnapshot(snapshot: SketchGroupRuntimeHistorySnapshot): void {
  getViewer()?.releaseSketchGroupRuntimeHistorySnapshot?.(snapshot);
}

/**
 * 释放事务中引用的草图运行时快照。
 *
 * @param transactions 被历史栈丢弃的事务集合。
 */
function releaseEditorSketchRuntimeHistoryTransactionSnapshots(transactions: readonly CadTransaction[]): void {
  const releasedSnapshotIds = new Set<string>();

  transactions.forEach((transaction) => {
    transaction.changeSet.patches.forEach((patch) => {
      if (patch.adapterId !== SKETCH_GROUP_RUNTIME_HISTORY_ADAPTER_ID) {
        return;
      }

      [patch.before.data, patch.after.data].forEach((snapshot) => {
        if (!isSketchGroupRuntimeHistorySnapshot(snapshot)) {
          return;
        }
        if (releasedSnapshotIds.has(snapshot.runtimeSnapshotId)) {
          return;
        }
        releasedSnapshotIds.add(snapshot.runtimeSnapshotId);
        releaseEditorSketchRuntimeHistorySnapshot(snapshot);
      });
    });
  });
}

/**
 * 释放未进入历史栈的草图运行时记录快照。
 *
 * @param record 草图组运行时历史记录。
 */
function releaseEditorSketchRuntimeHistoryRecordSnapshots(record: SketchGroupRuntimeHistoryRecord): void {
  releaseEditorSketchRuntimeHistorySnapshot(record.before);
  releaseEditorSketchRuntimeHistorySnapshot(record.after);
}

/**
 * 处理草图运行时历史提交失败。
 *
 * @param record 草图组运行时历史记录。
 * @param error 提交错误。
 */
/**
 * 编辑器草图组运行时历史补丁记录。
 */
interface EditorSketchGroupRuntimePatchRecord {
  /** 草图组 ID。 */
  readonly sketchId: string;
  /** 用户可读标签。 */
  readonly label: string;
  /** 命令类型。 */
  readonly commandType: string;
  /** 变更前快照。 */
  readonly before: HistoryEntitySnapshot<SketchGroupRuntimeHistorySnapshot>;
  /** 变更后快照。 */
  readonly after: HistoryEntitySnapshot<SketchGroupRuntimeHistorySnapshot>;
  /** 与草图组变更同事务提交的附加领域补丁。 */
  readonly extraPatches?: readonly EntityPatch[];
}

/**
 * 释放草图组运行时历史补丁记录中的快照。
 *
 * @param record 草图组运行时历史补丁记录。
 */
function releaseEditorSketchRuntimePatchRecordSnapshots(record: EditorSketchGroupRuntimePatchRecord): void {
  [record.before, record.after].forEach((snapshot) => {
    if (!snapshot.exists || !snapshot.data) {
      return;
    }
    releaseEditorSketchRuntimeHistorySnapshot(snapshot.data);
  });
}

/**
 * 提交草图组运行时历史补丁。
 *
 * 设计说明：
 * 设计树删除/复制草图会产生 create/delete patch，普通绘制和拖拽则是 update patch。
 * 两类操作必须共用同一个 adapter，才能让 Ctrl+Z/Ctrl+Y 在同一模型历史栈中保持顺序。
 *
 * @param record 草图组运行时历史补丁记录。
 * @returns 是否成功提交到模型历史栈。
 */
async function commitEditorSketchRuntimePatchRecordInMemory(
  record: EditorSketchGroupRuntimePatchRecord,
): Promise<boolean> {
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createSketchGroupRuntimeHistoryPatch({
    entityId: record.sketchId,
    operation: resolveEntityPatchOperation(record.before, record.after),
    before: record.before,
    after: record.after,
    metadata: {
      sketchId: record.sketchId,
    },
  });
  const patches = [patch, ...(record.extraPatches ?? [])];
  const affectedEntityIds = [...new Set(patches.map((currentPatch) => currentPatch.entityId))];
  const affectedSketchIds = [...new Set([
    record.sketchId,
    ...patches
      .map((currentPatch) => currentPatch.metadata?.sketchId ?? null)
      .filter((sketchId): sketchId is string => sketchId !== null),
  ])];
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: affectedEntityIds,
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: affectedEntityIds,
    sketchIds: affectedSketchIds,
  };
  const transaction: CadTransaction = {
    id: `editor-sketch-runtime-history-${historySeq}`,
    documentId: projectId.value || record.sketchId,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet(patches),
    status: 'committed',
  };

  const committedTransaction = await editorModelHistoryRuntime.commitTransaction(transaction);

  if (committedTransaction) {
    return true;
  }

  releaseEditorSketchRuntimePatchRecordSnapshots(record);
  return false;
}

/**
 * 草图退出会话历史草稿。
 */
interface EditorSketchSessionExitHistoryDraft {
  /** 草图组 ID。 */
  readonly sketchGroupId: string;
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 退出前的草图运行时快照。 */
  readonly beforeSnapshot: SketchGroupRuntimeHistorySnapshot;
}

/**
 * 判断值是否为草图退出会话历史草稿。
 *
 * @param value 待判断值。
 * @returns 是否为草图退出会话历史草稿。
 */
function isEditorSketchSessionExitHistoryDraft(value: unknown): value is EditorSketchSessionExitHistoryDraft {
  return Boolean(value)
    && typeof value === 'object'
    && typeof (value as EditorSketchSessionExitHistoryDraft).sketchGroupId === 'string'
    && isSketchGroupRuntimeHistorySnapshot((value as EditorSketchSessionExitHistoryDraft).beforeSnapshot);
}

/**
 * 提交进入草图时创建草图组的历史。
 *
 * @param input 草图会话历史输入。
 */
async function commitEditorSketchSessionCreateHistory(input: {
  readonly sketchGroupId: string;
  readonly label: string;
  readonly commandType: string;
}): Promise<void> {
  const afterSnapshot = getViewer()?.captureSketchGroupRuntimeHistorySnapshot?.(input.sketchGroupId, input.label) ?? null;
  if (!afterSnapshot) {
    throw new Error(`无法捕获新建草图 ${input.sketchGroupId} 的历史快照。`);
  }

  try {
    await commitEditorSketchRuntimePatchRecordInMemory({
      sketchId: input.sketchGroupId,
      label: input.label,
      commandType: input.commandType,
      before: createMissingHistorySnapshot(),
      after: createExistingHistorySnapshot(afterSnapshot),
    });
  } catch (error) {
    releaseEditorSketchRuntimeHistorySnapshot(afterSnapshot);
    reportEditorAsyncFailure(error, '提交进入草图历史失败');
    throw error;
  }
}

/**
 * 捕获退出草图前的历史草稿。
 *
 * @param input 草图会话历史输入。
 * @returns 草图退出历史草稿；无法捕获时返回 null。
 */
function captureEditorSketchSessionExitHistory(input: {
  readonly sketchGroupId: string;
  readonly label: string;
  readonly commandType: string;
}): EditorSketchSessionExitHistoryDraft | null {
  const beforeSnapshot = getViewer()?.captureSketchGroupRuntimeHistorySnapshot?.(input.sketchGroupId, input.label) ?? null;
  if (!beforeSnapshot) {
    return null;
  }
  return {
    sketchGroupId: input.sketchGroupId,
    label: input.label,
    commandType: input.commandType,
    beforeSnapshot,
  };
}

/**
 * 提交退出草图历史。
 *
 * @param draft 草图退出历史草稿。
 * @param result 退出结果。
 */
async function commitEditorSketchSessionExitHistory(
  draft: unknown,
  result: {
    readonly removed: boolean;
  },
): Promise<void> {
  if (!isEditorSketchSessionExitHistoryDraft(draft)) {
    return;
  }

  const afterSnapshot = result.removed
    ? null
    : getViewer()?.captureSketchGroupRuntimeHistorySnapshot?.(draft.sketchGroupId, draft.label) ?? null;

  if (!result.removed && !afterSnapshot) {
    releaseEditorSketchRuntimeHistorySnapshot(draft.beforeSnapshot);
    throw new Error(`无法捕获退出草图 ${draft.sketchGroupId} 后的历史快照。`);
  }

  try {
    await commitEditorSketchRuntimePatchRecordInMemory({
      sketchId: draft.sketchGroupId,
      label: draft.label,
      commandType: draft.commandType,
      before: createExistingHistorySnapshot(draft.beforeSnapshot),
      after: afterSnapshot ? createExistingHistorySnapshot(afterSnapshot) : createMissingHistorySnapshot(),
    });
  } catch (error) {
    releaseEditorSketchSessionExitHistory(draft);
    if (afterSnapshot) {
      releaseEditorSketchRuntimeHistorySnapshot(afterSnapshot);
    }
    reportEditorAsyncFailure(error, '提交退出草图历史失败');
    throw error;
  }
}

/**
 * 释放未提交的退出草图历史草稿。
 *
 * @param draft 草图退出历史草稿。
 */
function releaseEditorSketchSessionExitHistory(draft: unknown): void {
  if (!isEditorSketchSessionExitHistoryDraft(draft)) {
    return;
  }
  releaseEditorSketchRuntimeHistorySnapshot(draft.beforeSnapshot);
}

function handleEditorSketchRuntimeHistoryCommitFailure(
  record: SketchGroupRuntimeHistoryRecord,
  error: unknown,
): void {
  reportEditorAsyncFailure(error, `提交草图运行时历史失败：${record.label}`);
}

/**
 * 提交草图组运行时历史记录。
 *
 * 设计说明：
 * 1. 草图绘制、删除、裁剪会联动几何、控制点、约束和封闭轮廓；
 * 2. 在草图 normalized 主状态未完全落地前，先以运行时整组快照 ID 进入会话历史；
 * 3. 事务 journal 可进入 IndexedDB/OPFS 会话冷存储，但真实 Three.js 克隆体只保留到当前页面结束。
 *
 * @param record 草图组运行时历史记录。
 */
async function commitEditorSketchRuntimeHistoryRecordInMemory(record: SketchGroupRuntimeHistoryRecord): Promise<void> {
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createSketchGroupRuntimeHistoryPatch({
    entityId: record.sketchId,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
    metadata: {
      sketchId: record.sketchId,
    },
  });
  const patches = [patch, ...(record.extraPatches ?? [])];
  const affectedEntityIds = [...new Set(patches.map((currentPatch) => currentPatch.entityId))];
  const affectedSketchIds = [...new Set([
    record.sketchId,
    ...patches
      .map((currentPatch) => currentPatch.metadata?.sketchId ?? null)
      .filter((sketchId): sketchId is string => sketchId !== null),
  ])];
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: affectedEntityIds,
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: affectedEntityIds,
    sketchIds: affectedSketchIds,
  };
  const transaction: CadTransaction = {
    id: `editor-sketch-runtime-history-${historySeq}`,
    documentId: projectId.value || record.sketchId,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    // 附加领域补丁（如删除实体联动的尺寸悬空迁移）与运行时快照补丁同一笔事务（HIST-001）。
    changeSet: createChangeSet(patches),
    status: 'committed',
  };
  /**
   * 草图运行时快照的轻量事务会写入会话级冷存储；真实 Three.js 克隆体仍由 Viewer
   * 当前页面注册表持有，并在事务被丢弃或页面结束时释放，避免跨会话恢复运行时对象。
   */
  const committedTransaction = await editorModelHistoryRuntime.commitTransaction(transaction);

  if (committedTransaction) {
    return;
  }

  /**
   * Viewer 仍持有 before/after 快照的恢复与释放所有权。这里必须把“未入栈”
   * 视为提交失败并抛出，才能让 cad-core 统一恢复草图并补偿引用等额外领域状态。
   */
  throw new Error(`草图运行时历史事务未提交：${record.commandType}`);
}

/**
 * 提交草图组运行时历史记录。
 *
 * @param record 草图组运行时历史记录。
 */
async function commitEditorSketchRuntimeHistoryRecord(record: SketchGroupRuntimeHistoryRecord): Promise<void> {
  try {
    /**
     * 草图绘制会等待 cad-core 会话历史提交完成。这里的 IndexedDB/OPFS 只在当前页面会话内使用，
     * 页面刷新或关闭后会被清空，不参与 crash recovery。
     */
    await commitEditorSketchRuntimeHistoryRecordInMemory(record);
    return;
  } catch (error) {
    handleEditorSketchRuntimeHistoryCommitFailure(record, error);
    throw error;
  }
}

/**
 * 提交编辑器内的装配体 transform 历史记录。
 *
 * 设计说明：
 * 1. Viewer 只负责产生装配体 before/after record，不直接依赖 web-client store；
 * 2. 这里先执行 IndexedDB/OPFS 提交协议，成功后再写入内存 ModelHistoryManager；
 * 3. 如果持久化失败，必须用 before patch 回滚场景，避免模型已变但历史缺记录。
 *
 * @param record 装配体 transform 历史记录。
 */
/**
 * 读取设计树草图生命周期动作的历史标签。
 *
 * @param action 设计树草图生命周期动作。
 * @returns 用户可读历史标签。
 */
function getFeatureTreeSketchLifecycleHistoryLabel(action: FeatureTreeSketchLifecycleActionInput['action']): string {
  if (action === 'delete') {
    return '删除草图';
  }
  if (action === 'hide') {
    return '隐藏草图';
  }
  if (action === 'show') {
    return '显示草图';
  }
  return '复制草图';
}

/**
 * 执行设计树草图生命周期动作并提交历史。
 *
 * 设计说明：
 * 设计树动作不是普通 UI 状态，它会改变 CAD 模型中草图组是否存在或是否显示。
 * 因此必须在 Editor 统一捕获 before/after，写入 ModelHistoryManager，避免绕过 Ctrl+Z/Ctrl+Y。
 *
 * @param input 设计树草图生命周期动作。
 * @returns 动作是否成功。
 */
async function applyFeatureTreeSketchLifecycleActionInQueue(
  input: FeatureTreeSketchLifecycleActionInput,
): Promise<boolean> {
  const currentViewer = getViewer();
  const label = getFeatureTreeSketchLifecycleHistoryLabel(input.action);

  if (!currentViewer) {
    return false;
  }

  if (input.action === 'copy') {
    const duplication = currentViewer.duplicateSketchGroupWithDimensions?.(input.sketchGroupId) ?? null;

    if (!duplication) {
      return false;
    }
    const duplicatedSketchId = duplication.group.uuid;

    const afterSnapshot = currentViewer.captureSketchGroupRuntimeHistorySnapshot?.(duplicatedSketchId, label) ?? null;

    if (!afterSnapshot) {
      currentViewer.discardDuplicatedSketchGroup?.(duplicatedSketchId);
      return false;
    }

    try {
      const committed = await commitEditorSketchRuntimePatchRecordInMemory({
        sketchId: duplicatedSketchId,
        label,
        commandType: 'feature-tree.sketch.copy',
        before: createMissingHistorySnapshot(),
        after: createExistingHistorySnapshot(afterSnapshot),
        extraPatches: duplication.dimensionPatches,
      });

      if (!committed) {
        currentViewer.discardDuplicatedSketchGroup?.(duplicatedSketchId);
        return false;
      }
    } catch (error) {
      currentViewer.discardDuplicatedSketchGroup?.(duplicatedSketchId);
      releaseEditorSketchRuntimeHistorySnapshot(afterSnapshot);
      throw error;
    }
    return true;
  }

  const beforeSnapshot = currentViewer.captureSketchGroupRuntimeHistorySnapshot?.(input.sketchGroupId, label) ?? null;

  if (!beforeSnapshot) {
    return false;
  }

  const rollbackToBefore = (): void => {
    currentViewer.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
  };
  let actionSucceeded = false;

  if (input.action === 'delete') {
    actionSucceeded = currentViewer.removeSketchGroup?.(input.sketchGroupId) ?? false;
  } else if (input.action === 'hide') {
    actionSucceeded = currentViewer.setSketchGroupDisplayState?.(
      input.sketchGroupId,
      SketchDisplayState.Hidden,
    ) ?? false;
  } else {
    actionSucceeded = currentViewer.setSketchGroupDisplayState?.(
      input.sketchGroupId,
      SketchDisplayState.Shown,
    ) ?? false;
  }

  if (!actionSucceeded) {
    releaseEditorSketchRuntimeHistorySnapshot(beforeSnapshot);
    return false;
  }

  const afterSnapshot = input.action === 'delete'
    ? null
    : currentViewer.captureSketchGroupRuntimeHistorySnapshot?.(input.sketchGroupId, label) ?? null;

  if (input.action !== 'delete' && !afterSnapshot) {
    rollbackToBefore();
    releaseEditorSketchRuntimeHistorySnapshot(beforeSnapshot);
    return false;
  }

  try {
    await commitEditorSketchRuntimePatchRecordInMemory({
      sketchId: input.sketchGroupId,
      label,
      commandType: `feature-tree.sketch.${input.action}`,
      before: createExistingHistorySnapshot(beforeSnapshot),
      after: afterSnapshot ? createExistingHistorySnapshot(afterSnapshot) : createMissingHistorySnapshot(),
    });
  } catch (error) {
    rollbackToBefore();
    releaseEditorSketchRuntimeHistorySnapshot(beforeSnapshot);
    if (afterSnapshot) {
      releaseEditorSketchRuntimeHistorySnapshot(afterSnapshot);
    }
    throw error;
  }
  return true;
}

/**
 * 串行执行设计树草图生命周期动作。
 *
 * @param input 设计树草图生命周期动作。
 * @returns 动作是否成功。
 */
async function handleFeatureTreeSketchLifecycleAction(
  input: FeatureTreeSketchLifecycleActionInput,
): Promise<boolean> {
  let succeeded = false;

  try {
    if (input.action === 'delete') {
      // 删除草图会改变模型主状态，必须先使用与 Feature 删除一致的确认框收口用户意图。
      const sketchName = featureTreeStore.objectNameOverrides.get(input.sketchGroupId)?.trim()
        || getViewer()?.getSketchGroupName?.(input.sketchGroupId)
        || input.sketchGroupId;
      const confirmed = await requestFeatureTreeDeleteConfirmation({
        dialog,
        target: {
          id: input.sketchGroupId,
          label: sketchName,
          kind: 'sketch',
        },
        relatedItems: [],
      });

      if (!confirmed) {
        return false;
      }
    }

    await editorModelHistoryRuntime.runExclusive(async () => {
      succeeded = await applyFeatureTreeSketchLifecycleActionInQueue(input);
    });
  } catch (error) {
    reportEditorAsyncFailure(error, `${getFeatureTreeSketchLifecycleHistoryLabel(input.action)}失败`);
    return false;
  }

  return succeeded;
}

//------ 基准对象删除历史 开始------
/**
 * 读取设计树基准对象删除的历史标签。
 *
 * @param kind 基准对象类型。
 * @returns 用户可读历史标签。
 */
function getFeatureTreeDatumReferenceDeleteHistoryLabel(
  kind: FeatureTreeDatumReferenceDeleteActionInput['kind'],
): string {
  switch (kind) {
    case FeatureTreeDatumReferenceKind.Plane:
      return '删除基准面';
    case FeatureTreeDatumReferenceKind.Axis:
      return '删除基准轴';
    case FeatureTreeDatumReferenceKind.Point:
      return '删除基准点';
    default:
      return '删除基准对象';
  }
}

/**
 * 读取设计树基准对象删除的历史命令类型。
 *
 * @param kind 基准对象类型。
 * @returns 历史命令类型。
 */
function getFeatureTreeDatumReferenceDeleteCommandType(
  kind: FeatureTreeDatumReferenceDeleteActionInput['kind'],
): string {
  switch (kind) {
    case FeatureTreeDatumReferenceKind.Plane:
      return 'feature-tree.datum-plane.delete';
    case FeatureTreeDatumReferenceKind.Axis:
      return 'feature-tree.datum-axis.delete';
    case FeatureTreeDatumReferenceKind.Point:
      return 'feature-tree.datum-point.delete';
    default:
      return 'feature-tree.datum-reference.delete';
  }
}

/**
 * 删除设计树基准面并提交模型历史。
 *
 * 设计说明：
 * 基准面历史由 DatumPlaneManager 统一恢复，删除前后只保存用户创建的基准面 plain data。
 * 删除失败或历史提交异常时恢复 before，避免出现“模型已删但无法撤销”的状态。
 *
 * @param planeId 基准面 ID。
 * @returns 删除是否成功。
 */
async function applyFeatureTreeDatumPlaneDeleteActionInQueue(planeId: number): Promise<boolean> {
  const viewer = getViewer();
  const manager = viewer?.datumPlaneManager;
  const targetPlane = viewer?.getDatumPlane?.(planeId) ?? null;

  if (!manager || !targetPlane) {
    return false;
  }

  const rawBeforeSnapshot = manager.createHistorySnapshot();
  const beforeSnapshot = viewer?.referencePlaneController?.normalizeHistorySnapshotBeforeSubmit(rawBeforeSnapshot)
    ?? rawBeforeSnapshot;

  manager.removePlane(planeId);

  try {
    await viewer?.flushDatumPlaneFeatureRebuild();
    await commitEditorDatumPlaneHistoryInQueue({
      label: getFeatureTreeDatumReferenceDeleteHistoryLabel(FeatureTreeDatumReferenceKind.Plane),
      commandType: getFeatureTreeDatumReferenceDeleteCommandType(FeatureTreeDatumReferenceKind.Plane),
      before: beforeSnapshot,
      after: manager.createHistorySnapshot(),
    });
  } catch (error) {
    manager.restoreHistorySnapshot(beforeSnapshot);
    await viewer?.flushDatumPlaneFeatureRebuild();
    throw error;
  }

  return true;
}

/**
 * 删除设计树基准轴并提交模型历史。
 *
 * 设计说明：
 * before 优先通过 ReferenceAxisController 捕获可编辑特征记录，确保 Undo 能恢复轴对象与重算定义；
 * after 使用 manager 快照并过滤目标轴记录，让 Redo 删除时同步清空已失效的可编辑记录。
 *
 * @param axisId 基准轴 ID。
 * @returns 删除是否成功。
 */
async function applyFeatureTreeDatumAxisDeleteActionInQueue(axisId: number): Promise<boolean> {
  const viewer = getViewer();
  const manager = viewer?.datumAxisManager;
  const controller = viewer?.referenceAxisController;
  const targetAxis = viewer?.getDatumAxis?.(axisId) ?? null;

  if (!manager || !targetAxis) {
    return false;
  }

  const beforeSnapshot = controller?.createHistorySnapshot() ?? manager.createHistorySnapshot();

  manager.removeAxis(axisId);

  try {
    const afterSnapshot: DatumAxisHistorySnapshot = {
      ...manager.createHistorySnapshot(),
      // 删除一个基准轴只能移除自己的可编辑记录，不能污染其他基准轴的编辑链路。
      features: (beforeSnapshot.features ?? []).filter((record) => record.datumAxisId !== axisId),
    };

    await commitEditorDatumAxisHistoryInQueue({
      label: getFeatureTreeDatumReferenceDeleteHistoryLabel(FeatureTreeDatumReferenceKind.Axis),
      commandType: getFeatureTreeDatumReferenceDeleteCommandType(FeatureTreeDatumReferenceKind.Axis),
      before: beforeSnapshot,
      after: afterSnapshot,
    });
  } catch (error) {
    if (controller) {
      controller.restoreHistorySnapshot(beforeSnapshot);
    } else {
      manager.restoreHistorySnapshot(beforeSnapshot);
    }
    throw error;
  }

  return true;
}

/**
 * 删除设计树基准点并提交模型历史。
 *
 * 设计说明：
 * 默认原点不进入历史快照，设计树也不暴露删除入口；这里仍以目标点存在性作为保护。
 * before 保留 ReferencePointController 的特征记录，after 使用 manager 快照清掉被删除点的可编辑记录。
 *
 * @param pointId 基准点 ID。
 * @returns 删除是否成功。
 */
async function applyFeatureTreeDatumPointDeleteActionInQueue(pointId: number): Promise<boolean> {
  const viewer = getViewer();
  const manager = viewer?.datumPointManager;
  const controller = viewer?.referencePointController;
  const targetPoint = viewer?.getDatumPoint?.(pointId) ?? null;

  if (!manager || !targetPoint || targetPoint.isOrigin) {
    return false;
  }

  const beforeSnapshot = controller?.createHistorySnapshot() ?? manager.createHistorySnapshot();

  manager.removePoint(pointId);

  try {
    const afterSnapshot: DatumPointHistorySnapshot = {
      ...manager.createHistorySnapshot(),
      /**
       * 点特征可能一次生成多个点。删除其中一个正式点时，只移除包含该点的记录，
       * 避免 Redo 把其它点特征记录一并清空或留下指向已删除点的半失效记录。
       */
      features: (beforeSnapshot.features ?? []).filter((record) => !(record.datumPointIds ?? []).includes(pointId)),
    };

    await commitEditorDatumPointHistoryInQueue({
      label: getFeatureTreeDatumReferenceDeleteHistoryLabel(FeatureTreeDatumReferenceKind.Point),
      commandType: getFeatureTreeDatumReferenceDeleteCommandType(FeatureTreeDatumReferenceKind.Point),
      before: beforeSnapshot,
      after: afterSnapshot,
    });
  } catch (error) {
    if (controller) {
      controller.restoreHistorySnapshot(beforeSnapshot);
    } else {
      manager.restoreHistorySnapshot(beforeSnapshot);
    }
    throw error;
  }

  return true;
}

/**
 * 执行设计树基准对象删除并提交模型历史。
 *
 * @param input 基准对象删除动作。
 * @returns 删除是否成功。
 */
async function applyFeatureTreeDatumReferenceDeleteActionInQueue(
  input: FeatureTreeDatumReferenceDeleteActionInput,
): Promise<boolean> {
  switch (input.kind) {
    case FeatureTreeDatumReferenceKind.Plane:
      return applyFeatureTreeDatumPlaneDeleteActionInQueue(input.datumId);
    case FeatureTreeDatumReferenceKind.Axis:
      return applyFeatureTreeDatumAxisDeleteActionInQueue(input.datumId);
    case FeatureTreeDatumReferenceKind.Point:
      return applyFeatureTreeDatumPointDeleteActionInQueue(input.datumId);
    default:
      return false;
  }
}

/**
 * 串行执行设计树基准对象删除动作。
 *
 * @param input 基准对象删除动作。
 * @returns 删除是否成功。
 */
async function handleFeatureTreeDatumReferenceDeleteAction(
  input: FeatureTreeDatumReferenceDeleteActionInput,
): Promise<boolean> {
  let succeeded = false;

  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      succeeded = await applyFeatureTreeDatumReferenceDeleteActionInQueue(input);
    });
  } catch (error) {
    reportEditorAsyncFailure(error, `${getFeatureTreeDatumReferenceDeleteHistoryLabel(input.kind)}失败`);
    return false;
  }

  return succeeded;
}
//------ 基准对象删除历史 结束------

async function commitEditorAssemblyTransformHistoryInQueue(record: AssemblyTransformHistoryRecord): Promise<void> {
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const records = flattenAssemblyTransformHistoryRecords(record);
  const patches = records.map(createAssemblyTransformHistoryPatch);
  const entityIds = Array.from(new Set(records.map((item) => item.after.componentInstanceId)));
  const assemblyIds = Array.from(new Set(records.map((item) => item.after.assemblyId)));
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds,
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds,
    componentInstanceIds: entityIds,
    assemblyIds,
  };

  const transaction: CadTransaction = {
    id: `editor-assembly-history-${historySeq}`,
    documentId: projectId.value || record.after.assemblyId,
    seq: historySeq,
    historySeq,
    label: '移动零部件',
    commandType: `assembly.${record.operation}`,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet(patches),
    status: 'committed',
  };

  const committedTransaction = await commitEditorCadTransactionInQueue(transaction);
  if (!committedTransaction) {
    return;
  }

  records.forEach((item) => {
    glbLoader.applyAssemblyComponentHistorySnapshot(item.after);
  });
}

/**
 * 提交编辑器内的装配体 transform 历史记录。
 *
 * @param record 装配体 transform 历史记录。
 */
async function commitEditorAssemblyTransformHistory(record: AssemblyTransformHistoryRecord): Promise<void> {
  return editorModelHistoryRuntime.runExclusive(() => commitEditorAssemblyTransformHistoryInQueue(record))
    .catch((error) => {
      reportEditorAsyncFailure(error, '提交装配体变换历史失败');
      throw error;
    });
}

//------ 装配体引用复制历史 开始------
/**
 * 在模型历史队列中提交装配体引用复制创建记录。
 *
 * @param snapshot 复制体历史快照。
 */
async function commitEditorAssemblyReferenceCopyHistoryInQueue(
  snapshot: AssemblyComponentHistorySnapshot,
): Promise<void> {
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createAssemblyComponentHistoryPatch({
    entityId: snapshot.componentInstanceId,
    operation: 'create',
    before: createMissingHistorySnapshot(),
    after: createExistingHistorySnapshot(snapshot),
    metadata: {
      assemblyId: snapshot.assemblyId,
      componentInstanceId: snapshot.componentInstanceId,
    },
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: [snapshot.componentInstanceId],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: [snapshot.componentInstanceId],
    componentInstanceIds: [snapshot.componentInstanceId],
    assemblyIds: [snapshot.assemblyId],
  };
  const transaction: CadTransaction = {
    id: `editor-assembly-reference-copy-${historySeq}`,
    documentId: projectId.value || snapshot.assemblyId,
    seq: historySeq,
    historySeq,
    label: '复制零部件',
    commandType: 'assembly.reference-copy',
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}

/**
 * 提交装配体引用复制历史。
 *
 * @param snapshot 复制体历史快照。
 */
async function commitEditorAssemblyReferenceCopyHistory(
  snapshot: AssemblyComponentHistorySnapshot,
): Promise<void> {
  return editorModelHistoryRuntime.runExclusive(() => commitEditorAssemblyReferenceCopyHistoryInQueue(snapshot))
    .catch((error) => {
      reportEditorAsyncFailure(error, '提交装配体复制历史失败');
      throw error;
    });
}
//------ 装配体引用复制历史 结束------

/**
 * 装配结构历史记录。
 */
interface EditorAssemblyStructureHistoryRecord {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 操作前快照。 */
  readonly before: AssemblyStructureHistorySnapshot;
  /** 操作后快照。 */
  readonly after: AssemblyStructureHistorySnapshot;
}

/**
 * 在装配结构历史队列中提交记录。
 *
 * 设计说明：
 * 1. 删除零部件/子装配体会同时改变项目模型树、sceneBindings、装配文档索引和
 *    `working_assembly.json`，必须作为一个结构事务整体进入 Undo/Redo；
 * 2. 历史快照只保存 cad-core 项目模型 plain data，场景 Object3D 由 loader 在 Undo/Redo flush 阶段重建；
 * 3. 这里不触碰 `_assembly.json` 基线文档，base/working 的边界仍由 AssemblyDraftDocumentStore 维护。
 *
 * @param record 装配结构历史记录。
 */
async function commitEditorAssemblyStructureHistoryInQueue(
  record: EditorAssemblyStructureHistoryRecord,
): Promise<void> {
  if (JSON.stringify(record.before) === JSON.stringify(record.after)) {
    return;
  }

  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createAssemblyStructureHistoryPatch({
    entityId: ASSEMBLY_STRUCTURE_HISTORY_ENTITY_ID,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
    metadata: {
      assemblyId: record.before.rootNodeKey,
    },
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: [ASSEMBLY_STRUCTURE_HISTORY_ENTITY_ID],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: [ASSEMBLY_STRUCTURE_HISTORY_ENTITY_ID],
    assemblyIds: [record.before.rootNodeKey],
    fullRebuild: true,
  };
  const transaction: CadTransaction = {
    id: `editor-assembly-structure-history-${historySeq}`,
    documentId: projectId.value || record.before.rootNodeKey,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}

//------ 线性零部件阵列撤销重做 开始------
/**
 * 线性零部件阵列历史记录。
 */
interface EditorLinearComponentPatternHistoryRecord {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 操作前快照。 */
  readonly before: LinearComponentPatternHistorySnapshot;
  /** 操作后快照。 */
  readonly after: LinearComponentPatternHistorySnapshot;
}

/**
 * 在线性阵列历史队列中提交记录。
 *
 * 设计说明：
 * 1. 线性阵列创建、编辑、删除都会改变 CAD 主状态，必须进入模型 Undo/Redo；
 * 2. adapter 使用 manager 整体快照，避免单独维护阵列记录列表和场景分组时出现半恢复状态；
 * 3. 快照只包含 `definition`、`commitData`、节点 ID 等 plain data，不保存 Three.js 对象。
 *
 * @param record 线性阵列历史记录。
 */
async function commitEditorLinearComponentPatternHistoryInQueue(
  record: EditorLinearComponentPatternHistoryRecord,
): Promise<void> {
  if (JSON.stringify(record.before) === JSON.stringify(record.after)) {
    return;
  }

  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createLinearComponentPatternHistoryPatch({
    entityId: LINEAR_COMPONENT_PATTERN_HISTORY_ENTITY_ID,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: [LINEAR_COMPONENT_PATTERN_HISTORY_ENTITY_ID],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: [LINEAR_COMPONENT_PATTERN_HISTORY_ENTITY_ID],
  };
  const transaction: CadTransaction = {
    id: `editor-linear-component-pattern-history-${historySeq}`,
    documentId: projectId.value || LINEAR_COMPONENT_PATTERN_HISTORY_ENTITY_ID,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}
//------ 线性零部件阵列撤销重做 结束------

//------ 基准面撤销重做 开始------
/**
 * 基准面历史记录。
 */
/**
 * 在模型历史队列中提交装配体配合文档快照。
 *
 * 设计说明：
 * 1. Mate 定义、求解状态与拓扑引用都由 cad-core 输出为可序列化 plain data；
 * 2. web-client 只把 before/after 快照交给历史系统，不解析 BRep、不保存运行时 Object3D；
 * 3. 这里不写入 IndexedDB 业务表，刷新页面后不从本地缓存恢复 Mate。
 *
 * @param before 变更前的配合文档快照。
 * @param after 变更后的配合文档快照。
 * @param context cad-core 提供的文档变更上下文。
 */
async function commitEditorAssemblyMateDocumentHistoryInQueue(
  before: AssemblyMateDocumentModel,
  after: AssemblyMateDocumentModel,
  context: AssemblyMateDocumentChangeContext,
): Promise<void> {
  if (JSON.stringify(before) === JSON.stringify(after)) {
    return;
  }

  const entityId = createAssemblyMateDocumentHistoryEntityId(after.assemblyId);
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createAssemblyMateHistoryPatch({
    entityId,
    operation: 'update',
    before: createExistingHistorySnapshot(before),
    after: createExistingHistorySnapshot(after),
    metadata: {
      assemblyId: after.assemblyId,
      source: context.source,
    },
  });
  const transaction: CadTransaction = {
    id: `editor-assembly-mate-history-${historySeq}`,
    documentId: projectId.value || after.assemblyId,
    seq: historySeq,
    historySeq,
    label: '配合',
    commandType: `assembly.mate.${context.source}`,
    createdAt: Date.now(),
    clientId: 'web-client',
    snapshotScope: {
      ...createEmptySnapshotScope(),
      entityIds: [entityId],
    },
    dirtyScope: {
      ...createEmptyDirtyScope(),
      entityIds: [entityId],
      fullRebuild: true,
    },
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}

interface EditorDatumPlaneHistoryRecord {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 操作前快照。 */
  readonly before: DatumPlaneHistorySnapshot;
  /** 操作后快照。 */
  readonly after: DatumPlaneHistorySnapshot;
}

/**
 * 在历史队列中提交基准面历史记录。
 *
 * @param record 基准面历史记录。
 */
async function commitEditorDatumPlaneHistoryInQueue(record: EditorDatumPlaneHistoryRecord): Promise<void> {
  if (JSON.stringify(record.before) === JSON.stringify(record.after)) {
    return;
  }

  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createDatumPlaneHistoryPatch({
    entityId: DATUM_PLANE_HISTORY_ENTITY_ID,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
    metadata: {
      featureId: DATUM_PLANE_HISTORY_ENTITY_ID,
    },
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: [DATUM_PLANE_HISTORY_ENTITY_ID],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: [DATUM_PLANE_HISTORY_ENTITY_ID],
  };
  const transaction: CadTransaction = {
    id: `editor-datum-plane-history-${historySeq}`,
    documentId: projectId.value || DATUM_PLANE_HISTORY_ENTITY_ID,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}
//------ 基准面撤销重做 结束------

//------ 基准轴撤销重做 开始------
/**
 * 基准轴历史记录。
 */
interface EditorDatumAxisHistoryRecord {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 操作前快照。 */
  readonly before: DatumAxisHistorySnapshot;
  /** 操作后快照。 */
  readonly after: DatumAxisHistorySnapshot;
}

/**
 * 在历史队列中提交基准轴历史记录。
 *
 * @param record 基准轴历史记录。
 */
async function commitEditorDatumAxisHistoryInQueue(record: EditorDatumAxisHistoryRecord): Promise<void> {
  if (JSON.stringify(record.before) === JSON.stringify(record.after)) {
    return;
  }

  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createDatumAxisHistoryPatch({
    entityId: DATUM_AXIS_HISTORY_ENTITY_ID,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
    metadata: {
      featureId: DATUM_AXIS_HISTORY_ENTITY_ID,
    },
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: [DATUM_AXIS_HISTORY_ENTITY_ID],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: [DATUM_AXIS_HISTORY_ENTITY_ID],
  };
  const transaction: CadTransaction = {
    id: `editor-datum-axis-history-${historySeq}`,
    documentId: projectId.value || DATUM_AXIS_HISTORY_ENTITY_ID,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}
//------ 基准轴撤销重做 结束------

//------ 基准点撤销重做 开始------
/**
 * 基准点历史记录。
 */
interface EditorDatumPointHistoryRecord {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 操作前快照。 */
  readonly before: DatumPointHistorySnapshot;
  /** 操作后快照。 */
  readonly after: DatumPointHistorySnapshot;
}

/**
 * 在历史队列中提交基准点历史记录。
 *
 * @param record 基准点历史记录。
 */
async function commitEditorDatumPointHistoryInQueue(record: EditorDatumPointHistoryRecord): Promise<void> {
  if (JSON.stringify(record.before) === JSON.stringify(record.after)) {
    return;
  }

  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const patch = createDatumPointHistoryPatch({
    entityId: DATUM_POINT_HISTORY_ENTITY_ID,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
    metadata: {
      featureId: DATUM_POINT_HISTORY_ENTITY_ID,
    },
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds: [DATUM_POINT_HISTORY_ENTITY_ID],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds: [DATUM_POINT_HISTORY_ENTITY_ID],
  };
  const transaction: CadTransaction = {
    id: `editor-datum-point-history-${historySeq}`,
    documentId: projectId.value || DATUM_POINT_HISTORY_ENTITY_ID,
    seq: historySeq,
    historySeq,
    label: record.label,
    commandType: record.commandType,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await commitEditorCadTransactionInQueue(transaction);
}
//------ 基准点撤销重做 结束------

/**
 * 在当前会话内提交草图 solver island 历史记录。
 *
 * 设计说明：
 * 1. 当前 solver island 快照依赖场景里的草图运行时对象作为恢复入口；
 * 2. 页面重开时这些 Three.js 运行时对象未必已经存在，因此该记录只作为当前会话历史使用；
 * 3. 后续 normalized Sketch / Constraint 主状态落地后，再把 solver island 切回可跨会话恢复的文档级事务。
 *
 * @param record solver island 记录。
 */
async function commitEditorSketchSolverHistoryRecordInMemory(record: SketchSolverRecord): Promise<void> {
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const entityIds = Array.from(new Set([
    ...record.before.entityIds,
    ...record.after.entityIds,
    ...record.before.pointIds,
    ...record.after.pointIds,
    record.islandId,
  ]));
  const patch = createSketchSolverIslandHistoryPatch({
    entityId: record.islandId,
    operation: 'update',
    before: createExistingHistorySnapshot(record.before),
    after: createExistingHistorySnapshot(record.after),
    metadata: {
      sketchId: record.sketchId,
    },
  });
  const snapshotScope = {
    ...createEmptySnapshotScope(),
    entityIds,
    solverIslandIds: [record.islandId],
  };
  const dirtyScope = {
    ...createEmptyDirtyScope(),
    entityIds,
    sketchIds: [record.sketchId],
  };
  const transaction: CadTransaction = {
    id: `editor-sketch-solver-history-${historySeq}`,
    documentId: projectId.value || record.sketchId,
    seq: historySeq,
    historySeq,
    label: '草图求解',
    commandType: `${SKETCH_SOLVER_RUNTIME_HISTORY_COMMAND_PREFIX}${record.source}`,
    createdAt: Date.now(),
    snapshotScope,
    dirtyScope,
    changeSet: createChangeSet([patch]),
    status: 'committed',
  };

  await editorModelHistoryRuntime.commitTransaction(transaction);
}

/**
 * 将 cad-core solver record 接入编辑器模型历史。
 *
 * @param record solver island 记录。
 */
async function commitEditorSketchSolverHistoryRecord(record: SketchSolverRecord): Promise<void> {
  if (editorSketchSolverHistoryCommitPending) {
    throw new Error('前一笔草图求解历史尚未提交完成，已拒绝新的 solver 全局历史提交。');
  }

  /**
   * solver 操作已经把运行时推进到 after。这里写入 cad-core 会话历史；
   * 会话历史负责 10 条热窗口和 IndexedDB/OPFS 冷存储换入，不做跨刷新恢复。
   */
    editorSketchSolverHistoryCommitPending = true;
  try {
    editorSketchSolverIslandHistorySnapshots.set(record.islandId, record.after);
    await commitEditorSketchSolverHistoryRecordInMemory(record);
    return;
  } catch (error) {
    reportEditorAsyncFailure(error, '提交草图求解历史失败');
    throw error;
  } finally {
    editorSketchSolverHistoryCommitPending = false;
  }
}

/**
 * 配置当前编辑器的草图 solver history runtime。
 */
function configureEditorSketchSolverHistoryRuntime(): void {
  disposeEditorSketchSolverHistoryRuntime?.();
  const currentScene = getViewer()?.getScene?.() ?? null;
  if (!currentScene) {
    return;
  }
  const dimensionRepository = getViewer()?.getSketchDimensionRepository() ?? null;

  disposeEditorSketchSolverHistoryRuntime = configureSketchSolverHistoryRuntime({
    createRecordId(source) {
      editorSketchSolverRecordSeq += 1;

      return `editor-solver-record:${source}:${editorSketchSolverRecordSeq}`;
    },
    onSolverRecord(record) {
      return commitEditorSketchSolverHistoryRecord(record);
    },
    /** 尺寸补充状态 provider：使 solver island 快照携带真实 dimensionStates（含运行时草图 ID → 持久 ID 归一化）。 */
    ...(dimensionRepository
      ? {
        supplementProvider: createSketchDimensionSolverSupplementProvider(
          dimensionRepository,
          (runtimeSketchId) => getViewer()?.sketchGroupManager
            ?.getGroupById(runtimeSketchId)?.persistentId ?? null,
        ),
      }
      : {}),
  }, currentScene);
}

//------ 智能尺寸命令接入 开始------
/** 智能尺寸命令快照（弹层与状态展示的数据源）。 */
const editorSmartDimensionSnapshot = ref<SmartDimensionCommandSnapshot | null>(null);
/** 智能尺寸快照订阅清理函数。 */
let disposeEditorSmartDimensionSnapshotSubscription: (() => void) | null = null;

/**
 * 提交一笔尺寸原子事务（dimension patch + solver patch 同一 Model step）。
 *
 * @param input 尺寸事务输入。
 */
async function commitEditorSketchDimensionTransaction(input: SketchDimensionTransactionInput): Promise<void> {
  const historySeq = editorModelHistoryRuntime.allocateSequence();
  const solverIslandIds = input.patches
    .filter((patch) => patch.entityKind === 'solver-island')
    .map((patch) => patch.entityId);
  const transaction: CadTransaction = {
    id: `editor-sketch-dimension-${historySeq}`,
    documentId: projectId.value || input.sketchId,
    seq: historySeq,
    historySeq,
    label: input.label,
    commandType: input.commandType,
    createdAt: Date.now(),
    snapshotScope: {
      ...createEmptySnapshotScope(),
      entityIds: [input.dimensionId],
      solverIslandIds,
    },
    dirtyScope: {
      ...createEmptyDirtyScope(),
      entityIds: [input.dimensionId],
      sketchIds: [input.sketchId],
    },
    changeSet: createChangeSet([...input.patches]),
    status: 'committed',
  };

  // 同步 solver island 轻量镜像，保持与 solver record 提交路径一致的恢复语义。
  input.patches.forEach((patch) => {
    if (patch.entityKind === 'solver-island' && patch.after.exists && patch.after.data) {
      editorSketchSolverIslandHistorySnapshots.set(patch.entityId, patch.after.data as SketchSolverIslandSnapshot);
    }
  });

  await editorModelHistoryRuntime.commitTransaction(transaction);
}

/**
 * 配置智能尺寸命令运行时（事务端口注入 + 快照订阅 + 已有尺寸编辑处理器）。
 */
function configureEditorSmartDimensionRuntime(): void {
  const viewer = getViewer();
  if (!viewer) {
    return;
  }
  viewer.configureSmartDimensionTransactionPort({
    commitTransaction: (input) => commitEditorSketchDimensionTransaction(input),
  });
  viewer.configureSketchDimensionUiHandlers({
    onRequestModify: (payload) => {
      // 对齐 SOLIDWORKS：从动（参考）尺寸值由几何测量得到，双击不弹编辑框，只提示无法更改。
      const target = viewer.getSketchDimensionRepository().get(payload.sketchId, payload.dimensionId);
      if (target?.currentRole === 'driven') {
        message.warning('所选的尺寸为从动尺寸，其值无法更改。');
        return;
      }
      editorDimensionEditTarget.value = {
        sketchId: payload.sketchId,
        dimensionId: payload.dimensionId,
        x: payload.screen.x + 12,
        y: payload.screen.y + 12,
      };
    },
    onSelectionChange: (payload) => {
      editorSelectedSketchDimension.value = payload;
      if (payload) {
        /**
         * 尺寸标签使用独立 DOM 命中代理，不会进入普通实体 `SelectionUpdated` 订阅。
         * 因此必须在尺寸选择边界显式发布属性页意图，确保从设计树点击尺寸时立即显示尺寸 PropertyManager。
         */
        if (isSketchActive.value) {
          requestEditorPropertyTab(
            EditorPanelShowPropertyReason.SelectionUpdated,
            payload.dimensionId,
            1,
          );
        }
      } else {
        editorDimensionEditTarget.value = null;
      }
    },
  });
  disposeEditorSmartDimensionSnapshotSubscription?.();
  disposeEditorSmartDimensionSnapshotSubscription = viewer.observeSmartDimensionSnapshot((snapshot) => {
    editorSmartDimensionSnapshot.value = snapshot;
    // 控制器自行退出（空选择 Esc / 单次模式完成）时，双向收口 workflow 的工具栏状态。
    if (snapshot.state === 'inactive' && smartDimensionActive.value) {
      stopSmartDimensionWorkflow();
    }
  });
}

//------ 已有尺寸编辑 开始------
/** 当前选中的尺寸（PM/删除消费）。 */
const editorSelectedSketchDimension = ref<{sketchId: string; dimensionId: string} | null>(null);
/** 已有尺寸编辑弹层目标（双击触发）。 */
const editorDimensionEditTarget = ref<{sketchId: string; dimensionId: string; x: number; y: number} | null>(null);

/** 已有尺寸编辑弹层视图模型。 */
const editorDimensionEditPopover = computed(() => {
  const target = editorDimensionEditTarget.value;
  if (!target) {
    return null;
  }
  const snapshot = getViewer()?.getSketchDimensionRepository()
    ?.get(target.sketchId, target.dimensionId)?.toSnapshot() ?? null;
  if (!snapshot) {
    return null;
  }
  const isAngle = snapshot.parameter.quantityKind === 'angle';
  const coreValue = snapshot.role === 'driven' && snapshot.measuredValue !== null
    ? snapshot.measuredValue
    : snapshot.parameter.nominalValue;
  return {
    x: target.x,
    y: target.y,
    isAngle,
    initialValue: isAngle
      ? Number(((coreValue * 180) / Math.PI).toFixed(3))
      : Number(convertMToMm(coreValue).toFixed(3)),
    roleToggleLabel: snapshot.role === 'driving' ? '设为从动' : '设为驱动',
    arcConditionGroups: snapshot.references
      .filter((reference) => reference.arcCondition !== undefined)
      .map((reference) => ({
        selectionOrder: reference.selectionOrder,
        current: reference.arcCondition as 'center' | 'min' | 'max',
      })),
  };
});

/** 已有尺寸圆系附着条件切换（ARC-002：driving 尺寸重解驱动几何）。 */
async function handleEditorDimensionArcConditionChange(
  selectionOrder: number,
  condition: 'center' | 'min' | 'max',
): Promise<void> {
  const target = editorDimensionEditTarget.value;
  if (!target) {
    return;
  }
  try {
    await getViewer()?.changeSketchDimensionArcCondition(
      target.sketchId,
      target.dimensionId,
      selectionOrder,
      condition,
    );
    // 强制重算弹层视图模型（仓库非响应式，target 浅拷贝触发 computed）。
    editorDimensionEditTarget.value = {...target};
  } catch (error) {
    message.error(error instanceof Error ? error.message : '修改附着条件失败');
  }
}

/** 已有尺寸编辑确认（表达式解析→求解重驱动；解析失败/无解报错并保持弹层）。 */
async function handleEditorDimensionEditConfirm(rawText: string): Promise<void> {
  const target = editorDimensionEditTarget.value;
  const popover = editorDimensionEditPopover.value;
  if (!target || !popover) {
    return;
  }
  const parsed = parseSketchDimensionValueInput(rawText, popover.isAngle ? 'angle' : 'length');
  if (parsed.ok === false) {
    message.error(parsed.message);
    return;
  }
  try {
    await getViewer()?.modifySketchDimensionValue(
      target.sketchId,
      target.dimensionId,
      parsed.coreValue,
      // 负值输入语义 = 方向翻转（VAL-003），与新值同一笔事务。
      parsed.negated ? { flipDirectionSign: true } : undefined,
    );
    editorDimensionEditTarget.value = null;
  } catch (error) {
    message.error(error instanceof Error ? error.message : '修改尺寸值失败');
  }
}

/** 已有尺寸编辑取消。 */
function handleEditorDimensionEditCancel(): void {
  editorDimensionEditTarget.value = null;
}

/** 已有尺寸驱动角色切换。 */
async function handleEditorDimensionToggleRole(): Promise<void> {
  const target = editorDimensionEditTarget.value;
  if (!target) {
    return;
  }
  const snapshot = getViewer()?.getSketchDimensionRepository()
    ?.get(target.sketchId, target.dimensionId)?.toSnapshot() ?? null;
  if (!snapshot) {
    return;
  }
  try {
    await getViewer()?.changeSketchDimensionRole(
      target.sketchId,
      target.dimensionId,
      snapshot.role === 'driving' ? 'driven' : 'driving',
    );
    editorDimensionEditTarget.value = null;
  } catch (error) {
    reportEditorAsyncFailure(error, '切换尺寸驱动角色失败');
  }
}
//------ 已有尺寸编辑 结束------

/**
 * 把世界坐标投影为屏幕坐标（Modify 浮层定位）。
 *
 * @param world 世界坐标。
 * @returns 屏幕坐标；viewer 未就绪返回 null。
 */
function projectEditorWorldToScreen(world: readonly [number, number, number]): {x: number; y: number} | null {
  const viewer = getViewer();
  const camera = viewer?.camera;
  const container = viewer?.container;
  if (!camera || !container) {
    return null;
  }
  const rect = container.getBoundingClientRect();
  const projected = new THREE.Vector3(world[0], world[1], world[2]).project(camera as unknown as THREE.Camera);
  return {
    x: rect.left + ((projected.x + 1) / 2) * rect.width,
    y: rect.top + ((1 - projected.y) / 2) * rect.height,
  };
}

/** 智能尺寸 Modify 浮层视图模型。 */
// ponytail: 浮层位置在快照变化时计算一次，相机平移/缩放不跟随；placed 态用户通常立即输入，
// 若实测干扰明显再接相机变更事件重投影。
const editorSmartDimensionPopover = computed(() => {
  const snapshot = editorSmartDimensionSnapshot.value;
  if (!snapshot || !snapshot.candidate || !snapshot.placementWorld) {
    return null;
  }
  if (snapshot.state !== 'placed-pending-value' && snapshot.state !== 'driven-decision') {
    return null;
  }
  const screen = projectEditorWorldToScreen(snapshot.placementWorld);
  if (!screen) {
    return null;
  }
  const isAngle = snapshot.candidate.dimensionKind === 'angle'
    || snapshot.candidate.dimensionKind === 'three-point-angle';
  return {
    x: screen.x + 12,
    y: screen.y + 12,
    mode: snapshot.state === 'driven-decision' ? 'decision' as const : 'input' as const,
    isAngle,
    initialValue: isAngle
      ? Number(((snapshot.candidate.currentValue * 180) / Math.PI).toFixed(3))
      : Number(convertMToMm(snapshot.candidate.currentValue).toFixed(3)),
    decisionMessage: snapshot.drivenDecision?.message ?? '',
  };
});

/**
 * Modify 浮层确认（表达式解析 → 核心单位）。
 *
 * 创建路径负值输入取幅值：方向已由放置分支决定（VAL-003 的翻转语义只作用于编辑已有尺寸）。
 *
 * @param rawText 输入原文（默认单位 mm/°，可含表达式与单位后缀）。
 */
async function handleEditorSmartDimensionConfirm(rawText: string): Promise<void> {
  const popover = editorSmartDimensionPopover.value;
  if (!popover) {
    return;
  }
  const parsed = parseSketchDimensionValueInput(rawText, popover.isAngle ? 'angle' : 'length');
  if (parsed.ok === false) {
    message.error(parsed.message);
    return;
  }
  try {
    await getViewer()?.confirmSmartDimensionValue(parsed.coreValue);
  } catch (error) {
    reportEditorAsyncFailure(error, '创建尺寸失败');
  }
}

/** Modify 浮层取消（不产生历史）。 */
function handleEditorSmartDimensionCancel(): void {
  getViewer()?.cancelSmartDimensionPending();
}

/** 过定义/无解决策：转从动尺寸创建。 */
async function handleEditorSmartDimensionConvertDriven(): Promise<void> {
  try {
    await getViewer()?.convertSmartDimensionPendingToDriven();
  } catch (error) {
    reportEditorAsyncFailure(error, '创建从动尺寸失败');
  }
}
//------ 智能尺寸命令接入 结束------

/**
 * 重置当前页面会话内的模型历史状态。
 *
 * 设计说明：
 * 产品要求撤销重做不跨刷新、不跨关闭恢复，因此编辑器生命周期边界必须清空内存栈和轻量镜像。
 */
function resetEditorModelHistorySessionState(): void {
  editorModelHistoryRuntime.clearMemory();
  editorSketchEntityHistorySnapshots.clear();
  editorFeatureDefinitionHistorySnapshots.clear();
  clearEditorPendingFeatureRollbackHistoryRestore();
  editorSketchSolverIslandHistorySnapshots.clear();
  editorSketchSolverRecordSeq = 0;
  editorSketchSolverHistoryCommitPending = false;
}

/**
 * 清理浏览器中遗留的历史持久化数据。
 *
 * 设计说明：
 * 历史记录允许在当前页面会话内使用 IndexedDB/OPFS 作为冷存储。旧会话写入的数据必须在进入编辑器时
 * 主动清掉，避免下次启动恢复上一会话的历史或占用浏览器配额。
 */
async function clearEditorPersistedHistoryStorage(): Promise<void> {
  await Promise.all([
    clearIndexedDBHistoryPersistence(),
    clearOPFSHistoryStorage(),
    clearAssemblyDeletedSubtreePayloadStorage(),
  ]);
}

/**
 * 以全新历史会话启动编辑器。
 */
async function startFreshEditorHistorySession(): Promise<void> {
  resetEditorModelHistorySessionState();

  try {
    await clearEditorPersistedHistoryStorage();
  } catch (error) {
    reportEditorAsyncFailure(error, '清理撤销重做历史持久化数据失败');
  }
}

/**
 * 页面隐藏或卸载时尽力清理历史数据。
 */
function handleEditorHistoryPageHide(): void {
  resetEditorModelHistorySessionState();
  void clearEditorPersistedHistoryStorage().catch((error) => {
    reportEditorAsyncFailure(error, '页面关闭时清理撤销重做历史持久化数据失败');
  });
}

const projectName = ref(' ');
/**
 * 从 OpenProject 根节点上下文派生联调阶段的编辑器标题。
 *
 * 设计说明：项目名来自 `OpenProjectResponse.projectName`；
 * `instance_name` 是后端确认的节点名，和分支名一起用于替代旧文件名 `assembly.json`。
 */
const openProjectRootTitle = computed(() => {
  const context = openProjectRootDisplayContext.value;
  if (!context) {
    return '';
  }

  const projectLabel = context.projectName || (context.projectId ? `项目 ${context.projectId}` : '项目');
  return `${projectLabel} | ${context.nodeName}（${context.branchName}）`;
});

const propertyManagerForceConfirm = ref(false);
const initialPreferences = ref<EditorPreferences | null>(null);
const editorDisplayConfig = ref<EditorDisplayConfig>(createDefaultEditorDisplayConfig());
const isEditorDisplayConfigLoading = ref(true);

const multiSelectStore = useGetMultiSelect();
const {
  multipleEntitiesBoxSelected,
  multipleSelectedObjects,
  multipleSelectedObjectIds,
  multipleSelectedIsCrossing,
} = storeToRefs(multiSelectStore);

const lineSelectStore = useGetSelectLine();
const pointSelectStore = useGetSelectPoint();
const circleSelectStore = useGetSelectCircle();
const arcSelectStore = useGetSelectArc();
const ellipseSelectStore = useGetSelectEllipse();
const polygonSelectStore = useGetSelectPolygon();
const rectangleSelectStore = useGetSelectRectangle();
const slotSelectStore = useGetSelectSlot();
const splineSelectStore = useGetSelectSpline();
const styleSplineSelectStore = useGetSelectStyleSpline();

const selectionStores = [
  lineSelectStore,
  pointSelectStore,
  circleSelectStore,
  arcSelectStore,
  ellipseSelectStore,
  polygonSelectStore,
  rectangleSelectStore,
  slotSelectStore,
  splineSelectStore,
  styleSplineSelectStore,
  multiSelectStore,
] as const;

/**
 * 从选区 store 的 getter 中读取当前选中对象。
 *
 * 设计说明：
 * 1. 草图属性面板实际编辑的是选区 store 中的运行时对象；
 * 2. 这里不创建 ref/computed 保存 Object3D，只在提交历史前按需读取；
 * 3. 这样可以避免 Vue 响应式代理污染 three.js 对象。
 */
function readSketchSelectionGetter(store: unknown, getterName: string): unknown {
  const getter = (store as Record<string, unknown>)[getterName];
  if (typeof getter !== 'function') {
    return null;
  }
  return (getter as () => unknown)() ?? null;
}

/**
 * 解析当前 PropertyManager 正在编辑的草图对象。
 *
 * 说明：设计树 selectedObject 可能是节点数据，不是圆/线/点等真实草图运行时对象；
 * 属性 Undo/Redo 的 before 快照必须捕获真实对象，否则第一次 Undo 会误撤销绘制事务。
 */
function resolveCurrentSketchPropertySelectedObject(): unknown {
  return readSketchSelectionGetter(rectangleSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(lineSelectStore, 'getSelectedLine')
    ?? readSketchSelectionGetter(pointSelectStore, 'getSelectedPoint')
    ?? readSketchSelectionGetter(pointSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(circleSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(arcSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(ellipseSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(slotSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(splineSelectStore, 'getSelected')
    ?? readSketchSelectionGetter(styleSplineSelectStore, 'getSelected')
    ?? multipleSelectedObjects.value[0]
    ?? null;
}

const viewportContainerRef = ref<HTMLElement | null>(null);
const offlinePackageInputRef = ref<HTMLInputElement | null>(null);
const isViewportRendererReady = ref(false);

/**
 * 重置编辑器草图相关运行时状态。
 *
 * @param mode 重置后回落的 Viewer 模式。
 */
function resetEditorSketchRuntimeState(mode: ViewerMode = ViewerMode.Default): void {
  const currentViewer = getViewer();
  // 新项目与旧项目不共享草图会话，必须在 Viewer 生命周期边界统一清掉旧命令与旧基准面选择。
  currentViewer?.stopSketchDrawing?.();
  currentViewer?.clearSketchTrimMode?.(false);
  currentViewer?.clearSketchCornerMode?.(false);
  planeStore.resetPlaneRuntimeState(currentViewer);
  drawingStore.resetDrawingRuntimeState();
  sketchStateStore.resetSketchRuntimeState(mode);
  toolbarCommandStore.clearActiveCommands();
  currentViewer?.setMode(mode);
}

const isModelLoading = ref(false);
const modelLoadingText = ref('');
/** 首屏 OpenProject 辅助数据统一 loading 状态。 */
const isInitialOpenProjectAuxiliaryLoading = ref(false);
// Task 3: loading overlay 归属当前持有 ownership 的 tabId。被抢占的过时请求不得
// 覆盖/清除新请求的 overlay —— 通过这个 ref 把所有 showLoading/hideLoading 归到
// 最近一次 showLoading 的 targetTabId 上。`targetTabId` 未提供（legacy 调用）时走
// 'editor-global' 占位符，保证 hideLoading(undefined) 与 showLoading(undefined) 对齐。
const GLOBAL_LOADING_OWNER = 'editor-global';
const INITIAL_PROJECT_MODEL_LOADING_OWNER = 'editor-initial-project-model-load';
const STEP_EXPORT_LOADING_OWNER = 'editor-step-export';
const OFFLINE_PACKAGE_LOADING_OWNER = 'editor-offline-package';
/** 拉伸/旋转特征确认提交期间使用的全局 loading 归属标识。 */
const FEATURE_COMMIT_LOADING_OWNER = 'editor-feature-commit';
/** 曲线 profile 特征预览生成期间使用的全局 loading 归属标识。 */
const PROFILE_FEATURE_PREVIEW_LOADING_OWNER = 'editor-profile-feature-preview';
/** 回退控制棒重放特征历史期间使用的全局 loading 归属标识。 */
const FEATURE_ROLLBACK_LOADING_OWNER = 'editor-feature-rollback';

interface EditorInitialLoadContext {
  /** 本次首屏加载事务序号，用于阻止旧请求晚返回后继续写入 UI。 */
  readonly requestId: number;
  /** 贯穿 OpenProject 与模型恢复链路的取消信号。 */
  readonly signal: AbortSignal;
}

let editorInitialLoadRequestId = 0;
let editorInitialLoadAbortController: AbortController | null = null;
let workspaceWriteAbortController: AbortController | null = null;
let isEditorUnmounted = false;

/** 创建历史提交可编辑工作区后，编辑态重载链路使用的 loading 归属标识。 */
const EDITABLE_WORKSPACE_RELOAD_LOADING_OWNER = 'version-workspace-editable-reload';
/** 历史提交只读预览整项目切换进入 loader 前的 loading 归属标识。 */
const HISTORY_PREVIEW_RELOAD_LOADING_OWNER = 'version-workspace-history-preview-reload';
/** 根提交变化后重新 OpenProject 并替换场景模型的 loading 归属标识。 */
const ROOT_COMMIT_RELOAD_LOADING_OWNER = 'root-commit-project-reload';
/** 历史预览 loading owner 的本地递增序号，用于防止旧请求关闭新请求遮罩。 */
let historyPreviewReloadLoadingOwnerSequence = 0;
const loadingOwnerTabId = ref<string>(GLOBAL_LOADING_OWNER);
/** 防止 Enter 与属性面板确认按钮重复触发同一次拉伸/旋转提交。 */
const isFeatureCommitSubmitting = ref(false);
const editorModelLoadingCoordinator = new EditorModelLoadingCoordinator({
  isLoading: isModelLoading,
  loadingText: modelLoadingText,
  owner: loadingOwnerTabId,
  globalOwner: GLOBAL_LOADING_OWNER,
});
const editorInitialOpenProjectLoadCoordinator = new EditorInitialOpenProjectLoadCoordinator({
  auxiliaryLoading: isInitialOpenProjectAuxiliaryLoading,
});
/** 提交记录面板展示 loading，首屏统一加载期间也保持占位状态。 */
const isVersionHistoryPanelLoading = computed(() => (
  versionWorkspaceStore.isRecordsLoading || isInitialOpenProjectAuxiliaryLoading.value
));
/** 装配节点列表面板展示 loading，避免首屏 OpenProject 中途重复刷新造成闪烁。 */
const isAssemblyBranchNodeListPanelLoading = computed(() => (
  assemblyBranchNodeListStore.isLoading || isInitialOpenProjectAuxiliaryLoading.value
));

/**
 * 显示项目根模型加载失败后的确认重载弹窗。
 *
 * 设计说明：模型下载/解析失败后，重载必须回到 `useEditorGlbLoader` 提供的原始 retry，
 * 避免 UI 层自行重建下载链路而丢失 request ownership、缓存回滚和资源释放语义。
 *
 * @param input 项目根模型加载失败提示数据。
 */
function showProjectModelLoadFailureDialog(input: ProjectModelLoadFailureInput): void {
  dialog.warning({
    title: input.title,
    content: `${input.description}\n\n错误详情：${input.errorDetail}`,
    positiveText: '重新加载',
    negativeText: '取消',
    onPositiveClick: () => {
      void input.retry().catch((error: unknown) => {
        reportEditorAsyncFailure(error, '重新加载模型失败');
      });
    },
  });
}

const glbLoader = useEditorGlbLoader({
  getScene: () => getViewer()?.getScene(),
  getViewer: () => getViewer(),
  projectRootNodeReloadAdapter: cadNodeTreeReloadAdapter,
  showLoading: (text: string, targetTabId?: string) => {
    editorModelLoadingCoordinator.show(text, targetTabId);
  },
  hideLoading: (targetTabId?: string) => {
    editorModelLoadingCoordinator.hide(targetTabId);
  },
  /**
   * 权威清零 overlay：用于 cache-hit 快速路径这种"本 tabId 就是新 current owner"
   * 的场景。与 hideLoading 的区别：不走 owner-guard —— 即使当前 owner 是被抢占
   * tab，也一并归零。相比 `showLoading('', tabId) + hideLoading(tabId)` 的两段式：
   *   - 没有 "isModelLoading=true + 空文案" 的中间闪烁；
   *   - 单次写入，任何观察者只会看到 "已清零" 的终态；
   *   - 不依赖 showLoading/hideLoading 两次调用之间没有异常。
   */
  resetLoading: (targetTabId: string) => {
    editorModelLoadingCoordinator.reset(targetTabId);
  },
  showError: (text: string) => message.error(text),
  handleProjectModelLoadFailure: showProjectModelLoadFailureDialog,
});

const isSubmitWorkspaceDialogVisible = ref(false);
const isSubmittingWorkspace = ref(false);
const projectModelRequiresReload = ref(false);
const isSwitchNodeVersionDialogVisible = ref(false);
const switchNodeVersionTargetNode = ref<AssemblyChildNodeSummary | null>(null);
const switchNodeVersionCommits = ref<readonly AssemblyChildNodeCommit[]>([]);
const isSwitchNodeVersionCommitsLoading = ref(false);
let switchNodeVersionDialogRequestId = 0;

/**
 * 子节点提交展示元数据。
 *
 * 设计说明：这些字段只服务当前 Editor 会话内的列表展示，不写入 `_assembly.json`。
 */
interface AssemblyChildNodeCommitDisplayInfo {
  /** 提交所属分支 ID。 */
  readonly branchId: string;
  /** 提交所属分支显示名称。 */
  readonly branchName: string;
  /** 提交说明。 */
  readonly message: string;
}

const assemblyChildNodeCommitDisplayInfoByKey = new Map<string, AssemblyChildNodeCommitDisplayInfo>();
const assemblyBranchNodeRemoteMetadataByInstanceUuid = new Map<string, AssemblyChildNodeSummary>();
let hoveredAssemblyBranchNodeId: string | null = null;

/**
 * 归一化分支 ID 文本。
 *
 * @param branchId 后端或编码节点中的分支 ID。
 * @returns 可用于展示和写入草稿的分支 ID；协议默认值 `0` 会转为空字符串。
 */
function normalizeAssemblyBranchIdText(branchId: string | undefined): string {
  const normalized = branchId?.trim() ?? '';
  return normalized === '0' ? '' : normalized;
}

/**
 * 从 nodeKey 末段派生可读显示名。
 *
 * @param nodeKey 项目模型结构 key。
 * @returns 解码后的末段显示名。
 */
function resolveAssemblyNodeNameFromNodeKey(nodeKey: string): string {
  const segment = nodeKey.split('/').filter(Boolean).at(-1) ?? nodeKey;

  try {
    return decodeURIComponent(segment).replace(/#\d+$/u, '') || nodeKey;
  } catch {
    return segment || nodeKey;
  }
}

/**
 * 清空远端节点展示元信息缓存。
 */
function clearAssemblyBranchNodeRemoteMetadataCache(): void {
  assemblyBranchNodeRemoteMetadataByInstanceUuid.clear();
}

/**
 * 缓存远端节点列表返回的展示元信息。
 *
 * 设计说明：远端 `ListBranchNodes` 只补充分支、负责人和提交展示信息，不能决定当前
 * working 中有哪些子节点。因此缓存只按实例 UUID 建索引，后续列表集合仍由 working 派生。
 *
 * @param nodes 远端列表返回的节点摘要。
 */
function cacheAssemblyBranchNodeRemoteMetadata(nodes: readonly AssemblyChildNodeSummary[]): void {
  clearAssemblyBranchNodeRemoteMetadataCache();

  nodes.forEach((node) => {
    try {
      const reference = decodeAssemblyChildNodeReference(node.nodeId);
      const instanceUuid = reference.instanceUuid.trim();
      if (instanceUuid && !assemblyBranchNodeRemoteMetadataByInstanceUuid.has(instanceUuid)) {
        assemblyBranchNodeRemoteMetadataByInstanceUuid.set(instanceUuid, node);
      }
    } catch {
      // 旧节点 ID 无法解码时只是不参与 metadata 合并，working 基础摘要仍可展示。
    }
  });
}

/**
 * 为 working 子节点读取可合并的远端展示元信息。
 *
 * @param workingNode 工作装配文档中的直接子节点。
 * @param assetNode 当前模型树中对应的节点。
 * @returns 命中的远端展示摘要；无稳定实例身份时返回 null。
 */
function resolveRemoteMetadataForWorkingNode(
  workingNode: AssemblyDocumentNode,
  assetNode: ModelAssetNode | null,
): AssemblyChildNodeSummary | null {
  const instanceId = workingNode.instanceId?.trim() || assetNode?.instanceUuid?.trim() || '';

  return instanceId ? assemblyBranchNodeRemoteMetadataByInstanceUuid.get(instanceId) ?? null : null;
}

/**
 * 安全编码左侧列表节点 ID。
 *
 * @param input 节点引用字段。
 * @param fallbackId 编码失败时使用的结构 key。
 * @returns 可传给左侧列表的节点 ID。
 */
function encodeWorkingAssemblyChildNodeId(
  input: Parameters<typeof encodeAssemblyChildNodeReference>[0],
  fallbackId: string,
): string {
  try {
    return encodeAssemblyChildNodeReference(input);
  } catch (error) {
    logger.warn('[work-assemble] 左侧节点 ID 编码失败，使用结构 key 兜底', {
      fallbackId,
      error,
    });
    return fallbackId;
  }
}

/**
 * 将 working direct child 投影为左侧节点列表摘要。
 *
 * @param workingNode 工作装配文档直接子节点。
 * @param assetNode 当前模型树中对应的节点。
 * @param remoteMetadata 远端展示元信息。
 * @returns 左侧列表可消费的节点摘要。
 */
function createAssemblyBranchNodeSummaryFromWorkingNode(
  workingNode: AssemblyDocumentNode,
  assetNode: ModelAssetNode | null,
  remoteMetadata: AssemblyChildNodeSummary | null,
): AssemblyChildNodeSummary {
  const repositoryId = workingNode.repositoryId.trim() || assetNode?.repositoryId.trim() || '';
  const adoptedCommitId = workingNode.commitId.trim() || assetNode?.commitId.trim() || '';
  const adoptedBranchId = normalizeAssemblyBranchIdText(workingNode.branchId)
    || normalizeAssemblyBranchIdText(assetNode?.branchId);
  const instanceUuid = workingNode.instanceId?.trim() || assetNode?.instanceUuid?.trim() || '';
  const nodeId = encodeWorkingAssemblyChildNodeId(
    {
      repositoryId,
      adoptedBranchId,
      adoptedCommitId,
      instanceUuid,
      nodeKey: workingNode.nodeKey,
    },
    workingNode.nodeKey,
  );
  const name = assetNode?.displayName?.trim()
    || workingNode.instanceName?.trim()
    || remoteMetadata?.name.trim()
    || resolveAssemblyNodeNameFromNodeKey(workingNode.nodeKey);

  return {
    repositoryId,
    nodeId,
    name,
    nodeType: workingNode.nodeType,
    branchCount: remoteMetadata?.branchCount ?? (adoptedBranchId ? 1 : 0),
    adoptedCommitId: adoptedCommitId || null,
    adoptedVersionLabel: resolveAssemblyChildNodeCommitDisplayLabel({
      repositoryId,
      commitId: adoptedCommitId,
      branchId: adoptedBranchId,
      fallbackLabel: remoteMetadata?.adoptedVersionLabel,
    }),
    hasCommits: Boolean(adoptedCommitId) || Boolean(remoteMetadata?.hasCommits),
    branches: remoteMetadata?.branches ?? [],
  };
}

/**
 * 从工作装配文档派生左侧节点列表。
 *
 * @returns 成功派生时返回节点摘要列表；当前还不是项目模型装配时返回 null。
 */
function createWorkingFirstAssemblyBranchNodeList(): readonly AssemblyChildNodeSummary[] | null {
  const currentResult = glbLoader.getCurrentProjectModelLoadResult();
  if (!currentResult) {
    return null;
  }

  const draftRecord = glbLoader.assemblyDraftDocumentStore.get(currentResult.root.nodeKey);
  if (!draftRecord) {
    if (currentResult.root.nodeType === 'assembly') {
      logger.warn('[work-assemble] 根装配缺少工作草稿，清空左侧列表并要求刷新', {
        rootNodeKey: currentResult.root.nodeKey,
      });
      projectModelRequiresReload.value = true;
      return [];
    }

    return null;
  }

  let workingNodes: readonly AssemblyDocumentNode[];
  try {
    workingNodes = listAssemblyDocumentDirectChildNodes(
      draftRecord.workingAssemblyDocument,
      currentResult.root.nodeKey,
    );
  } catch (error) {
    logger.warn('[work-assemble] 工作装配 direct children 派生失败，清空左侧列表并要求刷新', {
      rootNodeKey: currentResult.root.nodeKey,
      error,
    });
    projectModelRequiresReload.value = true;
    return [];
  }

  return workingNodes.map((workingNode) => {
    const assetNode = resolveProjectModelDirectChildForWorkingNode(currentResult.root, workingNode);
    const remoteMetadata = resolveRemoteMetadataForWorkingNode(workingNode, assetNode);

    return createAssemblyBranchNodeSummaryFromWorkingNode(workingNode, assetNode, remoteMetadata);
  });
}

/**
 * 创建左侧节点列表动态高亮使用的交互语义。
 *
 * 设计说明：
 * 1. SolidWorks 中树节点 hover 属于 Dynamic Highlight，不是选择；
 * 2. 左侧节点列表虽然不是右侧 FeatureManager，但语义上同样代表装配体直接子组件；
 * 3. 传入 part/assembly 角色后，cad-core 可继续复用装配/零件包围盒表现。
 *
 * @param target 已解析出的场景 hover 目标。
 * @returns 可传给 Viewer 程序化 hover 的交互元数据。
 */
function createAssemblyBranchNodeHoverInteractionMeta(
  target: AssemblyBranchNodeHoverTarget,
): SelectionInteractionMeta {
  return {
    source: 'feature-tree',
    semantic: 'entity',
    glbNodeRole: target.nodeType === 'assembly' ? 'assembly' : 'part',
  };
}

/**
 * 清理左侧节点列表触发的程序化 hover。
 *
 * 设计说明：当侧栏切走、列表刷新或卡片移出时，hover 视觉必须立即消失；
 * 这里统一走 Viewer 选择中心入口，且只清理左侧节点列表自己创建的 hover，
 * 避免误清右侧设计树或视口正在维护的预选择反馈。
 *
 * @param nodeId 指定节点 ID 时，仅在它仍是当前左侧 hover 源时清理。
 */
function clearAssemblyBranchNodeProgrammaticHover(nodeId?: string): void {
  if (!hoveredAssemblyBranchNodeId) {
    return;
  }
  if (nodeId && hoveredAssemblyBranchNodeId !== nodeId) {
    return;
  }
  hoveredAssemblyBranchNodeId = null;
  getViewer()?.setProgrammaticHover?.(null);
}

/**
 * 处理左侧装配分支节点卡片动态高亮。
 *
 * 设计说明：
 * 1. 卡片传入的是业务节点摘要，不能直接当作场景对象 ID 使用；
 * 2. 先通过项目模型加载结果定位 direct child，再复用 Viewer 的程序化 hover；
 * 3. 旧节点 ID、空节点或尚未加载几何时不抛错，保持 hover 高频路径安静。
 *
 * @param node 当前被 hover 的左侧节点摘要。
 * @param hovered 是否处于鼠标移入状态。
 */
function handleAssemblyBranchNodeHover(
  node: AssemblyChildNodeSummary,
  hovered: boolean,
): void {
  if (!hovered) {
    clearAssemblyBranchNodeProgrammaticHover(node.nodeId);
    return;
  }

  clearAssemblyBranchNodeProgrammaticHover();
  const viewer = getViewer();
  if (!viewer) {
    return;
  }

  const target = resolveAssemblyBranchNodeHoverTarget({
    node,
    loadResult: glbLoader.getCurrentProjectModelLoadResult(),
    resolveFirstLevelNodeByReference: glbLoader.resolveCurrentProjectModelFirstLevelNodeByReference,
    resolveRuntimeHoverTargetByReference: glbLoader.resolveCurrentProjectModelFirstLevelHoverTargetByReference,
  });
  if (!target) {
    return;
  }

  hoveredAssemblyBranchNodeId = node.nodeId;
  viewer.setProgrammaticHover?.(
    target.objectId,
    {
      interactionMeta: createAssemblyBranchNodeHoverInteractionMeta(target),
    },
  );
}

/**
 * 生成子节点提交展示缓存 key。
 *
 * @param repositoryId 子节点仓库 ID。
 * @param commitId 子节点提交 ID。
 * @returns 当前会话内稳定的提交展示缓存 key。
 */
function buildAssemblyChildNodeCommitDisplayInfoKey(repositoryId: string, commitId: string): string {
  return `${repositoryId.trim()}::${commitId.trim()}`;
}

/**
 * 缓存单个子节点提交展示信息。
 *
 * @param repositoryId 子节点仓库 ID。
 * @param commit 提交列表返回的提交摘要。
 */
function cacheAssemblyChildNodeCommitDisplayInfo(
  repositoryId: string,
  commit: AssemblyChildNodeCommit,
): void {
  const normalizedRepositoryId = repositoryId.trim();
  const normalizedCommitId = commit.id.trim();
  if (!normalizedRepositoryId || !normalizedCommitId) {
    return;
  }

  assemblyChildNodeCommitDisplayInfoByKey.set(
    buildAssemblyChildNodeCommitDisplayInfoKey(normalizedRepositoryId, normalizedCommitId),
    {
      branchId: normalizeAssemblyBranchIdText(commit.branchId),
      branchName: commit.branchName.trim(),
      message: commit.message.trim(),
    },
  );
}

/**
 * 批量缓存某个节点提交列表的展示信息。
 *
 * @param node 当前分支节点列表中的子节点摘要。
 * @param commits 该节点仓库下的提交列表。
 */
function cacheAssemblyChildNodeCommitDisplayInfos(
  node: AssemblyChildNodeSummary,
  commits: readonly AssemblyChildNodeCommit[],
): void {
  let nodeReference: ReturnType<typeof decodeAssemblyChildNodeReference>;
  try {
    nodeReference = decodeAssemblyChildNodeReference(node.nodeId);
  } catch {
    return;
  }

  commits.forEach((commit) => {
    cacheAssemblyChildNodeCommitDisplayInfo(nodeReference.repositoryId, commit);
  });
}

/**
 * 读取子节点提交展示信息。
 *
 * @param repositoryId 子节点仓库 ID。
 * @param commitId 子节点提交 ID。
 * @returns 命中的展示元数据；未加载过该提交列表时返回 null。
 */
function resolveAssemblyChildNodeCommitDisplayInfo(
  repositoryId: string,
  commitId: string,
): AssemblyChildNodeCommitDisplayInfo | null {
  return assemblyChildNodeCommitDisplayInfoByKey.get(
    buildAssemblyChildNodeCommitDisplayInfoKey(repositoryId, commitId),
  ) ?? null;
}

/**
 * 生成采纳版本展示文案。
 *
 * @param input 展示文案所需的提交身份和 fallback 文案。
 * @returns 优先包含分支和提交信息的采纳版本文案。
 */
function resolveAssemblyChildNodeCommitDisplayLabel(input: {
  readonly repositoryId: string;
  readonly commitId: string;
  readonly branchId?: string;
  readonly fallbackLabel?: string;
}): string {
  const normalizedCommitId = input.commitId.trim();
  if (!normalizedCommitId) {
    return '--';
  }

  const cachedInfo = resolveAssemblyChildNodeCommitDisplayInfo(input.repositoryId, normalizedCommitId);
  if (cachedInfo) {
    const branchLabel = cachedInfo.branchName
      || normalizeAssemblyBranchIdText(cachedInfo.branchId)
      || normalizeAssemblyBranchIdText(input.branchId);
    const commitLabel = cachedInfo.message || normalizedCommitId;
    return branchLabel ? `${branchLabel} / ${commitLabel}` : commitLabel;
  }

  const fallbackLabel = input.fallbackLabel?.trim();
  if (fallbackLabel && fallbackLabel !== normalizedCommitId) {
    return fallbackLabel;
  }

  const branchLabel = normalizeAssemblyBranchIdText(input.branchId);
  return branchLabel ? `${branchLabel} / ${normalizedCommitId}` : normalizedCommitId;
}

/**
 * 分支节点列表加载使用的运行时上下文。
 */
interface AssemblyBranchNodeListRuntimeContext {
  /** 当前装配体节点 ID。 */
  readonly assemblyNodeId: string;
  /** 当前装配体分支 ID。 */
  readonly branchId: string;
  /** 当前装配体提交 ID；用于读取指定提交下的直接子节点。 */
  readonly commitId?: string;
  /** 当前装配体节点显示名称。 */
  readonly assemblyNodeName: string;
}

/**
 * 格式化子节点提交时间。
 *
 * @param committedAt ISO 格式提交时间。
 * @returns 便于弹框选项展示的短时间。
 */
function formatAssemblyNodeCommitTime(committedAt: string): string {
  const matched = committedAt.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (matched) {
    return `${matched[1]} ${matched[2]}`;
  }
  return committedAt;
}

/**
 * 解析当前分支节点列表使用的装配体上下文。
 *
 * 设计说明：真实 `ListBranchNodes` 以当前会话分支为入口，当前会话的
 * repositoryId/branchId 已由 OpenProject 写入 `cadSessionStore`，页面只把它们转成控制器上下文。
 *
 * @returns 可编辑分支节点上下文；只读预览态或缺少后端会话上下文时返回 null。
 */
function resolveActiveAssemblyBranchNodeListContext(): AssemblyBranchNodeListRuntimeContext | null {
  const activeTab = glbLoader.partTabsStore.activeTab;
  const repositoryId = cadSessionStore.currentRepositoryId;
  const branchId = cadSessionStore.currentBranchId;
  const commitId = resolveCurrentAssemblyWriteBaseCommitId();

  if (
    versionWorkspaceStore.isReadonlyPreview
    || activeEditorTabType.value !== 'assembly'
    || repositoryId === null
    || branchId === null
  ) {
    return null;
  }

  return {
    assemblyNodeId: repositoryId.toString(),
    branchId: branchId.toString(),
    ...(commitId ? { commitId } : {}),
    assemblyNodeName: activeTab?.label
      || cadSessionStore.openProjectRootDisplayContext?.nodeName
      || projectName.value
      || '当前模型',
  };
}

/**
 * 解析分支与节点面板在上下文不足时可展示的名称。
 *
 * 设计说明：面板是否允许打开由 `canOpenAssemblyBranchNodeListPanel` 统一判定；
 * 这里仅负责在上下文不足时提供用户可识别的模型名称。
 *
 * @returns 当前模型的友好名称。
 */
function resolveBranchNodeListFallbackDisplayName(): string {
  return glbLoader.partTabsStore.activeTab?.label
    || cadSessionStore.openProjectRootDisplayContext?.nodeName
    || projectName.value
    || '当前模型';
}

let assemblyChildNodeVersionReloadRequestId = 0;
let projectRootCommitReloadRequestId = 0;

/**
 * 切换子节点采纳版本前进行风险确认。
 *
 * @returns 用户确认继续时返回 true，取消或关闭时返回 false。
 */
function confirmAssemblyChildNodeVersionSwitch(): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    dialog.warning({
      title: '切换子节点版本',
      content: '切换会重新加载该子节点模型，并更新当前装配草稿。是否继续？',
      positiveText: '继续切换',
      negativeText: '取消',
      onPositiveClick: () => {
        settle(true);
      },
      onNegativeClick: () => {
        settle(false);
      },
      onClose: () => {
        settle(false);
      },
    });
  });
}

/**
 * 为子节点采纳版本切换构造联调阶段的模型快照。
 *
 * 设计说明：真实接口尚未返回子节点版本对应的模型下载上下文，本阶段仍复用当前项目文件；
 * 但必须显式生成快照对象，让模型重载继续走既有版本预览慢路径和资源释放策略。
 *
 * @param updatedNode 服务返回的最新子节点摘要。
 * @returns 可传入 glbLoader 的项目快照；缺少上下文时返回 null。
 */
function createAssemblyChildNodeVersionSnapshot(updatedNode: AssemblyChildNodeSummary): ProjectCommitSnapshot | null {
  const adoptedCommitId = updatedNode?.adoptedCommitId ?? null;

  if (!projectId.value || !updatedNode || !adoptedCommitId) {
    return null;
  }

  return {
    projectId: projectId.value,
    commitId: adoptedCommitId,
    title: `${updatedNode.name} ${updatedNode.adoptedVersionLabel}`,
    branchName: assemblyBranchNodeListStore.currentBranchId ?? '',
    modelSource: {
      kind: 'reuse-current-project-files',
    },
  };
}

/**
 * 切换子节点采纳版本后重载当前装配体。
 *
 * 设计说明：子节点采纳版本会改变父装配体表达，不能只更新列表状态；这里复用历史版本
 * 预览的慢路径，让模型、设计树、底部 tab 和资源释放策略保持同一个入口，再刷新独立节点列表。
 *
 * @param updatedNode 服务切换成功后返回的最新子节点摘要。
 * @returns 当前请求完成且上下文仍有效时返回 true；被更新请求取代或面板关闭时返回 false。
 */
async function reloadAssemblyAfterAssemblyChildVersionSwitch(
  updatedNode: AssemblyChildNodeSummary,
): Promise<boolean> {
  const sourceContext = resolveActiveAssemblyBranchNodeListContext();

  if (!sourceContext) {
    return false;
  }

  const snapshot = createAssemblyChildNodeVersionSnapshot(updatedNode);

  if (!snapshot) {
    throw new Error('缺少子节点采纳版本快照，无法重新加载当前装配体');
  }

  const requestId = assemblyChildNodeVersionReloadRequestId + 1;
  assemblyChildNodeVersionReloadRequestId = requestId;
  const context: VersionWorkspaceReloadContext = {
    isCurrent: () => assemblyChildNodeVersionReloadRequestId === requestId,
  };

  await glbLoader.reloadCurrentProjectForVersionPreview(snapshot, context);

  if (!context.isCurrent()) {
    return false;
  }

  if (!context.isCurrent() || activeSidePanel.value !== 'branch') {
    return false;
  }

  const latestContext = resolveActiveAssemblyBranchNodeListContext();
  if (!latestContext || !isSameAssemblyBranchNodeListContext(sourceContext, latestContext)) {
    return false;
  }

  await loadAssemblyBranchNodeListForContext(latestContext);

  const finalContext = resolveActiveAssemblyBranchNodeListContext();
  return context.isCurrent()
    && activeSidePanel.value === 'branch'
    && !!finalContext
    && isSameAssemblyBranchNodeListContext(sourceContext, finalContext);
}

const assemblyBranchNodeListController = createAssemblyBranchNodeListController({
  store: assemblyBranchNodeListStore,
  service: assemblyBranchNodeListService,
  showError: (content) => message.error(content),
});

let pendingAssemblyBranchNodeListLoadContext: AssemblyBranchNodeListRuntimeContext | null = null;
let currentAssemblyBranchNodeListLoadingContext: AssemblyBranchNodeListRuntimeContext | null = null;
let isAssemblyBranchNodeListLoadRunning = false;
let assemblyBranchNodeListLoadDrainPromise: Promise<void> | null = null;
let assemblyBranchNodeListLoadEpoch = 0;
let assemblyBranchNodeListAutoReloadSuppressionDepth = 0;

/**
 * 打开分支与节点面板但不发起加载。
 *
 * 设计说明：接口联调阶段需要先观察入口和后端上下文缺口；缺少分支上下文时若直接关闭面板，
 * 用户无法区分“入口没打开”和“后端上下文不足”。这里把两者拆开，但错误只走全局 message，
 * 避免把一次性接口错误写入节点列表区域。
 *
 * @param error 缺少上下文或临时不可加载时展示的错误信息。
 */
function openAssemblyBranchNodeListPanelWithoutLoading(error: string): void {
  assemblyBranchNodeListLoadEpoch += 1;
  pendingAssemblyBranchNodeListLoadContext = null;
  currentAssemblyBranchNodeListLoadingContext = null;
  clearAssemblyBranchNodeRemoteMetadataCache();
  closeAssemblyBranchNodeListDialogs();
  assemblyBranchNodeListController.reset();
  assemblyBranchNodeListStore.currentAssemblyNodeName = resolveBranchNodeListFallbackDisplayName();
  message.error(error);
  activeSidePanel.value = 'branch';
}

/**
 * 判断两个分支节点列表上下文是否指向同一个 mock 装配体分支。
 *
 * @param left 左侧上下文。
 * @param right 右侧上下文。
 * @returns 装配体、分支、提交和展示名称都一致时返回 true。
 */
function isSameAssemblyBranchNodeListContext(
  left: AssemblyBranchNodeListRuntimeContext,
  right: AssemblyBranchNodeListRuntimeContext,
): boolean {
  return left.assemblyNodeId === right.assemblyNodeId
    && left.branchId === right.branchId
    && (left.commitId ?? '') === (right.commitId ?? '')
    && left.assemblyNodeName === right.assemblyNodeName;
}

/**
 * 判断分支节点列表自动重载是否正被父级项目重载流程接管。
 *
 * @returns 父级项目重载尚未完成显式刷新时返回 true。
 */
function isAssemblyBranchNodeListAutoReloadSuppressed(): boolean {
  return assemblyBranchNodeListAutoReloadSuppressionDepth > 0;
}

/**
 * 判断目标上下文是否已经处于加载中或等待队列中。
 *
 * 设计说明：分支面板列表加载会进一步按子节点仓库查询分支；同一上下文重复排队会放大为多组
 * ListBranchNodes/ListRepositoryBranches 请求。入口统一去重，可以保留串行安全性，同时避免重载期间重复请求。
 *
 * @param context 目标装配体分支上下文。
 * @returns 相同上下文已在运行或已排队时返回 true。
 */
function isAssemblyBranchNodeListLoadContextAlreadyQueuedOrRunning(
  context: AssemblyBranchNodeListRuntimeContext,
): boolean {
  if (
    currentAssemblyBranchNodeListLoadingContext
    && isSameAssemblyBranchNodeListContext(context, currentAssemblyBranchNodeListLoadingContext)
  ) {
    return true;
  }
  if (
    pendingAssemblyBranchNodeListLoadContext
    && isSameAssemblyBranchNodeListContext(context, pendingAssemblyBranchNodeListLoadContext)
  ) {
    return true;
  }

  return false;
}

/**
 * 解析草稿采纳版本在节点列表中的展示文案。
 *
 * @param node 当前节点列表摘要。
 * @param adoptedCommitId 工作装配草稿中的采纳提交。
 * @returns 应展示的采纳版本文本。
 */
function resolveWorkingAssemblyAdoptedVersionLabel(
  node: AssemblyChildNodeSummary,
  repositoryId: string,
  adoptedCommitId: string,
  adoptedBranchId?: string,
): string {
  return resolveAssemblyChildNodeCommitDisplayLabel({
    repositoryId,
    commitId: adoptedCommitId,
    branchId: adoptedBranchId,
    fallbackLabel: node.adoptedCommitId === adoptedCommitId ? node.adoptedVersionLabel : undefined,
  });
}

/**
 * 用当前工作装配草稿覆盖单个节点列表摘要的采纳版本。
 *
 * 设计说明：后端节点列表表达的是当前分支已提交状态；当前会话内的采纳版本修改存放在
 * workingAssemblyDocument 中。分支面板展示必须以后者为准，避免用户切换版本后刷新列表又回到旧采纳值。
 *
 * @param node 后端或 UI 当前持有的节点摘要。
 * @returns 与工作草稿一致的节点摘要。
 */
function applyWorkingAssemblyDocumentToBranchNode(node: AssemblyChildNodeSummary): AssemblyChildNodeSummary {
  const currentResult = glbLoader.getCurrentProjectModelLoadResult();
  if (!currentResult) {
    return node;
  }

  const draftRecord = glbLoader.assemblyDraftDocumentStore.get(currentResult.root.nodeKey);
  if (!draftRecord) {
    return node;
  }

  let nodeReference: ReturnType<typeof decodeAssemblyChildNodeReference>;
  try {
    nodeReference = decodeAssemblyChildNodeReference(node.nodeId);
  } catch {
    return node;
  }

  const targetAssetNode = glbLoader.resolveCurrentProjectModelFirstLevelNodeByReference({
    repositoryId: nodeReference.repositoryId,
    instanceUuid: nodeReference.instanceUuid,
    adoptedCommitId: nodeReference.adoptedCommitId,
    nodeKey: nodeReference.nodeKey,
  });
  const targetReference = glbLoader.resolveCurrentProjectModelFirstLevelTargetByReference({
    repositoryId: nodeReference.repositoryId,
    instanceUuid: nodeReference.instanceUuid,
    adoptedCommitId: nodeReference.adoptedCommitId,
    nodeKey: nodeReference.nodeKey,
  });
  if (!targetReference) {
    return node;
  }

  const draftNode = targetReference.documentNode
    ?? getAssemblyDocumentNode(draftRecord.workingAssemblyDocument, targetReference.nodeKey);
  const adoptedCommitId = draftNode?.commitId.trim() ?? '';
  const adoptedBranchId = normalizeAssemblyBranchIdText(draftNode?.branchId)
    || normalizeAssemblyBranchIdText(nodeReference.adoptedBranchId);
  const nextNodeId = encodeAssemblyChildNodeReference({
    ...nodeReference,
    adoptedBranchId,
    adoptedCommitId,
  });

  return {
    ...node,
    nodeId: nextNodeId,
    adoptedCommitId: adoptedCommitId || null,
    adoptedVersionLabel: resolveWorkingAssemblyAdoptedVersionLabel(
      node,
      nodeReference.repositoryId,
      adoptedCommitId,
      adoptedBranchId,
    ),
    hasCommits: Boolean(adoptedCommitId) || node.hasCommits,
  };
}

/**
 * 同步整个分支节点列表，使采纳版本始终以工作装配草稿为准。
 */
function syncAssemblyBranchNodeListWithWorkingAssemblyDocument(): void {
  clearAssemblyBranchNodeProgrammaticHover();
  const workingFirstNodes = createWorkingFirstAssemblyBranchNodeList();

  if (workingFirstNodes) {
    assemblyBranchNodeListStore.setNodes(workingFirstNodes);
    return;
  }

  if (assemblyBranchNodeListStore.nodes.length > 0) {
    assemblyBranchNodeListStore.setNodes(
      assemblyBranchNodeListStore.nodes.map((node) => applyWorkingAssemblyDocumentToBranchNode(node)),
    );
  }
}

/**
 * 替换节点列表中的指定节点，随后用工作装配草稿再校准一次采纳版本。
 *
 * @param previousNodeId 被替换前的节点 ID。
 * @param nextNode 下一版节点摘要。
 */
function replaceAssemblyBranchNodeListNode(previousNodeId: string, nextNode: AssemblyChildNodeSummary): void {
  const currentNodes = assemblyBranchNodeListStore.nodes;
  const replacedNodes = currentNodes.map((node) => (
    node.nodeId === previousNodeId ? nextNode : node
  ));
  const wasReplaced = replacedNodes.some((node) => node === nextNode);

  if (wasReplaced) {
    assemblyBranchNodeListStore.setNodes(replacedNodes);
  } else {
    assemblyBranchNodeListStore.upsertNode(nextNode);
  }
  syncAssemblyBranchNodeListWithWorkingAssemblyDocument();
}

/**
 * 顺序消费分支节点列表加载请求。
 *
 * 设计说明：controller.load 会直接写 Store，若 Editor 层并发触发多个 load，
 * 旧请求可能在新请求之后完成并覆盖 nodes。这里把 Editor 侧入口收敛为串行 latest-context 队列：
 * 运行中只记录最新上下文，当前请求结束后若上下文已变化，立即再加载最新上下文。
 */
async function drainAssemblyBranchNodeListLoadQueue(): Promise<void> {
  if (isAssemblyBranchNodeListLoadRunning) {
    return assemblyBranchNodeListLoadDrainPromise ?? Promise.resolve();
  }

  isAssemblyBranchNodeListLoadRunning = true;
  assemblyBranchNodeListLoadDrainPromise = (async () => {
    try {
      while (pendingAssemblyBranchNodeListLoadContext) {
        const context = pendingAssemblyBranchNodeListLoadContext;
        const loadEpoch = assemblyBranchNodeListLoadEpoch;
        pendingAssemblyBranchNodeListLoadContext = null;
        currentAssemblyBranchNodeListLoadingContext = context;

        try {
          clearAssemblyBranchNodeRemoteMetadataCache();
          const loaded = await assemblyBranchNodeListController.load(context);
          if (loaded) {
            cacheAssemblyBranchNodeRemoteMetadata(assemblyBranchNodeListStore.nodes);
          }
          syncAssemblyBranchNodeListWithWorkingAssemblyDocument();

          if (loadEpoch !== assemblyBranchNodeListLoadEpoch) {
            // 面板关闭或上下文失效期间完成的旧请求可能刚写回 Store，需要再次清空以维持 reset 语义。
            if (activeSidePanel.value !== 'branch') {
              assemblyBranchNodeListController.reset();
            }
            continue;
          }

          const latestContext = resolveActiveAssemblyBranchNodeListContext();
          if (activeSidePanel.value !== 'branch' || !latestContext) {
            continue;
          }

          if (!isSameAssemblyBranchNodeListContext(context, latestContext)) {
            pendingAssemblyBranchNodeListLoadContext = latestContext;
          }
        } finally {
          if (
            currentAssemblyBranchNodeListLoadingContext
            && isSameAssemblyBranchNodeListContext(context, currentAssemblyBranchNodeListLoadingContext)
          ) {
            currentAssemblyBranchNodeListLoadingContext = null;
          }
        }
      }
    } finally {
      isAssemblyBranchNodeListLoadRunning = false;
      assemblyBranchNodeListLoadDrainPromise = null;
      if (pendingAssemblyBranchNodeListLoadContext) {
        void drainAssemblyBranchNodeListLoadQueue();
      }
    }
  })();

  return assemblyBranchNodeListLoadDrainPromise;
}

/**
 * 请求加载指定上下文的分支节点列表。
 *
 * @param context 目标装配体分支上下文。
 */
function loadAssemblyBranchNodeListForContext(context: AssemblyBranchNodeListRuntimeContext): Promise<void> {
  if (isAssemblyBranchNodeListLoadContextAlreadyQueuedOrRunning(context)) {
    return isAssemblyBranchNodeListLoadRunning
      ? assemblyBranchNodeListLoadDrainPromise ?? Promise.resolve()
      : drainAssemblyBranchNodeListLoadQueue();
  }

  pendingAssemblyBranchNodeListLoadContext = context;
  return drainAssemblyBranchNodeListLoadQueue();
}

/**
 * 关闭分支节点列表相关弹窗。
 */
function closeAssemblyBranchNodeListDialogs(): void {
  switchNodeVersionDialogRequestId += 1;
  isSwitchNodeVersionDialogVisible.value = false;
  switchNodeVersionTargetNode.value = null;
  switchNodeVersionCommits.value = [];
  isSwitchNodeVersionCommitsLoading.value = false;
}

/**
 * 关闭分支节点列表面板并重置其 UI 状态。
 */
function closeAndResetAssemblyBranchNodeListPanel(): void {
  assemblyBranchNodeListLoadEpoch += 1;
  pendingAssemblyBranchNodeListLoadContext = null;
  currentAssemblyBranchNodeListLoadingContext = null;
  clearAssemblyBranchNodeRemoteMetadataCache();
  clearAssemblyBranchNodeProgrammaticHover();
  activeSidePanel.value = null;
  closeAssemblyBranchNodeListDialogs();
  assemblyBranchNodeListController.reset();
}

/**
 * 在新标签页打开已有负责分支工作区。
 *
 * 设计说明：这里仅根据后端返回的 `canEdit` 暴露工作区入口，并通过路由打开新的编辑器标签页；
 * 不会修改当前 CAD 主状态、视图状态或命令交互状态，因此不接入 Undo/Redo。
 *
 * @param node 当前负责分支所属的子节点摘要，用于在分支缺失仓库 ID 时兜底。
 * @param branch 用户点击的负责分支摘要。
 */
function handleOpenResponsibleBranchWorkspace(
  node: AssemblyChildNodeSummary,
  branch: AssemblyChildNodeBranchSummary,
): void {
  if (!branch.canEdit) {
    return;
  }

  const projectIdText = projectId.value.trim();
  const repositoryIdText = branch.repositoryId.trim() || node.repositoryId.trim();
  const branchIdText = branch.branchId.trim();

  if (!projectIdText || !repositoryIdText || !branchIdText) {
    message.warning('缺少负责分支工作区上下文，无法进入工作区');
    return;
  }

  const targetRoute = router.resolve({
    name: 'editor',
    params: {
      projectId: projectIdText,
    },
    query: {
      repositoryId: repositoryIdText,
      branchId: branchIdText,
    },
  });
  window.open(targetRoute.href, '_blank', 'noopener,noreferrer');
}

/**
 * 打开切换子节点采纳版本弹窗，并按需加载目标节点提交列表。
 *
 * @param node 目标子节点。
 */
async function handleOpenSwitchNodeVersionDialog(node: AssemblyChildNodeSummary): Promise<void> {
  if (!resolveActiveAssemblyBranchNodeListContext()) {
    closeAndResetAssemblyBranchNodeListPanel();
    return;
  }

  switchNodeVersionTargetNode.value = node;
  switchNodeVersionCommits.value = [];
  isSwitchNodeVersionDialogVisible.value = true;
  isSwitchNodeVersionCommitsLoading.value = true;
  const requestId = ++switchNodeVersionDialogRequestId;

  try {
    const commits = await assemblyBranchNodeListController.loadNodeCommits({
      nodeId: node.nodeId,
    });
    if (
      requestId !== switchNodeVersionDialogRequestId
      || switchNodeVersionTargetNode.value?.nodeId !== node.nodeId
    ) {
      return;
    }
    cacheAssemblyChildNodeCommitDisplayInfos(node, commits);
    switchNodeVersionCommits.value = commits;
  } finally {
    if (
      requestId === switchNodeVersionDialogRequestId
      && switchNodeVersionTargetNode.value?.nodeId === node.nodeId
    ) {
      isSwitchNodeVersionCommitsLoading.value = false;
    }
  }
}

/**
 * 关闭切换子节点采纳版本弹窗。
 */
function handleCloseSwitchNodeVersionDialog(): void {
  switchNodeVersionDialogRequestId += 1;
  isSwitchNodeVersionDialogVisible.value = false;
  switchNodeVersionTargetNode.value = null;
  switchNodeVersionCommits.value = [];
  isSwitchNodeVersionCommitsLoading.value = false;
}

/**
 * 切换动作开始后只隐藏弹框，不打断当前切换请求。
 *
 * 设计说明：用户选择提交并确认后，弹框不再承载交互状态；后续模型加载失败由全局错误提示表达。
 * 这里不能递增 requestId 或清空 targetNode，否则当前异步切换完成后无法回写节点列表。
 */
function hideSwitchNodeVersionDialogForSwitching(): void {
  isSwitchNodeVersionDialogVisible.value = false;
  switchNodeVersionCommits.value = [];
  isSwitchNodeVersionCommitsLoading.value = false;
}

/**
 * 选择目标提交并切换父装配体对子节点采纳的版本。
 *
 * @param commitId 目标提交 ID。
 */
async function handleSelectSwitchNodeVersionCommit(commitId: string): Promise<void> {
  const targetNode = switchNodeVersionTargetNode.value;
  const targetCommit = switchNodeVersionCommits.value.find((commit) => commit.id === commitId) ?? null;

  if (!targetNode || !targetCommit || !resolveActiveAssemblyBranchNodeListContext()) {
    closeAndResetAssemblyBranchNodeListPanel();
    return;
  }
  if (guardProjectModelStructureWrite('切换子节点版本')) {
    return;
  }

  const requestId = switchNodeVersionDialogRequestId;
  const switchModelLoadingText = `正在切换子节点版本：${targetNode.name}`;
  let switchModelLoadingOwner: string | null = null;
  let mateRemovalPlan: AssemblySubtreeMateRemovalImpactPlan | null = null;
  let appliedMateDocument: AssemblyMateDocumentModel | null = null;
  let hasStartedSwitching = false;

  try {
    const currentResult = glbLoader.getCurrentProjectModelLoadResult();
    if (!currentResult) {
      throw new Error('当前项目模型尚未通过 OpenProject 新入口加载，无法切换子节点版本');
    }

    const draftRecord = glbLoader.assemblyDraftDocumentStore.get(currentResult.root.nodeKey);
    if (!draftRecord) {
      throw new Error(`当前根装配缺少 _assembly.json 工作草稿：${currentResult.root.nodeKey}`);
    }

    const nodeReference = decodeAssemblyChildNodeReference(targetNode.nodeId);
    const targetReference = glbLoader.resolveCurrentProjectModelFirstLevelTargetByReference({
      repositoryId: nodeReference.repositoryId,
      instanceUuid: nodeReference.instanceUuid,
      adoptedCommitId: nodeReference.adoptedCommitId,
      nodeKey: nodeReference.nodeKey,
    });
    if (!targetReference) {
      throw new Error(`无法在当前项目模型中定位子节点：${targetNode.name}`);
    }
    const targetNodeKey = targetReference.nodeKey;
    const assemblyDocumentNodeKey = targetReference.documentNode?.nodeKey;
    const targetRepositoryId = targetReference.repositoryId;
    const targetWasRuntimeEmbedded = targetReference.kind === 'runtime-embedded-child';
    const targetWasMissingFromProjectModel = targetReference.shouldAppendMissingNode;
    const targetRuntimeRootObjectIds = [
      targetReference.hierarchyNode?.objectId,
      targetReference.objectId,
    ].filter((objectId): objectId is string => Boolean(objectId?.trim()))
      .filter((objectId, index, array) => array.indexOf(objectId) === index);
    const targetDisplayName = targetReference.modelNode?.displayName?.trim()
      || targetReference.documentNode?.instanceName?.trim()
      || targetReference.documentNode?.instanceId?.trim()
      || targetNode.name;
    const subtreeRemovalTarget: AssemblySubtreeMateRemovalTarget = {
      nodeKey: targetNodeKey,
      modelNode: targetReference.modelNode,
      documentNode: targetReference.documentNode,
      runtimeRootObjectIds: targetRuntimeRootObjectIds,
    };
    mateRemovalPlan = createEditorAssemblySubtreeMateRemovalImpactPlan(
      subtreeRemovalTarget,
      currentResult,
    );
    const linearComponentPatternRemovalPlan =
      createEditorLinearComponentPatternSubtreeRemovalImpactPlan(
        subtreeRemovalTarget,
        currentResult,
      );
    const confirmed = await requestAssemblyChildNodeVersionSwitchConfirmation({
      dialog,
      confirmVersionSwitch: confirmAssemblyChildNodeVersionSwitch,
      target: {
        id: targetNodeKey,
        label: targetDisplayName,
        kind: targetReference.nodeType === 'assembly' ? 'assembly' : 'part',
      },
      relatedItems: [
        ...(mateRemovalPlan?.relatedItems ?? []),
        ...linearComponentPatternRemovalPlan.removedRecords.map((record) => ({
          id: `linear-component-pattern:${record.ownerAssemblyNodeId}:${record.featureId}`,
          label: record.displayName,
          kind: 'linear-component-pattern' as const,
        })),
      ],
    });
    if (!confirmed) {
      return;
    }

    hideSwitchNodeVersionDialogForSwitching();
    assemblyBranchNodeListStore.isSwitchingVersion = true;
    hasStartedSwitching = true;

    // 子节点为装配体时，后续重载会先下载并解析整棵替换子树；这里先占用根模型 tab 的 loading，避免网络已开始但画布无反馈。
    switchModelLoadingOwner = currentResult.root.nodeKey;
    editorModelLoadingCoordinator.show(switchModelLoadingText, switchModelLoadingOwner);

    const nextChildReloadInput = {
      nodeKey: targetNodeKey,
      repositoryId: targetRepositoryId,
      commitId,
    };
    const nextChildSnapshot = await cadNodeTreeReloadAdapter.reloadSnapshot(nextChildReloadInput);
    const nextChild = nextChildSnapshot.node;
    if (targetWasRuntimeEmbedded || targetWasMissingFromProjectModel) {
      const currentTargetReference = glbLoader.resolveCurrentProjectModelFirstLevelTargetByReference({
        repositoryId: nodeReference.repositoryId,
        instanceUuid: nodeReference.instanceUuid,
        adoptedCommitId: nodeReference.adoptedCommitId,
        nodeKey: nodeReference.nodeKey,
      });
      // reload 跨越异步边界；append 前必须确认 occurrence 仍处于开始时的槽位和来源状态。
      assertAssemblyBranchNodeReferenceTargetStableForAppend(targetReference, currentTargetReference);
    }
    const nextAdoptedCommitId = nextChild.commitId.trim() || commitId;
    const nextAdoptedBranchId = normalizeAssemblyBranchIdText(targetCommit.branchId)
      || (nextChild.branchId > 0n ? nextChild.branchId.toString() : '');
    cacheAssemblyChildNodeCommitDisplayInfo(targetRepositoryId, targetCommit);
    const rootPuppyPackageFileId = resolvePuppyAssemblyPackageRootFile(currentResult.root)?.fileId;
    const nextWorkingDocument = replaceAssemblyChildVersion(
      draftRecord.workingAssemblyDocument,
      {
        ...(assemblyDocumentNodeKey
          ? { assemblyDocumentNodeKey }
          : {}),
        assemblyNodeId: targetReference.assemblyNodeId,
        ...(rootPuppyPackageFileId ? { repositoryPuppyPackageFileId: rootPuppyPackageFileId } : {}),
        nodeKey: targetNodeKey,
        repositoryId: nextChild.repositoryId.toString(),
        ...(nextAdoptedBranchId ? { branchId: nextAdoptedBranchId } : {}),
        commitId: nextAdoptedCommitId,
      },
    );
    const replacementRootTransform = targetReference.documentNode?.transform
      ?? targetReference.modelNode?.localTransform
      ?? DEFAULT_INSERTED_COMPONENT_TRANSFORM;
    const assemblyDocumentOverridesByNodeKey = new Map([
      [currentResult.root.nodeKey, nextWorkingDocument],
    ]);
    let nextProjectModelResult: ProjectModelLoadResult | null = null;
    let preparedProjectModelFileReadResults: readonly ProjectNodeFileReadResult[] | null = null;
    let preparedCompleteGlbPackageSnapshotsByNodeKey: ReadonlyMap<
      string,
      PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
    > | null = null;

    const updateWorkingDocumentBeforeCommit = (): void => {
      glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
        currentResult.root.nodeKey,
        nextWorkingDocument,
      );
    };
    const rollbackWorkingDocumentBeforeCommit = (): void => {
      glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
        currentResult.root.nodeKey,
        draftRecord.workingAssemblyDocument,
      );
    };

    // 节点版本切换只在 Editor 层生成业务 loadUnit，文件预读、root `.puppy` 补齐和缺失补读统一交给 glbLoader。
    const prepareNodeVersionLoadUnit = async (loadUnit: ModelAssetNode) => (
      glbLoader.prepareProjectModelBackendLoadUnit({
        planInput: { kind: 'root-load', root: loadUnit },
        assemblyDocumentOverridesByNodeKey,
      })
    );
    appliedMateDocument = applyAssemblySubtreeMateRemovalImpactPlan(mateRemovalPlan);

    if (targetWasRuntimeEmbedded || targetWasMissingFromProjectModel) {
      // 两类节点都需要先通过 append load unit 物化到项目树；实际场景提交仍需区分
      // “替换已有内嵌运行时”与“插入真正缺失节点”，避免旧 topology/BRep 残留。
      const appendLoadUnit = glbLoader.createAppendMissingFirstLevelNodeVersionLoadUnit({
        currentResult,
        targetNodeKey,
        assemblyDocumentNodeKey,
        replacementRootTransform,
        assemblyDocumentOverridesByNodeKey,
        nextChildSnapshot,
      });
      const preparedSubtree = await prepareNodeVersionLoadUnit(appendLoadUnit);
      nextProjectModelResult = mergePreparedAppendMissingFirstLevelNodeVersionResult({
        currentResult,
        targetNodeKey,
        preparedSubtreeResult: preparedSubtree.loadResult,
        assemblyDocumentOverridesByNodeKey,
      });
      preparedProjectModelFileReadResults = preparedSubtree.fileReadResults;
      preparedCompleteGlbPackageSnapshotsByNodeKey =
        preparedSubtree.completeGlbPackageSnapshotsByNodeKey;
    } else {
      const switchLoadUnit = glbLoader.createSwitchFirstLevelNodeVersionLoadUnit({
        currentResult,
        targetNodeKey,
        assemblyDocumentNodeKey,
        replacementRootTransform,
        assemblyDocumentOverridesByNodeKey,
        nextChildSnapshot,
      });
      const preparedSubtree = await prepareNodeVersionLoadUnit(switchLoadUnit);
      nextProjectModelResult = mergePreparedSwitchFirstLevelNodeVersionResult({
        currentResult,
        targetNodeKey,
        preparedSubtreeResult: preparedSubtree.loadResult,
        assemblyDocumentOverridesByNodeKey,
      });
      preparedProjectModelFileReadResults = preparedSubtree.fileReadResults;
      preparedCompleteGlbPackageSnapshotsByNodeKey =
        preparedSubtree.completeGlbPackageSnapshotsByNodeKey;
    }

    const applyProjectModelScope: AssemblyChildVersionApplyScope = resolveAssemblyChildVersionApplyScope({
      targetNodeKey,
      parentNodeKey: currentResult.root.nodeKey,
      targetWasRuntimeEmbedded,
      targetWasMissingFromProjectModel,
    });

    if (!nextProjectModelResult) {
      throw new Error(`子节点版本切换缺少项目模型应用结果：${targetNode.name}`);
    }
    await glbLoader.applyProjectModelLoadResult(nextProjectModelResult, {
      ...applyProjectModelScope,
      loadingText: switchModelLoadingText,
      beforeCommit: updateWorkingDocumentBeforeCommit,
      rollbackBeforeCommit: rollbackWorkingDocumentBeforeCommit,
      preparedFileReadResults: preparedProjectModelFileReadResults ?? undefined,
      preparedCompleteGlbPackageSnapshotsByNodeKey:
        preparedCompleteGlbPackageSnapshotsByNodeKey ?? undefined,
      cleanedRootMateDocument: appliedMateDocument ?? undefined,
      linearComponentPatternRemovalGroupObjectIds:
        linearComponentPatternRemovalPlan.removedRecords.map(record => record.groupObjectId),
    });

    if (
      requestId === switchNodeVersionDialogRequestId
      && switchNodeVersionTargetNode.value?.nodeId === targetNode.nodeId
    ) {
      replaceAssemblyBranchNodeListNode(targetNode.nodeId, {
        ...targetNode,
        nodeId: encodeAssemblyChildNodeReference({
          ...nodeReference,
          adoptedBranchId: nextAdoptedBranchId,
          adoptedCommitId: nextAdoptedCommitId,
          nodeKey: targetNodeKey,
        }),
        adoptedCommitId: nextAdoptedCommitId,
        adoptedVersionLabel: resolveAssemblyChildNodeCommitDisplayLabel({
          repositoryId: targetRepositoryId,
          commitId: nextAdoptedCommitId,
          branchId: nextAdoptedBranchId,
          fallbackLabel: `${targetCommit.branchName || targetCommit.branchId} / ${targetCommit.message || commitId}`,
        }),
        hasCommits: true,
      });
      if (targetWasMissingFromProjectModel) {
        message.info('该节点此前未在设计树中显示，已按采纳版本加载模型、放置在原点，并加入设计树一级节点');
      } else if (targetWasRuntimeEmbedded) {
        message.info('离线包内嵌子节点已按统一设计树链路完成版本切换');
      }
      handleCloseSwitchNodeVersionDialog();
    }
  } catch (error) {
    if (appliedMateDocument) {
      try {
        rollbackAssemblySubtreeMateRemovalImpactPlan(mateRemovalPlan);
      } catch (rollbackError) {
        reportEditorAsyncFailure(rollbackError, '切换子节点版本失败，且恢复配合清理失败');
      }
    }
    reportEditorAsyncFailure(error, '切换子节点版本失败');
  } finally {
    assemblyBranchNodeListStore.isSwitchingVersion = false;
    if (
      switchModelLoadingOwner
      && editorModelLoadingCoordinator.isOwnedBy(switchModelLoadingOwner)
    ) {
      editorModelLoadingCoordinator.hide(switchModelLoadingOwner);
    }
    if (
      hasStartedSwitching
      && requestId === switchNodeVersionDialogRequestId
      && switchNodeVersionTargetNode.value?.nodeId === targetNode.nodeId
    ) {
      handleCloseSwitchNodeVersionDialog();
    }
  }
}

/**
 * 手动刷新提交记录面板。
 *
 * 设计说明：刷新仅重新拉取项目提交记录，不修改模型、设计树或命令状态，因此不接入 Undo/Redo。
 */
function handleRefreshVersionHistoryPanel(): void {
  if (isInitialOpenProjectAuxiliaryLoading.value) {
    return;
  }

  void versionWorkspaceController.loadCommitRecords({ forceRefresh: true });
}

/**
 * 手动刷新装配体分支节点列表。
 *
 * 设计说明：这里复用打开面板时的上下文解析和串行加载队列，避免按钮绕过节点身份缓存、
 * 工作装配草稿覆盖以及旧请求收尾保护。
 */
function handleRefreshAssemblyBranchNodeListPanel(): void {
  if (isInitialOpenProjectAuxiliaryLoading.value) {
    return;
  }

  if (!canOpenAssemblyBranchNodeListPanel.value) {
    closeAndResetAssemblyBranchNodeListPanel();
    return;
  }

  const context = resolveActiveAssemblyBranchNodeListContext();
  if (!context) {
    openAssemblyBranchNodeListPanelWithoutLoading('缺少当前模型分支上下文，暂无法刷新分支与节点。');
    return;
  }

  void loadAssemblyBranchNodeListForContext(context);
}

/**
 * 处理左侧工具栏面板选择。
 *
 * @param key 被点击的侧栏功能键。
 */
function handleSideRailSelect(key: string): void {
  const panel = key === 'history' || key === 'branch' ? key : null;
  void setEditorSidePanel(panel === activeSidePanel.value ? null : panel).catch((error) => {
    reportEditorAsyncFailure(error, '打开左侧面板失败');
  });
}

/**
 * 统一设置左侧面板；用户点击与引导共用权限、数据加载和分支清理路径。
 * @param panel 指定展开的面板，null 表示收起。
 * @returns 对应面板数据加载结束。
 */
async function setEditorSidePanel(panel: EditorOnBoardingSidePanel): Promise<void> {
  if (panel === 'branch' && !canOpenAssemblyBranchNodeListPanel.value) {
    closeAndResetAssemblyBranchNodeListPanel();
    return;
  }
  if (panel === activeSidePanel.value) return;
  if (activeSidePanel.value === 'branch') closeAndResetAssemblyBranchNodeListPanel();
  if (panel === 'branch') closeAssemblyBranchNodeListDialogs();
  activeSidePanel.value = panel;
  if (!panel || isInitialOpenProjectAuxiliaryLoading.value) return;
  if (panel === 'history') {
    await versionWorkspaceController.loadCommitRecords();
    return;
  }
  const context = resolveActiveAssemblyBranchNodeListContext();
  if (!context) {
    openAssemblyBranchNodeListPanelWithoutLoading('缺少当前模型分支上下文，暂无法加载分支与节点。');
    return;
  }
  await loadAssemblyBranchNodeListForContext(context);
}

/**
 * 临时展开一个教学面板，提前登记还原，避免异步加载期间跳过留下展开状态。
 * @param panel 本步骤需要介绍的面板。
 * @param context 当前步骤的资源作用域。
 * @returns 数据与真实展开动画均已完成。
 */
async function enterOnBoardingSidePanel(
  panel: Exclude<EditorOnBoardingSidePanel, null>,
  context: OnBoardingStepEnterContext,
): Promise<void> {
  const previousPanel = activeSidePanel.value;
  context.onCleanup((reason) => {
    // 页面销毁只做释放，不能为了恢复不可见的旧面板重新发起加载。
    if (reason === 'dispose' || isEditorUnmounted) {
      if (activeSidePanel.value === 'branch') closeAndResetAssemblyBranchNodeListPanel();
      else activeSidePanel.value = null;
      return;
    }
    void setEditorSidePanel(previousPanel).catch((error) => {
      reportEditorAsyncFailure(error, '恢复引导前的左侧面板失败');
    });
  });
  await setEditorSidePanel(panel);
  if (context.signal.aborted) return;
  await editorSidePanelDrawerRef.value?.whenSettled(context.signal);
}

/**
 * 开始拖拽左侧面板宽度。
 *
 * 设计说明：宽度仅保存在当前 Editor 会话的 ref 中，避免用户明确不需要的持久化副作用。
 *
 * @param event 指针按下事件。
 */
function handleEditorSidePanelResizePointerDown(event: PointerEvent): void {
  if (event.pointerType === 'mouse' && event.button !== 0) {
    return;
  }

  handleEditorSidePanelResizePointerUp();
  editorSidePanelResizeStartX = event.clientX;
  editorSidePanelResizeStartWidth = editorSidePanelWidth.value;
  isEditorSidePanelResizing.value = true;
  window.addEventListener('pointermove', handleEditorSidePanelResizePointerMove);
  window.addEventListener('pointerup', handleEditorSidePanelResizePointerUp);
  window.addEventListener('pointercancel', handleEditorSidePanelResizePointerUp);
  event.preventDefault();
}

/**
 * 根据拖拽位移更新左侧面板宽度。
 *
 * @param event 指针移动事件。
 */
function handleEditorSidePanelResizePointerMove(event: PointerEvent): void {
  const nextWidth = editorSidePanelResizeStartWidth + event.clientX - editorSidePanelResizeStartX;
  editorSidePanelWidth.value = resolveVersionHistoryPanelWidth(nextWidth);
}

/**
 * 结束左侧面板拖拽并清理全局监听。
 */
function handleEditorSidePanelResizePointerUp(): void {
  isEditorSidePanelResizing.value = false;
  window.removeEventListener('pointermove', handleEditorSidePanelResizePointerMove);
  window.removeEventListener('pointerup', handleEditorSidePanelResizePointerUp);
  window.removeEventListener('pointercancel', handleEditorSidePanelResizePointerUp);
}

/**
 * 从编辑态切换到历史版本前进行风险确认。
 *
 * @returns 用户确认继续时返回 true，取消或关闭时返回 false。
 */
function confirmSwitchVersionFromEditing(): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    dialog.warning({
      title: '切换历史版本',
      content: '切换历史版本前建议先提交当前修改。历史版本仅支持查看，是否继续切换？',
      positiveText: '继续切换',
      negativeText: '取消',
      onPositiveClick: () => {
        settle(true);
      },
      onNegativeClick: () => {
        settle(false);
      },
      onClose: () => {
        settle(false);
      },
    });
  });
}

/**
 * 重载历史提交快照对应的中间模型和设计树。
 *
 * @param snapshot 历史提交快照。
 * @param context 版本切换请求上下文。
 */
async function reloadCommitSnapshot(
  snapshot: ProjectCommitSnapshot,
  context: VersionWorkspaceReloadContext,
): Promise<void> {
  if (!context.isCurrent()) {
    throw new Error(`历史版本切换请求已失效：${snapshot.commitId}`);
  }
  const loadingText = `正在加载历史版本：${snapshot.title}`;
  const loadingOwner = `${HISTORY_PREVIEW_RELOAD_LOADING_OWNER}:${++historyPreviewReloadLoadingOwnerSequence}`;
  editorModelLoadingCoordinator.show(loadingText, loadingOwner);
  try {
    if (!context.isCurrent()) {
      throw new Error(`历史版本切换请求已失效：${snapshot.commitId}`);
    }
    await glbLoader.switchProjectModelRootSnapshot({
      snapshot,
      context,
      loadingText,
      loadingOwner,
      beforeRuntimeSwitch: () => recreateViewerForRootDocumentSwitch({
        preserveActiveProjectRootSnapshotSwitch: true,
        isCurrent: context.isCurrent,
      }),
    });
    if (!context.isCurrent()) {
      throw new Error(`历史版本切换请求已失效：${snapshot.commitId}`);
    }
  } finally {
    if (editorModelLoadingCoordinator.isOwnedBy(loadingOwner)) {
      editorModelLoadingCoordinator.hide(loadingOwner);
    }
  }
}

/**
 * 将当前编辑器打开上下文切换到新建的工作区分支。
 *
 * @param workspace CreateBranch 返回后映射出的工作区分支信息。
 */
async function replaceRouteWithVersionWorkspaceBranch(
  workspace: VersionWorkspaceCreationResult,
): Promise<void> {
  const repositoryId = cadSessionStore.currentRepositoryId?.toString()
    ?? openProjectRouteContext.value.sessionInput.repositoryIdText
    ?? '';
  const nextOpenProjectQuery = buildOpenProjectRouteQuery({
    repositoryId,
    branchId: workspace.branchId,
  });

  /**
   * 先更新 URL 再重开 OpenProject，保证后续刷新页面、下载上下文和提交记录查询都指向同一个
   * 工作区分支，而不是只在内存里临时切换模型。
   */
  await router.replace({
    query: {
      ...route.query,
      ...nextOpenProjectQuery,
    },
  });
}

/**
 * 将当前编辑器打开上下文切换到指定提交。
 *
 * 设计说明：后端装配写入会产生新的父装配提交。
 * 最终模型必须按后端返回的新提交重开，不能只在 store 中改写 commitId。
 *
 * @param commitId 需要重新打开的最终提交 ID。
 */
async function replaceRouteWithOpenProjectCommit(commitId: string): Promise<void> {
  const normalizedCommitId = commitId.trim();
  if (!normalizedCommitId) {
    throw new Error('重新加载项目缺少目标提交 ID');
  }

  const repositoryId = cadSessionStore.currentRepositoryId?.toString()
    ?? openProjectRouteContext.value.sessionInput.repositoryIdText
    ?? '';
  const branchId = cadSessionStore.currentBranchId?.toString()
    ?? openProjectRouteContext.value.sessionInput.branchIdText
    ?? '';
  const nextOpenProjectQuery = buildOpenProjectRouteQuery({
    repositoryId,
    branchId,
    commitId: normalizedCommitId,
  });

  await router.replace({
    query: {
      ...route.query,
      ...nextOpenProjectQuery,
    },
  });
}

/**
 * 根提交变化后重新加载当前项目模型。
 *
 * 设计说明：提交工作区会让后端产生新的根提交；只改 URL 会让路由状态前移，
 * 但当前场景仍停留在旧提交，所以必须强制切换根快照并刷新设计树。
 *
 * @param commitId 后端返回的新根提交 ID。
 * @param input.loadingText 加载遮罩文案。
 * @param input.syncBranchNodeListAfterRootReload 是否在根快照重载后同步分支节点列表实例身份。
 * @param input.refreshBranchNodeListAfterStructureChanged 是否在结构变化后刷新分支节点列表。
 */
async function reloadProjectAfterRootCommitChanged(
  commitId: string,
  input: {
    loadingText?: string;
    syncBranchNodeListAfterRootReload?: boolean;
    refreshBranchNodeListAfterStructureChanged?: boolean;
  } = {},
): Promise<void> {
  const normalizedCommitId = commitId.trim();
  if (!normalizedCommitId) {
    throw new Error('重新加载项目缺少目标提交 ID');
  }

  const projectIdText = projectId.value.trim();
  if (!projectIdText) {
    throw new Error('重新加载项目缺少项目 ID');
  }

  const repositoryId = cadSessionStore.currentRepositoryId?.toString()
    ?? openProjectRouteContext.value.sessionInput.repositoryIdText
    ?? '';
  if (!repositoryId) {
    throw new Error('重新加载项目缺少根仓库 ID');
  }

  const branchId = cadSessionStore.currentBranchId?.toString()
    ?? openProjectRouteContext.value.sessionInput.branchIdText
    ?? '';
  const snapshot: ProjectCommitSnapshot = {
    projectId: projectIdText,
    commitId: normalizedCommitId,
    title: normalizedCommitId,
    branchName: currentWorkspaceBranchName.value,
    modelSource: {
      kind: 'project-root-snapshot',
      repositoryId,
      ...(branchId ? { branchId } : {}),
      commitId: normalizedCommitId,
    },
  };
  const requestId = projectRootCommitReloadRequestId + 1;
  projectRootCommitReloadRequestId = requestId;
  const context: VersionWorkspaceReloadContext = {
    isCurrent: () => !isEditorUnmounted && projectRootCommitReloadRequestId === requestId,
  };
  const loadingText = input.loadingText ?? '正在重新加载装配模型';

  editorModelLoadingCoordinator.show(loadingText, ROOT_COMMIT_RELOAD_LOADING_OWNER);
  featureTreeStore.isTreeLoading = true;
  assemblyBranchNodeListAutoReloadSuppressionDepth += 1;
  try {
    clearEditorCommandSessionForRootDocumentSwitch();
    await replaceRouteWithOpenProjectCommit(normalizedCommitId);
    if (!context.isCurrent()) {
      return;
    }
    /**
     * 根提交变化后必须跳过默认标签页缓存判断，直接按目标提交加载根快照；否则界面只会看到
     * OpenProject 请求成功，真实场景仍停留在旧提交的已挂载模型。
     */
    await glbLoader.switchProjectModelRootSnapshot({
      snapshot,
      context,
      loadingText,
      loadingOwner: ROOT_COMMIT_RELOAD_LOADING_OWNER,
      preserveLoadingAfterApply: true,
      replaceDesignTreeAfterApply: true,
      beforeRuntimeSwitch: () => recreateViewerForRootDocumentSwitch({
        preserveActiveProjectRootSnapshotSwitch: true,
        isCurrent: context.isCurrent,
      }),
    });
    if (!context.isCurrent()) {
      return;
    }
    if (input.syncBranchNodeListAfterRootReload && activeSidePanel.value === 'branch') {
      // 根快照切换会重建模型树和 sceneBindings，左侧节点列表也必须同步到当前实例身份。
      syncAssemblyBranchNodeListWithWorkingAssemblyDocument();
      if (!context.isCurrent()) {
        return;
      }
    }
    if (input.refreshBranchNodeListAfterStructureChanged) {
      await refreshAssemblyBranchNodeListAfterStructureChanged();
      if (!context.isCurrent()) {
        return;
      }
    }
    void versionWorkspaceController.loadCommitRecords();
  } finally {
    assemblyBranchNodeListAutoReloadSuppressionDepth = Math.max(
      0,
      assemblyBranchNodeListAutoReloadSuppressionDepth - 1,
    );
    if (context.isCurrent()) {
      featureTreeStore.isTreeLoading = false;
    }
    if (!isEditorUnmounted && editorModelLoadingCoordinator.isOwnedBy(ROOT_COMMIT_RELOAD_LOADING_OWNER)) {
      editorModelLoadingCoordinator.hide(ROOT_COMMIT_RELOAD_LOADING_OWNER);
    }
  }
}

/**
 * 重载当前可编辑工作区。
 *
 * @param workspace 新建工作区分支信息。
 */
async function reloadEditableWorkspace(workspace: VersionWorkspaceCreationResult): Promise<void> {
  editorModelLoadingCoordinator.show('正在进入新工作区', EDITABLE_WORKSPACE_RELOAD_LOADING_OWNER);
  featureTreeStore.isTreeLoading = true;

  try {
    await replaceRouteWithVersionWorkspaceBranch(workspace);
    await recreateViewerForRootDocumentSwitch();
    // 创建工作区后回编辑态也必须重走模型和设计树刷新链路，避免只切 store 状态导致模型/树仍停留在历史预览。
    await loadProjectOrThrow();
    await glbLoader.loadDefaultTab();
    await featureTreeStore.refreshTree(glbLoader.partTabsStore.activeTabId);
  } finally {
    if (editorModelLoadingCoordinator.isOwnedBy(EDITABLE_WORKSPACE_RELOAD_LOADING_OWNER)) {
      featureTreeStore.isTreeLoading = false;
      editorModelLoadingCoordinator.hide(EDITABLE_WORKSPACE_RELOAD_LOADING_OWNER);
    }
  }
}

const versionWorkspaceController = useVersionWorkspaceController({
  store: versionWorkspaceStore,
  service: versionWorkspaceService,
  getProjectId: () => projectId.value,
  confirmSwitchFromEditing: confirmSwitchVersionFromEditing,
  reloadCommitSnapshot,
  reloadEditableWorkspace,
  syncEditableCommitRoute: replaceRouteWithOpenProjectCommit,
  notifySuccess: (content) => message.success(content),
  notifyError: (content) => message.error(content),
});



/**
 * 当前导入文档的命令能力。
 *
 * 装配体与零件的可编辑能力不同，统一在 cad-core 的能力模型中判定，
 * 避免 UI / workflow / feature-tree 各自散落 `type === 'assembly'` 分支。
 */
const activeEditorTabType = computed(() => (
  offlinePackageRuntimeDocumentType.value
    ?? resolveEditorActiveTabType(
      projectId.value,
      glbLoader.partTabsStore.activeTab?.type ?? null,
    )
));

const activeDocumentCapabilities = computed(() => resolveImportedDocumentCapabilities(
  activeEditorTabType.value,
));

/**
 * 判断节点与分支面板是否允许被打开。
 *
 * 设计说明：节点与分支只描述装配体根节点及其负责分支；零件标签和历史预览态没有可编辑的
 * 装配体节点上下文，因此入口保留但禁用，避免展示误导性的空面板。
 */
const canOpenAssemblyBranchNodeListPanel = computed(() => (
  activeEditorTabType.value === 'assembly'
  && !versionWorkspaceStore.isReadonlyPreview
));

const disabledSideRailKeys = computed<readonly string[]>(() => (
  canOpenAssemblyBranchNodeListPanel.value ? [] : ['branch']
));

watch(
  () => [
    glbLoader.partTabsStore.activeTab?.id ?? null,
    activeEditorTabType.value,
    versionWorkspaceStore.isReadonlyPreview,
    cadSessionStore.currentRepositoryId?.toString() ?? null,
    cadSessionStore.currentBranchId?.toString() ?? null,
    cadSessionStore.currentCommitId,
  ] as const,
  () => {
    if (activeSidePanel.value !== 'branch') {
      return;
    }

    if (isAssemblyBranchNodeListAutoReloadSuppressed()) {
      return;
    }

    if (isInitialOpenProjectAuxiliaryLoading.value) {
      return;
    }

    if (!canOpenAssemblyBranchNodeListPanel.value) {
      closeAndResetAssemblyBranchNodeListPanel();
      return;
    }

    const context = resolveActiveAssemblyBranchNodeListContext();
    if (!context) {
      openAssemblyBranchNodeListPanelWithoutLoading('缺少当前模型分支上下文，暂无法加载分支与节点。');
      return;
    }
    // 装配体标签或当前会话分支变化后，需要重新加载以避免旧列表残留。
    void loadAssemblyBranchNodeListForContext(context);
  },
);

const shadedSketchContoursEnabled = computed<boolean>({
  get: () => editorDisplayConfig.value.shadedSketchContoursEnabled,
  set: (value: boolean) => {
    editorDisplayConfig.value = {
      ...editorDisplayConfig.value,
      shadedSketchContoursEnabled: value,
    };
  },
});

watch(
  editorDisplayConfig,
  (next) => {
    if (isEditorDisplayConfigLoading.value) return;
    const id = projectId.value;
    if (!id) return;
    void editorPreferencesRepo.upsert(id, { editorDisplay: next });
  },
  { deep: true },
);

const { extrudeViewportRef } = useEditorViewportAdapter({
  getViewer,
  commitFeatureHistoryRecord: commitEditorFeatureHistoryRecord,
});

const updateState = async (): Promise<void> => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    throw new Error('Viewer ???????????????');
  }
  await currentViewer.getGeometryStateSummary({syncHistoryProjection: false});
  const counts = await featureTreeStore.refreshTree();
};

const getExtrudeSelectionObjectById = async (
  objectId: string,
): Promise<{ id: string; type: string; metadata?: Record<string, unknown> } | null> => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    throw new Error('Viewer ????????????????');
  }
  const state = await currentViewer.getGeometryStateSummary({syncHistoryProjection: false});
  const object = state.objects.find((item) => item.id === objectId) ?? null;
  if (!object) {
    return null;
  }
  return {
    id: object.id,
    type: object.type,
    metadata: object.metadata,
  };
};

const {
  isExtrudeMode,
  extrudeUiState,
  handleExtrudeCommand,
  handleCutExtrudeCommand,
  startEditingExtrudeFeature,
  handleExtrudeSelectionChange,
  cancelExtrude,
  submitExtrude,
  setExtrudeDepth,
  setExtrudeDirection,
  setExtrudeDirection1,
  setExtrudeEndTargetInputActive,
  setExtrudeEndTargetVisual,
  setExtrudeDirection2,
  setExtrudeCutOptions,
  setExtrudeThinFeature,
  setExtrudeMergeResult,
  setExtrudeSelectedContoursInputActive,
  removeExtrudeSelectedContour,
  highlightExtrudeSelectedContour,
} = useExtrudeFeature({
  viewportRef: extrudeViewportRef as any,
  getObjectById: getExtrudeSelectionObjectById,
  updateState,
  onPreviewLoadingChange: (state) => {
    if (state.active) {
      showProfileFeaturePreviewLoading({
        feature: 'extrude',
        operation: state.operation,
      });
      return;
    }
    hideProfileFeaturePreviewLoading();
  },
  message,
});

const {
  revolveUiState,
  handleRevolveCommand,
  handleRevolveSelectionChange,
  startEditingRevolveFeature,
  submitRevolve,
  cancelRevolve,
  activateRevolvePicker,
  clearRevolveSelectedAxis,
  clearRevolveSelectedContours,
  removeRevolveSelectedContour,
  highlightRevolveSelectedAxis,
  highlightRevolveSelectedContour,
  setRevolveDirection1Angle,
  setRevolveDirection2Enabled,
  setRevolveDirection2Angle,
  setRevolveDirectionReversed,
} = useRevolveFeature({
  viewportRef: extrudeViewportRef as any,
  updateState,
  onPreviewLoadingChange: (state) => {
    if (state.active) {
      showProfileFeaturePreviewLoading({
        feature: 'revolve',
        operation: state.operation,
      });
      return;
    }
    hideProfileFeaturePreviewLoading();
  },
  message,
});

const {
  revolveCutUiState,
  handleRevolveCutCommand,
  handleRevolveCutSelectionChange,
  startEditingCutRevolveFeature,
  submitRevolveCut,
  cancelRevolveCut,
  activateRevolveCutPicker,
  clearRevolveCutSelectedAxis,
  clearRevolveCutSelectedContours,
  removeRevolveCutSelectedContour,
  highlightRevolveCutSelectedAxis,
  highlightRevolveCutSelectedContour,
  setRevolveCutDirection1Angle,
  setRevolveCutDirection2Enabled,
  setRevolveCutDirection2Angle,
  setRevolveCutDirectionReversed,
  setRevolveCutFlipSideToCut,
  toggleRevolveCutFragment,
  setRevolveCutKeepBodiesSelectionMode,
  toggleRevolveCutKeepBodiesDraftFragment,
  confirmRevolveCutKeepBodiesDialog,
  cancelRevolveCutKeepBodiesDialog,
} = useRevolveCutFeature({
  viewportRef: extrudeViewportRef as any,
  updateState,
  onPreviewLoadingChange: (state) => {
    if (state.active) {
      showProfileFeaturePreviewLoading({
        feature: 'revolve',
        operation: state.operation,
      });
      return;
    }
    hideProfileFeaturePreviewLoading();
  },
  message,
});

const {
  filletUiState,
  chamferUiState,
  startFilletCommand,
  startChamferCommand,
  startEditingFilletFeature,
  startEditingChamferFeature,
  handleBlendSelectionChange,
  cancelBlend,
  submitFillet,
  submitChamfer,
  setFilletType,
  selectFilletItem,
  removeFilletItem,
  highlightFilletItem,
  setFilletAsymmetricRadiiFlipped,
  selectFilletVariablePoint,
  removeFilletVariablePoint,
  removeAllFilletVariableInstancePoints,
  assignFilletRadiusToAllPoints,
  assignFilletRadiusToUnassignedPoints,
  setFilletSelectionTarget,
  setFilletSelectThroughFaces,
  setFilletKeepFeatures,
  setFilletRadius,
  setFilletRadius2,
  setFilletChordWidth,
  setFilletEdgeFilletMode,
  setFilletOverflowType,
  setFilletVariablePointRadius,
  setFilletVariableInstanceCount,
  setFilletVariableMode,
  setFilletVariableTransition,
  setFilletFaceFilletMode,
  setFilletFaceFilletRadiiFlipped,
  setFilletPartialEdgeParametersEnabled,
  setFilletPartialCondition,
  setFilletPartialValue,
  setFilletPartialReferenceTarget,
  clearFilletPartialReference,
  setFilletFaceSetTarget,
  setChamferType,
  selectChamferItem,
  removeChamferItem,
  highlightChamferItem,
  setChamferTangentPropagation,
  setChamferFlipDirection,
  setChamferEqualDistance,
  setChamferSelectThroughFaces,
  setChamferKeepFeatures,
  setChamferSymmetric,
  setChamferDistance,
  setChamferDistance2,
  setChamferWidth,
  setChamferAngleDeg,
  setChamferVertexDistance,
  setChamferPartialEdgeParametersEnabled,
  setChamferPartialCondition,
  setChamferPartialValue,
  setChamferPartialReferenceTarget,
  clearChamferPartialReference,
  setChamferFaceFaceMode,
  setChamferFaceSetTarget,
  setChamferSelectionTarget,
} = useBlendFeature({
  viewportRef: extrudeViewportRef as any,
  updateState,
  message,
});

const {
  isInsertComponentActive,
  insertComponentFiles,
  insertComponentFilterKinds,
  insertComponentQuery,
  insertComponentLoading,
  insertComponentHasMore,
  insertComponentTotal,
  insertComponentPage,
  insertComponentFixationMode,
  selectedInsertComponentFiles,
  insertComponentPlacementIndex,
  insertComponentPlacementTotal,
  isPlacingComponent,
  placingComponentDescriptor,
  startInsertComponent,
  cancelInsertComponent,
  setInsertComponentFilterKinds,
  setInsertComponentQuery,
  setInsertComponentFixationMode,
  loadInsertComponentPage,
  loadMoreInsertComponents,
  selectInsertComponent,
  toggleInsertComponentSelection,
  confirmInsertComponentSelection,
  peekNextPlacementDescriptor,
  finishCurrentPlacement,
  finishPlacement,
} = useInsertComponent();

/**
 * 默认装配插入变换矩阵。
 */
const DEFAULT_INSERTED_COMPONENT_TRANSFORM: AssemblyTransform = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
];

/**
 * 将任意矩阵数组规范化成 `_assembly.json` 可写入的 4x4 变换。
 *
 * @param matrix 待校验矩阵。
 * @param context 错误定位上下文。
 * @returns 可写入装配草稿的矩阵。
 */
function toAssemblyTransform(matrix: readonly number[], context: string): AssemblyTransform {
  if (matrix.length !== 16) {
    throw new Error(`插入零部件变换矩阵长度无效：${context}, length=${matrix.length}`);
  }
  const invalidIndex = matrix.findIndex((value) => !Number.isFinite(value));
  if (invalidIndex >= 0) {
    throw new Error(`插入零部件变换矩阵包含非有限数：${context}, index=${invalidIndex}`);
  }

  return [
    matrix[0]!, matrix[1]!, matrix[2]!, matrix[3]!,
    matrix[4]!, matrix[5]!, matrix[6]!, matrix[7]!,
    matrix[8]!, matrix[9]!, matrix[10]!, matrix[11]!,
    matrix[12]!, matrix[13]!, matrix[14]!, matrix[15]!,
  ];
}

/**
 * 解析插入零部件的本地放置矩阵。
 *
 * @param result 后端插入零部件结果。
 * @returns 项目模型加载服务使用的放置矩阵。
 */
function resolveInsertedComponentPlacementTransform(
  result: InsertSubAssemblyResult,
): ResolvedPlacementTransform {
  const context = `repositoryId=${result.node.repositoryId.toString()}, commitId=${result.node.commitId}`;
  if (result.node.transform.length === 16) {
    return {
      matrix: toAssemblyTransform(result.node.transform, context),
      source: 'cad-node-transform',
    };
  }

  return {
    matrix: DEFAULT_INSERTED_COMPONENT_TRANSFORM,
    source: 'identity',
  };
}

/**
 * 创建可写入工作装配文档的新增子节点摘要。
 *
 * @param input 新增子节点上下文。
 * @returns cad-core 装配文档子节点。
 */
function createInsertedAssemblyDocumentNode(input: {
  readonly result: InsertSubAssemblyResult;
  readonly insertedNodeKey: string;
  readonly transform: AssemblyTransform;
  readonly fallbackDisplayName: string;
}): AssemblyDocumentNode {
  const node = input.result.node;
  const instanceUuid = node.instanceId.trim();
  const instanceName = node.instanceName.trim() || input.fallbackDisplayName.trim();

  return {
    nodeKey: input.insertedNodeKey,
    nodeType: node.type === CadFileKind.PART ? 'part' : 'assembly',
    repositoryId: node.repositoryId.toString(),
    ...(node.branchId > 0n ? { branchId: node.branchId.toString() } : {}),
    commitId: node.commitId.trim(),
    transform: input.transform,
    ...(instanceUuid ? { instanceId: instanceUuid } : {}),
    ...(instanceName ? { instanceName } : {}),
  };
}

/**
 * 插入零部件成功后刷新节点列表。
 */
async function refreshAssemblyBranchNodeListAfterStructureChanged(): Promise<void> {
  if (activeSidePanel.value !== 'branch') {
    return;
  }

  const context = resolveActiveAssemblyBranchNodeListContext();
  if (context) {
    // 插入零部件会改变父装配的直接子节点，分支面板打开时同步刷新列表。
    await loadAssemblyBranchNodeListForContext(context);
  }
}

/**
 * 在项目模型树中按 nodeKey 查找节点。
 *
 * @param node 当前模型节点。
 * @param nodeKey 目标 nodeKey。
 * @returns 命中的模型节点；未命中时返回 null。
 */
function findProjectModelNodeByNodeKey(node: ModelAssetNode, nodeKey: string): ModelAssetNode | null {
  if (node.nodeKey === nodeKey) {
    return node;
  }

  for (const child of node.children) {
    const matchedNode = findProjectModelNodeByNodeKey(child, nodeKey);
    if (matchedNode) {
      return matchedNode;
    }
  }

  return null;
}

/**
 * 收集当前 Viewer 选中对象及其父级链对象 ID。
 *
 * 设计说明：装配体场景中用户可能命中零部件内部 mesh，而 sceneBindings 通常绑定到组件
 * Group；因此场景右键删除需要把父级链也作为候选，但这些 Object3D 只在函数内同步读取，
 * 不进入 Vue 响应式状态。
 *
 * @param baseObjectIds 入口已捕获的对象 ID。
 * @returns 去重后的对象 ID 列表。
 */
function collectAssemblyDeleteCandidateObjectIds(baseObjectIds: readonly string[]): string[] {
  const objectIds = new Set(
    baseObjectIds
      .map((objectId) => objectId.trim())
      .filter((objectId) => objectId.length > 0),
  );
  const viewerRuntime = getViewer();
  const selectedObjects = viewerRuntime?.getSelectedObjects?.() ?? [];

  selectedObjects.forEach((selectedObject: THREE.Object3D) => {
    let currentObject: THREE.Object3D | null = selectedObject;
    while (currentObject) {
      objectIds.add(currentObject.uuid);
      currentObject = currentObject.parent;
    }
  });

  return Array.from(objectIds);
}

/**
 * 解析目标节点在当前根装配下的一级子节点祖先。
 *
 * @param root 当前项目模型根节点。
 * @param targetNode 目标模型节点。
 * @returns 一级子节点祖先；目标本身为一级子节点时返回目标。
 */
function resolveFirstLevelAssemblyChildNode(
  root: ModelAssetNode,
  targetNode: ModelAssetNode,
): ModelAssetNode | null {
  if (targetNode.parentNodeKey === root.nodeKey) {
    return targetNode;
  }

  let currentNode: ModelAssetNode | null = targetNode;
  while (currentNode?.parentNodeKey && currentNode.parentNodeKey !== root.nodeKey) {
    currentNode = findProjectModelNodeByNodeKey(root, currentNode.parentNodeKey);
  }

  return currentNode?.parentNodeKey === root.nodeKey ? currentNode : null;
}

/**
 * 判断模型节点是否是当前根装配的直接子节点。
 *
 * @param root 当前项目模型根节点。
 * @param targetNode 目标模型节点。
 * @returns 目标是直接子节点时返回 true。
 */
function isDirectAssemblyChildNode(root: ModelAssetNode, targetNode: ModelAssetNode): boolean {
  return targetNode.parentNodeKey === root.nodeKey;
}

/**
 * 解析装配组件删除确认框中的目标显示名称。
 *
 * @param targetNode 删除目标节点。
 * @param fallbackName 入口捕获的显示名。
 * @returns 用户可读节点名称。
 */
function resolveAssemblyComponentDeleteDisplayName(
  targetNode: ModelAssetNode,
  fallbackName?: string,
): string {
  return targetNode.displayName?.trim()
    || fallbackName?.trim()
    || targetNode.instanceUuid?.trim()
    || targetNode.nodeKey;
}

/**
 * 创建编辑器当前装配节点删除/切换的 Mate 清理影响计划。
 *
 * @param target 删除或切换前的旧子树目标。
 * @param projectModelLoadResult 当前项目模型加载结果。
 * @returns 有 Mate 被清理时返回影响计划，否则返回 null。
 */
function createEditorAssemblySubtreeMateRemovalImpactPlan(
  target: AssemblySubtreeMateRemovalTarget,
  projectModelLoadResult: ProjectModelLoadResult,
): AssemblySubtreeMateRemovalImpactPlan | null {
  const controller = getViewer()?.assemblyMateController;
  if (!controller) {
    return null;
  }

  return createAssemblySubtreeMateRemovalImpactPlanFromDocument({
    target,
    document: controller.toJSON(),
    projectModelLoadResult,
    resolveSceneObjectById: (objectId) => getViewer()?.getSceneObjectById(objectId) ?? null,
  });
}

/**
 * 创建编辑器当前装配节点切换的线性阵列删除影响计划。
 *
 * 设计说明：旧阵列只在确认阶段冻结 Group ID，不立即修改 Manager；真正的删除会与
 * 新子树阵列恢复一起交给 cad-core scoped 事务，取消确认时不会留下任何运行时变化。
 *
 * @param target 即将被替换的旧子树目标。
 * @param projectModelLoadResult 当前项目模型加载结果。
 * @returns 受旧子树影响的线性阵列冻结计划。
 */
function createEditorLinearComponentPatternSubtreeRemovalImpactPlan(
  target: AssemblySubtreeMateRemovalTarget,
  projectModelLoadResult: ProjectModelLoadResult,
): PuppyCAD.LinearComponentPatternSubtreeRemovalImpactPlan {
  const viewer = getViewer();
  if (!viewer) {
    return { removedRecords: [] };
  }

  const mateDocument = viewer.assemblyMateController.toJSON();
  const impactInput = {
    target,
    document: mateDocument,
    projectModelLoadResult,
    resolveSceneObjectById: (objectId: string) => viewer.getSceneObjectById(objectId),
  };

  return createLinearComponentPatternSubtreeRemovalImpactPlan({
    records: viewer.linearComponentPatternManager.getCommittedRecords(),
    subtreeRootObjects: collectAssemblySubtreeRemovalRuntimeRootObjects(impactInput),
    assemblyNodeIds: collectAssemblySubtreeMateRemovalAssemblyNodeIds(impactInput),
    defaultOwnerAssemblyNodeId: mateDocument.assemblyId,
    resolveSceneObjectById: (objectId) => viewer.getSceneObjectById(objectId),
  });
}

/**
 * 应用装配节点删除前计算出的 Mate 清理计划。
 *
 * @param plan Mate 清理计划；为空时不做任何处理。
 * @returns 实际写入 controller 后的 Mate 文档快照。
 */
function applyAssemblySubtreeMateRemovalImpactPlan(
  plan: AssemblySubtreeMateRemovalImpactPlan | null,
): AssemblyMateDocumentModel | null {
  if (!plan) {
    return null;
  }

  const controller = getViewer()?.assemblyMateController;
  if (!controller) {
    throw new Error('装配体配合控制器未初始化，无法清理删除节点关联配合。');
  }

  const sourceTabId = glbLoader.partTabsStore.activeTabId;
  controller.loadDocument(plan.afterDocument, { restoreReferences: true });
  editorAssemblyMateDocumentHistorySnapshot = controller.getDocument();
  // 结构子树由 Loader 更新；这里只同步删除影响计划改变的 Mate 文件夹。
  featureTreeStore.applyAssemblyMateDocument(
    sourceTabId,
    editorAssemblyMateDocumentHistorySnapshot,
  );
  return editorAssemblyMateDocumentHistorySnapshot;
}

/**
 * 回滚装配节点删除过程中已经应用的 Mate 清理。
 *
 * @param plan Mate 清理计划；为空时不做任何处理。
 */
function rollbackAssemblySubtreeMateRemovalImpactPlan(
  plan: AssemblySubtreeMateRemovalImpactPlan | null,
): void {
  if (!plan) {
    return;
  }

  const controller = getViewer()?.assemblyMateController;
  if (!controller) {
    throw new Error('装配体配合控制器未初始化，无法回滚删除节点关联配合。');
  }

  const sourceTabId = glbLoader.partTabsStore.activeTabId;
  controller.loadDocument(plan.beforeDocument, { restoreReferences: true });
  editorAssemblyMateDocumentHistorySnapshot = controller.getDocument();
  // 回滚与正向清理共用同一配合局部投影，避免异常路径残留删除后的 Mate 节点。
  featureTreeStore.applyAssemblyMateDocument(
    sourceTabId,
    editorAssemblyMateDocumentHistorySnapshot,
  );
}

/**
 * 按用户范围选择解析最终要删除的模型节点。
 *
 * @param root 当前项目模型根节点。
 * @param targetNode 用户原始命中节点。
 * @param scope 用户选择的删除范围。
 * @returns 最终删除目标。
 */
function resolveAssemblyComponentDeleteTargetByScope(
  root: ModelAssetNode,
  targetNode: ModelAssetNode,
  scope: AssemblyComponentDeleteScope,
): ModelAssetNode {
  if (scope === 'delete-selected-component') {
    return targetNode;
  }

  const firstLevelChild = resolveFirstLevelAssemblyChildNode(root, targetNode);
  if (!firstLevelChild) {
    throw new Error(`无法定位包含选定零部件的子装配体：${targetNode.nodeKey}`);
  }
  if (firstLevelChild.nodeType !== 'assembly') {
    throw new Error(`包含选定零部件的一级节点不是子装配体：${firstLevelChild.nodeKey}`);
  }

  return firstLevelChild;
}

/**
 * 请求装配组件删除范围。
 *
 * @param root 当前项目模型根节点。
 * @param targetNode 用户原始命中节点。
 * @returns 用户选择的删除范围；取消时返回 null。
 */
async function requestAssemblyComponentDeleteScopeIfNeeded(
  root: ModelAssetNode,
  targetNode: ModelAssetNode,
): Promise<AssemblyComponentDeleteScope | null> {
  if (isDirectAssemblyChildNode(root, targetNode)) {
    return 'delete-selected-component';
  }

  const selection = await requestAssemblyComponentDeleteScopeSelection({ dialog });
  return selection.scope;
}

/**
 * 使用根装配 working 文档覆盖项目模型加载结果中的装配文档索引。
 *
 * @param loadResult 当前项目模型加载结果。
 * @param rootNodeKey 根装配节点 key。
 * @param workingAssemblyDocument 当前根装配 working 文档。
 * @returns 覆盖后的项目模型加载结果。
 */
function createProjectModelLoadResultWithRootWorkingDocument(
  loadResult: ProjectModelLoadResult,
  rootNodeKey: string,
  workingAssemblyDocument: AssemblyDocument,
): ProjectModelLoadResult {
  return {
    ...loadResult,
    assemblyDocumentsByNodeKey: new Map(loadResult.assemblyDocumentsByNodeKey).set(
      rootNodeKey,
      workingAssemblyDocument,
    ),
  };
}

/**
 * 执行装配组件删除。
 *
 * @param input 设计树或场景右键传入的删除目标。
 * @returns 删除是否成功。
 */
async function handleAssemblyComponentDeleteAction(
  input: FeatureTreeAssemblyComponentDeleteActionInput,
): Promise<boolean> {
  if (guardProjectModelStructureWrite('删除装配组件')) {
    return false;
  }

  const currentResult = glbLoader.getCurrentProjectModelLoadResult();
  if (!currentResult || currentResult.root.nodeType !== 'assembly') {
    message.warning('当前不是可编辑装配体工程，无法删除装配组件');
    return false;
  }

  const targetObjectIds = input.includeViewerSelectionAncestors
    ? collectAssemblyDeleteCandidateObjectIds([input.objectId, input.sourceObjectId ?? ''])
    : [input.objectId, input.sourceObjectId ?? ''];
  const currentViewer = getViewer();
  const currentScene = currentViewer?.getScene?.() ?? null;
  const stableNodeKey = input.nodeKey?.trim() ?? '';
  const targetNode = resolveProjectModelNodeBySceneObjectIds({
    loadResult: currentResult,
    objectIds: targetObjectIds,
    stableNodeKey,
    getSceneObjectById: (objectId) =>
      currentViewer?.getSceneObjectById?.(objectId)
      ?? currentScene?.getObjectByProperty('uuid', objectId)
      ?? null,
  });
  if (!targetNode) {
    message.warning('未能从当前场景绑定中定位装配组件，无法删除');
    return false;
  }
  if (targetNode.nodeKey === currentResult.root.nodeKey) {
    message.warning('不能删除当前根装配体');
    return false;
  }

  try {
    const scope = await requestAssemblyComponentDeleteScopeIfNeeded(currentResult.root, targetNode);
    if (!scope) {
      return false;
    }

    const deleteTargetNode = resolveAssemblyComponentDeleteTargetByScope(currentResult.root, targetNode, scope);
    const mateRemovalPlan = createEditorAssemblySubtreeMateRemovalImpactPlan({
      nodeKey: deleteTargetNode.nodeKey,
      modelNode: deleteTargetNode,
    }, currentResult);
    const confirmed = await requestFeatureTreeDeleteConfirmation({
      dialog,
      target: {
        id: deleteTargetNode.nodeKey,
        label: resolveAssemblyComponentDeleteDisplayName(deleteTargetNode, input.displayName),
        kind: deleteTargetNode.nodeType === 'assembly' ? 'assembly' : 'part',
      },
      relatedItems: mateRemovalPlan?.relatedItems ?? [],
    });

    if (!confirmed) {
      return false;
    }

    const draftRecord = glbLoader.assemblyDraftDocumentStore.get(currentResult.root.nodeKey);
    if (!draftRecord) {
      throw new Error(`当前根装配缺少 _assembly.json 工作草稿：${currentResult.root.nodeKey}`);
    }

    const deleteWorkingDocumentResult = deleteAssemblyChildIfExists(draftRecord.workingAssemblyDocument, {
      nodeKey: deleteTargetNode.nodeKey,
      ...(deleteTargetNode.instanceUuid ? { instanceId: deleteTargetNode.instanceUuid } : {}),
      nodeType: deleteTargetNode.nodeType,
    });
    const nextWorkingDocument = deleteWorkingDocumentResult.document;
    const beforeProjectModelResult = createProjectModelLoadResultWithRootWorkingDocument(
      currentResult,
      currentResult.root.nodeKey,
      draftRecord.workingAssemblyDocument,
    );
    const prunedLoadResult = deleteProjectModelLoadResultNode({
      loadResult: currentResult,
      nodeKey: deleteTargetNode.nodeKey,
    }).loadResult;
    const nextProjectModelResult = createProjectModelLoadResultWithRootWorkingDocument(
      prunedLoadResult,
      currentResult.root.nodeKey,
      nextWorkingDocument,
    );
    let localDeleteResult: Awaited<ReturnType<typeof glbLoader.deleteProjectModelNodeLocally>> | null = null;
    let appliedMateDocument: AssemblyMateDocumentModel | null = null;
    try {
      await editorModelHistoryRuntime.runExclusive(async () => {
        localDeleteResult = await glbLoader.deleteProjectModelNodeLocally({
          nodeKey: deleteTargetNode.nodeKey,
          nextLoadResult: nextProjectModelResult,
          beforeCommit: () => {
            glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
              currentResult.root.nodeKey,
              nextWorkingDocument,
            );
          },
          rollbackBeforeCommit: () => {
            glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
              currentResult.root.nodeKey,
              draftRecord.workingAssemblyDocument,
            );
          },
        });
        appliedMateDocument = applyAssemblySubtreeMateRemovalImpactPlan(mateRemovalPlan);

        const beforeHistorySnapshot = createAssemblyStructureHistorySnapshot({
          loadResult: beforeProjectModelResult,
          workingAssemblyDocument: draftRecord.workingAssemblyDocument,
          ...(mateRemovalPlan ? { assemblyMateDocument: mateRemovalPlan.beforeDocument } : {}),
          deletedSubtreePayloadRef: localDeleteResult.payloadRef,
        });

        await commitEditorAssemblyStructureHistoryInQueue({
          label: `删除${deleteTargetNode.nodeType === 'assembly' ? '子装配体' : '零部件'}`,
          commandType: 'assembly.component.delete',
          before: beforeHistorySnapshot,
          after: createAssemblyStructureHistorySnapshot({
            loadResult: createProjectModelLoadResultWithRootWorkingDocument(
              localDeleteResult.loadResult,
              currentResult.root.nodeKey,
              nextWorkingDocument,
            ),
            workingAssemblyDocument: nextWorkingDocument,
            ...(appliedMateDocument ? { assemblyMateDocument: appliedMateDocument } : {}),
            deletedSubtreePayloadRef: localDeleteResult.payloadRef,
          }),
        });
      });
    } catch (error) {
      if (localDeleteResult) {
        try {
          await glbLoader.applyAssemblyStructureHistorySnapshotLocally(
            createAssemblyStructureHistorySnapshot({
              loadResult: beforeProjectModelResult,
              workingAssemblyDocument: draftRecord.workingAssemblyDocument,
              deletedSubtreePayloadRef: localDeleteResult.payloadRef,
            }),
            {
              beforeCommit: () => {
                glbLoader.assemblyDraftDocumentStore.updateWorkingDocument(
                  currentResult.root.nodeKey,
                  draftRecord.workingAssemblyDocument,
                );
              },
            },
          );
        } catch (rollbackError) {
          if (appliedMateDocument) {
            rollbackAssemblySubtreeMateRemovalImpactPlan(mateRemovalPlan);
          }
          throw new Error('删除装配组件失败，且恢复删除前结构失败', { cause: rollbackError });
        }
      }
      if (appliedMateDocument) {
        rollbackAssemblySubtreeMateRemovalImpactPlan(mateRemovalPlan);
      }
      throw error;
    }

    selectedKeys.value = [];
    selectedObject.value = null;
    message.success(`已删除${deleteTargetNode.nodeType === 'assembly' ? '子装配体' : '零部件'}：${resolveAssemblyComponentDeleteDisplayName(deleteTargetNode, input.displayName)}`);

    void refreshAssemblyBranchNodeListAfterStructureChanged().catch((error) => {
      reportEditorAsyncFailure(error, '刷新删除后装配体子节点列表失败');
    });
    // Loader 在结构主状态提交内已完成局部树投影或权威完整刷新；页面层不能再次重建整树。

    return true;
  } catch (error) {
    reportEditorAsyncFailure(error, '删除装配组件失败');
    return false;
  }
}

const INSERT_COMPONENT_LOAD_MAX_ATTEMPTS = 2;

interface InsertComponentPrefetchSlot {
  descriptor: InsertCadSourceDescriptor
  promise: Promise<InsertPreparationHandle | null>
  handle: InsertPreparationHandle | null
  settled: boolean
}

const pendingInsertionHandle = createPendingInsertionHandleRef();
let prefetchedInsertComponentSlot: InsertComponentPrefetchSlot | null = null;
//------ 装配体引用复制 开始------
// 引用复制句柄含 Three.js 对象和 WeakMap，必须与插入流程共用非深代理的状态入口。
const pendingAssemblyReferenceCopyHandle = createPendingInsertionHandleRef<AssemblyReferenceCopyPreparationHandle>();
const assemblyReferenceCopyClipboardObjectId = ref<string | null>(null);
//------ 装配体引用复制 结束------


/**
 * 结束插入零部件命令的工具栏交互状态。
 *
 * 设计说明：
 * 1. 插入零部件的模型结构提交由 `commitInsertion` 链路负责，这里只收口 UI 命令态；
 * 2. 放置成功、放置失败、取消和准备失败都必须清空同一组状态，避免工具栏按钮继续高亮；
 * 3. 不接入 Undo/Redo：这是命令交互状态清理，不生成新的模型历史事务。
 */
function finishAssemblyInsertionCommandState(): void {
  activeOperationCommand.value = null;
  toolbarCommandStore.clearActiveCommands();
  toolbarOperationOverride.value = null;
}

/**
 * 判断两个插入描述是否指向同一个队列项。
 *
 * @param current 当前描述。
 * @param next 待比较描述。
 * @returns 两者是否表示同一个插入来源与固定策略。
 */
function isSameInsertComponentDescriptor(
  current: InsertCadSourceDescriptor,
  next: InsertCadSourceDescriptor,
): boolean {
  return current.projectRootPath === next.projectRootPath
    && current.selectedFileId === next.selectedFileId
    && current.cadType === next.cadType
    && current.fixationMode === next.fixationMode;
}

/**
 * 解析插入零部件 loading 中使用的显示名称。
 *
 * @param descriptor 插入来源描述。
 * @returns 用户可读的零部件名称。
 */
function resolveInsertComponentLoadingDisplayName(descriptor: InsertCadSourceDescriptor): string {
  const explicitName = descriptor.displayName?.trim();
  if (explicitName) {
    return explicitName;
  }
  return descriptor.selectedFileId.split('/').pop() ?? descriptor.selectedFileId;
}

/**
 * 生成插入零部件队列 loading 文案。
 *
 * @param descriptor 插入来源描述。
 * @param index 当前 1-based 队列序号。
 * @param total 队列总数。
 * @param isRetry 是否为重试加载。
 * @returns 带进度的 loading 文案。
 */
function formatInsertComponentLoadingText(
  descriptor: InsertCadSourceDescriptor,
  index: number,
  total: number,
  isRetry: boolean,
): string {
  const safeTotal = total > 0 ? total : 1;
  const safeIndex = index > 0 ? Math.min(index, safeTotal) : 1;
  const retryText = isRetry ? '（重试）' : '';
  return `正在加载零部件 ${safeIndex}/${safeTotal}：${resolveInsertComponentLoadingDisplayName(descriptor)}${retryText}`;
}

/**
 * 更新插入零部件 loading 文案，并保持 owner 为当前装配体标签。
 *
 * @param ownerTabId loading 归属的装配体标签 ID。
 * @param text 最新 loading 文案。
 */
function updateInsertComponentLoadingText(ownerTabId: string, text: string): void {
  if (editorModelLoadingCoordinator.isOwnedBy(ownerTabId)) {
    editorModelLoadingCoordinator.updateText(ownerTabId, text);
    return;
  }
  editorModelLoadingCoordinator.show(text, ownerTabId);
}

/**
 * 判断插入零部件队列流程是否仍然有效。
 *
 * @returns 队列流程是否仍处于放置或命令状态。
 */
function isInsertComponentPlacementWorkflowActive(): boolean {
  return isPlacingComponent.value || activeOperationCommand.value === 'assembly-insert-component';
}

/**
 * 按队列语义加载插入零部件，失败时重试一次。
 *
 * @param descriptor 插入来源描述。
 * @param options loading 与队列进度选项。
 * @returns 准备好的插入句柄；两次失败、取消或被抢占时返回 null。
 */
async function prepareInsertComponentWithRetry(
  descriptor: InsertCadSourceDescriptor,
  options: {
    ownerTabId: string | null
    showLoading: boolean
    index: number
    total: number
  },
): Promise<InsertPreparationHandle | null> {
  for (let attempt = 1; attempt <= INSERT_COMPONENT_LOAD_MAX_ATTEMPTS; attempt += 1) {
    if (options.showLoading && options.ownerTabId) {
      updateInsertComponentLoadingText(
        options.ownerTabId,
        formatInsertComponentLoadingText(
          descriptor,
          options.index,
          options.total,
          attempt > 1,
        ),
      );
    }

    const handle = await glbLoader.prepareQueuedInsertion(descriptor, {
      showErrorOnFailure: false,
    });
    if (handle) {
      return handle;
    }
    if (!isInsertComponentPlacementWorkflowActive()) {
      return null;
    }
  }

  message.error(`插入零部件加载失败，已跳过：${resolveInsertComponentLoadingDisplayName(descriptor)}`);
  return null;
}

/**
 * 丢弃已经预加载但尚未进入放置流程的下一项。
 */
function discardPrefetchedInsertComponentSlot(): void {
  const slot = prefetchedInsertComponentSlot;
  prefetchedInsertComponentSlot = null;
  if (slot?.handle) {
    glbLoader.discardPreparedInsertion(slot.handle);
  }
}

/**
 * 开始预加载队列中的下一项。
 */
function startNextInsertComponentPrefetch(): void {
  const descriptor = peekNextPlacementDescriptor();
  if (!descriptor) {
    discardPrefetchedInsertComponentSlot();
    return;
  }
  const currentSlot = prefetchedInsertComponentSlot;
  if (currentSlot && isSameInsertComponentDescriptor(currentSlot.descriptor, descriptor)) {
    return;
  }
  discardPrefetchedInsertComponentSlot();

  const slot: InsertComponentPrefetchSlot = {
    descriptor,
    promise: Promise.resolve(null),
    handle: null,
    settled: false,
  };
  slot.promise = prepareInsertComponentWithRetry(descriptor, {
    ownerTabId: null,
    showLoading: false,
    index: insertComponentPlacementIndex.value + 1,
    total: insertComponentPlacementTotal.value,
  }).then((handle) => {
    slot.settled = true;
    if (prefetchedInsertComponentSlot !== slot) {
      if (handle) {
        glbLoader.discardPreparedInsertion(handle);
      }
      return null;
    }
    slot.handle = handle;
    return handle;
  });
  prefetchedInsertComponentSlot = slot;
  void slot.promise;
}

/**
 * 获取当前待放置项的插入准备句柄，优先消费已经预加载的下一项。
 *
 * @param descriptor 当前待放置的插入描述。
 * @returns 可进入放置流程的插入句柄；加载失败时返回 null。
 */
async function prepareInsertComponentForPlacement(
  descriptor: InsertCadSourceDescriptor,
): Promise<InsertPreparationHandle | null> {
  const currentTabId = glbLoader.mountedGlbTabId.value;
  const currentSlot = prefetchedInsertComponentSlot;
  if (currentSlot && isSameInsertComponentDescriptor(currentSlot.descriptor, descriptor)) {
    if (!currentSlot.settled && currentTabId) {
      updateInsertComponentLoadingText(
        currentTabId,
        formatInsertComponentLoadingText(
          descriptor,
          insertComponentPlacementIndex.value,
          insertComponentPlacementTotal.value,
          false,
        ),
      );
    }
    const handle = await currentSlot.promise;
    if (prefetchedInsertComponentSlot === currentSlot) {
      prefetchedInsertComponentSlot = null;
    }
    return handle;
  }

  return prepareInsertComponentWithRetry(descriptor, {
    ownerTabId: currentTabId,
    showLoading: true,
    index: insertComponentPlacementIndex.value,
    total: insertComponentPlacementTotal.value,
  });
}

/**
 * 将等待中的下一项静默预加载升级为可见 loading。
 *
 * 设计说明：当前零部件确认落场后，用户已经进入等待下一项模型出现的阶段；
 * 如果下一项仍在静默预加载，必须立即显示进度文案，避免界面看起来无响应。
 */
function showPendingInsertComponentPrefetchLoading(): void {
  const ownerTabId = glbLoader.mountedGlbTabId.value;
  const descriptor = placingComponentDescriptor.value;
  const slot = prefetchedInsertComponentSlot;
  if (!ownerTabId || !descriptor || !slot) {
    return;
  }
  if (!isSameInsertComponentDescriptor(slot.descriptor, descriptor)) {
    return;
  }
  updateInsertComponentLoadingText(
    ownerTabId,
    formatInsertComponentLoadingText(
      descriptor,
      insertComponentPlacementIndex.value,
      insertComponentPlacementTotal.value,
      false,
    ),
  );
}

/**
 * 立即隐藏插入零部件 loading。
 *
 * @param ownerTabId loading 归属的装配体标签 ID。
 */
function hideInsertComponentLoading(ownerTabId: string | null): void {
  if (!ownerTabId) {
    return;
  }
  editorModelLoadingCoordinator.hide(ownerTabId);
}

/**
 * 等待下一项预览首帧有机会绘制后再隐藏 loading。
 *
 * 设计说明：大装配体进入放置时，模型对象已经完成加载，但浏览器仍需要至少一帧把预览组
 * 写入画布；过早隐藏 loading 会让用户看到空白等待。双 rAF 能保证渲染循环有机会提交首帧。
 *
 * @param ownerTabId loading 归属的装配体标签 ID。
 */
function hideInsertComponentLoadingAfterPreviewFrame(ownerTabId: string | null): void {
  if (!ownerTabId) {
    return;
  }
  const hideLoading = (): void => {
    editorModelLoadingCoordinator.hide(ownerTabId);
  };
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(hideLoading, 0);
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(hideLoading);
  });
}

function handlePlacementConfirm(position: THREE.Vector3): void {
  //------ 装配体引用复制 开始------
  const referenceCopyHandle = pendingAssemblyReferenceCopyHandle.value;
  if (referenceCopyHandle) {
    componentPlacement.detachPreviewObject();
    const result = glbLoader.commitAssemblyReferenceCopy(referenceCopyHandle, position);
    if (!result) {
      message.error('复制零部件失败');
    } else {
      void commitEditorAssemblyReferenceCopyHistory(result.historySnapshot)
        .catch(() => {
          // 历史提交失败时必须回滚刚落场的 occurrence，避免模型状态与历史栈分裂。
          glbLoader.removeAssemblyReferenceCopy(result.componentInstanceId);
        });
    }
    pendingAssemblyReferenceCopyHandle.value = null;
    finishPlacement();
    return;
  }
  //------ 装配体引用复制 结束------

  const handle = pendingInsertionHandle.value;
  if (!handle) {
    finishPlacement();
    finishAssemblyInsertionCommandState();
    return;
  }
  componentPlacement.detachPreviewObject();
  const ok = glbLoader.commitInsertion(handle, position);
  if (!ok) {
    message.error('插入零部件失败');
    setPendingInsertionHandle(pendingInsertionHandle, null);
    discardPrefetchedInsertComponentSlot();
    glbLoader.cancelInsertion();
    finishPlacement();
    finishAssemblyInsertionCommandState();
    return;
  } else if (activeSidePanel.value === 'branch') {
    const context = resolveActiveAssemblyBranchNodeListContext();
    if (context) {
      // 插入零部件会改变当前装配体的子节点结构；面板已打开时只刷新左侧节点列表，
      // 复用 Editor 层 latest-context 队列，避免旧请求覆盖最新上下文且不触发模型重载。
      void loadAssemblyBranchNodeListForContext(context);
    }
  }
  setPendingInsertionHandle(pendingInsertionHandle, null);
  const hasNextQueuedPlacement = finishCurrentPlacement();
  if (!hasNextQueuedPlacement) {
    finishAssemblyInsertionCommandState();
  } else {
    showPendingInsertComponentPrefetchLoading();
  }
}

function handlePlacementCancel(): void {
  //------ 装配体引用复制 开始------
  if (pendingAssemblyReferenceCopyHandle.value) {
    pendingAssemblyReferenceCopyHandle.value = null;
    finishPlacement();
    return;
  }
  //------ 装配体引用复制 结束------

  const handle = pendingInsertionHandle.value;
  if (handle) {
    glbLoader.discardPreparedInsertion(handle);
  }
  discardPrefetchedInsertComponentSlot();
  glbLoader.cancelInsertion();
  setPendingInsertionHandle(pendingInsertionHandle, null);
  finishPlacement();
  finishAssemblyInsertionCommandState();
}

const componentPlacement = useComponentPlacement({
  getScene: () => getViewer()?.getScene() ?? null,
  getCamera: () => getViewer()?.getCamera() ?? null,
  getDomElement: () => viewportContainerRef.value,
  addObject: (object, parent, index) => getViewer()?.addObject(object, parent, index) ?? null,
  resolvePlacementSnap: ({ event }) => getViewer()?.resolveAssemblyComponentPlacementSnap({
    clientX: event.clientX,
    clientY: event.clientY,
  })?.position ?? null,
  clearPlacementSnap: () => getViewer()?.hideSnapIndicator(),
  // 放置仍逐条处理指针并更新最终落点，只合并画面提交，不合并命令输入。
  requestRender: () => getViewer()?.requestRender(),
  onConfirm: handlePlacementConfirm,
  onCancel: handlePlacementCancel,
  onFirstPreviewPositionResolved: (targetObject) => {
    getViewer()?.focusOnObjectBounds(targetObject);
  },
});

watch(placingComponentDescriptor, async (descriptor) => {
  if (!descriptor) return;

  const currentTabId = glbLoader.mountedGlbTabId.value;
  const currentTab = currentTabId
    ? glbLoader.partTabsStore.tabs.find((tab) => tab.id === currentTabId)
    : null;
  if (!currentTabId || currentTab?.type !== 'assembly') {
    message.warning('请在装配体环境下插入零部件');
    finishPlacement();
    finishAssemblyInsertionCommandState();
    return;
  }

  if (guardReadonlyPreviewEditing()) {
    finishPlacement();
    finishAssemblyInsertionCommandState();
    return;
  }
  if (guardProjectModelStructureWrite('插入零部件')) {
    finishPlacement();
    finishAssemblyInsertionCommandState();
    return;
  }

  let placementStarted = false;
  let skippedToNextQueuedPlacement = false;
  try {
    const handle = await prepareInsertComponentForPlacement(descriptor);
    if (!handle) {
      skippedToNextQueuedPlacement = finishCurrentPlacement();
      if (!skippedToNextQueuedPlacement) {
        finishAssemblyInsertionCommandState();
      }
      return;
    }

    setPendingInsertionHandle(pendingInsertionHandle, handle);

    const started = componentPlacement.startPlacement(handle.loaded.result, {
      anchorMode: ComponentPlacementAnchorMode.BoundingBoxCenter,
      focusOnFirstPreviewPosition: false,
      // 连续插入下一项时还没有新的 pointermove，必须复用确认上一项时记录的鼠标位置。
      syncToLastPointerPositionOnStart: true,
    });
    if (!started) {
      message.error('进入放置模式失败');
      glbLoader.discardPreparedInsertion(handle);
      setPendingInsertionHandle(pendingInsertionHandle, null);
      discardPrefetchedInsertComponentSlot();
      glbLoader.cancelInsertion();
      finishPlacement();
      finishAssemblyInsertionCommandState();
      return;
    }
    placementStarted = true;
    startNextInsertComponentPrefetch();
  } catch (error) {
    reportEditorAsyncFailure(error, '加载零部件失败');
    const handle = pendingInsertionHandle.value;
    if (handle) {
      glbLoader.discardPreparedInsertion(handle);
      setPendingInsertionHandle(pendingInsertionHandle, null);
    }
    discardPrefetchedInsertComponentSlot();
    glbLoader.cancelInsertion();
    finishPlacement();
    finishAssemblyInsertionCommandState();
  } finally {
    if (
      !placementStarted
      && !skippedToNextQueuedPlacement
      && activeOperationCommand.value === 'assembly-insert-component'
    ) {
      finishAssemblyInsertionCommandState();
    }
    // Insertion 完成 / 失败：只有当 owner 仍然是本 insertion 归属的 tab 时才清 loading。
    // 若途中有并发 tab-switch 通过 showLoading(tabX) 抢了 owner，这里贸然 reset 会把 owner
    // 覆盖成 global，接下来 tab-switch 的 hideLoading(tabX) 会因 owner 不匹配而被忽略，
    // 从而把 overlay 卡死。
    if (placementStarted) {
      hideInsertComponentLoadingAfterPreviewFrame(currentTabId);
    } else if (!skippedToNextQueuedPlacement) {
      hideInsertComponentLoading(currentTabId);
    }
  }
});

const rightPanelTab = ref('feature');

const entityInteractionSubscriptions: Subscription[] = [];
//------ 命令结束显示设计树 开始------
let showDesignTreeRequestSubscription: Subscription | null = null;

/**
 * 监听命令收口事件并切换到设计树。
 *
 * 设计说明：
 * 命令模块只发布“显示设计树”的事件，Editor.vue 作为右侧 tab 状态宿主统一消费，
 * 避免 PropertyManager、草图工作流和装配工作流直接互相耦合。
 */
function handleShowDesignTreeRequest(): void {
  rightPanelTab.value = 'feature';
}
//------ 命令结束显示设计树 结束------

//------ 视口选择显示属性面板 开始------
let showPropertyRequestSubscription: Subscription | null = null;

/**
 * 监听视口选择事件并切换到属性面板。
 *
 * 设计说明：
 * 选择订阅层只发布“显示属性面板”的事件，Editor.vue 作为右侧 tab 状态宿主统一消费，
 * 避免普通选择、框选和右侧面板实现细节互相耦合。
 */
function handleShowPropertyRequest(): void {
  rightPanelTab.value = 'property';
}
//------ 视口选择显示属性面板 结束------

//------ 装配体引用复制放置 开始------
/**
 * 记录视口内最近一次鼠标位置，供 Ctrl+V 刚启动放置时立即定位预览体。
 *
 * @param event 指针事件。
 */
const handleViewportContainerPointerPositionForPlacement = (event: PointerEvent): void => {
  componentPlacement.rememberPointerPosition(event);
};
//------ 装配体引用复制放置 结束------

const handleViewportContainerReady = (element: HTMLElement | null): void => {
  //------ 装配体引用复制放置 开始------
  if (viewportContainerRef.value) {
    viewportContainerRef.value.removeEventListener('pointerdown', handleViewportContainerPointerPositionForPlacement);
    viewportContainerRef.value.removeEventListener('pointermove', handleViewportContainerPointerPositionForPlacement);
  }
  //------ 装配体引用复制放置 结束------

  viewportContainerRef.value = element;

  //------ 装配体引用复制放置 开始------
  if (element) {
    element.addEventListener('pointerdown', handleViewportContainerPointerPositionForPlacement);
    element.addEventListener('pointermove', handleViewportContainerPointerPositionForPlacement);
  }
  //------ 装配体引用复制放置 结束------
};

/**
 * 同步草图编辑视觉层级。
 *
 * 设计说明：
 * 1. `current / other sketch` 的真相由 `cad-core` 内部计算，宿主只透传临时附加根对象；
 * 2. Convert Entities 等命令态可通过可选参数注入 preview / 外部高亮根对象，不在宿主硬编码业务来源；
 * 3. 非草图编辑态下统一调用 `clearSketchEditingVisualLayering()`，避免层级状态残留。
 *
 * @param options 可选的预览与外部高亮根对象。
 */
function syncSketchEditingVisualLayering(options: {
  previewRoots?: readonly THREE.Object3D[];
  highlightedExternalRoots?: readonly THREE.Object3D[];
} = {}): void {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  if (!isSketchActive.value) {
    currentViewer?.clearSketchEditingVisualLayering?.();
    return;
  }

  currentViewer.syncSketchEditingVisualLayering?.({
    previewRoots: options.previewRoots ?? [],
    highlightedExternalRoots: options.highlightedExternalRoots ?? [],
  });
}

const {
  activeOperationCommand,
  currentDrawingCommand,
  toolbarOperationOverride,
  sketchCornerMode,
  sketchCornerEnable,
  sketchTrimMode,
  sketchTrimOptions,
  selectedSketchTrimEntity,
  sketchOffsetDistance,
  sketchOffsetReverse,
  sketchOffsetBidirectional,
  sketchOffsetAddSize,
  sketchOffsetSelectChain,
  sketchOffsetCover,
  sketchOffsetBasicGeometry,
  sketchOffsetOffsetGeometry,
  sketchEntitiesTransformState,
  linePropertyVisible,
  arcPropertyVisible,
  circlePropertyVisible,
  ellipsePropertyVisible,
  polygonPropertyVisible,
  rectanglePropertyVisible,
  splinePropertyVisible,
  styleSplinePropertyVisible,
  slotPropertyVisible,
  pointPropertyVisible,
  insertLinePropertyVisible,
  selectedObjectName,
  syncToolbarOperationState,
  syncViewportInteractionMode,
  enterSketchEditingOnDatumPlane,
  resumeSketchEditing,
  handleViewportSelectionChange,
  handleViewportEmptySelectionChange,
  handleCommand,
  handleSketchCommand,
  handleDrawingCancel: runDrawingCancel,
  handleDrawingFinish: runDrawingFinish,
  handleDrawingUndoPoint: runDrawingUndoPoint,
  handleKeyDown: handleSketchWorkflowKeyDown,
  exitSketchMode,
  handlePropertyManagerHelp,
  handlePropertyManagerPreviewToggle,
  submitReferencePlane,
  startEditReferencePlane,
  cancelReferencePlane,
  submitReferenceAxis,
  startEditReferenceAxis,
  cancelReferenceAxis,
  submitReferencePoint,
  startEditReferencePoint,
  cancelReferencePoint,
  isReferenceFeatureCommand,
  submitReferenceFeatureCommand,
  cancelReferenceFeatureCommand,
  setSketchCornerModeFromProperty,
  setSketchTrimModeFromProperty,
  setSketchTrimOptionsFromProperty,
  setSketchOffsetDistanceFromProperty,
  setSketchOffsetReverseFromProperty,
  setSketchOffsetBidirectionalFromProperty,
  setSketchOffsetAddSizeFromProperty,
  setSketchOffsetSelectChainFromProperty,
  setSketchOffsetCoverFromProperty,
  setSketchOffsetBasicGeometryFromProperty,
  setSketchOffsetOffsetGeometryFromProperty,
  clearSketchOffsetWorkflow,
  generateAndExitSketchOffsetWorkflow,
  confirmConvertEntitiesWorkflow,
  setSketchEntitiesTransformInputMode,
  setSketchEntitiesTransformSelectionRole,
  setSketchEntitiesTransformTargetCoordinate,
  setSketchEntitiesTransformAngle,
  syncSketchEntitiesTransformState,
  removeSketchEntitiesTransformSelection,
  repeatSketchEntitiesTransform,
  confirmSketchEntitiesTransform,
  cancelSketchEntitiesTransform,
  confirmSketchCircularPatternWorkflow,
  cancelSketchCircularPatternWorkflow,
  clearSketchTrimWorkflow,
  clearConvertEntitiesWorkflow,
  //------ 智能尺寸命令 开始------
  smartDimensionActive,
  stopSmartDimensionWorkflow,
  //------ 智能尺寸命令 结束------
} = useEditorSketchWorkflow({
  getViewer,
  message,
  rightPanelTab,
  supportsFeatureCommands: computed(() => activeDocumentCapabilities.value.canExecuteFeatureCommands),
  isExtrudeMode,
  extrudeUiState,
  handleExtrudeCommand,
  handleCutExtrudeCommand,
  handleExtrudeSelectionChange,
  cancelExtrude,
  revolveUiState,
  handleRevolveCommand,
  handleRevolveSelectionChange,
  cancelRevolve,
  revolveCutUiState,
  handleRevolveCutCommand,
  handleRevolveCutSelectionChange,
  cancelRevolveCut,
  isBlendMode: computed(() => filletUiState.value.active || chamferUiState.value.active),
  activeBlendKind: computed(() => (
    filletUiState.value.active
      ? 'fillet'
      : chamferUiState.value.active
        ? 'chamfer'
        : null
  )),
  handleFilletCommand: startFilletCommand,
  handleChamferCommand: startChamferCommand,
  handleBlendSelectionChange,
  cancelBlend,
  multipleEntitiesBoxSelected,
  updateState,
  commitSketchSessionCreateHistory: commitEditorSketchSessionCreateHistory,
  captureSketchSessionExitHistory: captureEditorSketchSessionExitHistory,
  commitSketchSessionExitHistory: commitEditorSketchSessionExitHistory,
  releaseSketchSessionExitHistory: releaseEditorSketchSessionExitHistory,
  sketchStateStore,
  drawingStore,
  planeStore,
  featureTreeStore,
  shadedSketchContoursEnabledRef: shadedSketchContoursEnabled,
  convertEntitiesState,
  enterConvertEntities,
  confirmConvertEntities,
  cancelConvertEntities,
  startInsertComponent,
  cancelInsertComponent,
  isPlacingComponent,
  cancelComponentPlacement: () => cancelAssemblyInsertionWorkflow({
    isPlacingComponent: componentPlacement.isPlacing,
    pendingInsertionHandle,
    cancelPlacement: () => componentPlacement.cancelPlacement(),
    discardPreparedInsertion: (handle) => glbLoader.discardPreparedInsertion(handle),
    cancelInsertion: () => glbLoader.cancelInsertion(),
    finishPlacement,
  }),
  finishPlacement,
});

const evaluationWorkflow = useEditorEvaluationWorkflow({
  getViewer,
  syncToolbarOperationState,
  message,
});

const {
  measureDialogVisible,
  activeMeasureTool,
  measureDialogCollapsed,
  isEvaluationCommand,
  handleEvaluationCommand,
  closeActiveEvaluationSessions,
  handleEvaluationEscKey,
  handleMeasureToolClick,
  resumeMeasurePick,
  handleMeasureDialogClose,
  handleMeasureDialogHelp,
} = evaluationWorkflow;

const assemblyWorkflow = useEditorAssemblyWorkflow();
const { isEnterEditingComponentStatus, assemblyEditComponentDisabled, displayedProjectTitle } = storeToRefs(assemblyWorkflow);

//------ 基准面撤销重做 开始------
/**
 * 带历史事务提交基准面命令。
 *
 * @param input 提交参数。
 */
async function submitReferencePlaneCommandWithHistory(input: {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 实际提交函数。 */
  readonly submit: () => Promise<void>;
  /** 提交失败提示。 */
  readonly failureMessage: string;
}): Promise<void> {
  const viewer = getViewer();
  const manager = viewer?.datumPlaneManager;
  if (!manager) {
    await input.submit();
    return;
  }

  const rawBeforeSnapshot = manager.createHistorySnapshot();
  const beforeSnapshot = viewer?.referencePlaneController?.normalizeHistorySnapshotBeforeSubmit(rawBeforeSnapshot)
    ?? rawBeforeSnapshot;
  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      await input.submit();
      await viewer?.flushDatumPlaneFeatureRebuild();
      await commitEditorDatumPlaneHistoryInQueue({
        label: input.label,
        commandType: input.commandType,
        before: beforeSnapshot,
        after: manager.createHistorySnapshot(),
      });
    });
  } catch (error) {
    manager.restoreHistorySnapshot(beforeSnapshot);
    await viewer?.flushDatumPlaneFeatureRebuild();
    reportEditorAsyncFailure(error, input.failureMessage);
  }
}

/**
 * 提交零件态基准面并写入历史。
 */
async function submitReferencePlaneWithHistory(): Promise<void> {
  const isEditingFeature = getViewer()?.referencePlaneController?.isEditingFeature?.() === true;
  await submitReferencePlaneCommandWithHistory({
    label: isEditingFeature ? '编辑基准面' : '基准面',
    commandType: isEditingFeature ? 'datum-plane.edit' : 'datum-plane.create',
    submit: submitReferencePlane,
    failureMessage: '提交基准面历史失败',
  });
}

/**
 * 提交装配体基准面并写入历史。
 */
async function submitAssemblyReferencePlaneWithHistory(): Promise<void> {
  const isEditingFeature = getViewer()?.referencePlaneController?.isEditingFeature?.() === true;
  await submitReferencePlaneCommandWithHistory({
    label: isEditingFeature ? '编辑装配体基准面' : '装配体基准面',
    commandType: isEditingFeature ? 'assembly.datum-plane.edit' : 'assembly.datum-plane.create',
    submit: () => assemblyWorkflow.confirmAssemblyReferenceCommand(),
    failureMessage: '提交装配体基准面历史失败',
  });
}
//------ 基准面撤销重做 结束------

//------ 基准轴撤销重做 开始------
/**
 * 带历史事务提交基准轴命令。
 *
 * @param input 提交参数。
 */
async function submitReferenceAxisCommandWithHistory(input: {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 实际提交函数。 */
  readonly submit: () => Promise<void>;
  /** 提交失败提示。 */
  readonly failureMessage: string;
}): Promise<void> {
  const viewer = getViewer();
  const controller = viewer?.referenceAxisController;
  if (!controller) {
    await input.submit();
    return;
  }

  const rawBeforeSnapshot = controller.createHistorySnapshot();
  const beforeSnapshot = controller.normalizeHistorySnapshotBeforeSubmit(rawBeforeSnapshot);
  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      await input.submit();
      await commitEditorDatumAxisHistoryInQueue({
        label: input.label,
        commandType: input.commandType,
        before: beforeSnapshot,
        after: controller.createHistorySnapshot(),
      });
    });
  } catch (error) {
    controller.restoreHistorySnapshot(beforeSnapshot);
    reportEditorAsyncFailure(error, input.failureMessage);
  }
}

/**
 * 提交零件态基准轴并写入历史。
 */
async function submitReferenceAxisWithHistory(): Promise<void> {
  const isEditingFeature = getViewer()?.referenceAxisController?.isEditingFeature?.() === true;
  await submitReferenceAxisCommandWithHistory({
    label: isEditingFeature ? '编辑基准轴' : '基准轴',
    commandType: isEditingFeature ? 'datum-axis.edit' : 'datum-axis.create',
    submit: submitReferenceAxis,
    failureMessage: '提交基准轴历史失败',
  });
}

/**
 * 提交装配体基准轴并写入历史。
 */
async function submitAssemblyReferenceAxisWithHistory(): Promise<void> {
  const isEditingFeature = getViewer()?.referenceAxisController?.isEditingFeature?.() === true;
  await submitReferenceAxisCommandWithHistory({
    label: isEditingFeature ? '编辑装配体基准轴' : '装配体基准轴',
    commandType: isEditingFeature ? 'assembly.datum-axis.edit' : 'assembly.datum-axis.create',
    submit: () => assemblyWorkflow.confirmAssemblyReferenceCommand(),
    failureMessage: '提交装配体基准轴历史失败',
  });
}
//------ 基准轴撤销重做 结束------

//------ 基准点撤销重做 开始------
/**
 * 带历史事务提交基准点命令。
 *
 * @param input 提交参数。
 */
async function submitReferencePointCommandWithHistory(input: {
  /** 用户可读历史标签。 */
  readonly label: string;
  /** 历史命令类型。 */
  readonly commandType: string;
  /** 实际提交函数。 */
  readonly submit: () => Promise<void>;
  /** 提交失败提示。 */
  readonly failureMessage: string;
}): Promise<void> {
  const controller = getViewer()?.referencePointController;
  if (!controller) {
    await input.submit();
    return;
  }

  const rawBeforeSnapshot = controller.createHistorySnapshot();
  const beforeSnapshot = controller.normalizeHistorySnapshotBeforeSubmit(rawBeforeSnapshot);
  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      await input.submit();
      await commitEditorDatumPointHistoryInQueue({
        label: input.label,
        commandType: input.commandType,
        before: beforeSnapshot,
        after: controller.createHistorySnapshot(),
      });
    });
  } catch (error) {
    controller.restoreHistorySnapshot(beforeSnapshot);
    reportEditorAsyncFailure(error, input.failureMessage);
  }
}

/**
 * 提交零件态基准点并写入历史。
 */
async function submitReferencePointWithHistory(): Promise<void> {
  const isEditingFeature = getViewer()?.referencePointController?.isEditingFeature?.() === true;
  await submitReferencePointCommandWithHistory({
    label: isEditingFeature ? '编辑基准点' : '基准点',
    commandType: isEditingFeature ? 'datum-point.edit' : 'datum-point.create',
    submit: submitReferencePoint,
    failureMessage: '提交基准点历史失败',
  });
}

/**
 * 提交装配体基准点并写入历史。
 */
async function submitAssemblyReferencePointWithHistory(): Promise<void> {
  const isEditingFeature = getViewer()?.referencePointController?.isEditingFeature?.() === true;
  await submitReferencePointCommandWithHistory({
    label: isEditingFeature ? '编辑装配体基准点' : '装配体基准点',
    commandType: isEditingFeature ? 'assembly.datum-point.edit' : 'assembly.datum-point.create',
    submit: () => assemblyWorkflow.confirmAssemblyReferenceCommand(),
    failureMessage: '提交装配体基准点历史失败',
  });
}
//------ 基准点撤销重做 结束------

/**
 * 最终传递给顶部标题栏的显示名称。
 *
 * 设计说明：进入零部件编辑时仍优先显示 EditingFocusManager 的编辑焦点标题；
 * 普通装配体视角则优先显示 OpenProject 根节点标题，避免继续暴露 `_assembly.json` 文件名。
 */
const effectiveProjectTitle = computed(() => {
  if (isEnterEditingComponentStatus.value && displayedProjectTitle.value) {
    return displayedProjectTitle.value;
  }

  return openProjectRootTitle.value || displayedProjectTitle.value || projectName.value;
});

assemblyWorkflow.initialize({
  resolveFeatureDocumentScope: (objectId, nodeKey) => glbLoader.resolveFeatureDocumentScope(objectId, nodeKey),
  viewerMode,
  rightPanelTab,
  activeOperationCommand,
  currentDrawingCommand,
  toolbarOperationOverride,
  message,
  isPlacingComponent: componentPlacement.isPlacing,
  hasPendingSketchCommand: computed(() => Boolean(pendingSketchToolCommand.value)),
  isSketchEditMode: computed(() => (
    viewerMode.value === ViewerMode.Sketch
    || viewerMode.value === ViewerMode.SketchDrawing
    || viewerMode.value === ViewerMode.SketchTrim
    || viewerMode.value === ViewerMode.SketchOffset
  )),
  isDrawingMode,
  sketchTrimMode,
  sketchCornerEnable,
  isExtrudeMode,
  revolveUiState,
  revolveCutUiState,
  isBlendMode: computed(() => filletUiState.value.active || chamferUiState.value.active),
  setViewerMode: (mode) => {
    // 同时更新 Pinia store 和直接通知 Viewer 实例，确保 viewer.modeContext 即时生效，
    // 不依赖 useEditorSketchWorkflow watcher 的异步触发。
    sketchStateStore.setViewerMode(mode);
    getViewer()?.setMode(mode);
  },
  updateState,
});

//------ 命令结束显示设计树 开始------
/**
 * 请求命令收口后显示设计树。
 *
 * @param reason 命令收口原因。
 * @param command 命令标识。
 */
function requestDesignTreeAfterEditorCommand(
  reason: EditorPanelShowDesignTreeReason,
  command: string | null,
): void {
  // Editor.vue 本地命令同样只派发事件，实际 tab 写入由统一监听器完成。
  requestEditorDesignTreeTab(reason, command);
}

/**
 * 取消拉伸命令并请求显示设计树。
 *
 * @returns 是否实际取消了拉伸命令。
 */
function cancelExtrudeAndRequestDesignTree(): boolean {
  const command = extrudeUiState.value.operation === 'cut' ? 'extrude-cut' : 'extrude';
  const canceled = cancelExtrude();
  if (canceled) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
  }
  return canceled;
}

/**
 * 取消旋转凸台命令并请求显示设计树。
 *
 * @returns 是否实际取消了旋转命令。
 */
function cancelRevolveAndRequestDesignTree(): boolean {
  const canceled = cancelRevolve();
  if (canceled) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, 'revolve');
  }
  return canceled;
}

/**
 * 取消旋转切除命令并请求显示设计树。
 *
 * @returns 是否实际取消了旋转切除命令。
 */
function cancelRevolveCutAndRequestDesignTree(): boolean {
  const canceled = cancelRevolveCut();
  if (canceled) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, 'revolve-cut');
  }
  return canceled;
}

/**
 * 取消圆角或倒角命令并请求显示设计树。
 */
function cancelBlendAndRequestDesignTree(): void {
  const command = filletUiState.value.active
    ? 'fillet'
    : chamferUiState.value.active
      ? 'chamfer'
      : null;
  cancelBlend();
  if (command) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
  }
}

/**
 * 提交圆角命令并在成功退出后请求显示设计树。
 */
async function submitFilletAndRequestDesignTree(): Promise<void> {
  await submitFillet();
  if (!filletUiState.value.active) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, 'fillet');
  }
}

/**
 * 提交倒角命令并在成功退出后请求显示设计树。
 */
async function submitChamferAndRequestDesignTree(): Promise<void> {
  await submitChamfer();
  if (!chamferUiState.value.active) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, 'chamfer');
  }
}

/**
 * 取消插入零部件 PropertyManager 并请求显示设计树。
 */
function cancelInsertComponentAndRequestDesignTree(): void {
  if (activeOperationCommand.value === 'assembly-insert-component') {
    cancelAssemblyInsertionWorkflow({
      isPlacingComponent: componentPlacement.isPlacing,
      pendingInsertionHandle,
      cancelPlacement: () => componentPlacement.cancelPlacement(),
      discardPreparedInsertion: (handle) => glbLoader.discardPreparedInsertion(handle),
      cancelInsertion: () => glbLoader.cancelInsertion(),
      finishPlacement,
    });
    finishAssemblyInsertionCommandState();
  } else {
    cancelInsertComponent();
  }
  requestDesignTreeAfterEditorCommand(
    EditorPanelShowDesignTreeReason.CommandCanceled,
    'assembly-insert-component',
  );
}

/**
 * 取消转换实体引用命令并请求显示设计树。
 */
function cancelConvertEntitiesAndRequestDesignTree(): void {
  clearConvertEntitiesWorkflow();
  requestDesignTreeAfterEditorCommand(
    EditorPanelShowDesignTreeReason.CommandCanceled,
    'sketch-convert-entities',
  );
}

/**
 * 更新草图圆角/倒角模式，并在退出命令时请求显示设计树。
 *
 * @param mode 草图圆角/倒角模式。
 */
function setSketchCornerModeFromPropertyAndRequestDesignTree(
  mode: Parameters<typeof setSketchCornerModeFromProperty>[0],
): void {
  const command = activeOperationCommand.value;
  setSketchCornerModeFromProperty(mode);
  if (mode === null) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
  }
}

/**
 * 更新修剪命令模式，并在退出命令时请求显示设计树。
 *
 * @param mode 修剪命令模式。
 */
function setSketchTrimModeFromPropertyAndRequestDesignTree(
  mode: Parameters<typeof setSketchTrimModeFromProperty>[0],
): void {
  const command = activeOperationCommand.value;
  setSketchTrimModeFromProperty(mode);
  if (mode === null) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
  }
}

/**
 * 提交零件态基准几何体命令，并在成功退出后请求显示设计树。
 *
 * @param command 提交前的命令标识。
 * @param submit 实际提交函数。
 */
async function submitPartReferenceFeatureAndRequestDesignTree(
  command: string,
  submit: () => Promise<void>,
): Promise<void> {
  await submit();
  if (activeOperationCommand.value !== command) {
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, command);
  }
}
//------ 命令结束显示设计树 结束------

/**
 * 取消当前绘图命令。
 */
function handleDrawingCancel(): void {
  if (guardReadonlyPreviewEditing()) {
    return;
  }
  void runDrawingCancel();
}

/**
 * 完成当前绘图命令。
 */
async function handleDrawingFinish(): Promise<void> {
  if (guardReadonlyPreviewEditing()) {
    return;
  }
  await runDrawingFinish();
}

/**
 * 撤销当前多点绘图命令的上一个点。
 *
 * @returns 已处理撤销点时返回 true。
 */
function handleDrawingUndoPoint(): boolean {
  if (guardReadonlyPreviewEditing()) {
    return true;
  }
  return runDrawingUndoPoint();
}

const viewportInteractionSync = useEditorViewportInteractionSync({
  getViewer: () => getViewer(),
  glbFeatureTree: glbLoader.glbFeatureTree,
  selectedKeys,
  selectedObject,
  canSync: () => (
    !getViewer()?.measureController?.isMeasureActive?.()
    && isViewerIdleMode(viewerMode.value)
    && !isExtrudeMode.value
    && !revolveUiState.value.active
    && !revolveCutUiState.value.active
    && !filletUiState.value.active
    && !chamferUiState.value.active
    && activeOperationCommand.value === null
    && sketchTrimMode.value === null
    && !pendingSketchToolCommand.value
  ),
  handleViewportSelectionChange,
  isSketchSelectionMode: () => isSketchActive.value,
  // 镜向实体与草图圆角都是独占命令，普通 SelectionUpdated 不能把当前 PropertyManager 替换掉。
  canShowSketchSelectionProperty: () => (
    sketchCornerEnable.value !== true
    && activeOperationCommand.value !== 'sketch-mirror-entities'
  ),
  //------ 草图空选显示设计树 开始------
  handleViewportEmptySelectionChange,
  //------ 草图空选显示设计树 结束------
});

const measureTreeSelectionSync = useEditorMeasureTreeSelectionSync({
  getViewer: () => getViewer(),
  featureTreeNodeMap: featureTreeStore.featureTreeNodeMap,
  glbFeatureTree: glbLoader.glbFeatureTree,
  selectedKeys,
  selectedObject,
  canSync: () => Boolean(getViewer()?.measureController?.isMeasureActive?.()),
});

watch(
  [isSketchActive, currentSketchGroupId, activeOperationCommand],
  () => {
    syncSketchEditingVisualLayering();
  },
);

featureTreeStore.initProviders({
  message,
  dialog,
  enterSketchEditingOnDatumPlane: (planeId, planeName) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    return enterSketchEditingOnDatumPlane(planeId, planeName);
  },
  handleSketchCommand: async (command) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    return handleSketchCommand(command);
  },
  handleSketchLifecycleAction: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    return handleFeatureTreeSketchLifecycleAction(input);
  },
  //------ 基准对象删除历史 开始------
  handleDatumReferenceDeleteAction: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    return handleFeatureTreeDatumReferenceDeleteAction(input);
  },
  //------ 基准对象删除历史 结束------
  handleFeatureDeleteAction: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    return handleFeatureTreeFeatureDeleteAction(input);
  },
  handleAssemblyComponentDeleteAction: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    return handleAssemblyComponentDeleteAction(input);
  },
  handleFeatureVisibilityAction: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    return handleFeatureTreeFeatureVisibilityAction(input);
  },
  handleFeatureRollbackAction: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    return handleFeatureTreeRollbackAction(input);
  },
  //------ 线性零部件阵列节点删除 开始------
  handleLinearComponentPatternDelete: async ({ groupObjectId }) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    const viewer = getViewer();
    const manager = viewer?.linearComponentPatternManager;
    if (!manager) {
      message.warning('线性阵列管理器未就绪，无法删除');
      return;
    }

    const beforeSnapshot = manager.createHistorySnapshot();
    try {
      let deleted = false;
      await editorModelHistoryRuntime.runExclusive(async () => {
        deleted = manager.removeCommittedRecordByGroupObjectId(groupObjectId);
        if (!deleted) {
          return;
        }

        await commitEditorLinearComponentPatternHistoryInQueue({
          label: '删除线性零部件阵列',
          commandType: 'assembly.linear-component-pattern.delete',
          before: beforeSnapshot,
          after: manager.createHistorySnapshot(),
        });
      });

      if (!deleted) {
        message.warning('未找到目标线性阵列节点，删除失败');
        return;
      }
    } catch (error) {
      manager.restoreHistorySnapshot(beforeSnapshot);
      reportEditorAsyncFailure(error, '提交线性阵列删除历史失败');
      return;
    }

    glbLoader.syncLinearComponentPatternFeatureTree(manager.getCommittedRecords());
    message.success('线性阵列节点已删除');
  },
  //------ 线性零部件阵列编辑特征 开始------
  handleLinearComponentPatternEdit: async ({ groupObjectId }) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    const viewer = getViewer();
    const manager = viewer?.linearComponentPatternManager;
    if (!manager) {
      message.warning('线性阵列管理器未就绪，无法编辑特征');
      return;
    }
    const restored = manager.beginEditCommittedRecordByGroupObjectId(groupObjectId);
    if (!restored) {
      message.warning('线性阵列引用已失效，无法恢复编辑参数');
      return;
    }

    enterAssemblyLinearComponentPatternCommandUi({
      mode: LinearComponentPatternCommandOpenMode.EditFeature,
      groupObjectId,
    });
  },
  //------ 线性零部件阵列编辑特征 结束------
  //------ 线性零部件阵列节点删除 结束------
  handleAssemblyComponentFixationChange: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    return glbLoader.setAssemblyComponentFixationState(input);
  },
  handleAssemblyMateEdit: async ({ mateId }) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }
    cleanupBeforeAssemblyMateEditCommand(mateId);
    return true;
  },
  //------ 基准面编辑特征 开始------
  handleReferencePlaneEdit: async ({ planeId }) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    await startEditReferencePlane(planeId);
  },
  //------ 基准面编辑特征 结束------
  //------ 基准轴编辑特征 开始------
  handleReferenceAxisEdit: async ({ axisId }) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    await startEditReferenceAxis(axisId);
  },
  //------ 基准轴编辑特征 结束------
  //------ 基准点编辑特征 开始------
  handleReferencePointEdit: async ({ pointId }) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    await startEditReferencePoint(pointId);
  },
  //------ 基准点编辑特征 结束------
  handleFeatureEdit: async ({ featureId, featureKind }) => {
    if (guardReadonlyPreviewEditing()) {
      return;
    }
    if (
      activeDocumentCapabilities.value.canExecuteFeatureCommands !== true
      && isImportedDocumentFeatureKind(featureKind)
    ) {
      message.warning('装配体标签页不支持特征编辑');
      return;
    }
    if (featureKind === 'boss-extrude' || featureKind === 'cut-extrude') {
      await startEditingExtrudeFeature(featureId);
      return;
    }
    if (featureKind === 'boss-revolve') {
      await startEditingRevolveFeature(featureId);
      return;
    }
    if (featureKind === 'cut-revolve') {
      await startEditingCutRevolveFeature(featureId);
      return;
    }
    if (featureKind === 'fillet') {
      await startEditingFilletFeature(featureId);
      return;
    }
    if (featureKind === 'chamfer') {
      await startEditingChamferFeature(featureId);
      return;
    }
    message.warning('?????????????');
  },
  handleFeatureEditSketch: async (input) => {
    if (guardReadonlyPreviewEditing()) {
      return false;
    }

    /**
     * 设计树动作层已经把草图节点与特征节点统一解析为 sketchGroupId。
     * Editor 只负责恢复草图 UI 会话，再调用 cad-core 唯一严格前缀门面；
     * 消费者识别、Worker replay 和 inactive 投影都不能回流到 UI。
     */
    const resumed = await resumeSketchEditing(input.sketchGroupId);
    if (!resumed) {
      return false;
    }

    const prefixStarted = await getViewer()?.startSketchFeaturePrefixEdit({
      sketchGroupId: input.sketchGroupId,
    }) ?? false;
    if (prefixStarted) {
      return true;
    }

    /**
     * 严格前缀是已有草图编辑的必要运行环境。
     * 启动失败时复用完整退出链，统一恢复 Viewer 模式、平面显示和宿主 UI 状态，
     * 避免留下“草图已恢复但 Worker 仍是原历史”的半完成会话。
     */
    await exitSketchMode({ silent: true });
    message.warning('无法建立草图特征前缀，已退出草图编辑');
    return false;
  },
});

watch(
  () => activeDocumentCapabilities.value.canExecuteFeatureCommands,
  (canExecuteFeatureCommands) => {
    if (canExecuteFeatureCommands) {
      if (isViewerIdleMode(viewerMode.value)) {
        resetEditorSketchRuntimeState(ViewerMode.Default);
      }
      return;
    }
    cancelExtrude();
    cancelRevolve();
    cancelRevolveCut();
    cancelBlend();
    resetEditorSketchRuntimeState(ViewerMode.Assembly);
    getViewer()?.clearSelectionProgrammatically?.();
    multiSelectStore.reset();
    rectangleSelectStore.syncCurrentSelection();
  },
);

const handlePropertyManagerCancel = (): void => {
  if (activeOperationCommand.value === 'sketch-circular-pattern') {
    cancelSketchCircularPatternWorkflow();
    return;
  }
  if (activeOperationCommand.value === 'assembly-linear-component-pattern') {
    cancelAssemblyLinearComponentPatternCommand();
    return;
  }
  if (assemblyWorkflow.isAssemblyTransformCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyTransformCommand();
    return;
  }
  if (activeOperationCommand.value === 'assembly-insert-component') {
    cancelInsertComponentAndRequestDesignTree();
    return;
  }
  if (assemblyWorkflow.isAssemblyReferenceCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyReferenceCommand();
    return;
  }
  if (assemblyWorkflow.isAssemblyMateCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyMateCommand();
    return;
  }
  if (isReferenceFeatureCommand(activeOperationCommand.value)) {
    const command = activeOperationCommand.value;
    cancelReferenceFeatureCommand(command);
    requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandCanceled, command);
    return;
  }
  if (isExtrudeMode.value) {
    cancelExtrudeAndRequestDesignTree();
    return;
  }
  if (revolveUiState.value.active) {
    cancelRevolveAndRequestDesignTree();
    return;
  }
  if (revolveCutUiState.value.active) {
    cancelRevolveCutAndRequestDesignTree();
    return;
  }
  if (filletUiState.value.active || chamferUiState.value.active) {
    cancelBlendAndRequestDesignTree();
  }
};

/**
 * 拉伸/旋转特征确认提交时所需的执行描述。
 */
type ProfileFeatureCommitDescriptor = {
  /** 全局 loading 文案。 */
  loadingText: string;
  /** 异步提交失败时用于定位的错误上下文。 */
  failureText: string;
  /** 实际提交当前特征的动作。 */
  submit: () => Promise<void>;
};

/**
 * 解析当前可由属性面板确认的拉伸/旋转特征提交动作。
 *
 * @returns 当前活动特征提交描述；没有活动拉伸/旋转会话时返回 null。
 */
function resolveActiveProfileFeatureCommitDescriptor(): ProfileFeatureCommitDescriptor | null {
  if (isExtrudeMode.value) {
    const isCut = extrudeUiState.value.operation === 'cut';
    return {
      loadingText: isCut ? '正在生成拉伸切除...' : '正在生成拉伸凸台...',
      failureText: isCut ? '提交拉伸切除失败' : '提交拉伸凸台失败',
      submit: submitExtrude,
    };
  }
  if (revolveUiState.value.active) {
    return {
      loadingText: '正在生成旋转凸台...',
      failureText: '提交旋转凸台失败',
      submit: submitRevolve,
    };
  }
  if (revolveCutUiState.value.active) {
    return {
      loadingText: '正在生成旋转切除...',
      failureText: '提交旋转切除失败',
      submit: submitRevolveCut,
    };
  }
  return null;
}

/**
 * 打开拉伸/旋转特征提交 loading。
 *
 * @param text loading 文案。
 */
function showFeatureCommitLoading(text: string): void {
  editorModelLoadingCoordinator.show(text, FEATURE_COMMIT_LOADING_OWNER);
}

/**
 * 关闭拉伸/旋转特征提交 loading。
 */
function hideFeatureCommitLoading(): void {
  editorModelLoadingCoordinator.hide(FEATURE_COMMIT_LOADING_OWNER);
}

/**
 * 打开曲线 profile 特征预览 loading。
 *
 * @param input 当前 profile 特征预览上下文。
 */
function showProfileFeaturePreviewLoading(input: {
  feature: 'extrude' | 'revolve';
  operation: 'boss' | 'cut';
}): void {
  const loadingTextByKind: Record<'extrude' | 'revolve', Record<'boss' | 'cut', string>> = {
    extrude: {
      boss: '正在生成拉伸凸台预览...',
      cut: '正在生成拉伸切除预览...',
    },
    revolve: {
      boss: '正在生成旋转凸台预览...',
      cut: '正在生成旋转切除预览...',
    },
  };
  editorModelLoadingCoordinator.show(
    loadingTextByKind[input.feature][input.operation],
    PROFILE_FEATURE_PREVIEW_LOADING_OWNER,
  );
}

/**
 * 关闭曲线 profile 特征预览 loading。
 */
function hideProfileFeaturePreviewLoading(): void {
  editorModelLoadingCoordinator.hide(PROFILE_FEATURE_PREVIEW_LOADING_OWNER);
}

/**
 * 使用全局 loading 提交当前拉伸/旋转特征。
 */
async function confirmActiveProfileFeatureWithLoading(): Promise<void> {
  if (guardReadonlyPreviewEditing() || isFeatureCommitSubmitting.value) {
    return;
  }
  const descriptor = resolveActiveProfileFeatureCommitDescriptor();
  if (!descriptor) {
    return;
  }
  const command = activeOperationCommand.value
    ?? (isExtrudeMode.value
      ? (extrudeUiState.value.operation === 'cut' ? 'extrude-cut' : 'extrude')
      : (revolveUiState.value.active ? 'revolve' : 'revolve-cut'));
  isFeatureCommitSubmitting.value = true;
  showFeatureCommitLoading(descriptor.loadingText);
  try {
    await descriptor.submit();
    if (!resolveActiveProfileFeatureCommitDescriptor()) {
      requestDesignTreeAfterEditorCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, command);
    }
  } catch (error) {
    reportEditorAsyncFailure(error, descriptor.failureText);
  } finally {
    hideFeatureCommitLoading();
    isFeatureCommitSubmitting.value = false;
  }
}

const handlePropertyManagerConfirm = (): void => {
  if (guardReadonlyPreviewEditing()) {
    return;
  }
  if (activeOperationCommand.value === 'sketch-circular-pattern') {
    confirmSketchCircularPatternWorkflow();
    return;
  }
  if (activeOperationCommand.value === 'assembly-linear-component-pattern') {
    void confirmAssemblyLinearComponentPatternCommand().catch((error) => {
      reportEditorAsyncFailure(error, '提交线性零部件阵列失败');
    });
    return;
  }
  if (assemblyWorkflow.isAssemblyTransformCommand(activeOperationCommand.value)) {
    void assemblyWorkflow.confirmAssemblyTransformCommand().catch((error) => {
      reportEditorAsyncFailure(error, '提交装配体变换失败');
    });
    return;
  }
  if (activeOperationCommand.value === 'assembly-insert-component') {
    cancelInsertComponentAndRequestDesignTree();
    return;
  }
  if (activeOperationCommand.value === 'assembly-reference-plane') {
    void submitAssemblyReferencePlaneWithHistory();
    return;
  }
  if (activeOperationCommand.value === 'assembly-reference-axis') {
    void submitAssemblyReferenceAxisWithHistory();
    return;
  }
  if (activeOperationCommand.value === 'assembly-reference-point') {
    void submitAssemblyReferencePointWithHistory();
    return;
  }
  if (assemblyWorkflow.isAssemblyReferenceCommand(activeOperationCommand.value)) {
    void assemblyWorkflow.confirmAssemblyReferenceCommand();
    return;
  }
  if (assemblyWorkflow.isAssemblyMateCommand(activeOperationCommand.value)) {
    void assemblyWorkflow.confirmAssemblyMateCommand().catch((error) => {
      reportEditorAsyncFailure(error, '提交装配体配合失败');
    });
    return;
  }
  if (activeOperationCommand.value === 'geometry-reference-plane') {
    void submitPartReferenceFeatureAndRequestDesignTree(
      'geometry-reference-plane',
      submitReferencePlaneWithHistory,
    );
    return;
  }
  if (activeOperationCommand.value === 'geometry-reference-axis') {
    void submitPartReferenceFeatureAndRequestDesignTree(
      'geometry-reference-axis',
      submitReferenceAxisWithHistory,
    );
    return;
  }
  if (activeOperationCommand.value === 'geometry-reference-point') {
    void submitPartReferenceFeatureAndRequestDesignTree(
      'geometry-reference-point',
      submitReferencePointWithHistory,
    );
    return;
  }
  if (isReferenceFeatureCommand(activeOperationCommand.value)) {
    const command = activeOperationCommand.value;
    void submitPartReferenceFeatureAndRequestDesignTree(
      command,
      () => submitReferenceFeatureCommand(command),
    );
    return;
  }
  if (isExtrudeMode.value) {
    void confirmActiveProfileFeatureWithLoading();
    return;
  }
  if (revolveUiState.value.active) {
    void confirmActiveProfileFeatureWithLoading();
    return;
  }
  if (revolveCutUiState.value.active) {
    void confirmActiveProfileFeatureWithLoading();
    return;
  }
  if (filletUiState.value.active) {
    void submitFilletAndRequestDesignTree();
    return;
  }
  if (chamferUiState.value.active) {
    void submitChamferAndRequestDesignTree();
    return;
  }
  if (isDrawingMode.value) {
    void handleDrawingCancel();
  }
};

const handlePropertyManagerClearSelection = (): void => {
  getViewer()?.clearSelectionProgrammatically?.();
};

/**
 * 清理进入装配体变换命令前不兼容的宿主工作流。
 *
 * @param commandType 目标装配体命令。
 */
const cleanupBeforeAssemblyTransformCommand = (commandType: 'assembly-move-component' | 'assembly-rotate-component'): void => {
  if (assemblyWorkflow.isAssemblyReferenceCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyReferenceCommand();
  }
  if (
    activeOperationCommand.value === 'geometry-reference-axis'
    || activeOperationCommand.value === 'geometry-reference-plane'
    || activeOperationCommand.value === 'geometry-reference-point'
  ) {
    cancelReferenceFeatureCommand(activeOperationCommand.value);
  }
  if (isExtrudeMode.value) {
    cancelExtrude();
  }
  if (revolveUiState.value.active) {
    cancelRevolve();
  }
  if (revolveCutUiState.value.active) {
    cancelRevolveCut();
  }
  if (filletUiState.value.active || chamferUiState.value.active) {
    cancelBlend();
  }
  if (isDrawingMode.value) {
    handleDrawingCancel();
  }
  assemblyWorkflow.openAssemblyTransformCommand(commandType);
};

/**
 * 清理进入装配体参考几何体命令前不兼容的宿主工作流。
 *
 * @param commandType 目标装配体参考几何体命令。
 */
const cleanupBeforeAssemblyReferenceCommand = (
  commandType: 'assembly-reference-plane' | 'assembly-reference-axis' | 'assembly-reference-point',
): void => {
  if (
    assemblyWorkflow.isAssemblyReferenceCommand(activeOperationCommand.value)
    && activeOperationCommand.value !== commandType
  ) {
    assemblyWorkflow.cancelAssemblyReferenceCommand();
  }
  if (activeOperationCommand.value === 'assembly-insert-component') {
    cancelAssemblyInsertionWorkflow({
      isPlacingComponent: componentPlacement.isPlacing,
      pendingInsertionHandle,
      cancelPlacement: () => componentPlacement.cancelPlacement(),
      discardPreparedInsertion: (handle) => glbLoader.discardPreparedInsertion(handle),
      cancelInsertion: () => glbLoader.cancelInsertion(),
      finishPlacement,
    });
    finishAssemblyInsertionCommandState();
  }
  if (isReferenceFeatureCommand(activeOperationCommand.value)) {
    cancelReferenceFeatureCommand(activeOperationCommand.value);
  }
  if (isExtrudeMode.value) {
    cancelExtrude();
  }
  if (revolveUiState.value.active) {
    cancelRevolve();
  }
  if (revolveCutUiState.value.active) {
    cancelRevolveCut();
  }
  if (filletUiState.value.active || chamferUiState.value.active) {
    cancelBlend();
  }
  if (isDrawingMode.value) {
    handleDrawingCancel();
  }
  assemblyWorkflow.openAssemblyReferenceCommand(commandType);
};

/**
 * 清理进入编辑零部件命令前不兼容的宿主工作流。
 *
 * @param commandType 目标编辑零部件命令。
 */
const cleanupBeforeAssemblyEditCommand = (commandType: 'assembly-edit-component'): void => {
  // 当前还不知道 不兼容的宿主工作流有什么，先缺省

  assemblyWorkflow.openAssemblyEditCommand(commandType);
};

//------ 线性零部件阵列入口 开始------
/**
 * 线性零部件阵列命令打开方式。
 */
/**
 * 进入装配体配合命令前清理互斥工作流。
 *
 * 设计说明：
 * 1. Mate 与移动、插入、基准几何、草图特征命令都共享场景选择输入；
 * 2. 打开 Mate 前必须让这些命令有机会释放预览、选择态和临时资源；
 * 3. 本函数只做 UI 命令状态收口，配合定义、验证和求解仍由 cad-core 控制器负责。
 */
const cleanupBeforeAssemblyMateSession = (): void => {
  if (activeOperationCommand.value === 'assembly-linear-component-pattern') {
    cancelAssemblyLinearComponentPatternCommand();
  }
  if (assemblyWorkflow.isAssemblyTransformCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyTransformCommand();
  }
  if (assemblyWorkflow.isAssemblyReferenceCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyReferenceCommand();
  }
  if (assemblyWorkflow.isAssemblyMateCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyMateCommand();
  }
  if (activeOperationCommand.value === 'assembly-insert-component') {
    cancelAssemblyInsertionWorkflow({
      isPlacingComponent: componentPlacement.isPlacing,
      pendingInsertionHandle,
      cancelPlacement: () => componentPlacement.cancelPlacement(),
      discardPreparedInsertion: (handle) => glbLoader.discardPreparedInsertion(handle),
      cancelInsertion: () => glbLoader.cancelInsertion(),
      finishPlacement,
    });
    finishAssemblyInsertionCommandState();
  }
  if (isReferenceFeatureCommand(activeOperationCommand.value)) {
    cancelReferenceFeatureCommand(activeOperationCommand.value);
  }
  if (isExtrudeMode.value) {
    cancelExtrude();
  }
  if (revolveUiState.value.active) {
    cancelRevolve();
  }
  if (revolveCutUiState.value.active) {
    cancelRevolveCut();
  }
  if (filletUiState.value.active || chamferUiState.value.active) {
    cancelBlend();
  }
  if (isDrawingMode.value) {
    handleDrawingCancel();
  }
};

/**
 * 打开标准配合新建命令。
 *
 * @param commandType 配合命令标识。
 */
const cleanupBeforeAssemblyMateCommand = (commandType: 'assembly-mate'): void => {
  cleanupBeforeAssemblyMateSession();
  assemblyWorkflow.openAssemblyMateCommand(commandType);
};

/**
 * 从设计树打开既有配合编辑命令。
 *
 * @param mateId 需要编辑的配合 ID。
 */
const cleanupBeforeAssemblyMateEditCommand = (mateId: string): void => {
  cleanupBeforeAssemblyMateSession();
  assemblyWorkflow.openAssemblyMateEditCommand('assembly-mate', mateId);
};

enum LinearComponentPatternCommandOpenMode {
  /** 从装配体工具栏创建新阵列。 */
  Create = 'create',
  /** 从特征树编辑既有阵列特征。 */
  EditFeature = 'edit-feature',
}

/** 当前正在编辑的线性零部件阵列分组对象 ID。 */
const editingLinearComponentPatternGroupObjectId = ref<string | null>(null);

/**
 * 进入线性零部件阵列命令前清理互斥工作流。
 *
 * 设计说明：
 * 1. 创建新阵列与编辑既有阵列都进入同一个 PropertyManager；
 * 2. 互斥工作流清理必须复用同一入口，避免编辑特征保留插入、参考几何或草图命令残留；
 * 3. 该函数只做宿主命令态收口，不直接修改阵列控制器状态。
 */
function cleanupBeforeAssemblyLinearComponentPatternCommand(): void {
  if (assemblyWorkflow.isAssemblyReferenceCommand(activeOperationCommand.value)) {
    assemblyWorkflow.cancelAssemblyReferenceCommand();
  }
  if (activeOperationCommand.value === 'assembly-insert-component') {
    cancelAssemblyInsertionWorkflow({
      isPlacingComponent: componentPlacement.isPlacing,
      pendingInsertionHandle,
      cancelPlacement: () => componentPlacement.cancelPlacement(),
      discardPreparedInsertion: (handle) => glbLoader.discardPreparedInsertion(handle),
      cancelInsertion: () => glbLoader.cancelInsertion(),
      finishPlacement,
    });
    finishAssemblyInsertionCommandState();
  }
  if (isReferenceFeatureCommand(activeOperationCommand.value)) {
    cancelReferenceFeatureCommand(activeOperationCommand.value);
  }
  if (isExtrudeMode.value) {
    cancelExtrude();
  }
  if (revolveUiState.value.active) {
    cancelRevolve();
  }
  if (revolveCutUiState.value.active) {
    cancelRevolveCut();
  }
  if (filletUiState.value.active || chamferUiState.value.active) {
    cancelBlend();
  }
  if (isDrawingMode.value) {
    handleDrawingCancel();
  }
}

/**
 * 进入线性零部件阵列 PropertyManager。
 *
 * @param input 打开参数。
 */
function enterAssemblyLinearComponentPatternCommandUi(input: {
  /** 命令打开方式。 */
  mode: LinearComponentPatternCommandOpenMode;
  /** 编辑特征时的阵列分组对象 ID。 */
  groupObjectId?: string;
}): void {
  cleanupBeforeAssemblyLinearComponentPatternCommand();
  assemblyWorkflow.handleBeforeNonAssemblyCommand('assembly-linear-component-pattern');
  sketchStateStore.setViewerMode(ViewerMode.LinearComponentPattern);
  getViewer()?.setMode(ViewerMode.LinearComponentPattern);
  activeOperationCommand.value = 'assembly-linear-component-pattern';
  toolbarCommandStore.setActiveCommands(['assembly-linear-component-pattern']);
  toolbarOperationOverride.value = {
    name: '线性零部件阵列',
    hint: '设置方向、实例数和要阵列的零部件。',
  };
  editingLinearComponentPatternGroupObjectId.value = input.mode === LinearComponentPatternCommandOpenMode.EditFeature
    ? input.groupObjectId ?? null
    : null;
  rightPanelTab.value = 'property';
}

/**
 * 处理装配体线性零部件阵列入口。
 *
 * 设计说明：
 * 1. 当前迭代创建与“移动零部件”同级的装配体 tab 入口；
 * 2. web-client 只负责打开 PropertyManager，并把 Viewer 切换到 cad-core 的线性零部件阵列命令态；
 * 3. 进入命令前仍先收口默认 Move / 显式 Move/Rotate 会话，避免输入语义残留。
 */
const openAssemblyLinearComponentPatternCommand = (): void => {
  getViewer()?.linearComponentPatternController?.open();
  enterAssemblyLinearComponentPatternCommandUi({
    mode: LinearComponentPatternCommandOpenMode.Create,
  });
};

/**
 * 确认装配体线性零部件阵列命令。
 *
 * 说明：当前阶段 cad-core 已接管命令会话与选择状态，求解提交后续继续在 pattern 模块扩展。
 */
const confirmAssemblyLinearComponentPatternCommand = async (): Promise<void> => {
  const viewer = getViewer();
  if (!viewer) return;
  const manager = viewer.linearComponentPatternManager;
  if (!manager) {
    message.warning('线性阵列管理器未就绪，无法提交');
    return;
  }

  const beforeSnapshot = manager.createHistorySnapshot();
  const editingGroupObjectId = editingLinearComponentPatternGroupObjectId.value;
  let committed = false;

  try {
    await editorModelHistoryRuntime.runExclusive(async () => {
      const commitData = manager.controller.buildCommitData();
      const definition = manager.controller.createDefinitionSnapshot();
      if (!commitData) {
        return;
      }

      if (editingGroupObjectId) {
        // 编辑特征时保持原设计树节点身份，只替换该阵列分组下的实例结果。
        committed = manager.replaceCommittedRecordByGroupObjectId(editingGroupObjectId, commitData, definition);
      } else {
        // 创建新阵列时，将预览克隆为场景实体并记录到管理器。
        committed = manager.commitPattern(commitData, definition);
      }

      if (!committed) {
        return;
      }

      await commitEditorLinearComponentPatternHistoryInQueue({
        label: editingGroupObjectId ? '编辑线性零部件阵列' : '线性零部件阵列',
        commandType: editingGroupObjectId
          ? 'assembly.linear-component-pattern.edit'
          : 'assembly.linear-component-pattern.create',
        before: beforeSnapshot,
        after: manager.createHistorySnapshot(),
      });
    });
  } catch (error) {
    manager.restoreHistorySnapshot(beforeSnapshot);
    reportEditorAsyncFailure(error, '提交线性阵列历史失败');
    return;
  }

  if (!committed) {
    if (editingGroupObjectId) {
      message.warning('线性阵列编辑提交失败，请检查引用和源零部件');
    } else {
      message.warning('线性阵列提交失败，请检查引用和源零部件');
    }
    return;
  }

  manager.controller.cancel();
  // 从管理器读取最新已提交记录，同步更新设计树节点。
  const committedRecords = manager.getCommittedRecords();
  glbLoader.syncLinearComponentPatternFeatureTree(committedRecords);
  sketchStateStore.setViewerMode(ViewerMode.Assembly);
  viewer.setMode(ViewerMode.Assembly);
  activeOperationCommand.value = null;
  toolbarCommandStore.clearActiveCommands();
  toolbarOperationOverride.value = null;
  editingLinearComponentPatternGroupObjectId.value = null;
  requestDesignTreeAfterEditorCommand(
    EditorPanelShowDesignTreeReason.CommandConfirmed,
    'assembly-linear-component-pattern',
  );
};

/**
 * 取消装配体线性零部件阵列命令。
 */
const cancelAssemblyLinearComponentPatternCommand = (): void => {
  const viewer = getViewer();
  const manager = viewer?.linearComponentPatternManager;
  const editingGroupObjectId = editingLinearComponentPatternGroupObjectId.value;
  if (manager && editingGroupObjectId) {
    manager.cancelEditCommittedRecordByGroupObjectId(editingGroupObjectId);
  }
  viewer?.linearComponentPatternController?.cancel();
  sketchStateStore.setViewerMode(ViewerMode.Assembly);
  viewer?.setMode(ViewerMode.Assembly);
  activeOperationCommand.value = null;
  toolbarCommandStore.clearActiveCommands();
  toolbarOperationOverride.value = null;
  editingLinearComponentPatternGroupObjectId.value = null;
  requestDesignTreeAfterEditorCommand(
    EditorPanelShowDesignTreeReason.CommandCanceled,
    'assembly-linear-component-pattern',
  );
};
//------ 线性零部件阵列入口 结束------

/**
 * 编辑器命令统一分发入口。
 *
 * 设计说明：
 * 1. 装配体移动/旋转命令在宿主层单独处理，不再走草图工作流；
 * 2. 评估类命令由 `useEditorEvaluationWorkflow` 处理；
 * 3. 其它命令继续复用 `useEditorSketchWorkflow` 的既有分发逻辑；
 * 4. 评估会话（测量）与底层命令可并行；装配体工作流负责收口默认 Move / 显式 Move/Rotate 会话。
 *
 * @param commandType 命令标识。
 */
const handleEditorCommand = async (commandType: string): Promise<void> => {
  if (guardReadonlyPreviewEditing()) {
    return;
  }
  if (commandType === 'rib') {
    /*
     * 与其它轮廓特征一致：草图态直接点击“筋”时先退出草图编辑，再进入筋命令。
     * 未落地的绘制预览由 stopSketchDrawing 清理，已落地的线段保留在场景中供筋吸收。
     */
    if (isSketchActive.value || Boolean(pendingSketchToolCommand.value)) {
      const exited = await exitSketchMode({
        silent: true,
        allowDrawingStop: true,
      });
      if (!exited) {
        return;
      }
    }
    activeOperationCommand.value = 'rib';
    toolbarCommandStore.setActiveCommands(['rib']);
    rightPanelTab.value = 'property';
    getViewer()?.ribFeatureController?.enterRibFeature();
    ensureRibControllerSubscription();
    return;
  }
  if (commandType === 'shell') {
    activeOperationCommand.value = 'shell';
    toolbarCommandStore.setActiveCommands(['shell']);
    rightPanelTab.value = 'property';
    getViewer()?.shellFeatureController?.enterShellFeature();
    ensureShellControllerSubscription();
    return;
  }
  if (isEvaluationCommand(commandType)) {
    handleEvaluationCommand(commandType);
    return;
  }

  closeActiveEvaluationSessions();
  if (assemblyWorkflow.isAssemblyTransformCommand(commandType)) {
    cleanupBeforeAssemblyTransformCommand(commandType);
    return;
  }
  if (assemblyWorkflow.isAssemblyReferenceCommand(commandType)) {
    assemblyWorkflow.handleBeforeNonAssemblyCommand(commandType);
    cleanupBeforeAssemblyReferenceCommand(commandType);
    return;
  }
  //------ 线性零部件阵列入口 开始------
  if (assemblyWorkflow.isAssemblyMateCommand(commandType)) {
    cleanupBeforeAssemblyMateCommand(commandType);
    return;
  }
  if (commandType === 'assembly-linear-component-pattern') {
    openAssemblyLinearComponentPatternCommand();
    return;
  }
  //------ 线性零部件阵列入口 结束------

  // ----- 编辑零部件 开始 ------
  if (assemblyWorkflow.isAssemblyEditCommand(commandType)) {
    cleanupBeforeAssemblyEditCommand(commandType);
    return;
  }
  // ----- 编辑零部件 结束 ------

  assemblyWorkflow.handleBeforeNonAssemblyCommand(commandType);
  await handleCommand(commandType);
};

// 临时监听
watch(isEnterEditingComponentStatus, (oldVal, newVal) => {
  console.log('isEnterEditingComponentStatus', oldVal, newVal);
});

const shellStateVersion = ref(0);
let shellControllerUnsubscribe: (() => void) | null = null;
const ensureShellControllerSubscription = (): void => {
  if (shellControllerUnsubscribe) {
    return;
  }
  const controller = getViewer()?.shellFeatureController;
  if (!controller) {
    return;
  }
  shellControllerUnsubscribe = controller.subscribe(() => {
    shellStateVersion.value += 1;
  });
};

const ribStateVersion = ref(0);
let ribControllerUnsubscribe: (() => void) | null = null;
const ensureRibControllerSubscription = (): void => {
  if (ribControllerUnsubscribe) {
    return;
  }
  const controller = getViewer()?.ribFeatureController;
  if (!controller) {
    return;
  }
  ribControllerUnsubscribe = controller.subscribe(() => {
    ribStateVersion.value += 1;
  });
};

const ribContext = computed(() => {
  // 依赖 ribStateVersion，让面板在选择实体/注入轮廓/改参数后响应式刷新。
  void ribStateVersion.value;
  const controller = getViewer()?.ribFeatureController;
  return {
    ribActive: controller?.isActive === true,
    ribCanSubmit: controller?.getRibCanSubmit() === true,
    ribBodySelected: controller?.getSelectedBodyObjectId() !== null,
    ribPreviewError: controller?.getRibPreviewError() ?? null,
    ribDefinition: controller?.getRibDefinition() ?? null,
    setRibThickness: (mm: number) => controller?.setThicknessMm(mm),
    setRibSecondThickness: (mm: number) => controller?.setSecondThicknessMm(mm),
    setRibThicknessType: (value: 'centered' | 'twoSides') => controller?.setThicknessType(value),
    setRibDirectionMode: (value: 'sketchNormal' | 'profileNormal' | 'explicit') => controller?.setDirectionMode(value),
    setRibOperation: (value: 'rib' | 'groove') => controller?.setOperation(value),
    setRibEnableSliding: (value: boolean) => controller?.setEnableSliding(value),
    setRibReverseMaterialSide: (value: boolean) => controller?.setReverseMaterialSide(value),
    clearRibPreviewError: () => controller?.clearRibPreviewError(),
    /** 供草图工作流注入轮廓；注入后 ribCanSubmit 才会为真。 */
    setRibProfile: (profile: {
      points: Array<[number, number, number]>;
      plane: {
        origin: [number, number, number];
        normal: [number, number, number];
        xAxis: [number, number, number];
        yAxis: [number, number, number];
      };
    }) => controller?.setProfile(profile),
    submitRib: async () => {
      if (!controller) {
        return;
      }
      // 若轮廓尚未注入，尝试从当前场景选择中吸收草图轮廓（选中线和实体后直接点确认）。
      if (controller.getRibDefinition() === null && !controller.absorbSelectedSketchProfile()) {
        return;
      }
      const payload = controller.getRibCommitPayload();
      if (!payload.upstreamBodyObjectId) {
        return;
      }
      // 走 viewport adapter 的 commitFeature，内部会以 feature.commit.rib 捕获模型历史（Undo/Redo 入栈）。
      const result = await extrudeViewportRef.value?.commitFeature?.({
        featureKind: 'rib',
        featureId: payload.featureId,
        definition: payload.definitionModel,
        bindings: payload.bindings,
      });
      if (!result) {
        throw new Error('筋特征提交失败。');
      }
      // 与抽壳提交一致：把新结果体加入场景，并移除旧实体与残留预览。
      if (!result.replaced && result.meshData) {
        await extrudeViewportRef.value?.addMesh?.(result.meshData, {
          prototype: result.prototype,
        });
        if (payload.upstreamBodyObjectId && result.objectId && payload.upstreamBodyObjectId !== result.objectId) {
          extrudeViewportRef.value?.removeMesh?.(payload.upstreamBodyObjectId);
        }
      }
      controller.exitRibFeature();
      activeOperationCommand.value = null;
      toolbarCommandStore.clearActiveCommands();
      rightPanelTab.value = 'feature';
      // 筋提交后显式刷新设计树，避免节点要等回退棒交互才出现。
      await refreshFeatureTreeAfterHistoryRestore();
    },
    cancelRib: () => {
      controller?.exitRibFeature();
      activeOperationCommand.value = null;
      toolbarCommandStore.clearActiveCommands();
      rightPanelTab.value = 'feature';
    },
  };
});

const shellContext = computed(() => {
  // 依赖 shellStateVersion，让面板在选择面/改参数后响应式刷新。
  void shellStateVersion.value;
  return {
    shellActive: getViewer()?.shellFeatureController?.isActive === true,
    shellCanSubmit: getViewer()?.shellFeatureController?.getShellCanSubmit() === true,
    shellPreviewError: getViewer()?.shellFeatureController?.getShellPreviewError() ?? null,
    shellDefinition: getViewer()?.shellFeatureController?.getShellDefinition() ?? null,
    hollowWholeBody: getViewer()?.shellFeatureController?.isHollowWholeBody === true,
    setHollowWholeBody: (checked: boolean) => getViewer()?.shellFeatureController?.setHollowWholeBody(checked),
    setShellThickness: (mm: number) => getViewer()?.shellFeatureController?.setThicknessMm(mm),
    setShellDirection: (direction: 'inward' | 'outward' | 'midplane') => (
      getViewer()?.shellFeatureController?.setDirection(direction)
    ),
    clearShellPreviewError: () => getViewer()?.shellFeatureController?.clearShellPreviewError(),
    submitShell: async () => {
      const controller = getViewer()?.shellFeatureController;
      if (!controller) {
        return;
      }
      const payload = controller.getShellCommitPayload();
      if (!payload.upstreamBodyObjectId || payload.bindings.length === 0) {
        return;
      }
      // 走 viewport adapter 的 commitFeature，内部会以 feature.commit.shell 捕获模型历史（Undo/Redo 入栈）。
      const result = await extrudeViewportRef.value?.commitFeature?.({
        featureKind: 'shell',
        featureId: payload.featureId,
        definition: payload.definitionModel,
        bindings: payload.bindings,
      });
      if (!result) {
        throw new Error('抽壳特征提交失败。');
      }
      // 与 blend 提交一致：把新结果体加入场景，并移除旧实体与残留预览。
      if (!result.replaced && result.meshData) {
        await extrudeViewportRef.value?.addMesh?.(result.meshData, {
          prototype: result.prototype,
        });
        if (payload.upstreamBodyObjectId && result.objectId && payload.upstreamBodyObjectId !== result.objectId) {
          extrudeViewportRef.value?.removeMesh?.(payload.upstreamBodyObjectId);
        }
      }
      controller.exitShellFeature();
      activeOperationCommand.value = null;
      toolbarCommandStore.clearActiveCommands();
      rightPanelTab.value = 'feature';
    },
    cancelShell: () => {
      getViewer()?.shellFeatureController?.exitShellFeature();
      activeOperationCommand.value = null;
      toolbarCommandStore.clearActiveCommands();
      rightPanelTab.value = 'feature';
    },
  };
});

const blendContext = computed(() => ({
  filletActive: filletUiState.value.active,
  filletCanSubmit: filletUiState.value.canSubmit,
  filletPreviewError: filletUiState.value.previewError,
  filletDefinition: filletUiState.value.definition,
  filletType: filletUiState.value.definition?.type ?? 'constant',
  filletRadius: filletUiState.value.definition?.radius ?? 10,
  filletItems: filletUiState.value.items,
  selectedFilletItemRef: filletUiState.value.selectedItemRef,
  selectedFilletVariablePointKey: filletUiState.value.selectedVariablePointKey,
  filletVariablePoints: filletUiState.value.definition?.variablePoints ?? [],
  filletVariableInstanceCount: filletUiState.value.definition?.variableInstanceCount ?? 3,
  filletPartialEdges: filletUiState.value.definition?.partialEdges ?? [],
  filletPartialReferenceTarget: filletUiState.value.partialReferenceTarget,
  filletActiveFaceSetId: filletUiState.value.activeFaceSetId,
  filletSelectionTarget: filletUiState.value.selectionTarget,
  chamferActive: chamferUiState.value.active,
  chamferDefinition: chamferUiState.value.definition,
  chamferType: chamferUiState.value.definition?.type ?? 'angle-distance',
  chamferDistance: chamferUiState.value.definition?.distance ?? DEFAULT_CHAMFER_DISTANCE_M,
  chamferDistance2: chamferUiState.value.definition?.distance2 ?? DEFAULT_CHAMFER_DISTANCE_M,
  chamferWidth: chamferUiState.value.definition?.width ?? DEFAULT_CHAMFER_DISTANCE_M,
  chamferAngleDeg: chamferUiState.value.definition?.angleDeg ?? 45,
  chamferSymmetric: chamferUiState.value.definition?.symmetric !== false,
  chamferFlipDirection: chamferUiState.value.definition?.flipDirection === true,
  chamferEqualDistance: chamferUiState.value.definition?.equalDistance !== false,
  chamferVertexDistances: chamferUiState.value.definition?.vertexDistances ?? [...DEFAULT_CHAMFER_VERTEX_DISTANCES_M],
  chamferPartialEdges: chamferUiState.value.definition?.partialEdges ?? [],
  chamferPartialReferenceTarget: chamferUiState.value.partialReferenceTarget,
  chamferActiveFaceSetId: chamferUiState.value.activeFaceSetId,
  chamferSelectionTarget: chamferUiState.value.selectionTarget,
  chamferFaceFaceMode: chamferUiState.value.definition?.faceFaceMode ?? 'symmetric',
  chamferItems: chamferUiState.value.items,
  selectedChamferItemRef: chamferUiState.value.selectedItemRef,
  chamferCanSubmit: chamferUiState.value.canSubmit,
  chamferPreviewError: chamferUiState.value.previewError,
  submitFillet: submitFilletAndRequestDesignTree,
  submitChamfer: submitChamferAndRequestDesignTree,
  cancelBlend: cancelBlendAndRequestDesignTree,
  setFilletType,
  selectFilletItem,
  removeFilletItem,
  highlightFilletItem,
  setFilletAsymmetricRadiiFlipped,
  selectFilletVariablePoint,
  removeFilletVariablePoint,
  removeAllFilletVariableInstancePoints,
  assignFilletRadiusToAllPoints,
  assignFilletRadiusToUnassignedPoints,
  setFilletSelectionTarget,
  setFilletSelectThroughFaces,
  setFilletKeepFeatures,
  setFilletRadius,
  setFilletRadius2,
  setFilletChordWidth,
  setFilletEdgeFilletMode,
  setFilletOverflowType,
  setFilletVariablePointRadius,
  setFilletVariableInstanceCount,
  setFilletVariableMode,
  setFilletVariableTransition,
  setFilletFaceFilletMode,
  setFilletFaceFilletRadiiFlipped,
  setFilletPartialEdgeParametersEnabled,
  setFilletPartialCondition,
  setFilletPartialValue,
  setFilletPartialReferenceTarget,
  clearFilletPartialReference,
  setFilletFaceSetTarget,
  setChamferType,
  selectChamferItem,
  removeChamferItem,
  highlightChamferItem,
  setChamferTangentPropagation,
  setChamferFlipDirection,
  setChamferEqualDistance,
  setChamferSelectThroughFaces,
  setChamferKeepFeatures,
  setChamferSymmetric,
  setChamferDistance,
  setChamferDistance2,
  setChamferWidth,
  setChamferAngleDeg,
  setChamferVertexDistance,
  setChamferPartialEdgeParametersEnabled,
  setChamferPartialCondition,
  setChamferPartialValue,
  setChamferPartialReferenceTarget,
  clearChamferPartialReference,
  setChamferFaceFaceMode,
  setChamferFaceSetTarget,
  setChamferSelectionTarget,
}));

const blendPreviewGuideOverlayState = computed(() => ({
  active: filletUiState.value.active || chamferUiState.value.active,
  guides: filletUiState.value.active
    ? filletUiState.value.previewGuides
    : chamferUiState.value.active
      ? chamferUiState.value.previewGuides
      : [],
  items: filletUiState.value.active
    ? filletUiState.value.items
    : chamferUiState.value.active
      ? chamferUiState.value.items
      : [],
}));

const blendPreviewGuideOverlay = registerBlendPreviewGuideOverlay({
  viewportRef: extrudeViewportRef as any,
  overlayState: blendPreviewGuideOverlayState,
  onControlPointSelect: selectFilletVariablePoint,
});

/**
 * 处理 cad-core 配合文档变化。
 *
 * 设计说明：
 * 1. 配合文档由 cad-core 维护，web-client 只根据回调刷新设计树投影；
 * 2. 只有 cad-core 明确要求记录历史时，才把 before/after 快照提交到模型历史；
 * 3. 历史恢复期间会触发 load 回调，必须用恢复标记避免形成递归历史。
 *
 * @param document 最新配合文档快照。
 * @param context 文档变化上下文。
 */
const handleAssemblyMateDocumentChanged = async (
  document: AssemblyMateDocumentModel,
  context: AssemblyMateDocumentChangeContext,
): Promise<void> => {
  const sourceTabId = glbLoader.partTabsStore.activeTabId;
  const sourceController = getViewer()?.assemblyMateController;
  const before = context.beforeDocument ?? editorAssemblyMateDocumentHistorySnapshot;

  if (before && context.recordHistory && !isRestoringAssemblyMateHistory) {
    await editorModelHistoryRuntime.runExclusive(() => (
      commitEditorAssemblyMateDocumentHistoryInQueue(before, document, context)
    ));
  }

  // 历史提交可能跨越标签/Viewer 切换，不能把旧文档投影到等待结束后的新标签。
  if (sourceTabId !== glbLoader.partTabsStore.activeTabId
    || (sourceController && sourceController !== getViewer()?.assemblyMateController)) return;
  // 完整刷新原本会重新读取 SDK；局部入口也必须消费最新文档，避免晚回调覆盖新诊断/定义。
  const currentDocument = sourceController?.getDocument() ?? document;
  editorAssemblyMateDocumentHistorySnapshot = currentDocument;
  if (context.source === 'diagnostics') {
    // 求解诊断不改变配合归属或树结构；保留历史快照写回，只更新所有 Mate 出现项的展示字段。
    featureTreeStore.applyAssemblyMateDiagnostics(sourceTabId, currentDocument);
  } else {
    // 配合定义和恢复只更新相关文件夹；模型加载与结构变更仍由原加载链路发布完整模型树。
    featureTreeStore.applyAssemblyMateDocument(sourceTabId, currentDocument);
  }
};

const { buildPropertyManagerContext } = useEditorPropertyManagerContext({
  activeOperationCommand,
  currentDrawingCommand,
  currentDrawingTool: computed(() => currentDrawingTool.value ? String(currentDrawingTool.value) : null),
  isDrawingMode,
  isSketchActive,
  readonlyPreview: computed(() => versionWorkspaceStore.isReadonlyPreview),
  currentDrawingDatumPlaneId,
  selectedObject,
  dialog: propertyManagerDialog,
  getSketchPropertySelectedObject: resolveCurrentSketchPropertySelectedObject,
  getViewer,
  sketchDimensionSelection: editorSelectedSketchDimension,
  commitSketchRuntimeHistoryRecord: commitEditorSketchRuntimeHistoryRecord,
  reportSketchPropertyHistoryFailure: reportEditorAsyncFailure,
  extrudeUiState,
  revolveUiState,
  revolveCutUiState,
  submitExtrude,
  cancelExtrude: cancelExtrudeAndRequestDesignTree,
  setExtrudeDepth,
  setExtrudeDirection,
  setExtrudeDirection1,
  setExtrudeEndTargetInputActive,
  setExtrudeEndTargetVisual,
  setExtrudeDirection2,
  setExtrudeCutOptions,
  setExtrudeThinFeature,
  setExtrudeMergeResult,
  setExtrudeSelectedContoursInputActive,
  removeExtrudeSelectedContour,
  highlightExtrudeSelectedContour,
  submitRevolve,
  cancelRevolve: cancelRevolveAndRequestDesignTree,
  activateRevolvePicker,
  clearRevolveSelectedAxis,
  clearRevolveSelectedContours,
  removeRevolveSelectedContour,
  highlightRevolveSelectedAxis,
  highlightRevolveSelectedContour,
  setRevolveDirection1Angle,
  setRevolveDirection2Enabled,
  setRevolveDirection2Angle,
  setRevolveDirectionReversed,
  submitRevolveCut,
  cancelRevolveCut: cancelRevolveCutAndRequestDesignTree,
  activateRevolveCutPicker,
  clearRevolveCutSelectedAxis,
  clearRevolveCutSelectedContours,
  removeRevolveCutSelectedContour,
  highlightRevolveCutSelectedAxis,
  highlightRevolveCutSelectedContour,
  setRevolveCutDirection1Angle,
  setRevolveCutDirection2Enabled,
  setRevolveCutDirection2Angle,
  setRevolveCutDirectionReversed,
  setRevolveCutFlipSideToCut,
  toggleRevolveCutFragment,
  setRevolveCutKeepBodiesSelectionMode,
  toggleRevolveCutKeepBodiesDraftFragment,
  confirmRevolveCutKeepBodiesDialog,
  cancelRevolveCutKeepBodiesDialog,
  referenceAxisActive: computed(() => activeOperationCommand.value === 'geometry-reference-axis'),
  submitReferenceAxis: () => submitPartReferenceFeatureAndRequestDesignTree(
    'geometry-reference-axis',
    submitReferenceAxisWithHistory,
  ),
  cancelReferenceAxis: () => {
    cancelReferenceAxis();
    requestDesignTreeAfterEditorCommand(
      EditorPanelShowDesignTreeReason.CommandCanceled,
      'geometry-reference-axis',
    );
  },
  referencePlaneActive: computed(() => activeOperationCommand.value === 'geometry-reference-plane'),
  submitReferencePlane: () => submitPartReferenceFeatureAndRequestDesignTree(
    'geometry-reference-plane',
    submitReferencePlaneWithHistory,
  ),
  cancelReferencePlane: () => {
    cancelReferencePlane();
    requestDesignTreeAfterEditorCommand(
      EditorPanelShowDesignTreeReason.CommandCanceled,
      'geometry-reference-plane',
    );
  },
  referencePointActive: computed(() => activeOperationCommand.value === 'geometry-reference-point'),
  submitReferencePoint: () => submitPartReferenceFeatureAndRequestDesignTree(
    'geometry-reference-point',
    submitReferencePointWithHistory,
  ),
  cancelReferencePoint: () => {
    cancelReferencePoint();
    requestDesignTreeAfterEditorCommand(
      EditorPanelShowDesignTreeReason.CommandCanceled,
      'geometry-reference-point',
    );
  },
  assemblyReferenceAxisActive: computed(() => activeOperationCommand.value === 'assembly-reference-axis'),
  submitAssemblyReferenceAxis: submitAssemblyReferenceAxisWithHistory,
  cancelAssemblyReferenceAxis: () => assemblyWorkflow.cancelAssemblyReferenceCommand(),
  assemblyReferencePlaneActive: computed(() => activeOperationCommand.value === 'assembly-reference-plane'),
  submitAssemblyReferencePlane: submitAssemblyReferencePlaneWithHistory,
  cancelAssemblyReferencePlane: () => assemblyWorkflow.cancelAssemblyReferenceCommand(),
  assemblyReferencePointActive: computed(() => activeOperationCommand.value === 'assembly-reference-point'),
  submitAssemblyReferencePoint: submitAssemblyReferencePointWithHistory,
  cancelAssemblyReferencePoint: () => assemblyWorkflow.cancelAssemblyReferenceCommand(),
  confirmAssemblyLinearComponentPattern: confirmAssemblyLinearComponentPatternCommand,
  cancelAssemblyLinearComponentPattern: cancelAssemblyLinearComponentPatternCommand,
  sketchCornerMode,
  sketchCornerEnable,
  setSketchCornerMode: setSketchCornerModeFromPropertyAndRequestDesignTree,
  sketchTrimMode,
  sketchTrimOptions,
  selectedSketchTrimEntity,
  setSketchTrimMode: setSketchTrimModeFromPropertyAndRequestDesignTree,
  setSketchTrimOptions: setSketchTrimOptionsFromProperty,
  //------ 等距实体会话上下文 开始------
  sketchOffsetDistance,
  sketchOffsetReverse,
  sketchOffsetBidirectional,
  sketchOffsetAddSize,
  sketchOffsetSelectChain,
  sketchOffsetCover,
  sketchOffsetBasicGeometry,
  sketchOffsetOffsetGeometry,
  setSketchOffsetDistance: setSketchOffsetDistanceFromProperty,
  setSketchOffsetReverse: setSketchOffsetReverseFromProperty,
  setSketchOffsetBidirectional: setSketchOffsetBidirectionalFromProperty,
  setSketchOffsetAddSize: setSketchOffsetAddSizeFromProperty,
  setSketchOffsetSelectChain: setSketchOffsetSelectChainFromProperty,
  setSketchOffsetCover: setSketchOffsetCoverFromProperty,
  setSketchOffsetBasicGeometry: setSketchOffsetBasicGeometryFromProperty,
  setSketchOffsetOffsetGeometry: setSketchOffsetOffsetGeometryFromProperty,
  clearSketchOffsetWorkflow,
  generateAndExitSketchOffsetWorkflow,
  //------ 等距实体会话上下文 结束------
  convertEntitiesState,
  confirmConvertEntities: confirmConvertEntitiesWorkflow,
  cancelConvertEntities: cancelConvertEntitiesAndRequestDesignTree,
  removeConvertEntitiesSelection,
  setConvertEntitiesAsConstruction,
  setConvertEntitiesSelectChain,
  setConvertEntitiesInnerLoopsOneByOne,
  selectAllConvertEntitiesInnerLoops,
  sketchEntitiesTransformState,
  setSketchEntitiesTransformInputMode,
  setSketchEntitiesTransformSelectionRole,
  setSketchEntitiesTransformTargetCoordinate,
  setSketchEntitiesTransformAngle,
  syncSketchEntitiesTransformState,
  removeSketchEntitiesTransformSelection,
  repeatSketchEntitiesTransform,
  confirmSketchEntitiesTransform,
  cancelSketchEntitiesTransform,
  removeSketchRelation,
  lineDrawingPointCount,
  drawingPointCount,
  multipleEntitiesBoxSelected,
  multipleSelectedObjects,
  multipleSelectedObjectIds,
  multipleSelectedIsCrossing,
  linePropertyVisible,
  arcPropertyVisible,
  circlePropertyVisible,
  ellipsePropertyVisible,
  polygonPropertyVisible,
  rectanglePropertyVisible,
  splinePropertyVisible,
  styleSplinePropertyVisible,
  slotPropertyVisible,
  pointPropertyVisible,
  insertLinePropertyVisible,
  insertComponentActive: isInsertComponentActive,
  insertComponentFiles,
  insertComponentFilterKinds,
  insertComponentQuery,
  insertComponentLoading,
  insertComponentHasMore,
  insertComponentTotal,
  insertComponentFixationMode,
  selectedInsertComponentFiles,
  setInsertComponentFilterKinds,
  setInsertComponentQuery,
  setInsertComponentFixationMode,
  loadMoreInsertComponents,
  selectInsertComponent,
  toggleInsertComponentSelection,
  confirmInsertComponentSelection,
  cancelInsertComponent: cancelInsertComponentAndRequestDesignTree,
  clearSelection: handlePropertyManagerClearSelection,
  handlePropertyManagerCancel,
  handlePropertyManagerConfirm,
  handlePropertyManagerHelp,
  handlePropertyManagerPreviewToggle,
  /** 面板内部切换命令（移动/旋转互切），复用 handleCommand 链路更新 activeOperationCommand。 */
  handleSwitchActiveCommand: (command: string) => handleEditorCommand(command),
  shellContext,
  ribContext,
  blendContext,
});

const loadEditorPreferences = async (): Promise<void> => {
  isEditorDisplayConfigLoading.value = true;
  try {
    const preferences = await editorPreferencesRepo.get(projectId.value);
    initialPreferences.value = preferences ?? null;
    editorDisplayConfig.value = normalizeEditorDisplayConfig(preferences?.editorDisplay);
  } catch (error) {
    reportEditorAsyncFailure(error, '加载编辑器偏好失败');
  } finally {
    isEditorDisplayConfigLoading.value = false;
  }
};

/**
 * 开始一次编辑页首屏项目加载事务。
 *
 * 设计说明：首屏加载包含 OpenProject、模型解析、默认 tab 挂载等多个异步阶段，
 * 必须用统一 requestId 与 AbortController 收口生命周期，避免卸载后继续写状态。
 *
 * @returns 本次加载事务上下文。
 */
function beginEditorInitialLoad(): EditorInitialLoadContext {
  editorInitialLoadAbortController?.abort();
  const requestId = editorInitialLoadRequestId + 1;
  const abortController = new AbortController();
  editorInitialLoadRequestId = requestId;
  editorInitialLoadAbortController = abortController;
  editorInitialOpenProjectLoadCoordinator.begin(requestId);
  featureTreeStore.isTreeLoading = true;

  return {
    requestId,
    signal: abortController.signal,
  };
}

/**
 * 判断首屏加载事务是否仍然属于当前页面实例。
 *
 * @param requestId 待校验的加载事务序号。
 * @returns 是否仍允许继续写入 UI 状态。
 */
function isActiveEditorInitialLoad(requestId: number): boolean {
  return !isEditorUnmounted && editorInitialLoadRequestId === requestId;
}

/**
 * 判断首屏加载异常是否属于主动取消或过期请求。
 *
 * @param error 捕获到的异常。
 * @param context 当前首屏加载事务上下文。
 * @returns 是否应静默结束而不是提示用户。
 */
function isEditorInitialLoadCanceled(error: unknown, context: EditorInitialLoadContext): boolean {
  return context.signal.aborted
    || !isActiveEditorInitialLoad(context.requestId)
    || isImportedModelLoadAbortedError(error);
}

/**
 * 释放首屏加载事务持有的 loading owner。
 *
 * @param context 当前首屏加载事务上下文。
 */
function releaseEditorInitialLoad(context: EditorInitialLoadContext): void {
  if (editorInitialLoadRequestId === context.requestId) {
    editorInitialLoadAbortController = null;
  }

  if (editorInitialOpenProjectLoadCoordinator.finish(context.requestId)) {
    featureTreeStore.isTreeLoading = false;
  }

  if (editorModelLoadingCoordinator.isOwnedBy(INITIAL_PROJECT_MODEL_LOADING_OWNER)) {
    editorModelLoadingCoordinator.hide(INITIAL_PROJECT_MODEL_LOADING_OWNER);
  }
}

/**
 * 取消当前仍在进行的首屏加载事务。
 *
 * 设计说明：卸载阶段先递增 requestId 再 abort，使已经恢复执行的旧异步分支也会失去写入资格。
 */
function cancelActiveEditorInitialLoad(): void {
  editorInitialLoadRequestId += 1;
  editorInitialLoadAbortController?.abort();
  editorInitialLoadAbortController = null;
  editorInitialOpenProjectLoadCoordinator.cancel();
  featureTreeStore.isTreeLoading = false;
  if (editorModelLoadingCoordinator.isOwnedBy(INITIAL_PROJECT_MODEL_LOADING_OWNER)) {
    editorModelLoadingCoordinator.hide(INITIAL_PROJECT_MODEL_LOADING_OWNER);
  }
}

/**
 * 开始一次工作区写入请求。
 *
 * 设计说明：提交和装配结构写入都会导出 `.puppy` 并发起后端写入，必须用独立 AbortController
 * 管住生命周期。这里先取消旧 controller，确保页面上始终只有当前写入可以继续占用网络与状态。
 *
 * @returns 本次写入请求使用的 controller。
 */
function beginWorkspaceWriteRequest(): AbortController {
  workspaceWriteAbortController?.abort();
  const abortController = new AbortController();
  workspaceWriteAbortController = abortController;
  return abortController;
}

/**
 * 判断工作区写入异常是否属于主动取消。
 *
 * @param error 捕获到的异常。
 * @param signal 本次写入请求使用的取消信号。
 * @returns 属于取消或页面卸载时返回 true。
 */
function isWorkspaceWriteCanceled(error: unknown, signal: AbortSignal): boolean {
  return signal.aborted
    || isEditorUnmounted
    || isImportedModelLoadAbortedError(error)
    || (error instanceof ConnectError && error.code === Code.Canceled);
}

/**
 * 释放已完成的工作区写入请求。
 *
 * @param abortController 本次写入请求使用的 controller。
 */
function releaseWorkspaceWriteRequest(abortController: AbortController): void {
  if (workspaceWriteAbortController === abortController) {
    workspaceWriteAbortController = null;
  }
}

/**
 * 取消当前仍在进行的工作区写入请求。
 */
function cancelActiveWorkspaceWriteRequest(): void {
  workspaceWriteAbortController?.abort();
  workspaceWriteAbortController = null;
}

/**
 * 打开当前路由指向的项目并刷新编辑页基础状态。
 *
 * @param context 首屏加载事务上下文；缺省时保持工作区重载等既有同步调用语义。
 */
const loadProjectOrThrow = async (context?: EditorInitialLoadContext): Promise<void> => {
  const fallbackName = context
    ? await glbLoader.initSessionAndLoadFiles(
      openProjectRouteContext.value.sessionInput,
      { signal: context.signal },
    )
    : await glbLoader.initSessionAndLoadFiles(openProjectRouteContext.value.sessionInput);
  if (context && !isActiveEditorInitialLoad(context.requestId)) {
    return;
  }
  // OpenProject 响应后仍处在首屏模型恢复事务中：如果尚未被 tab/model 加载接管，
  // 只更新文案，不关闭遮罩，避免出现 OpenProject 与模型加载两段视觉断层。
  editorModelLoadingCoordinator.updateText(INITIAL_PROJECT_MODEL_LOADING_OWNER, '正在加载模型');
  // OpenProject 已返回项目文件清单，Viewer/Worker registrar 已就绪后启动默认标签的下载与解析；
  // 设计树同步进入加载态，避免 BRep hydration 在缺少 registrar 时提前推进。
  void glbLoader.preloadDefaultTabModel();
  projectName.value = fallbackName;
  // 首屏路径由 loadInitialOpenProjectAuxiliaryData 在默认 tab 挂载后统一刷新设计树，避免中途多次闪烁。
  if (!context) {
    await featureTreeStore.refreshTree();
  }
  if (context && !isActiveEditorInitialLoad(context.requestId)) {
    return;
  }
  projectModelRequiresReload.value = false;
};

/**
 * 加载当前项目并把非取消异常转成用户可见错误。
 *
 * @param context 首屏加载事务上下文；用于静默处理卸载或过期请求。
 */
const loadProject = async (context?: EditorInitialLoadContext): Promise<void> => {
  try {
    await loadProjectOrThrow(context);
  } catch (error) {
    if (context && isEditorInitialLoadCanceled(error, context)) {
      return;
    }
    reportEditorAsyncFailure(error, '加载项目失败');
  }
};

/**
 * 加载首屏 OpenProject 结束后的辅助数据。
 *
 * 设计说明：首屏打开项目时，设计树、提交记录、装配节点列表都属于用户立即可见的项目上下文。
 * 它们必须在同一个 loading 事务内收口，避免节点列表或设计树在 OpenProject 中途被 watcher / 面板入口重复刷新。
 *
 * @param context 首屏加载事务上下文。
 */
async function loadInitialOpenProjectAuxiliaryData(context: EditorInitialLoadContext): Promise<void> {
  if (!isActiveEditorInitialLoad(context.requestId)) {
    return;
  }

  const tasks: Promise<void>[] = [
    featureTreeStore.refreshTree(glbLoader.partTabsStore.activeTabId).then(() => undefined),
    versionWorkspaceController.loadCommitRecords({ forceRefresh: true }),
  ];
  const branchNodeListContext = canOpenAssemblyBranchNodeListPanel.value
    ? resolveActiveAssemblyBranchNodeListContext()
    : null;

  if (branchNodeListContext) {
    tasks.push(loadAssemblyBranchNodeListForContext(branchNodeListContext));
  }

  const results = await Promise.allSettled(tasks);
  if (!isActiveEditorInitialLoad(context.requestId)) {
    return;
  }

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      return;
    }

    reportEditorAsyncFailure(result.reason, '首屏项目辅助数据加载失败');
  });
}

const handleViewportReady = async (): Promise<void> => {
  const currentViewer = getViewer();
  if (!currentViewer) {
    return;
  }

  entityInteractionSubscriptions.forEach((subscription) => subscription.unsubscribe());
  entityInteractionSubscriptions.length = 0;

  viewportInteractionSync.register();
  measureTreeSelectionSync.subscribe(currentViewer);
  const lineDrawingPointCountSubscription = currentViewer.observeLineDrawingPointCount?.().subscribe((pointCount) => {
    lineDrawingPointCount.value = pointCount;
    if (currentDrawingTool.value === 'line') {
      drawingPointCount.value = pointCount;
    }
  });
  const sketchGroupLifecycleObservable = currentViewer.observeSketchGroupLifecycle?.();
  const sketchGroupLifecycleSubscription = sketchGroupLifecycleObservable?.subscribe(() => {
    syncSketchEditingVisualLayering();
    /**
     * 草图组创建/结束/移除都会直接影响设计树与统计数字，
     * 这里统一通过 Viewer 暴露的生命周期事件刷新宿主状态。
     */
    void updateState().catch((error) => {
      reportEditorAsyncFailure(error, '草图组生命周期刷新失败');
    });
  });

  if (lineDrawingPointCountSubscription) {
    entityInteractionSubscriptions.push(lineDrawingPointCountSubscription);
  }
  if (sketchGroupLifecycleSubscription) {
    entityInteractionSubscriptions.push(sketchGroupLifecycleSubscription);
  }

  const trimEntitySyncSubscription = currentViewer.observeRawPointerEvent?.(
    RawPointerEventType.Click,
  )?.subscribe(() => {
    if (sketchTrimMode.value === null) {
      selectedSketchTrimEntity.value = null;
      return;
    }
    selectedSketchTrimEntity.value = currentViewer.getLastSelectedTrimEntitySnapshot?.() as
      | { objectId: string; segmentIndex: number | null; hitPoint?: [number, number, number] }
      | null
      ?? null;
  });
  if (trimEntitySyncSubscription) {
    entityInteractionSubscriptions.push(trimEntitySyncSubscription);
  }
  syncViewportInteractionMode();
  syncSketchEditingVisualLayering();

  currentViewer.onDrawingFinished(() => {
    sketchStateStore.setViewerMode('sketch' as never);
    currentDrawingTool.value = null;
    drawingStore.stopDrawingHintTimer();
    if (!isSketchActive.value) {
      currentDrawingDatumPlaneId.value = -1;
    }
    void updateState().catch((error) => {
      reportEditorAsyncFailure(error, '绘图完成后刷新编辑器状态失败');
    });
  });

  currentViewer.onDrawingNotice((content: string, level?: 'info' | 'warning' | 'error') => {
    if (level === 'warning') {
      message.warning(content);
      return;
    }
    if (level === 'error') {
      message.error(content);
      return;
    }
    message.info(content);
  });

  currentViewer.setShadedSketchContoursEnabled?.(shadedSketchContoursEnabled.value);

  if (editorInitialOpenProjectLoadCoordinator.isLoading()) {
    return;
  }

  try {
    await featureTreeStore.refreshTree();
  } catch (error) {
    reportEditorAsyncFailure(error, '刷新设计树失败');
  }
};

/**
 * 在 Puppy CAD 内核完成初始化后执行调试建模。
 *
 * 设计说明：
 * 1. `viewer.whenReady()` 仅代表渲染链 ready，不代表 Worker 内的 Puppy CAD 内核已可用；
 * 2. 这里通过一次 `getGeometryStateSummary()` 触发 Worker 初始化，确保 Puppy CAD 内核已完成装载；
 * 3. 调试建模失败不应阻断编辑器打开，因此统一收口为宿主可见错误提示。
 */
const runDebugToolAfterPuppyReady = async (
  currentViewer: Viewer,
): Promise<void> => {
  try {
    //------ 编辑器禁用DebugTool调用 开始------
    // 编辑器内暂时禁用所有 DebugTool 调试建模调用，避免启动时自动画草图并创建 box。
    void currentViewer;
    // await currentViewer.getGeometryStateSummary();
    // await currentViewer.restoreProjectState([]);
    // await DebugTool.createTwoDistancedSquareSketches(currentViewer);
    // await updateState();
    //------ 编辑器禁用DebugTool调用 结束------
  } catch (error) {
    reportEditorAsyncFailure(error, 'Puppy CAD 内核初始化后的调试建模执行失败');
  }
};

const waitForContainerSize = (el: HTMLElement, timeout = 3000): Promise<void> => {
  return new Promise((resolve) => {
    if (el.clientWidth > 0 && el.clientHeight > 0) {
      resolve();
      return;
    }

    const observer = new ResizeObserver(() => {
      if (el.clientWidth > 0 && el.clientHeight > 0) {
        observer.disconnect();
        resolve();
      }
    });

    observer.observe(el);
    setTimeout(() => {
      observer.disconnect();
      resolve();
    }, timeout);
  });
};

const initViewportRendererInEditor = async (): Promise<void> => {
  if (!viewportContainerRef.value) {
    message.error('视口容器尚未准备好');
    return;
  }
  await waitForContainerSize(viewportContainerRef.value);
  const snapSettings = initialPreferences.value?.snapSettings ?? undefined;
  viewer = new Viewer(viewportContainerRef.value, {
    gridSize: 0.01,
    gridDivisions: 0.05,
    cameraPosition: [0.1, 0.1, 0.1],
    snapSettings,
    onAssemblyTransformCommitted: commitEditorAssemblyTransformHistory,
    onAssemblyMateDocumentChanged: async (document, context) => {
      try {
        await handleAssemblyMateDocumentChanged(document, context);
      } catch (error) {
        reportEditorAsyncFailure(error, '同步装配体配合文档失败');
        throw error;
      }
    },
    onSketchRuntimeHistoryCommitted: commitEditorSketchRuntimeHistoryRecord,
  });
  editorAssemblyMateDocumentHistorySnapshot = viewer.assemblyMateController?.getDocument?.() ?? null;
  setViewer(viewer);
  await viewer.whenReady();
  //------ Three.js 场景调试面板 开始------
  /**
   * Inspector、场景导出按钮和 Window 调试函数只允许在 Vite 本地开发环境创建。
   * 动态导入位于 DEV 常量分支内，使生产构建可以同时移除调试服务运行时代码；本地 Editor
   * 在 Viewer 完成异步初始化后默认打开 Inspector，关闭与再次打开仍复用同一服务生命周期。
   */
  if (import.meta.env.DEV) {
    const { EditorSceneDebugInspectorService: SceneDebugInspectorService } = await import(
      '@/services/debug/EditorSceneDebugInspectorService'
    );
    editorSceneDebugInspectorService = new SceneDebugInspectorService(
      viewer,
      (successMessage) => message.success(successMessage),
      reportEditorAsyncFailure,
    );
    editorSceneDebugInspectorService.mountToWindow();
    editorSceneDebugInspectorService.openDebugTools();
  }
  //------ Three.js 场景调试面板 结束------
  // 再走统一宿主同步，保证其它模式仍复用工作流层的单一出口。
  syncViewportInteractionMode();
  assemblyWorkflow.handleViewerReady();
  //------ DebugTool 控制台挂载 开始------
  DebugTool.mountToWindow(viewer);
  //------ DebugTool 控制台挂载 结束------
  //------ 编辑器禁用DebugTool调用 开始------
  // await runDebugToolAfterPuppyReady(viewer);
  //------ 编辑器禁用DebugTool调用 结束------
  selectionStores.forEach((store) => {
    store.subscribe();
  });
  isViewportRendererReady.value = true;
  await handleViewportReady();
};

/**
 * 清理 Editor 侧绑定在当前 Viewer 上的事件订阅。
 *
 * 设计说明：根文档切换会销毁 Viewer，宿主侧订阅必须先解绑，避免旧 Viewer dispose 过程中晚到的
 * 选择、绘图或测量事件继续写入新文档 UI 状态。
 */
function releaseEditorViewerSubscriptions(): void {
  entityInteractionSubscriptions.forEach((subscription) => subscription.unsubscribe());
  entityInteractionSubscriptions.length = 0;
  viewportInteractionSync.dispose();
  measureTreeSelectionSync.unsubscribe();
  selectionStores.forEach((store) => {
    store.unsubscribe();
  });
}

/**
 * Editor Viewer 释放选项。
 */
interface EditorViewerReleaseOptions {
  /**
   * 是否终止 cad-core 共享 Worker runtime。
   *
   * 设计说明：页面卸载需要终止 Worker；根文档切换重建 Viewer 时要保留 WorkerManager，
   * 由后续完整文档 runtime switch 清理旧拓扑并接管新文档。
   */
  readonly terminateWorker?: boolean;
}

/**
 * 释放当前 Editor 持有的 Viewer 实例。
 *
 * 设计说明：Viewer 是完整文档的 Scene/Topo 边界；根文档切换时复用释放顺序清掉旧 Viewer 的
 * 渲染和交互状态，但 Worker 是否终止由调用方按页面卸载或根文档切换场景决定。
 *
 * @param options Viewer 释放选项；不传时保留 cad-core 默认的完整释放语义。
 */
function releaseCurrentEditorViewer(options?: EditorViewerReleaseOptions): void {
  releaseEditorViewerSubscriptions();
  isViewportRendererReady.value = false;
  editorSceneDebugInspectorService?.dispose();
  editorSceneDebugInspectorService = null;
  DebugTool.unmountFromWindow();
  viewer?.clearSketchEditingVisualLayering?.();
  if (options) {
    viewer?.dispose({
      terminateWorker: options.terminateWorker,
    });
  } else {
    viewer?.dispose();
  }
  viewer = null;
  clearViewer();
}

/**
 * 清理根文档切换前遗留的编辑命令会话。
 *
 * 设计说明：
 * 1. 根提交 reload 会重建 Viewer 与 CAD runtime，旧 PropertyManager 命令不能跨文档存活；
 * 2. 这里先调用各工作流既有取消入口释放预览、选择、插入句柄和 cad-core 控制器状态；
 * 3. 最后统一把宿主 UI 壳压回设计树，避免某个取消入口因命令不匹配而保留旧面板。
 */
function clearEditorCommandSessionForRootDocumentSwitch(): void {
  const command = activeOperationCommand.value;

  if (command === 'assembly-linear-component-pattern') {
    cancelAssemblyLinearComponentPatternCommand();
  }
  if (assemblyWorkflow.isAssemblyTransformCommand(command)) {
    assemblyWorkflow.cancelAssemblyTransformCommand();
  }
  if (assemblyWorkflow.isAssemblyReferenceCommand(command)) {
    assemblyWorkflow.cancelAssemblyReferenceCommand();
  }
  if (assemblyWorkflow.isAssemblyMateCommand(command)) {
    assemblyWorkflow.cancelAssemblyMateCommand();
  }
  if (command === 'assembly-insert-component') {
    cancelAssemblyInsertionWorkflow({
      isPlacingComponent: componentPlacement.isPlacing,
      pendingInsertionHandle,
      cancelPlacement: () => componentPlacement.cancelPlacement(),
      discardPreparedInsertion: (handle) => glbLoader.discardPreparedInsertion(handle),
      cancelInsertion: () => glbLoader.cancelInsertion(),
      finishPlacement,
    });
    finishAssemblyInsertionCommandState();
  }
  if (isReferenceFeatureCommand(command)) {
    cancelReferenceFeatureCommand(command);
  }
  if (isExtrudeMode.value) {
    cancelExtrude();
  }
  if (revolveUiState.value.active) {
    cancelRevolve();
  }
  if (revolveCutUiState.value.active) {
    cancelRevolveCut();
  }
  if (filletUiState.value.active || chamferUiState.value.active) {
    cancelBlend();
  }
  if (command === 'sketch-trim') {
    clearSketchTrimWorkflow({ clearToolbarState: false });
  }
  if (command === 'sketch-convert-entities') {
    clearConvertEntitiesWorkflow({ clearToolbarState: false });
  }
  if (command === 'sketch-move-entities'
    || command === 'sketch-copy-entities'
    || command === 'sketch-rotate-entities'
    || command === 'sketch-mirror-entities') {
    cancelSketchEntitiesTransform();
  }
  if (command === 'sketch-circular-pattern') {
    cancelSketchCircularPatternWorkflow();
  }
  if (isDrawingMode.value) {
    void runDrawingCancel();
  }

  getViewer()?.clearSelectionProgrammatically?.();
  activeOperationCommand.value = null;
  toolbarCommandStore.clearActiveCommands();
  toolbarOperationOverride.value = null;
  propertyManagerForceConfirm.value = false;
  rightPanelTab.value = 'feature';
}

/**
 * 为根文档切换重建 Viewer。
 *
 * 设计说明：该入口只用于根项目/根提交/工作区切换，节点子树替换仍然使用 glbLoader 的 scoped
 * runtime switch，以保留未受影响子树和当前交互上下文。
 */
interface RootDocumentViewerRebuildOptions {
  /** 是否保留当前正在执行的根快照切换请求。 */
  readonly preserveActiveProjectRootSnapshotSwitch?: boolean;
  /** 当前根文档切换请求是否仍有效。 */
  readonly isCurrent?: () => boolean;
}

async function recreateViewerForRootDocumentSwitch(
  options: RootDocumentViewerRebuildOptions = {},
): Promise<void> {
  if (options.isCurrent && !options.isCurrent()) {
    throw new Error('根文档切换请求已失效，已取消 Viewer 重建');
  }
  resetEditorSketchRuntimeState(ViewerMode.Default);
  await startFreshEditorHistorySession();
  if (options.isCurrent && !options.isCurrent()) {
    throw new Error('根文档切换请求已失效，已取消 Viewer 重建');
  }
  glbLoader.resetForRootViewerRebuild({
    preserveActiveProjectRootSnapshotSwitch: options.preserveActiveProjectRootSnapshotSwitch,
  });
  releaseCurrentEditorViewer({ terminateWorker: false });
  await initViewportRendererInEditor();
}

const handleViewportContextMenuDeleted = async (): Promise<void> => {
  try {
    await updateState();
  } catch (error) {
    reportEditorAsyncFailure(error, '删除草图后刷新状态失败');
  }
};

/**
 * 处理装配体视口右键菜单的删除动作。
 *
 * 设计说明：
 * 1. 视口菜单只知道当前命中的 Three.js 对象，真实装配节点解析仍统一交给设计树删除事务；
 * 2. 传入 includeViewerSelectionAncestors 后，事务会把当前 Viewer 选中对象的父链也纳入候选，
 *    避免命中子 Mesh 时找不到对应装配组件节点；
 * 3. 当前入口不直接操作场景资源，删除、working 文档同步和回滚全部由 handleAssemblyComponentDeleteAction 收口。
 *
 * @param selectedObjectIds 视口右键菜单打开时捕获的选中对象 UUID 列表。
 */
//------ 特征面草图绘制 开始------
/**
 * 处理右键菜单"草图绘制"动作。
 *
 * 设计说明：
 * 1. 复用 handleSketchCommand('sketch-enter') 已有工作流；
 * 2. 该方法内部会检测当前选中面（getSelectedPlanarFace）并进入草图编辑环境；
 * 3. 右键菜单弹出时选中状态保持不变，因此无需缓存选中面信息。
 */
const handleViewportContextMenuInsertSketch = (): void => {
  handleSketchCommand('sketch-enter');
};
//------ 特征面草图绘制 结束------

/**
 * 运行 STEP 导出。
 *
 * 设计说明：导出使用独立 loading owner，避免与 GLB 加载、Tab 切换等异步加载流程互相清理 overlay。
 */
async function runStepExport(): Promise<void> {
  editorModelLoadingCoordinator.show('正在导出 STEP...', STEP_EXPORT_LOADING_OWNER);
  const exportTimingLabel = `STEP导出总耗时:${projectName.value || 'model'}:${Date.now()}`;

  console.time(exportTimingLabel);
  try {
    const source = await glbLoader.createCurrentStepExportSource();
    const request = createStepExportRequest({
      fileBaseName: projectName.value,
      source,
      options: {
        productName: projectName.value,
      },
    });
    const result = await exportStep(request);

    downloadExportResult(result);
    message.success('STEP 导出完成');
  } catch (error) {
    reportEditorAsyncFailure(error, 'STEP 导出失败');
  } finally {
    console.timeEnd(exportTimingLabel);
    editorModelLoadingCoordinator.hide(STEP_EXPORT_LOADING_OWNER);
  }
}

/**
 * 导出标准 STEP 文件。
 */
const handleExportStep = async (): Promise<void> => {
  await runStepExport();
};

/**
 * 运行当前可见模型 GLB 导出。
 */
async function runGlbExport(): Promise<void> {
  editorModelLoadingCoordinator.show('正在导出 GLB...', OFFLINE_PACKAGE_LOADING_OWNER);

  try {
    const viewer = getViewer();
    const sceneRoot = viewer?.getScene();
    if (!sceneRoot) {
      throw new Error('当前 Viewer 场景未初始化，无法导出 GLB');
    }

    const result = await exportGlb({
      fileBaseName: projectName.value,
      sceneRoot,
    });
    downloadExportResult(result);
    message.success('GLB 导出完成');
  } catch (error) {
    reportEditorAsyncFailure(error, 'GLB 导出失败');
  } finally {
    editorModelLoadingCoordinator.hide(OFFLINE_PACKAGE_LOADING_OWNER);
  }
}

/**
 * 导出当前可见模型 GLB。
 */
const handleExportGlb = async (): Promise<void> => {
  await runGlbExport();
};

/**
 * 读取当前离线包导出的 CAD 文档类型。
 *
 * 设计说明：
 * 1. 离线包必须携带导出时的零件/装配体语义，导入空工程时不能继承空工程默认类型；
 * 2. 当前激活标签是编辑器模式与设计树模式的权威来源，这里只将它转换为 `.puppy` manifest 字段。
 *
 * @returns `.puppy` manifest 文档类型。
 */
function resolveCurrentPuppyDocumentType(): PuppyPackageDocumentType {
  return glbLoader.partTabsStore.activeTab?.type === 'assembly' ? 'assembly' : 'part';
}

/**
 * 清理离线包导出前的临时选中态。
 *
 * 设计说明：
 * 1. 选中高亮 overlay 属于编辑器运行时辅助对象，不属于可恢复 CAD 模型；
 * 2. 导出前主动清理 Viewer 与设计树选中态，避免临时高亮残留污染场景快照。
 *
 * @param viewer 当前 Viewer。
 */
function clearOfflinePackageExportSelection(viewer: Viewer): void {
  viewer.clearSelectionProgrammatically?.();
  featureTreeStore.clearSelection();
}

/**
 * 运行 PuppyCAD 离线包导出。
 */
async function runOfflinePackageExport(): Promise<void> {
  editorModelLoadingCoordinator.show('正在导出离线包...', OFFLINE_PACKAGE_LOADING_OWNER);

  try {
    const viewer = getViewer();
    const sceneRoot = viewer?.getScene();
    if (!viewer || !sceneRoot) {
      throw new Error('当前 Viewer 场景未初始化，无法导出离线包');
    }

    clearOfflinePackageExportSelection(viewer);

    const documentType = resolveCurrentPuppyDocumentType();
    const resultBodyDisplayNameByObjectId = await viewer.createResultBodyDisplayNameMap({
      objectNameOverrides: featureTreeStore.objectNameOverrides,
    });
    const result = await exportPuppyPackage({
      fileBaseName: projectName.value,
      documentType,
      sceneRoot,
      workerManager: viewer.workerManager,
      assemblySemanticSource: glbLoader.createCurrentPuppyExportAssemblySemanticSource(),
      resultBodyDisplayNameByObjectId,
      datumExtension: viewer.createPuppyDatumExtensionInput(),
      //------ 线性零部件阵列离线包定义持久化 开始------
      linearComponentPatternDocument: viewer.createPuppyLinearComponentPatternDocument(),
      //------ 线性零部件阵列离线包定义持久化 结束------
      //------ 草图离线包定义持久化 开始------
      sketchExtensionEntries: viewer.createPuppySketchExtensionInput(),
      sketchEntityReferenceRecords: viewer.createPuppySketchEntityReferenceExtensionInput(),
      //------ 草图离线包定义持久化 结束------
      //------ 特征离线包定义持久化 开始------
      featureExtensionInput: viewer.createPuppyFeatureExtensionInput(),
      //------ 特征离线包定义持久化 结束------
      sectionViewSnapshot: viewer.createPuppySectionViewSnapshot(),
      mates: documentType === 'assembly'
        ? viewer.assemblyMateController.toJSON()
        : undefined,
    });
    downloadExportResult(result);
    message.success('离线包导出完成');
  } catch (error) {
    reportEditorAsyncFailure(error, '离线包导出失败');
  } finally {
    editorModelLoadingCoordinator.hide(OFFLINE_PACKAGE_LOADING_OWNER);
  }
}

/**
 * 导出 PuppyCAD 离线包。
 */
const handleExportOfflinePackage = async (): Promise<void> => {
  await runOfflinePackageExport();
};

/**
 * 读取用户选择的离线包文件。
 *
 * @param file 离线包文件。
 * @returns 文件字节。
 */
async function readOfflinePackageFileBytes(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}

/**
 * 将离线包文档类型转换为空闲 Viewer 模式。
 *
 * @param documentType 离线包 manifest 中的文档类型。
 * @returns 导入后应该进入的 Viewer 模式。
 */
function resolveOfflinePackageIdleViewerMode(documentType: PuppyPackageDocumentType): ViewerMode {
  return documentType === 'assembly'
    ? ViewerMode.Assembly
    : ViewerMode.Default;
}

/**
 * 应用离线包导入后的运行时文档类型。
 *
 * 设计说明：
 * 离线包导入是运行时替换当前 CAD 文档，不能继续让原路由 `.part.cad` 或 `.assembly.cad`
 * 覆盖当前工具栏与鼠标交互模式。这里用 manifest 的文档类型作为当前页面运行时语义。
 *
 * @param documentType 离线包 manifest 中的文档类型。
 */
function applyOfflinePackageRuntimeDocumentType(documentType: PuppyPackageDocumentType): void {
  handlePropertyManagerCancel();
  activeOperationCommand.value = null;
  offlinePackageRuntimeDocumentType.value = documentType;
  resetEditorSketchRuntimeState(resolveOfflinePackageIdleViewerMode(documentType));
}

/**
 * 清理离线包导入前残留的 Mate 文档。
 *
 * @param viewer 当前 Viewer。
 * @param assemblyId 新离线包 Mate 文档所属装配体 ID。
 */
function resetOfflinePackageMateDocument(viewer: Viewer, assemblyId: string): void {
  const emptyMateDocument = createAssemblyMateDocument({ assemblyId });
  viewer.assemblyMateController.loadDocument(emptyMateDocument);
  editorAssemblyMateDocumentHistorySnapshot = emptyMateDocument;
}

/**
 * 运行 PuppyCAD 离线包导入。
 *
 * 设计说明：
 * 1. 离线包导入属于“加载离线模型快照”，不写入当前建模历史事务；
 * 2. display.glb 只作为显示层挂载，BRep payload 通过 Worker 注册为后续命令可使用的权威 Shape；
 * 3. 场景挂载复用 imported-model 的资源释放 helper，避免旧导入装配体残留。
 *
 * @param file 用户选择的 `.puppy` 文件。
 */
async function runOfflinePackageImport(file: File): Promise<void> {
  editorModelLoadingCoordinator.show('正在导入离线包...', OFFLINE_PACKAGE_LOADING_OWNER);

  try {
    const viewer = getViewer();
    if (!viewer) {
      throw new Error('当前 Viewer 未初始化，无法导入离线包');
    }

    const packageBytes = await readOfflinePackageFileBytes(file);
    const result = await importPuppyPackage(packageBytes, {
      workerManager: viewer.workerManager,
      packageName: file.name,
    });
    logger.info('[Editor] PuppyCAD 离线包导入耗时', {
      fileName: file.name,
      byteLength: packageBytes.byteLength,
      registeredBrepShapeCount: result.registeredBrepShapes.length,
      bodyTopologyAssetCount: result.topologySource.bodyEntries.length,
      timing: result.timing,
    });

    applyOfflinePackageRuntimeDocumentType(result.manifest.documentType);
    resetOfflinePackageMateDocument(viewer, result.mates.assemblyId);
    const mountedTab = await glbLoader.mountOfflinePuppyPackage({
      result: result.modelResult,
      documentType: result.manifest.documentType,
      datumExtension: result.datumExtension,
      //------ 线性零部件阵列离线包定义持久化 开始------
      linearComponentPatternExtension: result.linearComponentPatternExtension,
      //------ 线性零部件阵列离线包定义持久化 结束------
      //------ 草图离线包定义持久化 开始------
      sketchExtension: result.sketchExtension,
      sketchEntityReferenceExtension: result.sketchEntityReferenceExtension,
      //------ 草图离线包定义持久化 结束------
      //------ 特征离线包定义持久化 开始------
      featureExtension: result.featureExtension,
      //------ 特征离线包定义持久化 结束------
      sectionViewExtension: result.sectionViewExtension,
      tabTemplates: result.tabTemplates,
      tabModelResultsByNodeId: result.tabModelResultsByNodeId,
      tabId: result.modelResult.fileInfo.fileId,
      tabLabel: result.manifest.projectName || file.name.replace(/\.puppy$/i, ''),
    });
    if (result.manifest.documentType === 'assembly') {
      viewer.assemblyMateController.fromJSON(result.mates, {
        restoreReferences: true,
        notifyDocumentChanged: true,
      });
      editorAssemblyMateDocumentHistorySnapshot = viewer.assemblyMateController.getDocument();
    }
    viewer.requestImmediateRender?.();
    message.success(`离线包导入完成：${mountedTab.label}`);
  } catch (error) {
    reportEditorAsyncFailure(error, '离线包导入失败');
  } finally {
    editorModelLoadingCoordinator.hide(OFFLINE_PACKAGE_LOADING_OWNER);
  }
}
/**
 * 打开离线包文件选择器。
 */
function handleImportOfflinePackage(): void {
  offlinePackageInputRef.value?.click();
}

/**
 * 处理离线包文件选择。
 *
 * @param event 文件选择事件。
 */
async function handleOfflinePackageFileChange(event: Event): Promise<void> {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  const file = input?.files?.[0] ?? null;
  if (input) {
    input.value = '';
  }
  if (!file) {
    return;
  }

  await runOfflinePackageImport(file);
}

function handleOpenSubmitWorkspaceDialog(): void {
  if (guardReadonlyPreviewEditing()) return;
  if (guardProjectModelStructureWrite('提交')) {
    return;
  }
  isSubmitWorkspaceDialogVisible.value = true;
}

function handleCloseSubmitWorkspaceDialog(): void {
  if (isSubmittingWorkspace.value) return;
  isSubmitWorkspaceDialogVisible.value = false;
}

const handleConfirmSubmitWorkspace = async (commitMessage: string): Promise<void> => {
  const normalizedMessage = commitMessage.trim();
  if (normalizedMessage.length === 0 || isSubmittingWorkspace.value) return;
  if (guardProjectModelStructureWrite('提交')) {
    return;
  }

  isSubmittingWorkspace.value = true;
  const submitAbortController = beginWorkspaceWriteRequest();
  try {
    const rootNode = cadSessionStore.openProjectRootNode;
    if (!rootNode) {
      throw new Error('当前项目尚未完成 OpenProject 加载，无法提交');
    }
    const currentResult = glbLoader.getCurrentProjectModelLoadResult();
    if (!currentResult) {
      throw new Error('当前项目模型尚未通过 OpenProject 新入口加载，无法提交');
    }
    assertImportedDocumentCanPersist(currentResult);
    if (rootNode.type === CadFileKind.ASSEMBLY) {
      const draftRecord = glbLoader.assemblyDraftDocumentStore.get(currentResult.root.nodeKey);
      if (!draftRecord) {
        throw new Error(`当前根装配缺少 _assembly.json 工作草稿：${currentResult.root.nodeKey}`);
      }
      const viewer = getViewer();
      const sceneRoot = viewer?.getScene();
      if (!viewer || !sceneRoot) {
        throw new Error('当前 Viewer 场景未初始化，无法提交装配工作区');
      }

      /**
       * 装配保存只提交协同节点工作草稿和当前场景生成的新离线包。
       * 移动姿态不再回写 workAssemble，避免 workingAssemblyDocument 承担完整装配树职责。
       */
      const result = await assemblyWorkspaceCommitService.commitCurrentAssemblyWorkspace({
        rootNode,
        assemblyDocument: draftRecord.workingAssemblyDocument,
        projectModelLoadResult: currentResult,
        sceneRoot,
        workerManager: viewer.workerManager,
        assemblySemanticSource: glbLoader.createCurrentPuppyExportAssemblySemanticSource(),
        mates: viewer.assemblyMateController.toJSON(),
        datumExtension: viewer.createPuppyDatumExtensionInput(),
        //------ 线性零部件阵列离线包定义持久化 开始------
        linearComponentPatternDocument: viewer.createPuppyLinearComponentPatternDocument(),
        //------ 线性零部件阵列离线包定义持久化 结束------
        //------ 草图离线包定义持久化 开始------
        sketchExtensionEntries: viewer.createPuppySketchExtensionInput(),
        sketchEntityReferenceRecords: viewer.createPuppySketchEntityReferenceExtensionInput(),
        //------ 草图离线包定义持久化 结束------
        //------ 特征离线包定义持久化 开始------
        featureExtensionInput: viewer.createPuppyFeatureExtensionInput(),
        //------ 特征离线包定义持久化 结束------
        sectionViewSnapshot: viewer.createPuppySectionViewSnapshot(),
        baseCommitId: resolveCurrentAssemblyWriteBaseCommitId(),
        message: normalizedMessage,
        signal: submitAbortController.signal,
      });
      if (submitAbortController.signal.aborted || isEditorUnmounted) {
        return;
      }
      isSubmitWorkspaceDialogVisible.value = false;
      try {
        await reloadProjectAfterRootCommitChanged(result.commit.commitId, {
          syncBranchNodeListAfterRootReload: true,
        });
        message.success(`已提交到分支：${currentWorkspaceBranchName.value}`);
      } catch (reloadError) {
        projectModelRequiresReload.value = true;
        reportEditorAsyncFailure(reloadError, '提交已成功，但重新加载项目失败');
      }
      return;
    }
    if (rootNode.type !== CadFileKind.PART) {
      throw new Error('当前根节点类型不支持提交');
    }

    const viewer = getViewer();
    const sceneRoot = viewer?.getScene();
    if (!viewer || !sceneRoot) {
      throw new Error('当前 Viewer 场景未初始化，无法提交零件包');
    }

    /**
     * 根节点为零件时仍提交当前可见模型导出的 `.puppy` 包；
     * 根节点为装配体时已经在上方分支通过装配保存服务提交 workAssemble 和新离线包。
     */
    const result = await partPackageCommitService.commitCurrentPartPackage({
      rootNode,
      projectModelLoadResult: currentResult,
      sceneRoot,
      workerManager: viewer.workerManager,
      datumExtension: viewer.createPuppyDatumExtensionInput(),
      //------ 草图离线包定义持久化 开始------
      sketchExtensionEntries: viewer.createPuppySketchExtensionInput(),
      sketchEntityReferenceRecords: viewer.createPuppySketchEntityReferenceExtensionInput(),
      //------ 草图离线包定义持久化 结束------
      //------ 特征离线包定义持久化 开始------
      featureExtensionInput: viewer.createPuppyFeatureExtensionInput(),
      //------ 特征离线包定义持久化 结束------
      sectionViewSnapshot: viewer.createPuppySectionViewSnapshot(),
      message: normalizedMessage,
      signal: submitAbortController.signal,
    });
    if (submitAbortController.signal.aborted || isEditorUnmounted) {
      return;
    }
    isSubmitWorkspaceDialogVisible.value = false;
    try {
      await reloadProjectAfterRootCommitChanged(result.commit.commitId, {
        loadingText: '正在重新加载零件模型',
      });
      message.success(`已提交到分支：${currentWorkspaceBranchName.value}`);
    } catch (reloadError) {
      projectModelRequiresReload.value = true;
      reportEditorAsyncFailure(reloadError, '提交已成功，但重新加载项目失败');
    }
  } catch (error) {
    if (isWorkspaceWriteCanceled(error, submitAbortController.signal)) {
      return;
    }
    reportEditorAsyncFailure(error, '提交更改失败');
  } finally {
    releaseWorkspaceWriteRequest(submitAbortController);
    if (!isEditorUnmounted) {
      isSubmittingWorkspace.value = false;
    }
  }
};

/**
 * 确认是否基于所选历史版本继续编辑。
 *
 * 设计说明：当前后端暂不接收编辑基准提交，确认框只表达本次前端会话将加载历史模型并回到编辑态，
 * 不展示也不持久化任何“基于版本”的血缘信息，避免用户误以为版本图已产生真实提交关系。
 *
 * @returns 用户确认继续时返回 true。
 */
function confirmContinueEditingFromHistoryVersion(): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    dialog.warning({
      title: '基于此版本继续编辑',
      content: '将加载所选历史版本并返回编辑态，当前未提交修改可能丢失。是否继续？',
      positiveText: '继续编辑',
      negativeText: '取消',
      onPositiveClick: () => {
        settle(true);
      },
      onNegativeClick: () => {
        settle(false);
      },
      onClose: () => {
        settle(false);
      },
    });
  });
}

/**
 * 基于所选历史版本加载模型并退出只读预览。
 *
 * @param commitId 用户选择的历史提交 ID。
 */
async function handleContinueEditingFromCommit(commitId: string): Promise<void> {
  if (versionWorkspaceStore.isVersionSwitching || versionWorkspaceStore.isCreatingWorkspace) return;
  const confirmed = await confirmContinueEditingFromHistoryVersion();
  if (!confirmed) return;
  await versionWorkspaceController.continueEditingFromCommit(commitId);
}

/**
 * 判断键盘事件是否来自可编辑输入控件。
 *
 * @param target 事件目标。
 * @returns 是否应跳过全局快捷键。
 */
function isEditableKeyboardEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  // 全局编辑器快捷键只应在视口上下文生效，输入组件内部节点冒泡时也要识别为编辑态。
  return target.closest('input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]') !== null;
}

/**
 * 判断只读历史预览态下是否需要拦截草图键盘工作流。
 *
 * @param event 键盘事件。
 * @returns 需要阻断时返回 true。
 */
function isReadonlyPreviewSketchWorkflowKey(event: KeyboardEvent): boolean {
  if (!versionWorkspaceStore.isReadonlyPreview || isEditableKeyboardEventTarget(event.target)) {
    return false;
  }
  return event.key === 'Escape'
    || event.key === 'Enter'
    || event.key === 'Backspace'
    || event.key === 'Delete';
}

//------ 装配体引用复制快捷键 开始------
/**
 * 规范化装配体引用复制候选对象 ID。
 *
 * @param objectId 候选对象 ID。
 * @returns 可用于场景查询的对象 ID；无效时返回 null。
 */
function normalizeAssemblyReferenceCopyObjectId(objectId: unknown): string | null {
  if (typeof objectId !== 'string') {
    return null;
  }

  const normalizedObjectId = objectId.trim();
  return normalizedObjectId.length > 0 ? normalizedObjectId : null;
}

/**
 * 从设计树当前选中节点读取可复制的场景对象 ID。
 *
 * 设计说明：
 * 1. 设计树 GLB 节点可能通过 selection proxy 指向可选对象，优先使用 `objectId`；
 * 2. 如果当前节点是实体、面或子项，则用 `glbSourceObjectId` 兜底，让 loader 再向上解析组件根；
 * 3. 这里只读取 ID，不直接保存 Object3D，避免 Vue 响应式链路代理 three.js 对象。
 *
 * @returns 场景对象 ID；没有可用设计树选中时返回 null。
 */
function resolveAssemblyReferenceCopyTreeObjectId(): string | null {
  const selectedKey = selectedKeys.value[0] ?? null;
  if (!selectedKey) {
    return null;
  }

  const nodeMeta = featureTreeStore.featureTreeNodeMap.get(selectedKey);
  return normalizeAssemblyReferenceCopyObjectId(nodeMeta?.objectId)
    ?? normalizeAssemblyReferenceCopyObjectId(nodeMeta?.glbSourceObjectId);
}

/**
 * 解析当前装配体引用复制的源对象 ID。
 *
 * 设计说明：
 * 视口选择更接近用户当前鼠标操作，因此优先于设计树；设计树没有选中时再使用
 * `selectedObject` 摘要兜底。真正的“组件根”解析仍交给 loader / cad-core 完成。
 *
 * @returns 当前源对象 ID；没有选中时返回 null。
 */
function resolveSelectedAssemblyReferenceCopyObjectId(): string | null {
  const currentViewer = getViewer();
  // SDK 将选中的点线面折叠为所属组件，不再用物理场景 UUID 过滤拓扑选择。
  const viewportSelectedObjectId = currentViewer?.resolveAssemblyReferenceCopySourceObject()?.uuid ?? null;

  if (viewportSelectedObjectId) {
    return viewportSelectedObjectId;
  }

  const treeObjectId = resolveAssemblyReferenceCopyTreeObjectId();
  if (treeObjectId) {
    return treeObjectId;
  }

  const selectedSummaryObjectId = normalizeAssemblyReferenceCopyObjectId(selectedObject.value?.id);
  if (selectedSummaryObjectId && currentViewer?.getSceneObjectById(selectedSummaryObjectId)) {
    return selectedSummaryObjectId;
  }

  return null;
}

/**
 * 判断当前是否允许启动装配体引用复制放置。
 *
 * @returns 允许启动时返回 true。
 */
function canStartAssemblyReferenceCopyPlacement(): boolean {
  return activeEditorTabType.value === 'assembly'
    && isViewerIdleMode(viewerMode.value)
    && activeOperationCommand.value === null
    && !pendingSketchToolCommand.value
    && !isExtrudeMode.value
    && !revolveUiState.value.active
    && !revolveCutUiState.value.active
    && !filletUiState.value.active
    && !chamferUiState.value.active
    && !getViewer()?.measureController?.isMeasureActive?.();
}

/**
 * 启动装配体引用复制放置流程。
 *
 * @param sourceObjectId 源对象 ID。
 * @returns 快捷键是否已被消费。
 */
function startAssemblyReferenceCopyPlacement(sourceObjectId: string | null): boolean {
  logger.info('[Editor] 装配体引用复制：准备执行粘贴', {
    sourceObjectId,
    cachedSourceObjectId: assemblyReferenceCopyClipboardObjectId.value,
    activeEditorTabType: activeEditorTabType.value,
    viewerMode: viewerMode.value,
    activeOperationCommand: activeOperationCommand.value,
    isPlacing: componentPlacement.isPlacing.value,
  });

  if (guardReadonlyPreviewEditing()) {
    logger.warn('[Editor] 装配体引用复制：只读预览态阻止粘贴');
    return true;
  }

  if (!canStartAssemblyReferenceCopyPlacement()) {
    logger.warn('[Editor] 装配体引用复制：当前命令状态不允许粘贴', {
      activeEditorTabType: activeEditorTabType.value,
      viewerMode: viewerMode.value,
      activeOperationCommand: activeOperationCommand.value,
      pendingSketchToolCommand: pendingSketchToolCommand.value,
      isExtrudeMode: isExtrudeMode.value,
      revolveActive: revolveUiState.value.active,
      revolveCutActive: revolveCutUiState.value.active,
      filletActive: filletUiState.value.active,
      chamferActive: chamferUiState.value.active,
      measureActive: getViewer()?.measureController?.isMeasureActive?.() ?? false,
    });
    message.warning('请先结束当前命令，再复制零部件');
    return true;
  }

  if (componentPlacement.isPlacing.value) {
    logger.warn('[Editor] 装配体引用复制：已有放置流程正在进行');
    message.warning('请先完成当前放置流程');
    return true;
  }

  const handle = glbLoader.prepareAssemblyReferenceCopy(sourceObjectId);
  if (!handle) {
    logger.warn('[Editor] 装配体引用复制：创建复制预览句柄失败', {
      sourceObjectId,
      selectedKeys: [...selectedKeys.value],
      selectedObjectId: selectedObject.value?.id ?? null,
      viewportSelectedObjectIds: getViewer()?.getSelectedObjectIds() ?? [],
    });
    message.warning('请先选择装配体零部件');
    return true;
  }

  setPendingInsertionHandle(pendingAssemblyReferenceCopyHandle, handle);
  assemblyReferenceCopyClipboardObjectId.value = sourceObjectId;

  const started = componentPlacement.startObjectPlacement(handle.previewObject, {
    // 装配体引用复制的源对象原点可能偏离实体中心，必须用视觉包围盒中心跟随鼠标。
    anchorMode: ComponentPlacementAnchorMode.BoundingBoxCenter,
    // Ctrl+V 粘贴不能触发相机适配，否则用户当前观察角度会被放置预览改掉。
    focusOnFirstPreviewPosition: false,
    // Ctrl+V 进入放置时还没有新的 pointermove，必须先用进入前的最后鼠标位置定位复制体。
    syncToLastPointerPositionOnStart: true,
    // 引用复制沿用 SolidWorks 式自由放置，不复用插入零部件的吸附点。
    enableSnap: false,
  });
  logger.info('[Editor] 装配体引用复制：粘贴放置流程启动结果', {
    started,
    sourceNodeId: handle.sourceNodeId,
    copiedNodeId: handle.copiedNodeId,
    previewObjectId: handle.previewObject.uuid,
  });
  if (!started) {
    pendingAssemblyReferenceCopyHandle.value = null;
    message.error('进入复制放置模式失败');
  }

  return true;
}

/**
 * 处理装配体引用复制快捷键。
 *
 * @param event 键盘事件。
 * @returns 快捷键是否已被消费。
 */
function handleEditorAssemblyReferenceCopyKeyDown(event: KeyboardEvent): boolean {
  if (isEditableKeyboardEventTarget(event.target)) {
    return false;
  }

  const modifierPressed = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  const isCopyShortcut = modifierPressed && !event.altKey && key === 'c';
  const isPasteShortcut = modifierPressed && !event.altKey && key === 'v';

  if (!isCopyShortcut && !isPasteShortcut) {
    return false;
  }

  logger.info('[Editor] 装配体引用复制：捕获复制粘贴快捷键', {
    key,
    isCopyShortcut,
    isPasteShortcut,
    activeEditorTabType: activeEditorTabType.value,
    viewerMode: viewerMode.value,
    selectedKeys: [...selectedKeys.value],
    selectedObjectId: selectedObject.value?.id ?? null,
    viewportSelectedObjectIds: getViewer()?.getSelectedObjectIds() ?? [],
    cachedSourceObjectId: assemblyReferenceCopyClipboardObjectId.value,
  });

  if (activeEditorTabType.value !== 'assembly') {
    logger.info('[Editor] 装配体引用复制：非装配体标签页，跳过快捷键', {
      activeEditorTabType: activeEditorTabType.value,
    });
    return false;
  }

  event.preventDefault();

  if (isCopyShortcut) {
    if (guardReadonlyPreviewEditing()) {
      logger.warn('[Editor] 装配体引用复制：只读预览态阻止复制');
      return true;
    }

    const selectedObjectId = resolveSelectedAssemblyReferenceCopyObjectId();
    logger.info('[Editor] 装配体引用复制：执行复制', {
      selectedObjectId,
      selectedKeys: [...selectedKeys.value],
      selectedObjectSummaryId: selectedObject.value?.id ?? null,
      viewportSelectedObjectIds: getViewer()?.getSelectedObjectIds() ?? [],
    });
    if (!selectedObjectId) {
      message.warning('请先选择装配体零部件');
      return true;
    }

    assemblyReferenceCopyClipboardObjectId.value = selectedObjectId;
    return true;
  }

  const sourceObjectId = resolveSelectedAssemblyReferenceCopyObjectId()
    ?? assemblyReferenceCopyClipboardObjectId.value;
  logger.info('[Editor] 装配体引用复制：执行粘贴', {
    sourceObjectId,
    cachedSourceObjectId: assemblyReferenceCopyClipboardObjectId.value,
    selectedKeys: [...selectedKeys.value],
    selectedObjectSummaryId: selectedObject.value?.id ?? null,
    viewportSelectedObjectIds: getViewer()?.getSelectedObjectIds() ?? [],
  });
  return startAssemblyReferenceCopyPlacement(sourceObjectId);
}
//------ 装配体引用复制快捷键 结束------

/**
 * 判断当前是否处于特征 PropertyManager 命令会话。
 *
 * 设计说明：
 * 拉伸、旋转、圆角、倒角等属性面板在 OK 前属于命令内预览状态，不应让全局 Ctrl+Z
 * 直接回退模型历史栈，否则会撤销上一条“创建特征”事务，表现为特征被删除。
 *
 * @returns 是否应阻止全局模型撤销重做。
 */
function isFeaturePropertyManagerUndoRedoGuardActive(): boolean {
  return isExtrudeMode.value
    || revolveUiState.value.active
    || revolveCutUiState.value.active
    || filletUiState.value.active
    || chamferUiState.value.active;
}

/**
 * 在历史操作队列中执行模型 undo / redo。
 *
 * 设计说明：
 * 撤销重做只存在于当前页面会话，执行时由 cad-core 会话历史管理器从内存热栈
 * 或 IndexedDB/OPFS 冷存储读取目标事务，再切换场景状态。
 *
 * @param shouldUndo 是否执行 undo；否则执行 redo。
 */
async function applyQueuedEditorModelUndoRedo(shouldUndo: boolean): Promise<void> {
  const historyStateBeforeApply = editorModelHistoryRuntime.getState();
  if (shouldUndo ? !historyStateBeforeApply.canUndo : !historyStateBeforeApply.canRedo) {
    return;
  }

  await editorHistoryFeatureTreeRefreshCoordinator.run(async () => {
    clearEditorPendingFeatureHistoryRestores();
    clearEditorPendingFeatureRollbackHistoryRestore();
    clearEditorPendingAssemblyStructureHistoryRestore();
    try {
      const transaction = shouldUndo
        ? await editorModelHistoryRuntime.undo()
        : await editorModelHistoryRuntime.redo();

      if (!transaction) {
        return;
      }

      await flushEditorAssemblyStructureHistoryRestore(shouldUndo ? 'undo' : 'redo');
      await flushEditorFeatureHistoryRestores(transaction, shouldUndo ? 'undo' : 'redo');
      await flushEditorFeatureRollbackHistoryRestore();
      await getViewer()?.flushDatumPlaneFeatureRebuild();
      // 所有异步派生恢复已完成；不在领域快照重绘之外再强制提交同步帧。
      getViewer()?.requestRender();
    } finally {
      clearEditorPendingFeatureHistoryRestores();
      clearEditorPendingFeatureRollbackHistoryRestore();
      clearEditorPendingAssemblyStructureHistoryRestore();
    }
  });
}

/**
 * 处理全局模型 undo / redo 快捷键。
 *
 * @param event 键盘事件。
 * @returns 是否已经消费该事件。
 */
function handleEditorModelUndoRedoKeyDown(event: KeyboardEvent): boolean {
  if (isEditableKeyboardEventTarget(event.target)) {
    return false;
  }

  const modifierPressed = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  const shouldUndo = modifierPressed && key === 'z' && !event.shiftKey;
  const shouldRedo = modifierPressed && (key === 'y' || (key === 'z' && event.shiftKey));

  if (!shouldUndo && !shouldRedo) {
    return false;
  }

  event.preventDefault();
  if (guardReadonlyPreviewEditing()) {
    return true;
  }

  if (shouldUndo && isDrawingMode.value && handleDrawingUndoPoint()) {
    return true;
  }
  if (isFeaturePropertyManagerUndoRedoGuardActive()) {
    return true;
  }

  void editorModelHistoryRuntime.runExclusive(() => applyQueuedEditorModelUndoRedo(shouldUndo))
    .catch((error) => {
      reportEditorAsyncFailure(error, '执行撤销重做失败');
    });

  return true;
}

/**
 * 处理拉伸/旋转 PropertyManager 的 Enter 确认。
 *
 * @param event 键盘事件。
 * @returns 是否已消费事件。
 */
function handleProfileFeatureConfirmKeyDown(event: KeyboardEvent): boolean {
  if (
    event.key !== 'Enter'
    || event.isComposing
    || event.ctrlKey
    || event.metaKey
    || event.altKey
    || event.shiftKey
  ) {
    return false;
  }
  if (!resolveActiveProfileFeatureCommitDescriptor()) {
    return false;
  }
  if (isEditableKeyboardEventTarget(event.target)) {
    return false;
  }
  event.preventDefault();
  event.stopPropagation();
  void confirmActiveProfileFeatureWithLoading();
  return true;
}

/**
 * 编辑器统一键盘入口。
 *
 * @param event 键盘事件。
 */
function handleEditorKeyDown(event: KeyboardEvent): void {
  if (handleEditorModelUndoRedoKeyDown(event)) {
    return;
  }

  if (handleEditorAssemblyReferenceCopyKeyDown(event)) {
    return;
  }

  if (isReadonlyPreviewSketchWorkflowKey(event) && guardReadonlyPreviewEditing()) {
    event.preventDefault();
    return;
  }
  if (event.key === 'Escape' && handleEvaluationEscKey()) {
    return;
  }

  handleSketchWorkflowKeyDown(event);
}

onMounted(async () => {
  //------ 命令结束显示设计树 开始------
  showDesignTreeRequestSubscription = useAddEvent(
    EditorPanelEventName.ShowDesignTreeRequested,
    handleShowDesignTreeRequest,
  );
  //------ 命令结束显示设计树 结束------
  //------ 视口选择显示属性面板 开始------
  showPropertyRequestSubscription = useAddEvent(
    EditorPanelEventName.ShowPropertyRequested,
    handleShowPropertyRequest,
  );
  //------ 视口选择显示属性面板 结束------
  resetEditorSketchRuntimeState(resolveEditorIdleViewerModeAfterHistoryRestore());
  await startFreshEditorHistorySession();
  if (isEditorUnmounted) {
    return;
  }
  configureEditorSketchSolverHistoryRuntime();
  await loadEditorPreferences();
  if (isEditorUnmounted) {
    return;
  }
  const initialLoadContext = beginEditorInitialLoad();
  editorModelLoadingCoordinator.show('正在打开项目', INITIAL_PROJECT_MODEL_LOADING_OWNER);
  try {
    await initViewportRendererInEditor();
    if (!isActiveEditorInitialLoad(initialLoadContext.requestId)) {
      return;
    }
    //------ 智能尺寸命令接入 开始------
    configureEditorSmartDimensionRuntime();
    //------ 智能尺寸命令接入 结束------
    // 首屏失败由外层统一处理，不能将已报错的加载当作成功并启动新手引导。
    await loadProjectOrThrow(initialLoadContext);
    if (!isActiveEditorInitialLoad(initialLoadContext.requestId)) {
      return;
    }
    await glbLoader.loadDefaultTab();
    await loadInitialOpenProjectAuxiliaryData(initialLoadContext);
  } catch (error) {
    if (isEditorInitialLoadCanceled(error, initialLoadContext)) {
      return;
    }
    reportEditorAsyncFailure(error, '加载项目失败');
    return;
  } finally {
    releaseEditorInitialLoad(initialLoadContext);
  }
  if (!isActiveEditorInitialLoad(initialLoadContext.requestId)) {
    return;
  }
  // 默认标签真实挂载后再补一次实例级对齐，避免异步加载链把模式回写成 default。
  syncEditorIdleViewerModeFromRoute();
  window.addEventListener('keydown', handleProfileFeatureConfirmKeyDown, true);
  //------ 装配体引用复制快捷键 开始------
  window.addEventListener('keydown', handleEditorKeyDown);
  //------ 装配体引用复制快捷键 结束------
  window.addEventListener('pagehide', handleEditorHistoryPageHide);
  isOnBoardingReady.value = true;
  await onBoarding.startIfNeeded();
});

onUnmounted(() => {
  isEditorUnmounted = true;
  editorHistoryFeatureTreeRefreshCoordinator.dispose();
  onBoarding.dispose();
  projectRootCommitReloadRequestId += 1;
  cancelActiveEditorInitialLoad();
  cancelActiveWorkspaceWriteRequest();
  handleEditorSidePanelResizePointerUp();
  //------ 装配体引用复制放置 开始------
  viewportContainerRef.value?.removeEventListener('pointerdown', handleViewportContainerPointerPositionForPlacement);
  viewportContainerRef.value?.removeEventListener('pointermove', handleViewportContainerPointerPositionForPlacement);
  //------ 装配体引用复制放置 结束------
  versionWorkspaceController.dispose();
  versionWorkspaceStore.reset();
  resetEditorModelHistorySessionState();
  void clearEditorPersistedHistoryStorage().catch((error) => {
    reportEditorAsyncFailure(error, '卸载编辑器时清理撤销重做历史持久化数据失败');
  });
  disposeEditorSketchSolverHistoryRuntime?.();
  disposeEditorSketchSolverHistoryRuntime = null;
  //------ 智能尺寸命令接入 开始------
  disposeEditorSmartDimensionSnapshotSubscription?.();
  disposeEditorSmartDimensionSnapshotSubscription = null;
  //------ 智能尺寸命令接入 结束------
  blendPreviewGuideOverlay.dispose();
  //------ 命令结束显示设计树 开始------
  showDesignTreeRequestSubscription?.unsubscribe();
  showDesignTreeRequestSubscription = null;
  //------ 命令结束显示设计树 结束------
  //------ 视口选择显示属性面板 开始------
  showPropertyRequestSubscription?.unsubscribe();
  showPropertyRequestSubscription = null;
  //------ 视口选择显示属性面板 结束------
  resetEditorSketchRuntimeState(ViewerMode.Default);
  glbLoader.dispose();
  releaseCurrentEditorViewer();
  assemblyWorkflow.dispose();
  window.removeEventListener('keydown', handleProfileFeatureConfirmKeyDown, true);
  //------ 装配体引用复制快捷键 开始------
  window.removeEventListener('keydown', handleEditorKeyDown);
  //------ 装配体引用复制快捷键 结束------
  window.removeEventListener('pagehide', handleEditorHistoryPageHide);
});
</script>

<style scoped>
.model-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  backdrop-filter: blur(2px);
  z-index: 20;
}

.model-loading-text {
  font-size: 13px;
}

</style>
